import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  Building2,
  Layers,
  Users,
  LayoutDashboard, 
  Palette, 
  Calendar as CalendarIcon, 
  Settings, 
  Plus, 
  Camera,
  CheckCircle2, 
  XCircle,
  Clock, 
  AlertCircle, 
  LogOut, 
  ChevronRight, 
  ChevronLeft, 
  Sparkles, 
  Image as ImageIcon, 
  Edit3, 
  Edit2,
  Save, 
  Send,
  Facebook,
  Linkedin,
  Instagram,
  Twitter,
  Check,
  X,
  Menu,
  Upload,
  Loader2,
  Download,
  Share2,
  Youtube,
  Layout,
  Box,
  FileText,
  Globe,
  Share,
  RefreshCw,
  RotateCcw,
  Trash2,
  HelpCircle,
  Info,
  Copy,
  Link as LinkIcon,
  Zap,
  Shield,
  ShieldAlert,
  HardDrive,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  ArrowRightLeft,
  Star,
  CreditCard,
  Lock,
  Search,
  Type as TypeIcon,
  Eye,
  ExternalLink,
  MessageSquare,
  CheckSquare,
  Square,
  BarChart3,
  Inbox,
  Coins,
  History,
  Archive,
  Monitor,
  AlertTriangle,
  ShieldCheck,
  ListFilter,
  Images,
  FileCode,
  FileImage,
  Sliders,
  Calendar
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import axios from 'axios';
import { POPULAR_GOOGLE_FONTS } from './lib/fonts';
import { auth, db, signIn, logOut, storage } from './firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  getDocs,
  deleteDoc,
  collection, 
  query, 
  where, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  orderBy,
  limit,
  getDocFromServer,
  increment,
  runTransaction
} from 'firebase/firestore';

// Connection test
async function testFirebaseConnection() {
  try {
    const testDoc = doc(db, 'test', 'connection');
    await getDocFromServer(testDoc);
    console.log("[Firebase] Connection test successful (from server).");
  } catch (error: any) {
    if (error.message?.includes('the client is offline')) {
      console.warn("[Firebase] Client is offline - check project config or internet.");
    }
  }
}
testFirebaseConnection();

import { ref, uploadBytes, getDownloadURL, uploadString, deleteObject } from 'firebase/storage';
import { BrandProfile, MonthlyPlan, ContentIdea, ContentSuggestion, ShareFeedback } from './types';
import { generateMonthlyCalendar, generateArtwork, generateVideo, analyzeInspirationImage, generateWebsiteContext, extractBrandProfileFromContext, analyzeWebsiteScreenshot, generateSinglePost, refineContent, reflowCarousel, refineMonthlyPlan, getApiKey, setSecureConfig, extractPersonality, generatePanoramaBackgroundConcept, analyzeAndProcessBrandTemplate } from './lib/gemini';
import { logError } from './lib/logger';
import { compressBase64Image, generateThumbnail } from './lib/utils';
import { SlideLayoutEditorModal } from './components/SlideLayoutEditorModal';
import * as htmlToImage from 'html-to-image';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns';
import Markdown from 'react-markdown';
import { useTranslation } from './hooks/useTranslation';

import { ShareView } from './components/ShareView';
import { ConnectPortal } from './components/ConnectPortal';
import { ShareModal } from './components/ShareModal';
import { ReviewFeedbackView } from './components/ReviewFeedbackView';
import { TrendsManager } from './components/TrendsManager';

import { ErrorLogsView } from './components/ErrorLogsView';
import { BlueprintEngine } from './components/BlueprintEngine';
import { Routes, Route, Link, useLocation, Navigate, useNavigate, useParams } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import Header from './components/Header';
import Footer from './components/Footer';
import Features from './pages/Features';
import Pricing from './pages/Pricing';
import HowItWorks from './pages/HowItWorks';
import FAQ from './pages/FAQ';
import About from './pages/About';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AIPostGenerator from './pages/features/AIPostGenerator';
import SocialMediaCalendar from './pages/features/SocialMediaCalendar';
import AICaptionGenerator from './pages/features/AICaptionGenerator';
import PostScheduler from './pages/features/PostScheduler';
import BrandVoice from './pages/features/BrandVoice';
import InstagramPage from './pages/platforms/Instagram';
import LinkedInPage from './pages/platforms/LinkedIn';
import TikTokPage from './pages/platforms/TikTok';
import FacebookPage from './pages/platforms/Facebook';
import XTwitterPage from './pages/platforms/XTwitter';
import SmallBusiness from './pages/use-cases/SmallBusiness';
import Agencies from './pages/use-cases/Agencies';
import Creators from './pages/use-cases/Creators';
import MarketingTeams from './pages/use-cases/MarketingTeams';
import AIForSMBs from './pages/blog/AIForSMBs';
import SocialMediaTrends2026 from './pages/blog/SocialMediaTrends2026';
import HowToScaleAgency from './pages/blog/HowToScaleAgency';

import { Blog } from './pages/Blog';
import { Showcase } from './pages/Showcase';
import { ShowcaseDetail } from './pages/ShowcaseDetail';
import { DataDeletion } from './pages/DataDeletion';
import { AdminImpersonate } from './pages/AdminImpersonate';
import SEO from './components/SEO';
import Home from './pages/Home';
import AuthModal from './components/AuthModal';

// --- Error Handling ---

// --- Components ---

async function getFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 32); 
}

async function getBase64Hash(base64: string): Promise<string> {
  // Extract data part if it's a data URL
  const data = base64.includes(',') ? base64.split(',')[1] : base64;
  const buffer = new TextEncoder().encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 32);
}

// Cleanup helper
const safeDeleteFile = async (fileUrl: string | undefined | null) => {
  if (!fileUrl || !fileUrl.includes('firebasestorage.googleapis.com')) return;
  try {
    const fileRef = ref(storage, fileUrl);
    await deleteObject(fileRef);
  } catch (err) {
    // Ignore errors for already deleted files
    console.warn("Cleanup: File not found or couldn't be deleted", fileUrl);
  }
};

// Parse strings without timezone offset shifting
const parseLocalDate = (dateStr: string) => {
  if (!dateStr) return new Date();
  const parts = dateStr.substring(0, 10).split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // 0-based
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateStr);
};

import { 
  exportPlanToZip, 
  exportPlanToPDF 
} from './lib/exportUtils';

const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isQuotaError, setIsQuotaError] = useState(false);

  useEffect(() => {
    const handleError = (e: ErrorEvent) => {
      // Ignore benign errors that don't affect functionality
      if (e.message?.includes('WebSocket closed without opened') || 
          e.message?.includes('ResizeObserver loop limit exceeded') ||
          e.message?.includes('ResizeObserver loop completed with undelivered notifications')) {
        return;
      }

      setHasError(true);
      
      try {
        // Try to parse as JSON (from handleFirestoreError)
        const parsedError = JSON.parse(e.message);
        if (parsedError && parsedError.error) {
          if (parsedError.error.includes('Quota limit exceeded') || parsedError.error.includes('Quota exceeded')) {
            setIsQuotaError(true);
            setError("Your Firestore database has reached a quota or budget limit. If you are on the Spark plan, this will reset tomorrow. If you are on the Blaze plan, please check your Google Cloud budget caps or billing status.");
          } else {
            setError(parsedError.error);
          }
        } else {
          setError(e.message);
        }
      } catch (err) {
        // Not JSON
        if (e.message && (e.message.includes('Quota limit exceeded') || e.message.includes('Quota exceeded'))) {
          setIsQuotaError(true);
          setError("Your Firestore database has reached a quota or budget limit. If you are on the Spark plan, this will reset tomorrow. If you are on the Blaze plan, please check your Google Cloud budget caps or billing status.");
        } else {
          setError(e.message);
        }
      }
    };
    
    const handlePromiseRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason?.message || String(e.reason);

      // Ignore benign errors
      if (reason?.includes('WebSocket closed without opened') || 
          reason?.includes('ResizeObserver loop limit exceeded') ||
          reason?.includes('ResizeObserver loop completed with undelivered notifications')) {
        return;
      }

      setHasError(true);
      
      try {
        const parsedError = JSON.parse(reason);
        if (parsedError && parsedError.error) {
          if (parsedError.error.includes('Quota limit exceeded') || parsedError.error.includes('Quota exceeded')) {
            setIsQuotaError(true);
            setError("Your Firestore database has reached a quota or budget limit. If you are on the Spark plan, this will reset tomorrow. If you are on the Blaze plan, please check your Google Cloud budget caps or billing status.");
          } else {
            setError(parsedError.error);
          }
        } else {
          setError(reason);
        }
      } catch (err) {
        if (reason && (reason.includes('Quota limit exceeded') || reason.includes('Quota exceeded'))) {
          setIsQuotaError(true);
          setError("Your Firestore database has reached a quota or budget limit. If you are on the Spark plan, this will reset tomorrow. If you are on the Blaze plan, please check your Google Cloud budget caps or billing status.");
        } else {
          setError(reason);
        }
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handlePromiseRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handlePromiseRejection);
    };
  }, []);

  if (hasError) {
    const handleExitImpersonation = () => {
      localStorage.removeItem('impersonatedUserId');
      window.location.reload();
    };

    const handleLogout = async () => {
      localStorage.removeItem('impersonatedUserId');
      await logOut();
      window.location.href = '/';
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 font-sans">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
          {isQuotaError ? (
            <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-orange-500" />
            </div>
          ) : (
            <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          )}
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {isQuotaError ? 'Daily Limit Reached' : 'Application Error'}
          </h2>
          <div className="bg-gray-50 rounded-lg p-3 mb-6 border border-gray-100">
            <p className="text-gray-600 text-sm break-words overflow-hidden text-ellipsis italic">
              {error || 'An unexpected error occurred.'}
            </p>
          </div>
          
          <div className="space-y-3">
            <button 
              onClick={() => window.location.reload()}
              className={`w-full text-white py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 ${
                isQuotaError ? 'bg-orange-500 hover:bg-orange-600' : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              <RefreshCw className="w-4 h-4" />
              Reload Application
            </button>

            {localStorage.getItem('impersonatedUserId') && (
              <button 
                onClick={handleExitImpersonation}
                className="w-full bg-white text-gray-700 border border-gray-200 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Exit Impersonation Mode
              </button>
            )}

            <button 
              onClick={handleLogout}
              className="w-full bg-white text-red-600 border border-red-100 py-3 rounded-xl font-semibold hover:bg-red-50 transition-colors"
            >
              Sign Out
            </button>
          </div>
          
          <p className="mt-6 text-xs text-gray-400">
            If the issue persists, please contact support with the error details above.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const LoadingScreen = ({ message = "Loading..." }: { message?: string }) => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
    <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
    <p className="text-indigo-900 font-medium animate-pulse">{message}</p>
  </div>
);

const DashboardNavbar = ({ 
  user, 
  brand, 
  onSignOut, 
  onOpenCommand, 
  isAdmin, 
  isTrial,
  userRole,
  isCollaborator,
  onSignIn,
  isSigningIn,
  tokens,
  userStats,
  setActiveTab,
  activeTab,
  brands,
  activeBrandId,
  setActiveBrandId,
  onNewBrand
}: { 
  user: User | null, 
  brand?: BrandProfile | null, 
  onSignOut?: () => void, 
  onOpenCommand?: () => void, 
  isAdmin?: boolean, 
  isTrial?: boolean,
  userRole?: string,
  isCollaborator?: boolean,
  onSignIn?: () => void,
  isSigningIn?: boolean,
  tokens: number,
  userStats?: { totalBrands: number, totalPlans: number, totalPosts: number, storageMB: number, estimatedBytes: number },
  setActiveTab?: (tab: any) => void,
  activeTab?: any,
  brands?: BrandProfile[],
  activeBrandId?: string | null,
  setActiveBrandId?: (id: string | null) => void,
  onNewBrand?: () => void
}) => {
  const { t, isBrazil } = useTranslation();
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isImpersonating = !!localStorage.getItem('impersonatedUserId');

  return (
    <div className={`sticky ${isImpersonating ? 'top-[56px]' : 'top-0'} z-50 flex flex-col transition-all duration-300`}>
      <nav className="bg-white border-b border-gray-100 shadow-sm px-6 py-4 transition-all duration-300">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-10 h-10 flex items-center justify-center group-hover:rotate-12 transition-transform duration-300 overflow-hidden">
                <img src="/PlataPost-P.png" alt="P" className="w-10 h-10 object-contain" />
              </div>
              <span className="text-xl font-bold tracking-tight text-gray-900">PlataPost</span>
            </Link>
          </div>
          
          {!user && (
            <div className="hidden lg:flex items-center justify-center gap-8 absolute left-1/2 -translate-x-1/2">
              <Link to="/" className="text-sm font-bold text-gray-600 hover:text-indigo-600 transition-colors">{isBrazil ? "Início" : "Home"}</Link>
              <a href="/#features" className="text-sm font-bold text-gray-600 hover:text-indigo-600 transition-colors">{t.features}</a>
              <a href="/#pricing" className="text-sm font-bold text-gray-600 hover:text-indigo-600 transition-colors">{t.pricing}</a>
              <Link to="/blog" className="text-sm font-bold text-gray-600 hover:text-indigo-600 transition-colors">{t.blog}</Link>
              {isAdmin && <Link to="/showcase" className="text-sm font-bold text-gray-600 hover:text-indigo-600 transition-colors">{t.portfolio}</Link>}
            </div>
          )}

          <div className="flex items-center gap-4">
            {user ? (
              <>
                <button 
                  onClick={onOpenCommand}
                  className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100 rounded-full border transition-colors text-sm font-medium"
                >
                  <Search className="w-4 h-4" />
                  <span>{t.search}</span>
                </button>
                {isImpersonating && (
                  <button 
                    onClick={() => {
                      localStorage.removeItem('impersonatedUserId');
                      window.location.reload();
                    }}
                    className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-700 rounded-full border border-red-200 hover:bg-red-100 transition-colors text-xs font-bold"
                  >
                    <Shield className="w-4 h-4" />
                    {t.stopImpersonation}
                  </button>
                )}
                {user && (
                  <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-full border border-indigo-100" title={t.aiTokens}>
                    <Zap className="w-4 h-4" />
                    <span className="text-xs font-bold">
                      {isAdmin ? '∞' : tokens}
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-3 px-3 py-1.5 bg-gray-50 border-gray-100 rounded-full border">
                  {user.photoURL ? (
                    <img src={user.photoURL} alt={user.displayName || ''} className="w-8 h-8 rounded-full border border-indigo-100" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-bold border border-indigo-200">
                      {user.displayName?.charAt(0) || 'U'}
                    </div>
                  )}
                  <span className="text-sm font-medium hidden sm:inline text-gray-700">{user.displayName}</span>
                </div>
                {isAdmin && (
                  <Link 
                    to="/admin/impersonate" 
                    className="hidden sm:flex p-2 rounded-lg transition-all text-gray-500 hover:text-indigo-600 hover:bg-indigo-50"
                    title="Impersonate User"
                  >
                    <Shield className="w-5 h-5" />
                  </Link>
                )}
                <button 
                  onClick={onSignOut}
                  className="p-2 rounded-lg transition-all text-gray-500 hover:text-red-600 hover:bg-red-50"
                  title={t.logout}
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <button 
                  onClick={onSignIn}
                  disabled={isSigningIn}
                  className="hidden sm:block px-4 py-2 text-sm font-bold transition-colors text-gray-600 hover:text-indigo-600 disabled:opacity-50 flex items-center gap-2"
                >
                  {isSigningIn && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSigningIn ? (isBrazil ? 'Entrando...' : 'Signing in...') : t.login}
                </button>
                <button 
                  onClick={onSignIn}
                  className="px-6 py-2.5 bg-indigo-600 text-white rounded-full text-sm font-bold hover:bg-indigo-500 transition-all shadow-lg shadow-indigo-600/20 flex items-center gap-2"
                >
                  {t.startFreeTrial}
                </button>
              </div>
            )}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-50"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-gray-100 bg-white overflow-y-auto max-h-[80vh] mt-4"
            >
              <div className="flex flex-col gap-2 py-4">
                {!user && (
                  <>
                    <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="px-4 py-3 text-sm font-bold transition-colors text-gray-900 hover:bg-gray-50">{isBrazil ? "Início" : "Home"}</Link>
                    <a href="/#features" onClick={() => setIsMobileMenuOpen(false)} className="px-4 py-3 text-sm font-bold transition-colors text-gray-900 hover:bg-gray-50">{t.features}</a>
                    <a href="/#pricing" onClick={() => setIsMobileMenuOpen(false)} className="px-4 py-3 text-sm font-bold transition-colors text-gray-900 hover:bg-gray-50">{t.pricing}</a>
                    <Link to="/blog" onClick={() => setIsMobileMenuOpen(false)} className="px-4 py-3 text-sm font-bold transition-colors text-gray-900 hover:bg-gray-50">{t.blog}</Link>
                    {isAdmin && <Link to="/showcase" onClick={() => setIsMobileMenuOpen(false)} className="px-4 py-3 text-sm font-bold transition-colors text-gray-900 hover:bg-gray-50">{t.portfolio}</Link>}
                    <button 
                      onClick={() => { setIsMobileMenuOpen(false); onSignIn?.(); }} 
                      className="mx-4 mt-2 px-6 py-3 bg-indigo-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2 text-center w-auto"
                    >
                      {t.startFreeTrial}
                    </button>
                  </>
                )}
                {user && setActiveTab && (
                  <div className="flex flex-col gap-1 py-2">
                    <div className="px-4 py-2 border-b border-gray-100 mb-2 flex items-center justify-between">
                      <span className="text-xs font-black uppercase tracking-wider text-gray-400">
                        {isBrazil ? "Painel de Controle" : "Dashboard Menu"}
                      </span>
                      {tokens !== undefined && (
                        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full border border-indigo-100">
                          <Zap className="w-3.5 h-3.5" />
                          <span className="text-xs font-bold">
                            {isAdmin ? '∞' : tokens}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Brand Selection for Mobile view */}
                    {brands && brands.length > 0 && setActiveBrandId && (
                      <div className="px-4 py-2 border-b border-gray-100 mb-3 flex flex-col gap-2">
                        <span className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-400">
                          {isBrazil ? "Marca Ativa" : "Active Brand"}
                        </span>
                        
                        <div className="space-y-1.5 max-h-[160px] overflow-y-auto pr-1 scrollbar-thin">
                          {brands.filter(b => b && b.id).map(b => (
                            <button
                              key={b.id}
                              onClick={() => {
                                setActiveBrandId(b.id || null);
                                localStorage.setItem('activeBrandId', b.id || '');
                                setIsMobileMenuOpen(false);
                              }}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all text-left ${
                                activeBrandId === b.id 
                                  ? 'bg-indigo-600 text-white shadow-sm' 
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${
                                activeBrandId === b.id ? 'bg-white/20' : 'bg-indigo-100 text-indigo-600'
                              }`}>
                                {b.companyName?.charAt(0) || 'B'}
                              </div>
                              <span className="text-sm font-bold truncate text-left flex-1">{b.companyName || (isBrazil ? 'Marca Sem Nome' : 'Unnamed Brand')}</span>
                            </button>
                          ))}
                          
                          {onNewBrand && (
                            <button
                              onClick={() => {
                                onNewBrand();
                                setIsMobileMenuOpen(false);
                              }}
                              className="w-full flex items-center gap-3 px-3 py-1.5 rounded-xl text-indigo-600 hover:bg-indigo-50 transition-all border border-dashed border-indigo-200 mt-1"
                            >
                              <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                                <Plus className="w-3.5 h-3.5" />
                              </div>
                              <span className="text-sm font-bold text-left">{t.newBrand}</span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {[
                      { id: 'dashboard', label: t.dashboard, icon: <LayoutDashboard className="w-4 h-4" /> },
                      { id: 'create-plan', label: t.createMonthlyPlan, icon: <Sparkles className="w-4 h-4" /> },
                      { id: 'brand', label: t.brandProfile, icon: <Palette className="w-4 h-4" /> },
                      { id: 'plans', label: t.contentPlans, icon: <CalendarIcon className="w-4 h-4" /> },
                      ...(user?.email?.toLowerCase() === 'dewaldjordaan5@gmail.com' || user?.email?.toLowerCase() === 'dewald.jordan@sparkpr.com'
                        ? [{ id: 'trends', label: isBrazil ? "Tendências e Foco Estratégico" : "Trends & Strategic Focus", icon: <Sparkles className="w-4 h-4" /> }]
                        : []
                      ),
                      ...(isAdmin ? [{ id: 'bookings', label: t.calendarBookings, icon: <CalendarIcon className="w-4 h-4" /> }] : []),
                      { id: 'engagement', label: t.insights, icon: <BarChart3 className="w-4 h-4" /> },
                      { id: 'inbox', label: t.socialInbox, icon: <Inbox className="w-4 h-4" /> },
                      { id: 'review', label: isBrazil ? "Espaço de Revisão" : "Client Review Space", icon: <MessageSquare className="w-4 h-4" /> },
                      { id: 'settings', label: t.settings, icon: <Settings className="w-4 h-4" /> },
                      ...(user?.email === 'dewaldjordaan5@gmail.com'
                        ? [{ id: 'admin', label: t.adminStats, icon: <Shield className="w-4 h-4" /> }]
                        : []
                      ),
                    ].map((item) => {
                      const isActive = activeTab === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => {
                            if (!isAdmin && isTrial) {
                              if (item.id === 'review') {
                                alert("Client Review Space is not available on the trial tier. Please upgrade.");
                                setActiveTab?.('settings');
                                setIsMobileMenuOpen(false);
                                return;
                              }
                              if (item.id === 'inbox') {
                                alert("Social Inbox is only available on paid subscriptions. Please upgrade to access this feature.");
                                setActiveTab?.('settings');
                                setIsMobileMenuOpen(false);
                                return;
                              }
                              if (item.id === 'engagement') {
                                const createdAt = brand?.createdAt ? new Date(brand.createdAt) : new Date();
                                const now = new Date();
                                const diffDays = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 3600 * 24));
                                if (diffDays > 31) {
                                  alert("Social Insights are only available for the first 30 days of the free trial. Please upgrade to maintain access.");
                                  setActiveTab?.('settings');
                                  setIsMobileMenuOpen(false);
                                  return;
                                }
                              }
                            }
                            setActiveTab?.(item.id as any);
                            setIsMobileMenuOpen(false);
                          }}
                          className={`flex items-center gap-3 px-4 py-3 text-sm font-bold text-left transition-all cursor-pointer ${
                            isActive 
                              ? 'bg-indigo-600 text-white rounded-xl mx-2 shadow-sm' 
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </div>
  );
};

const DashboardFooter = ({ 
  user, 
  setActiveTab,
  isAdmin,
  onSignIn
}: { 
  user: User | null, 
  setActiveTab: (tab: any) => void,
  isAdmin: boolean,
  onSignIn?: () => void
}) => {
  const { t, lang, setLanguage, isBrazil } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  
  const handleTabClick = (tab: 'dashboard' | 'brand' | 'plans' | 'settings' | 'admin') => {
    if (user) {
      navigate('/dashboard');
      setActiveTab(tab);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#000103] border-t border-white/5 shadow-[0_-1px_3px_rgba(0,0,0,0.05)] py-12 px-6 lg:px-10 mt-auto transition-colors duration-300 text-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center overflow-hidden">
                <img src="/PlataPost-P.png" alt="P" className="w-8 h-8 object-contain" />
              </div>
              <span className="text-lg font-bold text-white">PlataPost</span>
            </div>
            <p className="text-sm leading-relaxed text-gray-400">
              {isBrazil 
                ? "Empoderando marcas com estratégias de rede social impulsionadas por IA e geração de conteúdo de alta qualidade."
                : "Empowering brands with AI-driven social media strategies and high-quality content generation."}
            </p>

            {/* Language Switcher */}
            <div className="pt-4 border-t border-white/5">
              <div className={`flex items-center gap-2 mb-3 ${isLandingPage ? 'text-gray-500' : 'text-gray-400'}`}>
                <Globe className="w-4 h-4" />
                <span className="text-[10px] font-black uppercase tracking-widest">{t.language}</span>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => setLanguage('en')}
                  className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${lang === 'en' ? 'bg-indigo-600 text-white shadow-md' : 'bg-brand-dark text-gray-400 border border-white/10 hover:border-indigo-600'}`}
                >
                  English
                </button>
                <button 
                  onClick={() => setLanguage('br')}
                  className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${lang === 'br' ? 'bg-indigo-600 text-white shadow-md' : 'bg-brand-dark text-gray-400 border border-white/10 hover:border-indigo-600'}`}
                >
                  Português
                </button>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-bold mb-4 text-sm uppercase tracking-widest text-white">{t.platform}</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-sm transition-colors text-gray-400 hover:text-indigo-600">{isBrazil ? "Início" : "Home"}</Link></li>
              <li><button onClick={() => handleTabClick('dashboard')} className="text-sm transition-colors text-left text-gray-400 hover:text-indigo-600">{t.dashboard}</button></li>
              <li><button onClick={() => handleTabClick('plans')} className="text-sm transition-colors text-left text-gray-400 hover:text-indigo-600">{t.contentPlans}</button></li>
              <li><button onClick={() => handleTabClick('settings')} className="text-sm transition-colors text-left text-gray-400 hover:text-indigo-600">{isBrazil ? "Assinatura" : "Subscription"}</button></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-4 text-sm uppercase tracking-widest text-white">{t.resources}</h4>
            <ul className="space-y-2">
              <li><Link to="/blog" className="text-sm transition-colors text-gray-400 hover:text-indigo-600">{t.blog}</Link></li>
              {isAdmin && <li><Link to="/showcase" className="text-sm transition-colors text-gray-400 hover:text-indigo-600">{t.portfolio}</Link></li>}
              <li><button onClick={() => { if(!user) onSignIn?.(); else navigate('/dashboard'); }} className="text-sm transition-colors text-gray-400 hover:text-indigo-600">{t.startFreeTrial}</button></li>
              <li><button onClick={() => window.open('https://github.com', '_blank')} className="text-sm transition-colors text-gray-400 hover:text-indigo-600">{t.documentation}</button></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-4 text-sm uppercase tracking-widest text-white">{t.company}</h4>
            <ul className="space-y-2">
              <li><Link to="/data-deletion" className="text-sm transition-colors text-gray-400 hover:text-indigo-600">{t.privacyPolicy}</Link></li>
              <li><button className="text-sm transition-colors text-gray-400 hover:text-indigo-600">{t.termsOfService}</button></li>
              <li><button className="text-sm transition-colors text-gray-400 hover:text-indigo-600">{t.contactUs}</button></li>
            </ul>
          </div>
        </div>
        
        <div className="mt-12 pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4 border-white/5">
          <p className="text-xs text-gray-500">© 2026 PlataPost AI. {t.rightsReserved}</p>
          <div className="flex items-center gap-6 text-gray-500">
            <button className="transition-colors hover:text-indigo-600"><Twitter className="w-4 h-4" /></button>
            <button className="transition-colors hover:text-indigo-600"><Linkedin className="w-4 h-4" /></button>
            <button className="transition-colors hover:text-indigo-600"><Instagram className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </footer>
  );
};

const AdminManagementBar = ({ 
  impersonatedUserId, 
  onExit 
}: { 
  impersonatedUserId: string, 
  onExit: () => void 
}) => {
  return (
    <motion.div 
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="h-[56px] bg-indigo-600 text-white px-6 py-0 flex items-center justify-between sticky top-0 z-[100] shadow-xl border-b border-indigo-700"
    >
      <div className="flex items-center gap-4">
        <div className="bg-white/20 p-2 rounded-xl">
          <ShieldAlert className="w-5 h-5" />
        </div>
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-80">Admin Viewing Mode</p>
          <p className="font-bold text-sm">Viewing Dashboard for User ID: <span className="font-mono bg-white/10 px-2 py-0.5 rounded">{impersonatedUserId}</span></p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <p className="text-[10px] font-medium opacity-60 italic hidden sm:block">Real-time data enabled. All edits will save to this user's profile.</p>
        <button 
          onClick={onExit}
          className="bg-white text-indigo-600 px-5 py-2 rounded-xl font-bold text-xs hover:bg-indigo-50 transition-all flex items-center gap-2 shadow-lg shadow-indigo-900/20"
        >
          <LogOut className="w-4 h-4" />
          Exit & Return to Admin
        </button>
      </div>
    </motion.div>
  );
};

// --- Main App ---

import { handleFirestoreError, OperationType } from './lib/firestore-errors';
import { CommandPalette } from './components/CommandPalette';

import { BookingPage } from './pages/BookingPage';

import { SocialInsights } from './components/SocialInsights';
import { SocialInbox } from './components/SocialInbox';
import { BookingsView } from './components/BookingsView';
import { CarouselTester } from './components/CarouselTester';
import { ApprovedMediaTab } from './components/ApprovedMediaTab';
import { HtmlOverlayLab } from './components/HtmlOverlayLab';
import { TemplateBakerLab, parseListLine, RenderListIcon, AutoFitText, parseOverlayTextToBoxes } from './components/TemplateBakerLab';

import { TranslationProvider } from './hooks/TranslationContext';
import { StorageCalculator } from './components/StorageCalculator';

export default function App() {
  const [isBlocked, setIsBlocked] = useState<boolean | null>(null);

  useEffect(() => {
    fetch('https://get.geojs.io/v1/ip/geo.json')
      .then(res => res.json())
      .then(data => {
        const blockedCountries = ['UA', 'IQ', 'CN', 'PK', 'AF'];
        localStorage.setItem('geo_data', JSON.stringify(data));
        if (blockedCountries.includes(data.country_code)) {
          setIsBlocked(true);
        } else {
          setIsBlocked(false);
        }
      })
      .catch(() => setIsBlocked(false));
  }, []);

  if (isBlocked === true) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white max-w-md p-8 rounded-2xl shadow-xl text-center border border-gray-100">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Service Unavailable</h1>
          <p className="text-gray-600">We are currently not accepting signups or traffic from your region.</p>
        </div>
      </div>
    );
  }

  return (
    <TranslationProvider>
      <AppContent />
    </TranslationProvider>
  );
}

function getDynamicColorDetails(colorHex: string, isBrazil: boolean) {
  let hex = (colorHex || '').trim().replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  
  let r = 0, g = 0, b = 0;
  if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  } else {
    r = 120; g = 120; b = 120;
  }

  const luma = 0.299 * r + 0.587 * g + 0.114 * b;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const chroma = max - min;
  const isVibrant = chroma > 42 && luma > 35 && luma < 215;

  if (luma < 85) {
    return {
      role: isBrazil ? "Tom Escuro" : "Dark Tone",
      desc: isBrazil ? "Ideal para textos principais, títulos importantes ou seções escuras." : "Perfect for principal text, major titles, or dark backgrounds."
    };
  } else if (luma >= 200) {
    return {
      role: isBrazil ? "Fundo Claro" : "Light BG",
      desc: isBrazil ? "Excelente para o fundo de páginas, caixas de conteúdo ou áreas claras." : "Best for page backgrounds, light-themed elements, or clean cards."
    };
  } else if (isVibrant) {
    return {
      role: isBrazil ? "Destaque" : "Brand Accent",
      desc: isBrazil ? "Ótimo para botões de ação, links importantes e pontos focais." : "Superb for call-to-actions, primary highlights, or focal design points."
    };
  } else {
    return {
      role: isBrazil ? "Tom de Apoio" : "Secondary Tone",
      desc: isBrazil ? "Ideal para subtítulos, detalhes secundários e elementos de apoio." : "Ideal for secondary subtitles, decoration, and supporting details."
    };
  }
}

function AppContent() {
  const { t, isBrazil, region, setRegion } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isLandingPage = location.pathname === '/';
  const [user, setUser] = useState<User | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(localStorage.getItem('impersonatedUserId'));
  
  const effectiveUserId = useMemo(() => {
    if (!user) return null;
    if (user.email !== 'dewaldjordaan5@gmail.com') return user.uid;
    if (impersonatedUserId === user.uid) return user.uid;
    return impersonatedUserId || user.uid;
  }, [impersonatedUserId, user]);

  const isImpersonating = useMemo(() => !!impersonatedUserId && user?.email === 'dewaldjordaan5@gmail.com' && impersonatedUserId !== user?.uid, [impersonatedUserId, user]);

  const isTester = useMemo(() => {
    const userEmail = user?.email?.toLowerCase();
    return userEmail === 'dewaldjordaan5@gmail.com' || userEmail === 'dewald.jordan@sparkpr.com';
  }, [user]);

  const getUserRole = (brand: BrandProfile | null, email: string | undefined): 'owner' | 'editor' | 'viewer' | 'approver' => {
    if (!brand || !email) return 'viewer';
    const lowerEmail = email.toLowerCase();
    if (brand.ownerId === user?.uid) return 'owner';
    return brand.teamRoles?.[lowerEmail] || 'editor'; // Default to editor for legacy members
  };

  const canEdit = (brand: BrandProfile | null): boolean => {
    const role = getUserRole(brand, user?.email || undefined);
    return role === 'owner' || role === 'editor';
  };

  const canApprove = (brand: BrandProfile | null): boolean => {
    const role = getUserRole(brand, user?.email || undefined);
    return role === 'owner' || role === 'editor' || role === 'approver';
  };
  const [hasApiKey, setHasApiKey] = useState(false);
  const [brands, setBrands] = useState<BrandProfile[]>([]);
  const [activeBrandId, setActiveBrandId] = useState<string | null>(localStorage.getItem('activeBrandId'));
  const brand = useMemo(() => {
    if (activeBrandId === 'new') return null;
    const found = brands.find(b => b.id === activeBrandId);
    if (found) return found;
    // If an ID is explicitly set but not found, don't fallback to the first brand immediately
    // as it might be a newly created brand not yet synced from Firestore.
    if (activeBrandId) return null;
    return brands.length > 0 ? brands[0] : null;
  }, [brands, activeBrandId]);
  const [plans, setPlans] = useState<MonthlyPlan[]>([]);
  const [allUserPlans, setAllUserPlans] = useState<MonthlyPlan[]>([]);
  const [shareFeedback, setShareFeedback] = useState<ShareFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'brand' | 'plans' | 'trends' | 'engagement' | 'inbox' | 'review' | 'settings' | 'admin' | 'bookings' | 'create-plan' | 'carousel-test' | 'blueprint-engine' | 'approved-media' | 'html-lab' | 'template-baker'>('dashboard');
  const [showAddPostModal, setShowAddPostModal] = useState<string | null>(null); // planId
  const [prefilledConcept, setPrefilledConcept] = useState<string>('');
  const [prefilledDate, setPrefilledDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [newPostDetails, setNewPostDetails] = useState({ description: '', date: format(new Date(), 'yyyy-MM-dd') });
  const [isAddingPost, setIsAddingPost] = useState(false);
  const [brandBlueprints, setBrandBlueprints] = useState<any[]>([]);

  useEffect(() => {
    if (!activeBrandId || activeBrandId === 'new') {
      setBrandBlueprints([]);
      return;
    }
    const q = query(
      collection(db, 'carousel_blueprints'),
      where('brandId', '==', activeBrandId)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBrandBlueprints(data);
    });
    return () => unsubscribe();
  }, [activeBrandId]);

  const userStats = useMemo(() => {
    let totalBrands = brands.length;
    let totalPlans = allUserPlans.length;
    let totalPosts = 0;
    let estimatedStorageBytes = 0;

    allUserPlans.forEach(plan => {
      const calendar = plan.calendar || [];
      totalPosts += calendar.length;
      calendar.forEach(post => {
        const slides = post.slides || [];
        slides.forEach(slide => {
          if (slide.url) {
            if (slide.isVideo) {
              estimatedStorageBytes += 8 * 1024 * 1024; // 8 MB per video/reel
            } else {
              estimatedStorageBytes += 1.5 * 1024 * 1024; // 1.5 MB per standard artwork
            }
          }
          if (slide.thumbnailUrl) {
            estimatedStorageBytes += 150 * 1024; // 150 KB per thumbnail
          }
        });
      });
    });

    brands.forEach(b => {
      if (b.logoUrl && b.logoUrl.includes('firebasestorage.googleapis.com')) {
        estimatedStorageBytes += 1 * 1024 * 1024; // 1 MB logo standard
      }
      const productImages = b.productImages || [];
      productImages.forEach(img => {
        if (img.url && img.url.includes('firebasestorage.googleapis.com')) {
          estimatedStorageBytes += 1.5 * 1024 * 1024;
        }
      });
      const brandTemplates = b.brandTemplates || [];
      brandTemplates.forEach(tpl => {
        if (tpl.url && tpl.url.includes('firebasestorage.googleapis.com')) {
          estimatedStorageBytes += 1.3 * 1024 * 1024;
        }
      });
      const brandDocuments = b.contextDocuments || [];
      brandDocuments.forEach(doc => {
        if (doc.url && doc.url.includes('firebasestorage.googleapis.com')) {
          estimatedStorageBytes += 3 * 1024 * 1024;
        }
      });
    });

    return {
      totalBrands,
      totalPlans,
      totalPosts,
      storageMB: parseFloat((estimatedStorageBytes / (1024 * 1024)).toFixed(1)),
      estimatedBytes: estimatedStorageBytes
    };
  }, [brands, allUserPlans]);

  const upcomingPost = useMemo(() => {
    if (!plans || plans.length === 0) return null;
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    
    const allPosts: { post: ContentIdea; plan: MonthlyPlan }[] = [];
    plans.forEach(plan => {
      if (plan.status === 'archived') return;
      const calendar = plan.calendar || [];
      calendar.forEach(post => {
        allPosts.push({ post, plan });
      });
    });

    if (allPosts.length === 0) return null;

    allPosts.sort((a, b) => a.post.date.localeCompare(b.post.date));

    const futurePost = allPosts.find(p => p.post.date >= todayStr);
    if (futurePost) return futurePost;

    return allPosts[0];
  }, [plans]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab && ['dashboard', 'brand', 'plans', 'trends', 'engagement', 'inbox', 'review', 'settings', 'admin', 'bookings', 'create-plan'].includes(tab)) {
      setActiveTab(tab as any);
    }

    const action = params.get('action');
    const concept = params.get('concept');
    const brandId = params.get('brandId');

    if (action === 'create' && concept && brandId) {
      setPrefilledConcept(concept);
      // We'll trigger the modal once the plans are loaded in another effect or here if we have them
    }
  }, []);

  // Effect to trigger modal once plans are loaded and pre-fill is ready
  useEffect(() => {
    if (prefilledConcept && plans.length > 0 && !showAddPostModal && activeBrandId) {
      // Find the latest active plan
      const sortedPlans = [...plans].sort((a, b) => b.year - a.year || b.month.localeCompare(a.month));
      const latestPlan = sortedPlans[0];
      if (latestPlan) {
        setShowAddPostModal(latestPlan.id);
      }
    }
  }, [prefilledConcept, plans, showAddPostModal, activeBrandId]);
  useEffect(() => {
    if (!activeBrandId || activeBrandId === 'new' || !effectiveUserId) {
      setShareFeedback([]);
      return;
    }
    const q = query(
      collection(db, 'share_feedback'),
      where('brandId', '==', activeBrandId)
    );
    return onSnapshot(q, (snapshot) => {
      const items: ShareFeedback[] = [];
      snapshot.forEach(doc => {
        items.push({ id: doc.id, ...doc.data() } as ShareFeedback);
      });
      // Sort client-side to avoid composite index requirement
      items.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA;
      });
      setShareFeedback(items);
    }, (err) => {
      console.warn("Share feedback listener error:", err);
    });
  }, [activeBrandId, effectiveUserId]);

  const [expandedPlanIds, setExpandedPlanIds] = useState<Set<string>>(new Set());
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editingPostData, setEditingPostData] = useState<{ copy: string; slides: any[]; linkedinFormat?: 'pdf' | 'gallery'; format?: string } | null>(null);
  const [selectedPostIds, setSelectedPostIds] = useState<Record<string, Set<string>>>({}); // planId -> Set of postId
  const [isBulkGenerating, setIsBulkGenerating] = useState<string | null>(null);
  const [isArchiving, setIsArchiving] = useState<string | null>(null);

  const handleViewFeedbackInCalendar = (planId: string, ideaId?: string | null) => {
    setActiveTab('plans');
    setExpandedPlanIds(prev => new Set(prev).add(planId));
    if (ideaId) {
      setTimeout(() => {
        const el = document.getElementById(`post-${ideaId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Flash effect
          el.classList.add('ring-4', 'ring-indigo-600', 'ring-offset-2');
          setTimeout(() => {
             el.classList.remove('ring-4', 'ring-indigo-600', 'ring-offset-2');
          }, 2000);
        }
      }, 500);
    }
  };

  const handleDeleteFeedback = async (id: string) => {
    try {
      const idToken = await auth.currentUser?.getIdToken();
      await axios.delete(`/api/share/feedback/${id}`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
    } catch (err) {
      console.error("Failed to delete feedback", err);
      alert("Failed to delete feedback");
    }
  };

  const handleArchivePlan = (planId: string) => {
    setPlanToArchive(planId);
    setArchiveStep('confirm');
  };

  const executeArchivePlan = async (planId: string) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan || !brand) return;

    setIsArchiving(planId);
    setArchiveStep('downloading');
    
    try {
      setToast({ message: "Starting archive process...", type: 'success' });
      await logAudit(brand, 'archive_started', 0, tokens, tokens, planId, 'started');

      // 1. Export to Zip
      setToast({ message: "Preparing Zip archive with media files...", type: 'success' });
      await exportPlanToZip(plan, brand.companyName);
      
      // 2. Export to PDF (we'll capture a special hidden div)
      setToast({ message: "Generating PDF summary...", type: 'success' });
      const fileName = `${brand.companyName}_Summary_${plan.month}_${plan.year}`;
      await exportPlanToPDF(`archive-summary-${planId}`, brand.companyName, fileName);

      // Change step to verifying
      setArchiveStep('verifying');
    } catch (err: any) {
      console.error("Archive error:", err);
      setToast({ message: `Archive failed: ${err.message}`, type: 'error' });
      setIsArchiving(null);
      setPlanToArchive(null);
    }
  };

  const handleFinalizeCleanup = async (planId: string) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan || !brand) return;

    try {
      setToast({ message: "Deleting remote media assets...", type: 'success' });
      
      const updatedCalendar = [...plan.calendar];
      for (const post of updatedCalendar) {
        if (post.slides) {
          for (const slide of post.slides) {
            if (slide.url && slide.url.includes('firebasestorage.googleapis.com')) {
              await safeDeleteFile(slide.url);
              slide.url = ''; // Remove from record
            }
          }
        }
      }

      await handleUpdatePlan(planId, { 
        calendar: updatedCalendar,
        status: 'archived',
        archivedAt: new Date().toISOString()
      });

      setToast({ message: "Month archived successfully! Media deleted.", type: 'success' });
      setPlanToArchive(null);
    } catch (err: any) {
      console.error("Cleanup error:", err);
      setToast({ message: `Cleanup failed: ${err.message}`, type: 'error' });
    } finally {
      setIsArchiving(null);
    }
  };
  const [sprintProgress, setSprintProgress] = useState<{ current: number; total: number; postTitle: string; postId?: string; slideId?: string } | null>(null);
  const processingRef = useRef<Set<string>>(new Set());
  const requestCountRef = useRef({ 
    global: { count: 0 as number, lastReset: Date.now() },
    user: {} as Record<string, { count: number, lastReset: number }>,
    brand: {} as Record<string, { count: number, lastReset: number }>
  });

  const logAudit = async (brand: BrandProfile, action: string, cost: number, tokensBefore: number, tokensAfter: number, planId?: string, status: 'success' | 'failed' | 'started' = 'success', metadata?: any) => {
    // CRITICAL: Ensure we have the actual Firebase Auth UID for Firestore permissions
    const currentUserId = auth.currentUser?.uid;
    
    if (!currentUserId) {
      console.warn("Skipping audit log: No authenticated Firebase user");
      return;
    }

    try {
      await addDoc(collection(db, 'audit_logs'), {
        userId: currentUserId,
        brandId: brand.id,
        planId: planId || '',
        actionType: action,
        estimatedCostZar: cost,
        tokensBefore,
        tokensAfter,
        timestamp: new Date().toISOString(),
        status,
        metadata: metadata || {}
      });
    } catch (err: any) {
      console.warn("Audit log creation denied:", err.message, err.code, "isAuth:", !!currentUserId);
    }
  };

  const checkCircuitBreaker = (brandId?: string) => {
    const now = Date.now();
    const limits = {
      global: { max: 500, interval: 60000 }, // 500/min
      user: { max: 100, interval: 600000 },    // 100/10min
      brand: { max: 150, interval: 3600000 }  // 150/hour
    };

    // Global
    if (now - requestCountRef.current.global.lastReset > limits.global.interval) {
      requestCountRef.current.global = { count: 1, lastReset: now };
    } else {
      requestCountRef.current.global.count++;
    }
    if (requestCountRef.current.global.count > limits.global.max) return 'Global limit exceeded';

    // User
    if (user?.uid) {
      const uStat = requestCountRef.current.user[user.uid] || { count: 0, lastReset: now };
      if (now - uStat.lastReset > limits.user.interval) {
        requestCountRef.current.user[user.uid] = { count: 1, lastReset: now };
      } else {
        uStat.count++;
        requestCountRef.current.user[user.uid] = uStat;
      }
      if (uStat.count > limits.user.max) return 'User rate limit exceeded (10/10m)';
    }

    // Brand
    if (brandId) {
      const bStat = requestCountRef.current.brand[brandId] || { count: 0, lastReset: now };
      if (now - bStat.lastReset > limits.brand.interval) {
        requestCountRef.current.brand[brandId] = { count: 1, lastReset: now };
      } else {
        const nextCount = bStat.count + 1;
        requestCountRef.current.brand[brandId] = { count: nextCount, lastReset: bStat.lastReset };
        if (nextCount > limits.brand.max) return `Brand rate limit exceeded (${limits.brand.max}/h)`;
      }
    }

    return null;
  };

  // Emergency Global Stop Check
  const [isSystemPaused, setIsSystemPaused] = useState(false);
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', 'system'), (snap) => {
      if (snap.exists() && snap.data().paused) {
        setIsSystemPaused(true);
      } else {
        setIsSystemPaused(false);
      }
    }, (err) => {
      console.warn("System config listener error:", err);
    });
    return () => unsub();
  }, []);
  const [showSprintVideo, setShowSprintVideo] = useState(true);
  const [confirmDeleteGuestId, setConfirmDeleteGuestId] = useState<string | null>(null);
  const stopSprintRef = useRef(false);
  const chimeRef = useRef<HTMLAudioElement | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning'; duration?: number | null } | null>(null);
  const [supportPin, setSupportPin] = useState<string | null>(null);
  const [isGeneratingPin, setIsGeneratingPin] = useState(false);
  const [planToArchive, setPlanToArchive] = useState<string | null>(null);
  const [archiveStep, setArchiveStep] = useState<'confirm' | 'downloading' | 'verifying'>( 'confirm');
  const [showSuggestionModal, setShowSuggestionModal] = useState<{ planId: string, postId: string } | null>(null);
  const [isSubmittingSuggestion, setIsSubmittingSuggestion] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [promoCode, setPromoCode] = useState('');
  const isPromoValid = useMemo(() => promoCode.toUpperCase() === 'WELCOME2026', [promoCode]);

  const pricingRegions = useMemo(() => [
    { 
      id: 'za', name: 'South Africa', currency: 'R', 
      solo_mo: 250, solo_yr: 2400,
      growth_mo: 550, growth_yr: 5500,
      agency_mo: 1450, agency_yr: 13500,
      discount: 100 
    },
    { 
      id: 'us', name: 'United States', currency: '$', 
      solo_mo: 15, solo_yr: 145,
      growth_mo: 34, growth_yr: 330,
      agency_mo: 85, agency_yr: 900,
      discount: 6 
    },
    { 
      id: 'uk', name: 'United Kingdom', currency: '£', 
      solo_mo: 12, solo_yr: 109,
      growth_mo: 29, growth_yr: 250,
      agency_mo: 69, agency_yr: 650,
      discount: 4.8 
    },
    { 
      id: 'br', name: 'Brasil', currency: 'R$', 
      solo_mo: 55, solo_yr: 750,
      growth_mo: 129, growth_yr: 1200,
      agency_mo: 299, agency_yr: 2999,
      discount: 30 
    }
  ], []);

  const selectedRegion = useMemo(() => pricingRegions.find(r => r.id === region) || pricingRegions[1], [region, pricingRegions]);

  const getEffectivePrice = useCallback((planId: 'solo' | 'growth' | 'agency', cycle: 'monthly' | 'yearly') => {
    const r = selectedRegion as any;
    const key = `${planId}_${cycle === 'monthly' ? 'mo' : 'yr'}`;
    const base = r[key];
    
    const symbol = selectedRegion.currency;
    const formatted = `${symbol}${base}`;

    if (isPromoValid && cycle === 'monthly') {
      const discounted = Math.max(0, base - selectedRegion.discount);
      return (
        <div className="inline-block text-left">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 line-through text-lg">{formatted}</span>
            <span className="text-indigo-600 font-black">{symbol}{discounted.toFixed(discounted % 1 === 0 ? 0 : 2)}</span>
          </div>
          <div className="text-[10px] text-indigo-500 font-bold leading-none mt-1">*{isBrazil ? "Apenas primeiro mês" : "First month only"}</div>
        </div>
      );
    }

    return formatted;
  }, [selectedRegion, isPromoValid, isBrazil]);
  const [adminStats, setAdminStats] = useState<{ 
    users: any[], 
    brands: any[], 
    plans: any[], 
    visits: number,
    bookingGuests: any[]
  } | null>(null);
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  const [ownerProfile, setOwnerProfile] = useState<any>(null);
  const [transferBrandData, setTransferBrandData] = useState({
    sourceBrandId: '',
    targetEmail: '',
    isTransferring: false
  });
  const [adjustTokenData, setAdjustTokenData] = useState({
    brandId: '',
    amount: '',
    isProcessing: false
  });

  /**
   * Mock function to simulate sending email notifications.
   * In a production environment, this would call a backend API (e.g., SendGrid, AWS SES).
   */
  const sendEmailNotification = async (to: string, subject: string, body: string) => {
    console.log(`[EMAIL SENT] To: ${to} | Subject: ${subject} | Body: ${body}`);
    // Simulate network delay
    return new Promise(resolve => setTimeout(resolve, 500));
  };

  const isAdmin = user?.email?.toLowerCase() === "dewaldjordaan5@gmail.com";
  const isFacebookDev = user?.email?.toLowerCase() === "facebookdev@blankslideagency.com";

  // Internal AI Cost Constants for Profitability Guardrail (in Rand R)
  const INTERNAL_COSTS = {
    image: 0.60,
    video: 9.00,
    plan: 0.50,
  };

  const calculateProfitability = (brand: BrandProfile) => {
    // Basic revenue estimation per month (Normalized to ZAR for comparison with INTERNAL_COSTS)
    const zaRegion = pricingRegions.find(r => r.id === 'za');
    const plan = brand.subscription?.plan || 'solo';
    const isYearly = brand.subscription?.interval === 'yearly';
    
    let defaultRevenue = 250; // Default ZAR solo monthly
    if (zaRegion) {
      const key = `${plan}_${isYearly ? 'yr' : 'mo'}`;
      // @ts-ignore
      const val = zaRegion[key];
      defaultRevenue = isYearly ? val / 12 : val;
    }

    const revenue = brand.usage?.customRevenueAmount || defaultRevenue;
    
    if (revenue <= 0) return { margin: 0, cost: 0, status: 'unknown', target: 60 };

    const imagesCount = brand.usage?.totalImagesGenerated || 0;
    const videosCount = brand.usage?.totalVideosGenerated || 0;
    const plansCount = brand.usage?.plansGeneratedThisMonth || 0;

    const totalCost = (imagesCount * INTERNAL_COSTS.image) + 
                      (videosCount * INTERNAL_COSTS.video) + 
                      (plansCount * INTERNAL_COSTS.plan);

    const margin = ((revenue - totalCost) / revenue) * 100;
    const isPromotional = brand.usage?.isPromotional;
    const target = isPromotional ? 40 : 60;

    return {
      margin,
      cost: totalCost,
      revenue,
      status: margin >= target ? 'healthy' : 'risk',
      target
    };
  };

  const syncBrandSubscription = async (brandId: string) => {
    try {
      const idToken = await user?.getIdToken();
      // setToast({ message: "Syncing with Stripe...", type: 'info' }); // type was invalid
      const response = await fetch('/api/admin/sync-brand-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ brandId })
      });
      const data = await response.json();
      if (data.success) {
        setToast({ message: `Successfully synced brand to ${data.plan} plan!`, type: 'success' });
        // Refresh admin stats to reflect changes
        fetchAdminStats();
      } else {
        setToast({ message: data.error || 'Failed to sync subscription.', type: 'error' });
      }
    } catch (err: any) {
      setToast({ message: err.message || 'Error syncing subscription.', type: 'error' });
    }
  };

  const handleTransferBrand = async () => {
    if (!transferBrandData.sourceBrandId || !transferBrandData.targetEmail) {
      setToast({ message: "Please enter Brand ID and Target Email", type: 'error' });
      return;
    }
    
    try {
      setTransferBrandData(prev => ({ ...prev, isTransferring: true }));
      const idToken = await user?.getIdToken();
      const response = await fetch('/api/admin/transfer-brand', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          sourceBrandId: transferBrandData.sourceBrandId,
          targetUserEmail: transferBrandData.targetEmail
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setToast({ message: `Successfully transferred brand and ${data.transferredPlans} plans!`, type: 'success' });
        setTransferBrandData({ sourceBrandId: '', targetEmail: '', isTransferring: false });
        fetchAdminStats();
      } else {
        setToast({ message: data.error || "Failed to transfer brand", type: 'error' });
      }
    } catch (err: any) {
      setToast({ message: err.message || "Error transferring brand", type: 'error' });
    } finally {
      setTransferBrandData(prev => ({ ...prev, isTransferring: false }));
    }
  };

  const handleAdjustTokens = async () => {
    if (!adjustTokenData.brandId || adjustTokenData.amount === '') {
      setToast({ message: "Please enter Brand ID and Amount", type: 'error' });
      return;
    }
    
    try {
      setAdjustTokenData(prev => ({ ...prev, isProcessing: true }));
      const idToken = await user?.getIdToken();
      const response = await fetch('/api/admin/adjust-tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          brandId: adjustTokenData.brandId,
          amount: Number(adjustTokenData.amount),
          mode: 'set'
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setToast({ message: `Successfully adjusted tokens to ${adjustTokenData.amount}!`, type: 'success' });
        setAdjustTokenData({ brandId: '', amount: '', isProcessing: false });
        fetchAdminStats();
      } else {
        setToast({ message: data.error || "Failed to adjust tokens", type: 'error' });
      }
    } catch (err: any) {
      setToast({ message: err.message || "Error adjusting tokens", type: 'error' });
    } finally {
      setAdjustTokenData(prev => ({ ...prev, isProcessing: false }));
    }
  };

  // Live Sync current user profile
  useEffect(() => {
    if (!effectiveUserId) {
      setCurrentUserProfile(null);
      return;
    }

    const unsubUser = onSnapshot(doc(db, 'users', effectiveUserId), (snap) => {
      if (snap.exists()) {
        setCurrentUserProfile({ id: snap.id, ...snap.data() });
      }
    }, (err) => {
      console.error("Error syncing user profile:", err);
    });

    return () => unsubUser();
  }, [effectiveUserId]);

  // Sync owner profile for tokens (shared access)
  useEffect(() => {
    if (!brand?.ownerId) {
      setOwnerProfile(null);
      return;
    }

    // Use effectiveUserId to stay consistent with impersonation
    if (brand.ownerId === effectiveUserId) {
      setOwnerProfile(currentUserProfile);
      return;
    }

    const unsubOwner = onSnapshot(doc(db, 'users', brand.ownerId), (snap) => {
      if (snap.exists()) setOwnerProfile({ id: snap.id, ...snap.data() });
    }, (err) => {
      console.error("Error syncing owner profile:", err);
    });

    return () => unsubOwner();
  }, [brand?.ownerId, effectiveUserId, currentUserProfile]);

  const isCollaborator = useMemo(() => brand && brand.ownerId !== user?.uid, [brand, user]);
  const userRole = useMemo(() => getUserRole(brand, user?.email || undefined), [brand, user]);
  const effectivePlan = useMemo(() => {
    if (brand?.subscription?.plan && brand.subscription.plan !== 'trial') return brand.subscription.plan;
    if (currentUserProfile?.subscription?.plan && currentUserProfile.subscription.plan !== 'trial') return currentUserProfile.subscription.plan;
    if (ownerProfile?.subscription?.plan && ownerProfile.subscription.plan !== 'trial') return ownerProfile.subscription.plan;
    return 'trial';
  }, [brand, currentUserProfile, ownerProfile]);

  const tokens = useMemo(() => {
    // Priority 1: User's own tokens (live profile) - whether impersonating or own account
    if (currentUserProfile?.tokens !== undefined) {
      return currentUserProfile.tokens;
    }
    if (currentUserProfile && effectivePlan === 'trial') {
      return 20;
    }
    
    // Priority 2: If we have a brand and it's owned by someone else, show owner's tokens
    if (brand && effectiveUserId && brand.ownerId !== effectiveUserId) {
      if (ownerProfile?.tokens !== undefined) {
        return ownerProfile.tokens;
      }
      if (ownerProfile && effectivePlan === 'trial') {
        return 20;
      }
    }
    
    // Fallback: Brand tokens
    return brand?.tokens !== undefined ? brand.tokens : (effectivePlan === 'trial' ? 20 : 0);
  }, [ownerProfile, currentUserProfile, brand, effectiveUserId, effectivePlan]);

  const isActuallyTrial = useMemo(() => {
    return effectivePlan === 'trial';
  }, [effectivePlan]);

  const isTrialUI = useMemo(() => {
    // Admins never see trial UI limitations (unless they explicitly want to see what a trial user sees)
    // Actually, admins should see everything always.
    if (isAdmin) return false;
    
    // Collaborators should only see trial limits if the brand's OWNER is actually on a trial
    // This allows editors to see the full UI on paid brands without needing their own subscription.
    if (isCollaborator) return isActuallyTrial;
    
    // If no brand selected (e.g. they deleted their brand), 
    // we should check their account subscription status to see if they are a "trial" user
    if (!brand) {
      if (currentUserProfile?.subscription?.plan && currentUserProfile.subscription.plan !== 'trial') return false;
      return true;
    }
    
    return isActuallyTrial;
  }, [isAdmin, isCollaborator, brand, isActuallyTrial, currentUserProfile]);

  useEffect(() => {
    // Track site visit
    const trackVisit = async () => {
      const sessionKey = 'site_visit_tracked_' + new Date().toISOString().split('T')[0];
      if (sessionStorage.getItem(sessionKey)) return;
      
      try {
        await axios.post('/api/admin/track-visit');
        sessionStorage.setItem(sessionKey, 'true');
      } catch (e) {
        console.error("Failed to track visit:", e);
      }
    };
    trackVisit();
  }, []);

  // 0. Manual Admin Stats Fetch (Optimized for performance)
  const [loadingAdminStats, setLoadingAdminStats] = useState(false);
  const fetchAdminStats = async () => {
    if (!isAdmin) return;
    try {
      setLoadingAdminStats(true);
      const [usersSnap, brandsSnap, plansSnap, bookingGuestsSnap] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'brands')),
        getDocs(collection(db, 'plans')),
        getDocs(collection(db, 'booking_guests'))
      ]);

      let totalVisits = 0;
      try {
        const statsSnap = await getDocs(collection(db, 'site_stats'));
        statsSnap.forEach((doc) => {
          const data = doc.data();
          totalVisits += data.totalVisits || data.count || 0;
        });
      } catch (e) {
        console.warn("Could not fetch site_stats");
      }

      setAdminStats({
        users: usersSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        brands: brandsSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        plans: plansSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        visits: totalVisits,
        bookingGuests: bookingGuestsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      });
      setToast({ message: "Admin stats refreshed.", type: 'success' });
    } catch (error) {
      console.error("Error fetching admin stats:", error);
      setToast({ message: "Failed to fetch admin stats. Resource limit reached?", type: 'error' });
    } finally {
      setLoadingAdminStats(false);
    }
  };

  useEffect(() => {
    if (isAdmin && activeTab === 'admin' && !adminStats) {
      // Auto-fetch ONLY the first time we enter admin and stats are empty
      fetchAdminStats();
    }
  }, [isAdmin, activeTab]);

  useEffect(() => {
    if (toast && toast.duration !== null) {
      const ms = toast.duration ?? (toast.type === 'warning' ? 8000 : 3000);
      const timer = setTimeout(() => setToast(null), ms);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Auth State
  useEffect(() => {
    // Proactive connection test to prevent "Pending promise was never set" error
    const testConnection = async () => {
      try {
        await getDocFromServer(doc(db, 'test', 'connection'));
      } catch (error) {
        // Failing initially is fine, it establishes connection
        console.log("Firestore connection initialized");
      }
    };
    testConnection();

    const checkKeys = () => {
      const geminiKey = getApiKey('standard');
      const userKey = getApiKey('premium');
      
      // Removed redundant init logs for production performance
      
      // Update the connected state based on detected status
      if (userKey) {
        setHasApiKey(true);
      }
    };
    checkKeys();

      // Auth State
      const unsubscribe = onAuthStateChanged(auth, async (u) => {
        setUser(u);
        setIsAuthReady(true);
        
        if (u) {
          // Fetch secure config
          try {
            const token = await u.getIdToken();
            const configRes = await axios.get('/api/config', {
              headers: { Authorization: `Bearer ${token}` }
            });
            setSecureConfig(configRes.data);
            if (configRes.data.GEMINI_API_KEY || configRes.data.PAID_GEMINI_API_KEY) {
              setHasApiKey(true);
            }
          } catch (err) {
            console.error("Failed to fetch secure configuration:", err);
          }

          // First, check if we already have the key from /env.js
          const env = (window as any)._env_ || {};
          if (env.GEMINI_API_KEY || env.PAID_GEMINI_API_KEY || env.PLATAPOST_AI_KEY) {
            setHasApiKey(true);
          }
  
          const checkKey = async (retries = 3) => {
            const selected = await (window as any).aistudio?.hasSelectedApiKey();
            if (selected) {
              setHasApiKey(true);
            } else if (retries > 0) {
              setTimeout(() => checkKey(retries - 1), 1000);
            }
          };
          checkKey();
  
          // Check if user doc
          const userRef = doc(db, 'users', u.uid);
          try {
            const userDoc = await getDoc(userRef);
            let geoInfo = {};
            try {
              const geoRaw = localStorage.getItem('geo_data');
              if (geoRaw) {
                const parsed = JSON.parse(geoRaw);
                geoInfo = {
                  ip: parsed.ip || '',
                  country: parsed.country || '',
                  countryCode: parsed.country_code || '',
                  city: parsed.city || ''
                };
              }
            } catch (e) {
              // Ignore
            }

            if (!userDoc.exists()) {
              await setDoc(userRef, {
                email: u.email,
                displayName: u.displayName,
                createdAt: new Date().toISOString(),
                lastLogin: new Date().toISOString(),
                tokens: 20,
                ...geoInfo
              });
              // Notify Admin of new signup
              try {
                const token = await u.getIdToken();
                await axios.post('/api/notify-signup', {
                  userEmail: u.email,
                  userDisplayName: u.displayName
                }, {
                  headers: { Authorization: `Bearer ${token}` }
                });
              } catch (notifyErr) {
                console.warn("Failed to notify admin of signup:", notifyErr);
              }
            } else {
              await updateDoc(userRef, {
                lastLogin: new Date().toISOString(),
                ...geoInfo
              });
            }
          } catch (err) {
            console.warn("Failed to update user profile on login, continuing anyway:", err);
          }
        }
      });
      return () => unsubscribe();
  }, []);

  // Track Visit
  useEffect(() => {
    const trackVisit = async () => {
      const hasTracked = sessionStorage.getItem('visitTracked');
      if (!hasTracked) {
        try {
          await axios.post('/api/admin/track-visit');
          sessionStorage.setItem('visitTracked', 'true');
        } catch (err) {
          console.error("Failed to track visit:", err);
        }
      }
    };
    trackVisit();
  }, []);

  const handleOpenKeyDialog = async () => {
    try {
      await (window as any).aistudio?.openSelectKey();
      setToast({ message: "Key selected! Updating environment... Page will refresh in 2 seconds.", type: "success" });
      
      // Delay reload to allow platform environment to propagate
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      console.error("Failed to open key dialog:", err);
      setToast({ message: "Failed to select key. Please try again.", type: "error" });
    }
  };

  const handleManualRefresh = () => {
    window.location.reload();
  };

  // Connection Test
  useEffect(() => {
    if (isAuthReady && user) {
      const testConnection = async () => {
        try {
          await getDoc(doc(db, 'test', 'connection'));
        } catch (error) {
          console.warn("Connection verification failed, but may recover:", error);
        }
      };
      testConnection();
    }
  }, [isAuthReady, user]);

  // Data Sync
  useEffect(() => {
    if (!isAuthReady) return;
    
    if (!effectiveUserId) {
      setBrands([]);
      setPlans([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    let brandsLoaded = false;
    let plansLoaded = false;

    const checkLoading = () => {
      if (brandsLoaded && plansLoaded) setLoading(false);
    };

    const brandsRef = collection(db, 'brands');
    const userEmail = (user?.email || '').toLowerCase();

    // 1. Core Brand Sync
    const ownerQuery = query(brandsRef, where('ownerId', '==', effectiveUserId));
    const teamQuery = query(brandsRef, where('teamMembers', 'array-contains', userEmail));

    const unsubOwner = onSnapshot(ownerQuery, (snapshot) => {
      const processSnapshot = async () => {
        const ownerBrands = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as BrandProfile));
        
        if (ownerBrands.length === 0 && effectiveUserId === user?.uid) {
          try {
            const legacyDoc = await getDoc(doc(db, 'brands', effectiveUserId));
            if (legacyDoc.exists()) {
              const legacyData = legacyDoc.data();
              if (!legacyData.ownerId) {
                await updateDoc(doc(db, 'brands', effectiveUserId), { ownerId: effectiveUserId });
              }
              ownerBrands.push({ ...legacyData as any, id: legacyDoc.id, ownerId: effectiveUserId });
            }
          } catch (err) { }
        }
        
        setBrands(prev => {
          const otherBrands = prev.filter(b => b.ownerId !== effectiveUserId);
          const combined = [...ownerBrands, ...otherBrands];
          const unique = Array.from(new Map(combined.filter(item => item && item.id).map(item => [item.id!, item])).values());
          return unique;
        });
        brandsLoaded = true;
        checkLoading();
      };
      processSnapshot();
    }, (err) => {
      brandsLoaded = true;
      checkLoading();
      if (err.message?.includes('Quota') || err.message?.includes('RESOURCE_EXHAUSTED')) {
         window.dispatchEvent(new ErrorEvent('error', { message: JSON.stringify({ error: err.message }) }));
      }
    });

    const unsubTeam = onSnapshot(teamQuery, (snapshot) => {
      const teamBrands = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as BrandProfile));
      setBrands(prev => {
        const myBrands = prev.filter(b => b.ownerId === effectiveUserId);
        const combined = [...myBrands, ...teamBrands];
        const unique = Array.from(new Map(combined.filter(item => item && item.id).map(item => [item.id!, item])).values());
        return unique;
      });
    }, (err) => {
      if (err.message?.includes('Quota') || err.message?.includes('RESOURCE_EXHAUSTED')) {
        console.warn("Quota exceeded for team brands.");
      }
    });

    const timeout = setTimeout(() => {
      setLoading(false);
    }, 2500);

    return () => {
      unsubOwner();
      unsubTeam();
      clearTimeout(timeout);
    };
  }, [isAuthReady, user?.uid, effectiveUserId]);

  // 2. Plan Sync (Depends on activeBrandId)
  useEffect(() => {
    if (!isAuthReady || !activeBrandId || activeBrandId === 'new') {
      setPlans([]);
      return;
    }

    const plansRef = collection(db, 'plans');
    const plansQuery = query(plansRef, where('brandId', '==', activeBrandId));

    const unsubPlans = onSnapshot(plansQuery, (snapshot) => {
      const p = snapshot.docs.map(d => {
        const data = d.data();
        let calendar = data.calendar || [];
        if (typeof calendar === 'string') {
          try { calendar = JSON.parse(calendar); } catch (e) { calendar = []; }
        }
        
        // Failsafe client-side ID deduplication to prevent model collision bugs in rendering
        const seenIds = new Set<string>();
        const sanitizedCalendar = (calendar || []).map((item: any) => {
          let postId = item.id;
          if (!postId || seenIds.has(postId)) {
            postId = 'post_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now().toString(36);
          }
          seenIds.add(postId);
          
          const seenSlideIds = new Set<string>();
          const sanitizedSlides = (item.slides || []).map((slide: any) => {
            let slideId = slide.id;
            if (!slideId || seenSlideIds.has(slideId)) {
              slideId = 'slide_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now().toString(36);
            }
            seenSlideIds.add(slideId);
            return { ...slide, id: slideId };
          });
          
          return { ...item, id: postId, slides: sanitizedSlides };
        });

        return { id: d.id, ...data, calendar: sanitizedCalendar } as unknown as MonthlyPlan;
      });
      const monthOrder: Record<string, number> = {
        'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
        'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
      };
      p.sort((a, b) => {
        if (b.year !== a.year) return b.year - a.year;
        if (b.month !== a.month) return monthOrder[b.month] - monthOrder[a.month];
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
      setPlans(p);
    }, (err) => {
      if (err.message?.includes('Quota')) console.warn("Quota exceeded for plans.");
      else if (!err.message?.includes('Insufficient permissions')) { 
        // Only report major errors, permissions errors during brand deletion are normal/race conditions
        handleFirestoreError(err, OperationType.GET, `plans?brandId=${activeBrandId}`);
      }
    });

    return () => unsubPlans();
  }, [isAuthReady, activeBrandId]);

  // Aggregate subscription for all plans across all user brands
  useEffect(() => {
    if (!isAuthReady || brands.length === 0) {
      setAllUserPlans([]);
      return;
    }

    const brandIds = brands.map(b => b.id).filter(Boolean) as string[];
    if (brandIds.length === 0) {
      setAllUserPlans([]);
      return;
    }

    // Partition brandIds into chunks of 30 to comply with firestore "in" query limits
    const chunks: string[][] = [];
    for (let i = 0; i < brandIds.length; i += 30) {
      chunks.push(brandIds.slice(i, i + 30));
    }

    const unsubs = chunks.map(chunk => {
      const plansRef = collection(db, 'plans');
      const q = query(plansRef, where('brandId', 'in', chunk));
      return onSnapshot(q, (snapshot) => {
        const pList = snapshot.docs.map(d => {
          const data = d.data();
          let calendar = data.calendar || [];
          if (typeof calendar === 'string') {
            try { calendar = JSON.parse(calendar); } catch (e) { calendar = []; }
          }
          return { id: d.id, ...data, calendar } as unknown as MonthlyPlan;
        });
        setAllUserPlans(prev => {
          const otherPlans = prev.filter(p => !chunk.includes(p.brandId));
          return [...otherPlans, ...pList];
        });
      }, (err) => {
        console.warn("Error loading aggregated plans footprint:", err);
      });
    });

    return () => {
      unsubs.forEach(unsub => unsub());
    };
  }, [isAuthReady, brands]);


  const [isSavingBrand, setIsSavingBrand] = useState(false);

  useEffect(() => {
    if (!loading) {
      if (brands.length > 0) {
        const isValid = activeBrandId === 'new' || brands.some(b => b.id === activeBrandId);
        if (!isValid) {
          const firstId = brands[0].id;
          if (firstId) {
            setActiveBrandId(firstId);
            localStorage.setItem('activeBrandId', firstId);
          }
        }
      } else {
        if (activeBrandId && activeBrandId !== 'new') {
          setActiveBrandId('new');
          localStorage.setItem('activeBrandId', 'new');
        }
      }
    }
  }, [brands, loading, activeBrandId]);

  const handleSaveBrand = async (data: Partial<BrandProfile>, skipRedirect: boolean = false, skipToast: boolean = skipRedirect) => {
    if (!user || isSavingBrand) return;
    try {
      setIsSavingBrand(true);
      const isNew = !activeBrandId || activeBrandId === 'new';
      if (!skipRedirect && isNew) setLoading(true);

      // Check brand limits for new brands
      if (!isAdmin && (!activeBrandId || activeBrandId === 'new')) {
        const ownedBrands = brands.filter(b => b.ownerId === user.uid);
        
        // Use user-level subscription as fallback for limit check
        const userPlan = currentUserProfile?.subscription?.plan;
        
        const hasEnterprise = userPlan === 'enterprise' || ownedBrands.some(b => b.subscription?.plan === 'enterprise');
        const hasAgency = userPlan === 'agency' || ownedBrands.some(b => b.subscription?.plan === 'agency');
        const hasGrowth = userPlan === 'growth' || ownedBrands.some(b => b.subscription?.plan === 'growth');
        
        const limit = hasEnterprise ? 50 : hasAgency ? 10 : hasGrowth ? 3 : 1;

        console.log(`[handleSaveBrand] Limit check: ${ownedBrands.length}/${limit} owned brands.`);

        if (ownedBrands.length >= limit) {
          setToast({ 
            message: `You've reached your ${limit}-brand limit. Please select an existing brand to upgrade or manage.`, 
            type: 'error' 
          });
          setLoading(false);
          return;
        }
      }

      const isImpersonating = !!localStorage.getItem('impersonatedUserId') && user.email?.toLowerCase() === 'dewaldjordaan5@gmail.com';
      
      const brandData = {
        ownerId: (brand?.ownerId || effectiveUserId),
        updatedAt: new Date().toISOString(),
        ...(isImpersonating && { admin_action: true }),
        ...data
      };

      // Ensure emails are lowercased
      if (brandData.email) brandData.email = brandData.email.toLowerCase();
      if (brandData.teamMembers) {
        brandData.teamMembers = brandData.teamMembers.map(m => m.toLowerCase());
      }
      if (brandData.teamRoles) {
        const newRoles: Record<string, "viewer" | "editor" | "approver"> = {};
        Object.entries(brandData.teamRoles).forEach(([email, role]) => {
          newRoles[email.toLowerCase()] = role as any;
        });
        brandData.teamRoles = newRoles;
      }

      // Helper to upload base64 to storage if needed
      const ensureStorageUrl = async (url: string, pathPrefix: string) => {
        if (url && url.startsWith('data:')) {
          try {
            const hash = await getBase64Hash(url);
            const storagePath = `${pathPrefix}_${hash}`;
            const storageRef = ref(storage, storagePath);
            
            // Check if already exists to save quota
            try {
              return await getDownloadURL(storageRef);
            } catch (e) {
              // Not found, proceed to upload
            }

            // Use a timeout for storage operations (120s)
            await Promise.race([
              uploadString(storageRef, url, 'data_url'),
              new Promise((_, reject) => setTimeout(() => reject(new Error("Storage upload timeout")), 120000))
            ]);
            return await getDownloadURL(storageRef);
          } catch (err) {
            console.error("Error in ensureStorageUrl:", err);
            return url;
          }
        }
        return url;
      };

      // Cleanup large base64 strings by uploading them to storage
      const oldLogoUrl = brand?.logoUrl;
      if (brandData.logoUrl) {
        const newLogoUrl = await ensureStorageUrl(brandData.logoUrl, `logos/${user.uid}/logo`);
        // If we replaced a storage URL with a DIFFERENT storage URL, clean up the old one
        if (oldLogoUrl && oldLogoUrl !== newLogoUrl && oldLogoUrl.includes('firebasestorage.googleapis.com')) {
          await safeDeleteFile(oldLogoUrl);
        }
        brandData.logoUrl = newLogoUrl;
      }

      if (brandData.productImages) {
        brandData.productImages = await Promise.all((brandData.productImages || []).map(async (img) => ({
          ...img,
          url: await ensureStorageUrl(img.url, `logos/${user.uid}/products/img`)
        })));
      }

      if (brandData.brandTemplates) {
        brandData.brandTemplates = await Promise.all((brandData.brandTemplates || []).map(async (t) => ({
          ...t,
          url: await ensureStorageUrl(t.url, `logos/${user.uid}/templates/tpl`),
          shellUrl: t.shellUrl ? await ensureStorageUrl(t.shellUrl, `logos/${user.uid}/templates/shell`) : undefined
        })));
      }

      if (brandData.approvedMedia) {
        brandData.approvedMedia = await Promise.all((brandData.approvedMedia || []).map(async (m) => ({
          ...m,
          url: await ensureStorageUrl(m.url, `logos/${user.uid}/approved-media/med`)
        })));
      }

      if (brandData.contextDocuments) {
        brandData.contextDocuments = await Promise.all((brandData.contextDocuments || []).map(async (doc) => ({
          ...doc,
          url: await ensureStorageUrl(doc.url, `logos/${user.uid}/documents/doc`)
        })));
      }

      const { id, ...rawCleanData } = brandData;
      
      const removeUndefined = (obj: any): any => {
        if (Array.isArray(obj)) {
          return obj.map(removeUndefined);
        } else if (obj !== null && typeof obj === 'object') {
          return Object.keys(obj).reduce((acc: any, key: string) => {
            if (obj[key] !== undefined) {
              acc[key] = removeUndefined(obj[key]);
            }
            return acc;
          }, {});
        }
        return obj;
      };

      const cleanData = removeUndefined(rawCleanData);
      
      if (activeBrandId && activeBrandId !== 'new') {
        try {
          console.log(`[handleSaveBrand] Updating brand ${activeBrandId}. My UID: ${user.uid}, Brand ownerId in payload: ${cleanData.ownerId}`);
          
          await updateDoc(doc(db, 'brands', activeBrandId), cleanData);
          if (!skipToast) {
            setToast({ message: "Brand profile updated!", type: 'success' });
          }
        } catch (updateErr: any) {
          console.error("[handleSaveBrand] updateDoc(brands) failed:", updateErr);
          throw updateErr;
        }
      } else {
        const targetEmail = (isImpersonating && cleanData.email) ? cleanData.email.toLowerCase() : user.email?.toLowerCase();
        const dataWithOwner = {
          ...cleanData,
          ownerId: effectiveUserId,
          createdAt: new Date().toISOString(),
          teamMembers: [targetEmail].filter(Boolean),
          teamRoles: targetEmail ? { [targetEmail]: 'owner' } : {}
        };
        
        // console.log(`[handleSaveBrand] Attempting addDoc(brands) with dataWithOwner:`, JSON.stringify(dataWithOwner));
        /* 
        console.log(`[handleSaveBrand] auth.currentUser status:`, {
          uid: auth.currentUser?.uid,
          email: auth.currentUser?.email,
          emailVerified: auth.currentUser?.emailVerified,
          isAnonymous: auth.currentUser?.isAnonymous
        });
        */
        let docRef;
        try {
          docRef = await addDoc(collection(db, 'brands'), dataWithOwner);
        } catch (addErr: any) {
          throw addErr;
        }

        // Update user profile if needed if this is likely their first brand
        if (user && brands.length === 0) {
          try {
            await updateDoc(doc(db, 'users', user.uid), {
              brandId: docRef.id,
              updatedAt: new Date().toISOString()
            });
          } catch (userUpdateErr: any) {
            console.error("[handleSaveBrand] updateDoc(users) failed (likely document missing or permission):", userUpdateErr);
            // Non-critical: If user doc doesn't exist yet, we don't block brand creation
          }
        }

        setActiveBrandId(docRef.id);
        localStorage.setItem('activeBrandId', docRef.id);
        if (!skipToast) {
          setToast({ message: "Brand profile created!", type: 'success' });
        }
      }
      if (!skipRedirect) setActiveTab('dashboard');
    } catch (err: any) {
      console.error("[handleSaveBrand] Error details:", {
        activeBrandId,
        effectiveUserId,
        userUid: user?.uid,
        error: err
      });
      handleFirestoreError(err, OperationType.WRITE, activeBrandId ? `brands/${activeBrandId}` : 'brands');
      if (!skipToast) {
        setToast({ message: `Failed to save brand profile: ${err.message || 'Unknown error'}`, type: 'error' });
      }
    } finally {
      setIsSavingBrand(false);
      setLoading(false);
    }
  };

  const handleDeleteBrand = async (brandId: string) => {
    console.log(`[handleDeleteBrand] Called for brandId: ${brandId}`);
    if (!user || brandId === 'new') {
      console.warn(`[handleDeleteBrand] Abort: user=${!!user}, brandId=${brandId}`);
      return;
    }
    
    try {
      setLoading(true);
      // 1. Delete associated plans
      const plansQuery = query(collection(db, 'plans'), where('brandId', '==', brandId));
      const plansSnap = await getDocs(plansQuery);
      for (const pDoc of plansSnap.docs) {
        await deleteDoc(pDoc.ref).catch(err => {
          throw new Error(`Failed to delete plan ${pDoc.id}: ${err.message}`);
        });
      }
      
      // 2. Delete associated feedback
      const feedbackQuery = query(collection(db, 'share_feedback'), where('brandId', '==', brandId));
      const feedbackSnap = await getDocs(feedbackQuery);
      const idToken = await auth.currentUser?.getIdToken();
      for (const fDoc of feedbackSnap.docs) {
        if (idToken) {
          await axios.delete(`/api/share/feedback/${fDoc.id}`, {
            headers: { Authorization: `Bearer ${idToken}` }
          }).catch(err => {
            console.error(`Failed to delete feedback ${fDoc.id} via API:`, err);
            // Fallback to client SDK if API fails, but we prefer API
            return deleteDoc(fDoc.ref);
          }).catch(err => {
            throw new Error(`Failed to delete feedback ${fDoc.id}: ${err.message}`);
          });
        } else {
          await deleteDoc(fDoc.ref).catch(err => {
            throw new Error(`Failed to delete feedback ${fDoc.id}: ${err.message}`);
          });
        }
      }

      // 3. Delete the brand itself
      await deleteDoc(doc(db, 'brands', brandId)).catch(err => {
        throw new Error(`Failed to delete brand doc: ${err.message}`);
      });
      
      setToast({ message: "Brand profile deleted successfully.", type: 'success' });
      
      // 4. Update the local state
      const updatedBrands = brands.filter(b => b.id !== brandId);
      setBrands(updatedBrands);
      
      // 5. Select another brand if available
      if (updatedBrands.length > 0) {
        const nextBrandId = updatedBrands[0].id!;
        setActiveBrandId(nextBrandId);
        localStorage.setItem('activeBrandId', nextBrandId);
      } else {
        setActiveBrandId('new');
        localStorage.setItem('activeBrandId', 'new');
      }
      
      setActiveTab('dashboard');
    } catch (err) {
      console.error("Failed to delete brand:", err);
      handleFirestoreError(err, OperationType.DELETE, `brands/${brandId}`);
      setToast({ message: "Failed to delete brand profile.", type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePublishPost = async (planId: string, postId: string, platform: string, testMode: boolean = false) => {
    if (!canEdit(brand)) {
      setToast({ message: "You do not have permission to publish posts for this brand.", type: 'error' });
      return;
    }
    if (!brand?.socialAccounts || brand.socialAccounts.length === 0) {
      setToast({ message: "No social accounts connected. Go to Settings to connect your accounts.", type: 'error' });
      return;
    }

    const isImpersonating = !!localStorage.getItem('impersonatedUserId');

    const account = brand.socialAccounts.find(a => a.platform.toLowerCase() === platform.toLowerCase());
    if (!account) {
      setToast({ message: `Please connect your ${platform} account first in Settings.`, type: 'error' });
      return;
    }

    const plan = plans.find(p => p.id === planId);
    const post = plan?.calendar.find(p => p.id === postId);
    if (!post) return;

    // Prevent accidental duplicate posting if already published
    if (!testMode && post.platformStatus?.[platform]?.status === 'published') {
      const confirmRepublish = window.confirm(`${platform} post already says "published". Are you sure you want to post it again?`);
      if (!confirmRepublish) return;
    }

    try {
      // Update status to publishing for this platform
      if (!testMode) {
        await runTransaction(db, async (transaction) => {
          const planRef = doc(db, 'plans', planId);
          const planDoc = await transaction.get(planRef);
          if (!planDoc.exists()) throw new Error("Plan not found");
          
          const currentPlanData = planDoc.data();
          const currentCalendar = typeof currentPlanData.calendar === 'string' 
            ? JSON.parse(currentPlanData.calendar) 
            : currentPlanData.calendar;
          
          const updatedCalendar = currentCalendar.map((p: any) => 
            p.id === postId ? { 
              ...p, 
              platformStatus: {
                ...(p.platformStatus || {}),
                [platform]: { status: 'publishing' as const }
              }
            } : p
          );
          
          transaction.update(planRef, { 
            calendar: JSON.stringify(updatedCalendar), 
            updatedAt: new Date().toISOString(),
            ...(isImpersonating && { admin_action: true }) 
          });
        });
      }

      const token = await auth.currentUser?.getIdToken();
      const response = await fetch('/api/publish', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          post, 
          account, 
          testMode,
          brandId: activeBrandId,
          planId: planId
        })
      });

      const responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseErr) {
        console.error("Failed to parse publish response:", responseText);
        throw new Error(`Server error: ${responseText.substring(0, 100)}${responseText.length > 100 ? '...' : ''}`);
      }

      if (!response.ok || result.error) {
        // If it failed, we should probably reset the publishing status to failed or idle
        if (!testMode) {
          await runTransaction(db, async (transaction) => {
            const planRef = doc(db, 'plans', planId);
            const planDoc = await transaction.get(planRef);
            if (planDoc.exists()) {
              const currentPlanData = planDoc.data();
              const currentCalendar = typeof currentPlanData.calendar === 'string' 
                ? JSON.parse(currentPlanData.calendar) 
                : currentPlanData.calendar;
              
              const failedCalendar = currentCalendar.map((p: any) => 
                p.id === postId ? { 
                  ...p, 
                  platformStatus: {
                    ...(p.platformStatus || {}),
                    [platform]: { 
                      status: 'failed' as const,
                      error: result.error || result.message || 'Failed to publish'
                    }
                  }
                } : p
              );
              
              transaction.update(planRef, { 
                calendar: JSON.stringify(failedCalendar), 
                updatedAt: new Date().toISOString()
              });
            }
          });
        }
        throw new Error(result.error || result.message || 'Failed to publish');
      }

      // Update account if it was refreshed
      if (result.updatedAccount && brand) {
        const updatedAccounts = brand.socialAccounts.map(a => 
          a.platform.toLowerCase() === result.updatedAccount.platform.toLowerCase() ? result.updatedAccount : a
        );
        await updateDoc(doc(db, 'brands', activeBrandId), { socialAccounts: updatedAccounts, ...(isImpersonating && { admin_action: true }) });
      }

      if (!testMode) {
        // Update status to published for this platform
        await runTransaction(db, async (transaction) => {
          const planRef = doc(db, 'plans', planId);
          const planDoc = await transaction.get(planRef);
          if (!planDoc.exists()) throw new Error("Plan not found");
          
          const currentPlanData = planDoc.data();
          const currentCalendar = typeof currentPlanData.calendar === 'string' 
            ? JSON.parse(currentPlanData.calendar) 
            : currentPlanData.calendar;
          
          const finalCalendar = currentCalendar.map((p: any) => 
            p.id === postId ? { 
              ...p, 
              publishedStatus: 'published' as const,
              platformStatus: {
                ...(p.platformStatus || {}),
                [platform]: { 
                  status: 'published' as const, 
                  publishedAt: new Date().toISOString(),
                  publishedUrl: result.publishedUrl,
                  nativeId: result.nativeId 
                }
              }
            } : p
          );
          
          transaction.update(planRef, { 
            calendar: JSON.stringify(finalCalendar), 
            updatedAt: new Date().toISOString(),
            ...(isImpersonating && { admin_action: true }) 
          });
        });
        setToast({ message: `Post published to ${platform} successfully!`, type: 'success' });
      } else {
        setToast({ message: "Test publish successful!", type: 'success' });
        if (result.publishedUrl) {
          window.open(result.publishedUrl, '_blank');
        }
      }
    } catch (err: any) {
      console.error("Publishing error:", err);
      if (!testMode) {
        const failedCalendar = plan!.calendar.map(p => 
          p.id === postId ? { 
            ...p, 
            platformStatus: {
              ...(p.platformStatus || {}),
              [platform]: { 
                status: 'failed' as const,
                error: err.message
              }
            }
          } : p
        );
        await updateDoc(doc(db, 'plans', planId), { 
          calendar: JSON.stringify(failedCalendar), 
          updatedAt: new Date().toISOString(),
          ...(isImpersonating && { admin_action: true }) 
        });
      }
      setToast({ message: err.message || "Failed to publish post.", type: 'error' });
    }
  };

  const handleTopUp = async () => {
    if (!user || !brand || !activeBrandId) return;
    
    // Only allow top-up if tokens are depleted
    if (tokens > 0) {
      setToast({ message: "You still have tokens available! Top-ups are only available when you run out.", type: 'error' });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('/api/create-checkout-session', {
        type: 'topup',
        brandId: activeBrandId,
        userId: user.uid
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      setToast({ message: "Failed to initiate payment.", type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan: 'solo' | 'growth' | 'agency') => {
    if (!user) {
      setToast({ message: "Please log in to upgrade.", type: 'error' });
      return;
    }
    
    // If they are on the "new" brand screen, they must save first so we have a brand document to attach the plan to
    if (activeBrandId === 'new' || !activeBrandId) {
       setToast({ message: "Please save your brand profile first before upgrading.", type: 'error' });
       return;
    }

    if (!brand) {
      setToast({ message: "Loading brand data... please try again in a moment.", type: 'error' });
      return;
    }

    try {
      setLoading(true);
      console.log(`[handleUpgrade] Initiating ${plan} upgrade for brand ${activeBrandId}`);
      const response = await axios.post('/api/create-checkout-session', {
        type: 'subscription',
        plan,
        interval: billingCycle,
        brandId: activeBrandId,
        userId: user.uid
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      setToast({ message: "Failed to initiate upgrade.", type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeFeature = async (feature: string) => {
    if (!user || !activeBrandId || activeBrandId === 'new') {
      setToast({ message: "Please select or save a brand first.", type: 'error' });
      return;
    }
    try {
      setLoading(true);
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: feature, // Use feature ID as plan type
          type: 'subscription',
          brandId: activeBrandId,
          userId: user.uid,
          interval: 'monthly'
        })
      });
      if (!response.ok) throw new Error('Failed to create session');
      const { url } = await response.json();
      window.location.href = url;
    } catch (err: any) {
      console.error("Upgrade error:", err);
      setToast({ message: "Failed to initiate upgrade: " + err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!user || !brand || !activeBrandId) return;
    try {
      setLoading(true);
      const response = await axios.post('/api/create-portal-session', {
        brandId: activeBrandId
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err: any) {
      setToast({ message: err.response?.data?.error || "Failed to open billing portal.", type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePlan = async (
    month: string, 
    year: number, 
    highlights: string[], 
    products: string, 
    goals: string, 
    numberOfPosts: string | number = 30, 
    targetChannel: string = 'default', 
    customInstructions: string = '', 
    includeLatestTrends: boolean = false
  ) => {
    if (isSystemPaused) {
      setToast({ message: "System Maintenance: Generations are temporarily paused.", type: 'error' });
      return;
    }

    if (!user || !brand || !activeBrandId) return;

    const breakerError = checkCircuitBreaker(activeBrandId);
    if (breakerError) {
      setToast({ message: `Security Warning: ${breakerError}. System paused for safety.`, type: 'error' });
      return;
    }
    
    if (!canEdit(brand)) {
      setToast({ message: "You do not have permission to generate content for this brand.", type: 'error' });
      return;
    }
    
    const isBlankPlan = numberOfPosts === 'blank';
    const isTrial = isTrialUI;
    const hasTrialHistory = (brand.usage?.trialGenerationsUsed ?? 0) > 0 || (plans.length > 0);
    const isSpecialUser = user?.email?.toLowerCase() === 'dewald.jordan@sparkpr.com' || user?.email?.toLowerCase() === 'dewaldjordaan5@gmail.com' || brand.companyName?.toLowerCase().includes('ventura');
    const planLimit = isTrial ? 1 : (brand.subscription?.plan === 'solo' ? (isSpecialUser ? 5 : 3) : (brand.subscription?.plan === 'agency' ? 30 : 10));
    
    const activePlansCount = plans.filter(p => p.status !== 'archived').length;
    
    if (!isAdmin && activePlansCount >= planLimit) {
      setToast({ 
        message: isTrial 
          ? `You have reached your limit of ${planLimit} active plan. Please archive your previous month to generate a new plan.` 
          : `Active plan limit reached (${planLimit}). Please archive an old plan to generate a new one.`, 
        type: 'error' 
      });
      return;
    }

    const isRegen = (brand.usage?.plansGeneratedThisMonth ?? 0) >= (hasTrialHistory && brand.subscription?.plan === 'solo' ? 2 : 1);
    let tokenCost = isBlankPlan ? 0 : (isTrial ? 5 : (!isRegen ? 0 : 5));
    if (includeLatestTrends && !isBlankPlan) {
      tokenCost += 2; // Enhanced Strategic Focus surcharge
    }
    
    if (!isAdmin && tokens < tokenCost) {
      setToast({ message: `Insufficient tokens. You need ${tokenCost} tokens to generate/regenerate this plan.`, type: 'error' });
      return;
    }

    // Profitability Check for Plan Regeneration
    if (!isAdmin && tokenCost > 0) {
      const profit = calculateProfitability(brand);
      if (profit.margin < profit.target) {
        setToast({ message: "Margin limit reached. Contact support to enable further plan regenerations.", type: 'error' });
        return;
      }
    }

    setIsGenerating(true);
    try {
      let calendar: any[] = [];

      if (!isBlankPlan) {
        // Fetch performance insights to guide generation
        let performanceInsightsText = "";
        try {
          const platformsToFetch = ['Instagram', 'Facebook', 'Twitter'].filter(p => 
            brand.socialAccounts?.[p as any]
          );

          if (platformsToFetch.length > 0) {
            const insightsPromises = platformsToFetch.map(async (platform) => {
              try {
                const res = await axios.get('/api/social/insights', {
                  params: { brandId: activeBrandId, platform }
                });
                if (res.data?.data) {
                  const insightsStr = JSON.stringify(res.data.data, null, 2);
                  return `--- ${platform} Insights ---\n${insightsStr.length > 5000 ? insightsStr.substring(0, 5000) + "..." : insightsStr}`;
                }
                return "";
              } catch (e) {
                console.warn(`Failed to fetch insights for ${platform}:`, e);
                return "";
              }
            });
            
            const results = await Promise.all(insightsPromises);
            performanceInsightsText = results.filter(Boolean).join('\n\n');
          }
        } catch (err) {
          console.warn("Could not fetch performance insights for content generation:", err);
        }

        let latestTrendsText = "";
        if (includeLatestTrends) {
          try {
            const trendsSnap = await getDocs(query(
              collection(db, "trend_scans"),
              where("brandId", "==", activeBrandId),
              orderBy("date", "desc"),
              limit(1)
            ));
            if (!trendsSnap.empty) {
              const trendData = trendsSnap.docs[0].data();
              latestTrendsText = `Trend Data from ${trendData.date}:\n`;
              if (trendData.trends) {
                latestTrendsText += trendData.trends.map((t: any) => 
                  `Concept: ${t.concept}\nSummary: ${t.summary}\nComment: ${t.comment}`
                ).join('\n\n');
              }
            }
          } catch (err) {
            console.warn("Failed to fetch latest trends for plan generation:", err);
          }
        }

        calendar = await generateMonthlyCalendar(brand, highlights, products, goals, month, year, isTrial, performanceInsightsText, numberOfPosts === 'upload' ? 'upload' : Number(numberOfPosts), targetChannel, customInstructions, latestTrendsText);
      }
      
      const isImpersonating = !!localStorage.getItem('impersonatedUserId');

      const planData: any = {
        ownerId: effectiveUserId,
        uid: effectiveUserId,
        brandId: activeBrandId,
        month,
        year,
        highlights: isBlankPlan ? [] : highlights,
        products: isBlankPlan ? "" : products,
        goals: isBlankPlan ? "" : goals,
        calendar: calendar.map(item => ({ 
          ...item, 
          status: 'pending',
          slides: item.slides.map((s: any) => ({ ...s, url: '', thumbnailUrl: '' }))
        })),
        status: 'generated',
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        ...(isImpersonating && { admin_action: true })
      };
      const planDoc = await addDoc(collection(db, 'plans'), planData);
      
      // Deduct tokens and update usage (skip for admin)
      // Done AFTER addDoc to ensure user is only charged/limited if plan successfully saves
      if (!isAdmin) {
        // Usage stats stay on Brand for per-brand analytics
        const brandUpdate: any = {
          'usage.plansGeneratedThisMonth': increment(1)
        };
        await updateDoc(doc(db, 'brands', activeBrandId), brandUpdate);

        // Tokens are shared on User account
        if (tokenCost > 0) {
          await updateDoc(doc(db, 'users', brand.ownerId), {
            tokens: increment(-tokenCost)
          });
        }
        
        await logAudit(brand, 'plan_generated', isBlankPlan ? 0 : INTERNAL_COSTS.plan, tokens, tokens - tokenCost, planDoc.id, 'success', { month, year, isRegen });
      }

      setToast({ 
        message: isBlankPlan 
          ? (isBrazil ? "Plano em branco criado! Comece a adicionar posts personalizados." : "Blank custom plan created! Start adding your custom posts.") 
          : "Content plan generated!", 
        type: 'success' 
      });
      setActiveTab('plans');
    } catch (err: any) {
      console.error("Failed to generate calendar:", err);
      if (brand) {
        await logAudit(brand, 'plan_generation_failed', 0, tokens, tokens, undefined, 'failed', { error: err.message });
      }
      const errorMsg = err.message || (typeof err === 'string' ? err : JSON.stringify(err));
      
      if (errorMsg.includes("API key expired") || errorMsg.includes("API_KEY_INVALID")) {
        setHasApiKey(false);
        setToast({ message: "API key expired. In published environments, please update PLATAPOST_AI_KEY or GEMINI_API_KEY in your Cloud Run variables. In AI Studio, open the preview again.", type: 'error' });
      } else if (errorMsg.includes("403") || errorMsg.includes("Forbidden")) {
        setToast({ message: "API Key restricted (403). For Google Cloud, referrers are often hidden in previews. Use an unrestricted API key, or if restricting, try '*.run.app/*' or use IP/API-level restrictions instead.", type: 'error' });
      } else if (errorMsg.includes("429") || errorMsg.includes("RESOURCE_EXHAUSTED")) {
        setToast({ message: "Rate limit exceeded (Too Many Requests). Please wait a few moments and try again.", type: 'error' });
      } else {
        setToast({ message: `Failed to generate plan: ${errorMsg}`, type: 'error' });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const togglePlanExpansion = (planId: string) => {
    setExpandedPlanIds(prev => {
      const next = new Set(prev);
      if (next.has(planId)) next.delete(planId);
      else next.add(planId);
      return next;
    });
  };

  useEffect(() => {
    if (editingPostData && plans.length > 0) {
      // Logic for editing post data synchronization removed for simplicity
    }
  }, [plans]);

  const handleUpdatePlan = async (planId: string, updates: Partial<MonthlyPlan>, silent: boolean = false) => {
    if (!canEdit(brand)) {
      setToast({ message: "You do not have permission to update plans for this brand.", type: 'error' });
      return;
    }
    try {
      const isImpersonating = !!localStorage.getItem('impersonatedUserId');
      const finalUpdates: any = { ...updates };
      
      // Robustly ensure calendar is stringified if it's an array to avoid nested array errors in Firestore
      if (finalUpdates.calendar && Array.isArray(finalUpdates.calendar)) {
        // Safety check: ensure no large base64 strings are in the calendar
        const cleanedCalendar = await Promise.all(finalUpdates.calendar.map(async (item: any) => {
          if (item.slides) {
            const cleanedSlides = await Promise.all(item.slides.map(async (slide: any) => {
              if (slide.quickAddImages) {
                const cleanedQuickAdd = await Promise.all(slide.quickAddImages.map(async (img: any) => {
                  if (img.url && img.url.startsWith('data:')) {
                    try {
                      const hash = await getBase64Hash(img.url);
                      const storageRef = ref(storage, `artwork/${user!.uid}/quick-add/qa_${hash}_${img.name || 'image'}`);
                      
                      // Check if exists
                      let url = "";
                      try {
                        url = await getDownloadURL(storageRef);
                      } catch (e) {
                         await uploadString(storageRef, img.url, 'data_url');
                         url = await getDownloadURL(storageRef);
                      }
                      return { ...img, url };
                    } catch (e) {
                      console.error("Safety cleanup upload failed:", e);
                      return img;
                    }
                  }
                  return img;
                }));
                return { ...slide, quickAddImages: cleanedQuickAdd };
              }
              return slide;
            }));
            return { ...item, slides: cleanedSlides };
          }
          return item;
        }));
        finalUpdates.calendar = JSON.stringify(cleanedCalendar);
      }
      
      // Sanitize updates to remove undefined values which can cause Firestore errors
      Object.keys(finalUpdates).forEach(key => {
        if (finalUpdates[key] === undefined) {
          delete finalUpdates[key];
        }
      });
      
      await updateDoc(doc(db, 'plans', planId), {
        ...finalUpdates,
        updatedAt: new Date().toISOString(),
        ...(isImpersonating && { admin_action: true })
      });
      if (!silent) {
        setToast({ message: "Plan updated.", type: 'success' });
      }
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, `plans/${planId}`);
      setToast({ message: "Failed to update plan.", type: 'error' });
    }
  };

  const handleDuplicatePost = async (planId: string, postId: string) => {
    if (!isAdmin && isTrialUI) {
      setToast({ message: "Upgrade account to unlock feature and other amazing tools.", type: 'error' });
      return;
    }
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    const postToDuplicate = plan.calendar.find(p => p.id === postId);
    if (!postToDuplicate) return;

    const newPost = {
      ...postToDuplicate,
      id: `post_${Math.random().toString(36).substr(2, 9)}`,
      status: 'pending' as const,
      platformStatus: {}, // Reset status for the new post
    };

    const updatedCalendar = [...plan.calendar, newPost];
    // Sort by date to keep it organized
    updatedCalendar.sort((a, b) => (a.date || '').localeCompare(b.date || ''));

    await handleUpdatePlan(planId, { calendar: updatedCalendar });
    setToast({ message: "Post duplicated successfully!", type: 'success' });
  };

  const handleRefinePlan = async (planId: string, refinementPrompt: string, targetChannels?: string[]) => {
    if (!user || !brand || !activeBrandId) return;

    if (!canEdit(brand)) {
      setToast({ message: "You do not have permission to edit plans for this brand.", type: 'error' });
      return;
    }

    const tokenCost = 1;
    if (!isAdmin && tokens < tokenCost) {
      setToast({ message: `Insufficient tokens. You need ${tokenCost} token to refine this plan.`, type: 'error' });
      return;
    }

    const originalPlan = plans.find(p => p.id === planId);
    if (!originalPlan) return;

    try {
      setToast({ message: "Refining plan... this might take a minute.", type: 'success' });
      const refinedCalendar = await refineMonthlyPlan(brand, originalPlan.calendar, refinementPrompt, targetChannels);

      await handleUpdatePlan(planId, {
        calendar: refinedCalendar,
        lastRefinementPrompt: refinementPrompt || ""
      });

      if (!isAdmin) {
        const ownerRef = doc(db, 'users', brand.ownerId);
        await updateDoc(ownerRef, {
          tokens: increment(-tokenCost)
        });
      }

      setToast({ message: "Plan refined successfully!", type: 'success' });
    } catch (err: any) {
      console.error("Failed to refine plan:", err);
      const errorMsg = err.message || (typeof err === 'string' ? err : JSON.stringify(err));
      if (errorMsg.includes('403') || errorMsg.includes('API_KEY_HTTP_REFERRER_BLOCKED') || errorMsg.includes('Forbidden')) {
        setToast({ message: `API Key restricted (403). For Google Cloud, referrers are often hidden in previews. Use an unrestricted API key, or if restricting, try '*.run.app/*' or use IP/API-level restrictions instead.`, type: 'error' });
      } else {
        setToast({ message: `Failed to refine plan: ${errorMsg}`, type: 'error' });
      }
    }
  };

  const handleDuplicatePlan = async (planId: string, refinementPrompt?: string) => {
    if (!user || !brand || !activeBrandId) return;

    if (!canEdit(brand)) {
      setToast({ message: "You do not have permission to duplicate plans for this brand.", type: 'error' });
      return;
    }

    const isTrial = isTrialUI;
    const hasTrialHistory = (brand.usage?.trialGenerationsUsed ?? 0) > 0 || (plans.length > 0);
    const isSpecialUser = user?.email?.toLowerCase() === 'dewald.jordan@sparkpr.com' || user?.email?.toLowerCase() === 'dewaldjordaan5@gmail.com' || brand.companyName?.toLowerCase().includes('ventura');
    const planLimit = isTrial ? 1 : (brand.subscription?.plan === 'solo' ? (isSpecialUser ? 5 : 3) : (brand.subscription?.plan === 'agency' ? 30 : 10));
    
    const activePlansCount = plans.filter(p => p.status !== 'archived').length;

    if (!isAdmin && activePlansCount >= planLimit) {
      setToast({ 
        message: isTrial 
          ? `You have reached your limit of ${planLimit} active plan. Please archive your previous month to duplicate an old plan.` 
          : `Active plan limit reached (${planLimit}). Please archive an old plan to duplicate this one.`, 
        type: 'error' 
      });
      return;
    }

    const originalPlan = plans.find(p => p.id === planId);
    if (!originalPlan) return;

    try {
      let refinedCalendar = [...originalPlan.calendar];

      if (refinementPrompt && refinementPrompt.trim()) {
        setToast({ message: "Refining duplicated plan... this might take a minute.", type: 'success' });
        refinedCalendar = await refineMonthlyPlan(brand, originalPlan.calendar, refinementPrompt);
      } else {
        // Reset IDs for the new plan if it's a direct copy
        refinedCalendar = refinedCalendar.map(post => ({
          ...post,
          id: `post_${Math.random().toString(36).substr(2, 9)}`,
          status: 'pending' as const,
          platformStatus: {},
          slides: post.slides.map(s => ({ ...s, id: `slide_${Math.random().toString(36).substr(2, 9)}` }))
        }));
      }

      const newPlan: any = {
        ownerId: effectiveUserId,
        uid: effectiveUserId,
        brandId: brand.id!,
        month: originalPlan.month,
        year: originalPlan.year,
        title: (originalPlan.title || `${originalPlan.month} ${originalPlan.year}`) + " (Copy)",
        highlights: originalPlan.highlights,
        products: originalPlan.products,
        goals: originalPlan.goals,
        calendar: JSON.stringify(refinedCalendar),
        status: 'draft',
        lastRefinementPrompt: refinementPrompt || "",
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'plans'), newPlan);

      // Update usage
      if (!isAdmin) {
        await updateDoc(doc(db, 'brands', brand.id!), {
          usage: {
            ...brand.usage,
            plansGeneratedThisMonth: (brand.usage?.plansGeneratedThisMonth ?? 0) + 1
          }
        });
      }

      setToast({ message: "Plan duplicated successfully!", type: 'success' });
      setExpandedPlanIds(prev => new Set(prev).add(docRef.id));
    } catch (err) {
      console.error("Failed to duplicate plan:", err);
      setToast({ message: "Failed to duplicate plan.", type: 'error' });
    }
  };

  const handleDeletePlan = async (planId: string) => {
    try {
      await deleteDoc(doc(db, 'plans', planId));
      setToast({ message: "Plan deleted successfully.", type: 'success' });
    } catch (err) {
      console.error("Error deleting plan:", err);
      setToast({ message: "Failed to delete plan.", type: 'error' });
    }
  };

  const handleGenerateArtworkForPost = async (
    planId: string, 
    postId: string, 
    slideId: string, 
    concept: string, 
    type: 'image' | 'video' = 'image', 
    useBrandElements: boolean = true,
    currentSlideData?: any,
    refinementPrompt?: string,
    inspirationImage?: string
  ): Promise<string | void> => {
    if (!user || !brand || !activeBrandId) return;

    if (!canEdit(brand)) {
      setToast({ message: "You do not have permission to generate content for this brand.", type: 'error' });
      return;
    }

    // --- GUARDRAIL: Concurrent Execution Lock ---
    const lockKey = `${planId}_${postId}_${slideId}`;
    if (processingRef.current.has(lockKey)) {
      console.warn("[Guardrail] Already processing this artwork, skipping duplicate request.");
      return;
    }

    if (isSystemPaused) {
      setToast({ message: "System Maintenance: Generations are temporarily paused.", type: 'error' });
      return;
    }

    // --- GUARDRAIL: Global Circuit Breaker ---
    const breakerError = checkCircuitBreaker(brand.id);
    if (breakerError) {
      setToast({ message: `Security Warning: ${breakerError}. System paused for safety.`, type: 'error' });
      return;
    }

    // --- GUARDRAIL: Profitability Guardrail (Internal Cost vs Revenue) ---
    if (!isAdmin) {
      const profit = calculateProfitability(brand);
      const nextInternalCost = type === 'video' ? INTERNAL_COSTS.video : INTERNAL_COSTS.image;
      const projectedCost = profit.cost + nextInternalCost;
      const projectedMargin = ((profit.revenue - projectedCost) / profit.revenue) * 100;
      
      if (projectedMargin < profit.target) {
        setToast({ 
          message: `Economic Guardrail: This generation would drop your plan margin below ${profit.target}%. Please contact support to upgrade your limits.`, 
          type: 'error' 
        });
        await logAudit(brand, `profit_block_${type}`, 0, tokens, tokens, planId, 'failed', { 
          reason: 'margin_too_low', 
          currentMargin: profit.margin, 
          projectedMargin 
        });
        return;
      }
    }

    const tokenCost = type === 'video' ? 12 : 1;
    if (!isAdmin && tokens < tokenCost) {
      setToast({ message: `Insufficient tokens. You need ${tokenCost} token${tokenCost > 1 ? 's' : ''} to generate artwork for this post.`, type: 'error' });
      return;
    }
    const availableTokens = tokens;
    
    try {
      processingRef.current.add(lockKey);

      // Fetch fresh plan data to prevent overwriting results from concurrent/sequential batch runs
      const planDoc = await getDoc(doc(db, 'plans', planId));
      if (!planDoc.exists() || !user) return;
      const planData = planDoc.data();
      
      // Handle potential stringified calendar from legacy data
      let calendar = planData.calendar || [];
      if (typeof calendar === 'string') {
        try {
          calendar = JSON.parse(calendar);
        } catch (e) {
          console.error("Failed to parse calendar for plan", planDoc.id, e);
          calendar = [];
        }
      }

      const plan = { id: planDoc.id, ...planData, calendar } as MonthlyPlan;

      // Check video limit for Solo
      if (type === 'video' && !isAdmin) {
        const currentVideos = plan.calendar.reduce((acc, p) => 
          acc + (p.slides?.filter(s => s.isVideo && s.url).length || 0), 0
        );
        if (currentVideos >= 3) {
          setToast({ message: "Solo Tier Limit: You have reached your limit of 3 videos for this month.", type: 'error' });
          return;
        }
      }

      const post = plan.calendar.find(p => p.id === postId);
      if (!post) return;

      const aspectRatio = post.format === 'story' ? '9:16' : '1:1';
      const videoAspectRatio = post.format === 'story' ? '9:16' : '16:9';

      console.log(`Starting handleGenerateArtworkForPost (${type}) for post:`, postId, "slide:", slideId, "Use Brand Elements:", useBrandElements);
      if (currentSlideData) {
        console.log("[GenerateArtwork] Using passed slide data. Selected products:", currentSlideData.selectedProducts?.length);
      }
      
      const isTrial = isTrialUI;
      if (!isAdmin && isTrial && type === 'video') {
        setToast({ message: "Upgrade account to unlock video generation and other amazing tools.", type: 'error' });
        return;
      }

      // Video and image generation are handled dynamically based on active tokens allocation
      
      if (type === 'video' && !hasApiKey) {
        setToast({ message: "Video generation requires a paid API key. Please contact the administrator.", type: 'error' });
        return;
      }

      let brandContext = "";
      if (useBrandElements) {
        let contextParts = [];
        
        // Add questionnaire vibes if available
        if (brand?.questionnaire?.brandVoice) contextParts.push(`Brand Voice: ${brand.questionnaire.brandVoice}`);
        if (brand?.questionnaire?.visualAesthetic) contextParts.push(`Visual Aesthetic: ${brand.questionnaire.visualAesthetic}`);
        if (brand?.colorPalette && brand.colorPalette.length > 0) contextParts.push(`Brand Color Palette: ${brand.colorPalette.join(', ')}`);
        if (brand?.brandingDetails) contextParts.push(`Overall Branding: ${brand.brandingDetails}`);

        if (brand?.visualStyles && brand.visualStyles.length > 0) {
          const stylesStr = brand.visualStyles.map(s => 
            `CONTEXT/USAGE: ${s.usage}\nAESTHETIC: ${s.aesthetic}\nPROMPT DNA: ${s.promptDNA}`
          ).join('\n\n');
          contextParts.push(`SPECIFIC VISUAL STYLES & PREFERENCES:\n${stylesStr}`);
        }

        if (brand?.contextDocuments && brand.contextDocuments.length > 0) {
          const docContents = await Promise.all(brand.contextDocuments.map(async (doc) => {
            try {
              if (doc.url.startsWith('data:')) {
                const base64 = doc.url.split(',')[1];
                return decodeURIComponent(escape(atob(base64)));
              }
              const res = await fetch(doc.url);
              const text = await res.text();
              // Truncate individual documents so distillBrandContext has enough material but isn't overwhelmed
              return text.length > 10000 ? text.substring(0, 10000) + "..." : text;
            } catch (e) {
              console.error("Failed to fetch context doc:", doc.name, e);
              return "";
            }
          }));
          contextParts.push(...docContents.filter(Boolean));
        }
        brandContext = contextParts.join("\n\n---\n\n");
      }

      let mediaData = "";
      let currentLt: any = null;
      // Prioritize passed slide data (local state from editor) over global state
      const slide = currentSlideData || post.slides?.find(s => s.id === slideId);
      
      const slides = post.slides || [];
      const slideIndex = slides.findIndex((s: any) => s.id === slideId);
      const totalSlidesCount = slides.length;
      
      if (type === 'video') {
        let videoPrompt = concept;
        if (useBrandElements) {
          // Fallback for legacy productImageUrl if selectedProducts is empty
          const productsToUse = (slide?.selectedProducts && slide.selectedProducts.length > 0) 
            ? slide.selectedProducts 
            : (slide?.productImageUrl) 
              ? [{ url: slide.productImageUrl, name: "Product", description: "Main product image" }]
              : [];

          if (productsToUse.length > 0) {
            videoPrompt += "\n\nCRITICAL: You MUST realistically showcase these products in the video. They should be the focal point. Use the following details:";
            videoPrompt += productsToUse.map((p: any) => `\n- ${p.name}: ${p.description || 'Showcase naturally.'} ${p.price ? `(Price: ${p.price})` : ''}`).join('');
          }
          if (brand?.brandTemplates && brand.brandTemplates.length > 0) {
            videoPrompt += "\n\nFollow these brand templates/styles for visual consistency: " + brand.brandTemplates.map(t => `${t.name} (${t.description || 'Follow visual style.'})`).join(', ');
          }
        }
        
        if (refinementPrompt) {
          videoPrompt += `\n\nUSER'S REFINEMENT REQUEST: ${refinementPrompt}\nYou must ensure the generation incorporates this constraint or change.`;
        }

        const startingImage = inspirationImage || slide?.url || undefined;
        mediaData = await generateVideo(videoPrompt, videoAspectRatio, false, brandContext, startingImage);
      } else {
        // Fallback for legacy productImageUrl if selectedProducts is empty
        const productsToUse = [
          ...((useBrandElements && slide?.selectedProducts) ? slide.selectedProducts : []),
          ...(slide?.quickAddImages || [])
        ];
        
        if (productsToUse.length === 0 && useBrandElements && slide?.productImageUrl) {
          productsToUse.push({ url: slide.productImageUrl, name: "Product", description: "Main product image" });
        }

        // Resolve Custom Layout Template Instructions early to guide generation
        currentLt = slide?.layoutTemplate;
        const isVolterra = brand?.companyName?.toLowerCase().includes('volterra');
        if (isVolterra && !currentLt && brandBlueprints && brandBlueprints.length > 0) {
          const slideConceptLower = (slide?.concept || "").toLowerCase();
          const slideTextLower = (slide?.overlayText || "").toLowerCase();
          let matchedBp = brandBlueprints.find(bp => {
            const bpName = (bp.name || "").toLowerCase();
            const bpCat = (bp.category || "").toLowerCase();
            return (bpName && (slideConceptLower.includes(bpName) || slideTextLower.includes(bpName))) || 
                   (bpCat && (slideConceptLower.includes(bpCat) || slideTextLower.includes(bpCat)));
          });
          if (!matchedBp && slideIndex !== -1) {
            matchedBp = brandBlueprints[slideIndex % brandBlueprints.length];
          }
          if (matchedBp && matchedBp.layoutTemplate) {
            let bpLt = JSON.parse(JSON.stringify(matchedBp.layoutTemplate));
            if (bpLt.boxes && bpLt.boxes.length > 0) {
              bpLt.boxes = parseOverlayTextToBoxes(slide?.overlayText || "", bpLt.boxes);
            }
            bpLt.canvasEditingMode = "overlay";
            currentLt = bpLt;
          }
        }

        const isQuickFixingComposedSlide = !!refinementPrompt && (
          refinementPrompt.startsWith("Quick fix:") || 
          (!!inspirationImage && inspirationImage === slide?.url)
        );

        const finalInspiration = isQuickFixingComposedSlide 
          ? (inspirationImage || slide?.url)
          : (inspirationImage || slide?.backgroundSliceUrl || currentLt?.backgroundImage);

        const slideNumberString = (slideIndex !== -1 && totalSlidesCount > 1 && currentLt?.enableFooter)
          ? `${String(slideIndex + 1).padStart(2, '0')} / ${String(totalSlidesCount).padStart(2, '0')}`
          : "";

        const logoInstruction = brand?.logoUrl 
          ? `STRICT RULE - LOGO PLACEMENT: You MUST use the official BRAND LOGO provided as a reference. Place it cleanly in the top-left or top-right corner over the canvas exactly as set in the template style, completely replacing and overwriting any outdated, hallucinated, or generic logo text/shapes from the template background.`
          : "";

        const slideNumberInstruction = slideNumberString
          ? `STRICT RULE - DYNAMIC SLIDE NUMBERING: Find any outdated, template, or hardcoded slide numbers/counters at the bottom edge (e.g. '01 / 06') and completely REPLACE and overwrite them with the dynamic actual page counter: "${slideNumberString}". Draw this elegantly and cleanly centered or aligned in the bottom corner matching the layout's margin system.`
          : "";

        let finalRefinement = undefined;

        if (isQuickFixingComposedSlide) {
          finalRefinement = `PRECISE_IMAGE_REFINEMENT_QUICK_FIX.
TASK: Modify the provided fully-designed slide image according to the user's quick fix request: "${refinementPrompt}".
STRICT RULE 1: The reference image is the fully-composed slide (it already contains background elements, main visual objects, and overlay typography).
STRICT RULE 2: Perform ONLY the requested modification. Do not add redundant overlays, do not duplicate existing text headers or labels, and do not repeat the main visual subject.
STRICT RULE 3: Keep the entire design and visual aesthetic (background color, leafy outlines, fonts, layout, and style of unaffected items) matches 100% with the reference image so it's perfectly consistent with other slides.
USER'S SPECIFIC ACTION REQUEST: "${refinementPrompt}".`;
        } else if (slide?.backgroundSliceUrl) {
          const slideText = slide?.overlayText || "";
          finalRefinement = `SEAMLESS_PANORAMA_SLIDE_OVERLAY.
TASK: Overlay the slide visual subject and typography onto this master background panel slice.
STRICT RULE 1 (BACKDROP PRESERVATION): Preserve 100% of the underlying background colors, flows, margins, and scene layout from the reference image. If the reference image or template contains pre-existing text placeholders, slide numbers/counters, logos, or headers, you MUST completely erase, paint over, and blank them out. Replace them solely, cleanly, and elegantly with the new typography and slide indicators. No text duplication or ghostly leakage is permitted.
STRICT RULE 2 (PORTRAIT ENHANCEMENT): Realistically draw or overlay the main visual subject: ${concept} onto the canvas backdrop. If drawing a person (practitioner, dentist, patient, woman, man, or model), you MUST isolate them cleanly against a perfectly flat, quiet, solid background matching the brand templates, with NO clinics, NO dental chairs/syringes, and NO busy clinic rooms behind them. Keep portraits clean, simple, and confidence-focused (prefer a warm, naturally smiling female dental/practitioner scrub-portrait, no clinical machinery).
STRICT RULE 3: For diagram slides, render a beautiful 2D vector schematic with clear component labels and indicator lines so the layout has comprehensive educational information.
STRICT RULE 4 (STRICT TEXT CONTAINER COMPOSITION): You MUST place, align, and print all overlay typography text: "${slideText}" ONLY inside the designated empty text containers/slots of the template layout (such as the off-white centrally positioned card box/panel). You are STRICTLY FORBIDDEN from drawing, printing, or duplicate-rendering the text on the outside green portions or surrounding panels. If the template layout card contains pre-drawn checkmarks or bullet checkboxes, write each text line horizontally next to each checkbox, perfectly matching them. Do not create secondary checkmarks or double print the text.
${logoInstruction ? `STRICT RULE 5: ${logoInstruction}\n` : ''}${slideNumberInstruction ? `STRICT RULE 6: ${slideNumberInstruction}\n` : ''}${refinementPrompt ? `\n\nUSER'S QUICK-FIX / SPECIFIC ADJUSTMENT REQUEST: "${refinementPrompt}"\nYou MUST apply this requested fix precisely while adhering to all composition and background preservation rules above.` : ''}`;
        } else {
          // Standard case: Generate a pristine, textless scenic backdrop and let React layer the editable copy text on top dynamically.
          // BUT only enforce textless backdrops if using the React layer (e.g. Volterra or if a blueprint is applied).
          if (isVolterra || currentLt) {
            const customRefinement = refinementPrompt ? `\n\nUSER'S ADJUSTMENT REQUEST: "${refinementPrompt}"\nYou MUST apply this requested adjustment precisely while adhering to the strict textless rules below.` : '';
            finalRefinement = `STANDARD_SLIDELESS_OVERLAY (STRICT TEXTLESS BACKDROP INSTRUCTION):
TASK: Generate a high-quality visual backdrop suitable for a social media slide based on the concept: "${concept}".
STRICT RULE 1 (SCENE GENERATION): Realistically draw or represent the main visual subject: "${slide?.imagePrompt || concept}". Keep it aligned, leaving plenty of negative space (typically in the top 40% or where text overlays would naturally sit).
STRICT RULE 2 (STRICTLY NO LETTERS/TEXT): Strictly do NOT write, print, or render any letters, text, words, numbers, labels, or logos on this image. It must be a 100% clean, empty, textless scenic backdrop.
STRICT RULE 3: Do NOT draw any mockup templates, frames, signs, or buttons. The final output must be a pure, high-fidelity background image suitable for dynamic text wrapping on top.${customRefinement}`;
          } else {
            finalRefinement = refinementPrompt;
          }
        }
        
        let finalConcept = concept;
        if (currentLt?.name && !finalConcept.includes('[TEMPLATE:')) {
          finalConcept = `[TEMPLATE: ${currentLt.name}]\n${finalConcept}`;
        }
        if (!isQuickFixingComposedSlide) {
           let slideText = slide?.overlayText || "";
           slideText = slideText.replace(/\[(?:EYEBROW|HEADING|BODY|BULLETS|CTA)\]\s*/ig, (match) => {
               return match.toLowerCase().includes('body') || match.toLowerCase().includes('bullets') || match.toLowerCase().includes('cta') ? '\n' : '';
           }).trim();
           // Only embed the text instructions directly if we are NOT using the React overlay DOM layer.
           const isOverlayActive = currentLt && ((currentLt.canvasEditingMode || "original") === "overlay");
           if (slideText && !isOverlayActive) {
               finalConcept = `${concept}\n\nSTRICT TEXT LAYOUT RULE: Write this exact text cleanly and perfectly on the image according to the underlying layout:\n"${slideText}"`;
           }
        }

        if (currentLt) {
          const lt = currentLt;
          const isOverlayMode = (lt.canvasEditingMode || "original") === "overlay";
          
          if (isOverlayMode) {
            let compositionAdvice = "";
            if (lt.enableContentPlaque) {
              const pLeft = lt.plaqueLeft ?? 10;
              const pWidth = lt.plaqueWidth ?? 80;
              const pTop = lt.plaqueTop ?? 15;
              const pHeight = lt.plaqueHeight ?? 70;
              
              if (lt.plaqueStyle === "split-left" || (pLeft < 15 && pWidth > 45 && pTop === 0 && pHeight === 100)) {
                compositionAdvice = "\nSTRICT VISUAL COMPOSITION REQUIREMENT: Since the text content cover card is positioned on the LEFT half (coordinates Left 0% to 55%), you MUST compose the background graphic so that the main visual subject (such as the tooth model, practitioner/dentist, person, or object) is positioned on the RIGHT half of the image (between 60% and 100% of horizontal space). Keep the left 55% completely quiet, flat, empty, or simple as background padding.";
              } else if (lt.plaqueStyle === "split-right" || (pLeft > 40 && pWidth > 45 && pTop === 0 && pHeight === 100)) {
                compositionAdvice = "\nSTRICT VISUAL COMPOSITION REQUIREMENT: Since the text content cover card is positioned on the RIGHT half (coordinates Left 45% to 100%), you MUST compose the background graphic so that the main visual subject is positioned on the LEFT half of the image (between 0% and 40% of horizontal space). Keep the right 55% completely quiet, flat, empty, or simple as background padding.";
              } else if (lt.plaqueStyle === "bottom-story" || (pTop > 45 && pHeight > 35)) {
                compositionAdvice = "\nSTRICT VISUAL COMPOSITION REQUIREMENT: Since the text content cover card takes up the BOTTOM portion (Top coord > 50%), you MUST position the main visual subject clearly in the TOP half or top-middle of the image. Keep the bottom half clean, quiet, and simple as background padding.";
              }
            }

            const currentBoxes = lt.boxes || [];
            const headingBox = currentBoxes.find((b: any) => b.type === "heading" || b.id === "h1" || (b.type || "").toLowerCase().includes("heading"));
            const headingText = headingBox?.text ? headingBox.text.trim() : "";
            
            const bodyBox = currentBoxes.find((b: any) => b.type === "body" || b.id === "b1" || (b.type || "").toLowerCase().includes("body") || b.type === "subheading");
            const bodyText = bodyBox?.text ? bodyBox.text.trim() : "";

            const bulletBox = currentBoxes.find((b: any) => b.type === "icon-list" || b.id === "bullets" || (b.type || "").toLowerCase().includes("list"));
            const bulletText = bulletBox?.text ? bulletBox.text.trim() : "";

            let contentVibe = "";
            if (headingText || bodyText || bulletText) {
              contentVibe = `\n- SLIDE'S ACTUAL TEXT OVERLAY COPYWRITING CONTEXT (to align visual motif and theme):`;
              if (headingText) contentVibe += `\n  * Main Heading: "${headingText}"`;
              if (bodyText) contentVibe += `\n  * Body/Subheading: "${bodyText}"`;
              if (bulletText) contentVibe += `\n  * Highlights/Lists: "${bulletText}"`;
            }

            const rawSceneDescription = slide?.imagePrompt || concept;
            const finalSceneDescription = lt.bgGenPrompt 
              ? `${lt.bgGenPrompt} (context: ${rawSceneDescription})`
              : rawSceneDescription;

            let layoutPrompt = `CRITICAL BACKGROUND-ONLY GENERATION INSTRUCTIONS:
We are generating a background image for a slide where editable text boxes and structural plaques are dynamically placed on top by the browser. 
- Image Concept/Scene description to draw: "${finalSceneDescription}"${contentVibe}
- STRICT RULE 1: STRICTLY NO TYPOGRAPHY. Strictly do NOT write, print, or render any letters, words, numbers, characters, titles, or logos on this image. It must be a 100% clean, text-free scenic backdrop.
- STRICT RULE 2: IGNORE PLAQUE AND TEXT BOX DRAWING INSTRUCTIONS. The concept might describe a "plaque", "bento grid", or "text blocks". DO NOT draw any structural cards, mockup templates, plaques, buttons, or frames. You MAY respect spatial/alignment instructions (e.g., if it says 'photograph on the right 45%', draw the subject on the right). However, leave the text-areas as clean negative space (e.g., empty wall, simple gradient, or blurred background) so the browser can overlay the HTML plaque and text later. Your ONLY job is to draw the realistic photograph or background scene.${compositionAdvice}`;
            
            if (refinementPrompt) {
              finalRefinement = `${layoutPrompt}\n\nUSER'S ADJUSTMENT REQUEST: "${refinementPrompt}"`;
            } else if (slide?.backgroundSliceUrl) {
              finalRefinement = `${layoutPrompt}\nSTRICT RULE 3 (BACKDROP MATCH): You MUST preserve 100% of the underlying background colors, flows, margins, and scene layout from the provided panorama reference image to ensure a seamless carousel.`;
            } else if (currentLt?.backgroundImage) {
              finalRefinement = `${layoutPrompt}\nSTRICT RULE 3 (STYLE MATCH & TEXT REMOVAL): You MUST use the provided inspiration image as a strict structural guide for colors, gradients, and photography framing. HOWEVER, if the inspiration image contains any dummy text (like 'Eyebrow text' or placeholders), YOU MUST COMPLETELY PAINT OVER IT AND ERASE ALL TEXT. Your output must be a pure, text-free scenic graphic matching the style, without any letters or placeholders. Strictly add the requested concept into the scene smoothly without altering the underlying clean style.`;
            } else {
              finalRefinement = layoutPrompt;
            }
          }
        }

        mediaData = await generateArtwork(
          finalConcept, 
          brand?.logoUrl, 
          aspectRatio, 
          productsToUse,
          useBrandElements ? brand?.brandTemplates : [],
          brandContext,
          finalInspiration,
          finalRefinement,
          brand?.id,
          brand?.approvedMedia
        );
      }
      
      console.log(`${type} generated successfully, starting upload...`);
      
      const thumbBase64 = await generateThumbnail(mediaData);
      
      let url = "";
      let thumbnailUrl = thumbBase64;

      const oldSlide = plan.calendar.find(p => p.id === postId)?.slides?.find(s => s.id === slideId);
      const oldUrl = oldSlide?.url;
      const oldThumbUrl = oldSlide?.thumbnailUrl;

      try {
        const isActuallyVideo = type === 'video' && mediaData.startsWith('data:video/');
        const ext = isActuallyVideo ? 'mp4' : 'png';
        const hash = await getBase64Hash(mediaData);
        const thumbHash = await getBase64Hash(thumbBase64);
        
        const storageRef = ref(storage, `artwork/${user.uid}/${planId}_${postId}_${slideId}_${hash}.${ext}`);
        const thumbRef = ref(storage, `artwork/${user.uid}/${planId}_${postId}_${slideId}_thumb_${thumbHash}.jpg`);
        
        // Use a timeout to trigger fallback faster if CORS blocks the request
        const uploadWithTimeout = (ref: any, data: string) => 
          Promise.race([
            uploadString(ref, data, 'data_url'),
            new Promise((_, reject) => setTimeout(() => reject(new Error("Storage upload timeout")), 120000))
          ]);

        await uploadWithTimeout(storageRef, mediaData);
        await uploadWithTimeout(thumbRef, thumbBase64);
        
        url = await getDownloadURL(storageRef);
        thumbnailUrl = await getDownloadURL(thumbRef);

        // cleanup old if successful and different
        if (oldUrl && oldUrl !== url) safeDeleteFile(oldUrl);
        if (oldThumbUrl && oldThumbUrl !== thumbnailUrl) safeDeleteFile(oldThumbUrl);

        // Save reference whether it was successfully generated as a video or image fallback
        const updatedCalendar = plan.calendar.map(item => {
          if (item.id === postId) {
            const updatedSlides = item.slides?.map(s => {
              if (s.id === slideId) {
                let lt = currentSlideData?.layoutTemplate || s.layoutTemplate;
                const slideIndex = item.slides?.findIndex((x: any) => x.id === s.id) ?? 0;
                
                // If standard image slide and no layout template, build a beautiful dynamic vector overlay template in editable overlay mode
                if (!isActuallyVideo) {
                  const initialText = s.overlayText || "";
                  const headingText = initialText.split("\n")[0] || "Your Title Here";
                  const bodyText = initialText.split("\n").slice(1).join("\n") || s.concept || "Detailed information goes here.";
                  
                  if (currentLt) {
                    lt = {
                      ...currentLt,
                      backgroundType: "image",
                      backgroundImage: url
                    };
                  } else if (!lt) {
                    lt = {
                      canvasEditingMode: "original",
                      backgroundType: "image",
                      backgroundColor: "#FAF8F4",
                      backgroundGradient: "linear-gradient(to bottom, #FAF8F4, #E2DCD5)",
                      backgroundImage: url,
                      enableContentPlaque: false,
                      plaqueStyle: "elegant",
                      plaqueBgColor: "#FFFFFF",
                      plaqueOpacity: 0.95,
                      plaqueBorderRadius: 16,
                      plaquePadding: 20,
                      plaqueTop: 15,
                      plaqueLeft: 10,
                      plaqueWidth: 80,
                      plaqueHeight: 70,
                      enableHeader: false,
                      headerStyle: "simple",
                      headerTextColor: "#333333",
                      headerBgColor: "transparent",
                      headerHeight: 10,
                      headerShowLogo: true,
                      headerShowEyebrow: false,
                      headerFontSize: 12,
                      enableFooter: true,
                      footerStyle: "simple",
                      footerText: brand?.companyName || "",
                      footerTextColor: "#666666",
                      footerBgColor: "transparent",
                      footerHeight: 10,
                      footerShowLogo: false,
                      footerShowPageNum: true,
                      footerShowUrl: false,
                      footerUrlAlign: "center",
                      footerTextAlign: "center",
                      footerPageNumAlign: "right",
                      logoPosition: "top-left",
                      logoAlignment: "left",
                      logoSize: 22,
                      logoTop: 6,
                      logoLeft: 6,
                      boxes: [
                        {
                          id: "h1",
                          type: "heading",
                          text: headingText,
                          top: 25,
                          left: 15,
                          width: 70,
                          height: 18,
                          fontSize: 22,
                          color: "#1A1A1A",
                          align: "center",
                          fontWeight: "bold"
                        },
                        {
                          id: "b1",
                          type: "body",
                          text: bodyText,
                          top: 48,
                          left: 15,
                          width: 70,
                          height: 25,
                          fontSize: 13,
                          color: "#4A4A4A",
                          align: "center",
                          fontWeight: "normal"
                        }
                      ]
                    };
                  } else {
                    lt = {
                      ...lt,
                      backgroundType: "image",
                      backgroundImage: url
                    };
                  }
                }

                return { 
                  ...s, 
                  ...(currentSlideData || {}), 
                  url, 
                  thumbnailUrl, 
                  layoutTemplate: lt,
                  backgroundSliceUrl: s.backgroundSliceUrl || currentSlideData?.backgroundSliceUrl || "",
                  isVideo: isActuallyVideo 
                };
              }
              return s;
            }) || [];
            return { ...item, slides: updatedSlides, status: 'pending' as const };
          }
          return item;
        });
        await handleUpdatePlan(planId, { calendar: updatedCalendar }, true);
      } catch (storageErr: any) {
        console.error("Storage upload failed:", storageErr);
        setToast({ message: "Failed to upload artwork to storage. Please check your connection.", type: 'error' });
        return; // Stop here, don't fallback to base64
      }
      
      // Deduct tokens (Moved inside transaction-like update for safety)
      if (!isAdmin && activeBrandId) {
        try {
          const brandRef = doc(db, 'brands', activeBrandId);
          // Tokens are shared on User account
          await updateDoc(doc(db, 'users', brand.ownerId), { 
            tokens: increment(-tokenCost)
          });
          // Usage stats stay on Brand for per-brand analytics
          await updateDoc(brandRef, {
            'usage.totalGenerations': increment(1),
            [type === 'video' ? 'usage.totalVideosGenerated' : 'usage.totalImagesGenerated']: increment(1)
          });
        } catch (tokenErr) {
          console.error("AI Token deduction failed:", tokenErr);
        }
      }
      return url;
    } catch (err: any) {
      if (err.message && (err.message.includes("API key expired") || err.message.includes("API_KEY_INVALID"))) {
        setHasApiKey(false);
        setToast({ message: "API key expired. In published environments, please update your PLATAPOST_AI_KEY environment variable. In AI Studio, open the preview again.", type: 'error' });
      } else if (err.message && (err.message.includes("403") || err.message.includes("Forbidden"))) {
        setToast({ message: "API Key restricted (403). For Google Cloud, referrers are often hidden in previews. Use an unrestricted API key, or if restricting, try '*.run.app/*' or use IP/API-level restrictions instead.", type: 'error' });
      }
      console.error("Error in handleGenerateArtworkForPost:", err);
      const errorMsg = err.message || (typeof err === 'string' ? err : JSON.stringify(err));
      
      if (errorMsg.includes('403') || errorMsg.includes('Forbidden')) {
        setToast({ 
          message: `Generation failed: Access Forbidden. The Gemini Image Generation model is restricted for this API key. Some countries or API key tiers do not support image generation yet.`, 
          type: 'error' 
        });
        // Scroll to settings or highlight the key button if possible
      } else if (errorMsg.includes('RESOURCE_EXHAUSTED') || errorMsg.includes('quota exceeded') || errorMsg.includes('429')) {
        setToast({ 
          message: `Generation failed: Quota Limit Exceeded. You have hit the Gemini API rate limit. Please wait about 1 minute before trying again, or try removing some reference images (products/templates) from the post to reduce the data sent.`, 
          type: 'error' 
        });
      } else {
        setToast({ message: `Failed to generate artwork: ${errorMsg}`, type: 'error' });
      }
    } finally {
      processingRef.current.delete(lockKey);
    }
  };

  const cropImageToSlices = async (imageUrl: string, numSlices: number): Promise<string[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        let finalSrc = imageUrl;
        if (imageUrl.startsWith('http')) {
          const response = await fetch(imageUrl, { referrerPolicy: 'no-referrer' });
          const blob = await response.blob();
          finalSrc = URL.createObjectURL(blob);
        }
        
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
          const sliceWidth = img.width / numSlices;
          const sliceHeight = sliceWidth; // Force square slices
          const startY = (img.height - sliceHeight) / 2; // Center vertically
          
          const slices: string[] = [];

          for (let i = 0; i < numSlices; i++) {
            const canvas = document.createElement('canvas');
            canvas.width = sliceWidth;
            canvas.height = sliceHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(img, i * sliceWidth, startY, sliceWidth, sliceHeight, 0, 0, sliceWidth, sliceHeight);
              slices.push(canvas.toDataURL('image/jpeg', 0.8));
            }
          }
          if (finalSrc.startsWith('blob:')) {
            URL.revokeObjectURL(finalSrc);
          }
          resolve(slices);
        };
        img.onerror = () => reject(new Error('Failed to load image for cropping'));
        img.src = finalSrc;
      } catch (err) {
        reject(err);
      }
    });
  };

  const handleGeneratePanoramaOnly = async (
    planId: string,
    postId: string,
    baseConcept: string,
    onProgress: (status: string) => void,
    useBrandElements: boolean = true
  ): Promise<string | null> => {
    if (!user || !brand || !activeBrandId) return null;

    if (!canEdit(brand)) {
      setToast({ message: "You do not have permission to generate content for this brand.", type: 'error' });
      return null;
    }

    if (isSystemPaused) {
      setToast({ message: "System Maintenance: Generations are temporarily paused.", type: 'error' });
      return null;
    }

    if (!isAdmin && tokens < 1) {
      setToast({ message: `Insufficient tokens. You need at least 1 token to generate the base panorama. You have ${tokens}.`, type: 'error' });
      return null;
    }

    // Profitability check block (cost 1 token)
    if (!isAdmin) {
      const profit = calculateProfitability(brand);
      const projectedCost = profit.cost + (1 * INTERNAL_COSTS.image);
      const projectedMargin = ((profit.revenue - projectedCost) / profit.revenue) * 100;
      
      if (projectedMargin < profit.target) {
        setToast({ 
          message: `Economic Guardrail: This generation would drop your plan margin below ${profit.target}%. Please contact support to upgrade your limits.`, 
          type: 'error' 
        });
        return null;
      }
    }

    let brandContext = "";
    let contextParts = [];
    if (useBrandElements) {
      if (brand?.questionnaire?.brandVoice) contextParts.push(`Brand Voice: ${brand.questionnaire.brandVoice}`);
      if (brand?.questionnaire?.visualAesthetic) contextParts.push(`Visual Aesthetic: ${brand.questionnaire.visualAesthetic}`);
      if (brand?.colorPalette && brand.colorPalette.length > 0) contextParts.push(`Brand Color Palette: ${brand.colorPalette.join(', ')}`);
      if (brand?.brandingDetails) contextParts.push(`Overall Branding: ${brand.brandingDetails}`);
      if (brand?.visualStyles && brand.visualStyles.length > 0) {
        const stylesStr = brand.visualStyles.map(s => 
          `CONTEXT/USAGE: ${s.usage}\nAESTHETIC: ${s.aesthetic}\nPROMPT DNA: ${s.promptDNA}`
        ).join('\n\n');
        contextParts.push(`SPECIFIC VISUAL STYLES & PREFERENCES:\n${stylesStr}`);
      }
      brandContext = contextParts.join("\n\n---\n\n");
    }

    try {
      onProgress('Generating unbroken base panorama background (Step 1)...');
      const baseArtworkUrl = await generateArtwork(
        `STRICT RULE: Generate a single, unbroken landscape wallpaper. DO NOT draw panels, borders, vertical lines, frames, or layouts. DO NOT mockup a carousel. NO TEXT. NO TYPOGRAPHY. NO LETTERS. It must be a clean, seamless, continuous texture or scene from the far left to the far right. CONCEPT: ${baseConcept}`,
        undefined, // NO LOGO
        "16:9",
        [],
        [], // Empty templates array to prevent template borders, panels, frames, structures or text blocks from being copied to the background panorama
        brandContext,
        undefined,
        undefined,
        brand.id
      );

      // Deduct 1 token for Base Panorama
      if (!isAdmin && activeBrandId) {
        await updateDoc(doc(db, 'users', brand.ownerId), { tokens: increment(-1) });
        await updateDoc(doc(db, 'brands', activeBrandId), {
          'usage.totalGenerations': increment(1),
          'usage.totalImagesGenerated': increment(1)
        });
      }

      onProgress('Base panorama generated!');
      return baseArtworkUrl;
    } catch (err: any) {
      console.error("Error generating panorama:", err);
      setToast({ message: `Panorama background generation failed: ${err.message || err}`, type: 'error' });
      return null;
    }
  };

  const handleOverlaySlides = async (
    planId: string,
    postId: string,
    basePanoramaUrl: string,
    onProgress: (status: string) => void,
    useBrandElements: boolean = true,
    isCancelled?: () => boolean
  ): Promise<boolean> => {
    if (!user || !brand || !activeBrandId) return false;

    if (!canEdit(brand)) {
      setToast({ message: "You do not have permission to edit this brand's posts.", type: 'error' });
      return false;
    }

    const planDoc = await getDoc(doc(db, 'plans', planId));
    if (!planDoc.exists()) return false;
    const planData = planDoc.data();
    let calendar = planData.calendar || [];
    if (typeof calendar === 'string') {
      try {
        calendar = JSON.parse(calendar);
      } catch (e) {
        calendar = [];
      }
    }
    const plan = { id: planDoc.id, ...planData, calendar } as MonthlyPlan;
    const post = plan.calendar.find(p => p.id === postId);
    if (!post || !post.slides || post.slides.length === 0) return false;

    const numSlides = post.slides.length;

    if (!isAdmin && tokens < numSlides) {
      setToast({ message: `Insufficient tokens. You need ${numSlides} tokens to overlay content on ${numSlides} slides. You have ${tokens}.`, type: 'error' });
      return false;
    }

    // Profitability check block (cost numSlides tokens)
    if (!isAdmin) {
      const profit = calculateProfitability(brand);
      const projectedCost = profit.cost + (numSlides * INTERNAL_COSTS.image);
      const projectedMargin = ((profit.revenue - projectedCost) / profit.revenue) * 100;
      
      if (projectedMargin < profit.target) {
        setToast({ 
          message: `Economic Guardrail: Overlaying slides would drop your package margin below ${profit.target}%. Please contact support to upgrade your limits.`, 
          type: 'error' 
        });
        return false;
      }
    }

    let brandContext = "";
    let contextParts = [];
    if (useBrandElements) {
      if (brand?.questionnaire?.brandVoice) contextParts.push(`Brand Voice: ${brand.questionnaire.brandVoice}`);
      if (brand?.questionnaire?.visualAesthetic) contextParts.push(`Visual Aesthetic: ${brand.questionnaire.visualAesthetic}`);
      if (brand?.colorPalette && brand.colorPalette.length > 0) contextParts.push(`Brand Color Palette: ${brand.colorPalette.join(', ')}`);
      if (brand?.brandingDetails) contextParts.push(`Overall Branding: ${brand.brandingDetails}`);
      if (brand?.visualStyles && brand.visualStyles.length > 0) {
        const stylesStr = brand.visualStyles.map(s => 
          `CONTEXT/USAGE: ${s.usage}\nAESTHETIC: ${s.aesthetic}\nPROMPT DNA: ${s.promptDNA}`
        ).join('\n\n');
        contextParts.push(`SPECIFIC VISUAL STYLES & PREFERENCES:\n${stylesStr}`);
      }
      brandContext = contextParts.join("\n\n---\n\n");
    }

    try {
      onProgress('Slicing landscape panorama background in square slides...');
      const slices = await cropImageToSlices(basePanoramaUrl, numSlides);

      if (isCancelled && isCancelled()) {
        onProgress('Stopped by user.');
        return false;
      }

      const completedSlides: { id: string, url: string, thumbnailUrl: string, backgroundSliceUrl?: string }[] = [];
      
      for (let i = 0; i < numSlides; i++) {
        if (isCancelled && isCancelled()) {
          onProgress('Stopped by user.');
          return false;
        }

        const slide = post.slides[i];
        onProgress(`Processing Slide ${i + 1} of ${numSlides}: Positioning overlays...`);
        
        const sliceData = slices[i];
        
        // We carefully parse out any layout/background keywords in the design concepts so they do not literally print on the image!
        const cleanSlideTextForOverlay = (conceptText: string, fallback: string): string => {
          if (!conceptText) return fallback;
          const sentences = conceptText.split(/[.!?]/);
          const cleanSentences = sentences.filter(s => {
            const sLower = s.toLowerCase();
            return !(
              sLower.includes("background") || 
              sLower.includes("layout") || 
              sLower.includes("aesthetic") || 
              sLower.includes("canvas") || 
              sLower.includes("wallpaper") || 
              sLower.includes("continuing") || 
              sLower.includes("from the left") || 
              sLower.includes("to the right") || 
              sLower.includes("unbroken") ||
              sLower.includes("transition") ||
              sLower.includes("tone") ||
              sLower.includes("gradient") ||
              sLower.includes("border") ||
              sLower.includes("seamless") ||
              sLower.includes("unbroken scene")
            ) && s.trim().length > 0;
          });
          const result = cleanSentences.join(". ").trim();
          return result.length > 3 ? result : fallback;
        };

        const slideText = slide.overlayText && slide.overlayText.trim().length > 0 
          ? slide.overlayText.trim() 
          : cleanSlideTextForOverlay(slide.concept, "Elevating Excellence");

        const slideNumberString = (numSlides > 1 && slide.layoutTemplate?.enableFooter)
          ? `${String(i + 1).padStart(2, '0')} / ${String(numSlides).padStart(2, '0')}`
          : "";

        const logoInstruction = brand?.logoUrl 
          ? `STRICT RULE - LOGO PLACEMENT: You MUST use the official BRAND LOGO provided as a reference. Place it cleanly in the top-left or top-right corner over the canvas exactly as set in the template style, completely replacing and overwriting any outdated, hallucinated, or generic logo text/shapes from the template background.`
          : "";

        const slideNumberInstruction = slideNumberString
          ? `STRICT RULE - DYNAMIC SLIDE NUMBERING: Find any outdated, template, or hardcoded slide numbers/counters at the bottom edge (e.g. '01 / 06') and completely REPLACE and overwrite them with the dynamic actual page counter: "${slideNumberString}". Draw this elegantly and cleanly centered or aligned in the bottom corner matching the layout's margin system.`
          : "";

        // We ensure consistent typography, headings, and distinct slide subjects
        const isOverlayMode = (slide.layoutTemplate?.canvasEditingMode || "original") === "overlay";
        let overlayConceptClean = `SEAMLESS_PANORAMA_SLIDE_OVERLAY.
MAIN VISUAL SUBJECT:
${slide.concept}`;
        if (slide.layoutTemplate?.name && !overlayConceptClean.includes('[TEMPLATE:')) {
          overlayConceptClean = `[TEMPLATE: ${slide.layoutTemplate.name}]\n${overlayConceptClean}`;
        }
        overlayConceptClean += `${!isOverlayMode ? `
TEXT TO OVERLAY:
"${slideText}"

STRICT COMPOSITION RULES:
1. Keep the provided background cropped slice (the Reference Image) perfectly intact as the underlying scenery backdrop. If the reference image or any brand templates contain any outdated pre-existing text, placeholders, numbers, page indicators (such as '01 / 06'), logos, or headers, you MUST completely erase, blank out, cover, and paint over them. Replace them cleanly, solely, and elegantly with the new custom typography overlay and dynamic slide indicators requested for this slide. Do not allow double printing, residual phantom labels, or text leakage.
2. Blend the main visual subject (such as the target object, person, gear, or a fully annotated informative diagram) seamlessly and realistically on top of this background backdrop. If the subject is a person (practitioner, dentist, patient, woman, man, or model), you MUST isolate them cleanly against a perfectly flat, quiet, solid background matching the brand templates, with NO clinics, NO dental chairs/syringes, and NO busy clinic rooms behind them. Keep portraits clean, simple, and confidence-focused (prefer a warm, naturally smiling female dental/practitioner scrub-portrait, no clinical machinery).
3. UNIFORM CONTENT GRID (STRICT TEXT CONTAINER COMPOSITION): You MUST place, align, and print all overlay typography text ONLY inside the designated empty text containers/slots of the template layout (such as the off-white centrally positioned card box/panel). You are STRICTLY FORBIDDEN from drawing, printing, or duplicate-rendering the text on the outside green portions or surrounding panels. If the template layout card contains pre-drawn checkmarks or bullet checkboxes, write each text line horizontally next to each checkbox, perfectly matching them. Do not create secondary checkmarks or double print the text.
4. DETAILED DIAGRAMS & CHARTS: If this slide explicitly requires an educational diagram, professional chart, or procedural flowchart, represent a highly descriptive schematic with clear indicator pointers, pointing lines, and precise text labels corresponding to the elements described in the concept. Never draw empty or unannotated shapes, and do not draw anatomical structures (like implants or teeth) unless explicitly requested.
5. Cleanly overlay the professional typography text: "${slideText}" over the blended scene with high legibility and contrast, rendering it exactly ONCE in the designated layout area to prevent duplication.
${logoInstruction ? `6. ${logoInstruction}\n` : ''}${slideNumberInstruction ? `7. ${slideNumberInstruction}\n` : ''}` : `
STRICT COMPOSITION RULES:
1. Keep the underlying style perfectly intact. If the reference image or any brand templates contain any outdated pre-existing text, placeholders, numbers, page indicators, logos, headers, or structural plaques/mockups, you MUST completely erase and ignore them. Your job is ONLY to render the main visual subject cleanly over a pure, textless canvas.
2. STRICTLY NO TYPOGRAPHY. Do NOT render any letters, words, or layout boxes.
`}`;

        const refinementPromptClean = `SEAMLESS_PANORAMA_SLIDE_OVERLAY.
TASK: Overlay the slide visual subject ${!isOverlayMode ? "and typography " : ""}onto this master background panel slice.
STRICT RULE 1 (BACKDROP PRESERVATION): Preserve 100% of the underlying background colors, flows, margins, and scene layout from the reference image. If the reference image or template contains pre-existing text placeholders, slide numbers/counters, logos, or headers, you MUST completely erase, paint over, and blank them out.${!isOverlayMode ? " Replace them solely, cleanly, and elegantly with the new typography and slide indicators. No text duplication or ghostly leakage is permitted." : " Your output MUST be completely text-free."}
STRICT RULE 2 (PORTRAIT ENHANCEMENT): Realistically draw or overlay the main visual subject: ${slide.concept} onto the canvas backdrop. If drawing a person (practitioner, dentist, patient, woman, man, or model), you MUST isolate them cleanly against a perfectly flat, quiet, solid background matching the brand templates, with NO clinics, NO dental chairs/syringes, and NO busy clinic rooms behind them. Keep portraits clean, simple, and confidence-focused (prefer a warm, naturally smiling female dental/practitioner scrub-portrait, no clinical machinery).
STRICT RULE 3: For diagram slides, render a beautiful 2D vector schematic with clear component labels and indicator lines so the layout has comprehensive educational information.
${!isOverlayMode ? `STRICT RULE 4 (STRICT TEXT CONTAINER COMPOSITION): You MUST place, align, and print all overlay typography text: "${slideText}" ONLY inside the designated empty text containers/slots of the template layout (such as the off-white centrally positioned card box/panel). You are STRICTLY FORBIDDEN from drawing, printing, or duplicate-rendering the text on the outside green portions or surrounding panels. If the template layout card contains pre-drawn checkmarks or bullet checkboxes, write each text line horizontally next to each checkbox, perfectly matching them. Do not create secondary checkmarks or double print the text.
${logoInstruction ? `STRICT RULE 5: ${logoInstruction}\n` : ''}${slideNumberInstruction ? `STRICT RULE 6: ${slideNumberInstruction}\n` : ''}` : `STRICT RULE 4: STRICTLY NO TYPOGRAPHY. You MUST NOT render any letters, words, logos, or text on the image. Leave spaces empty so HTML text can be overlaid.`}`;

        const productsToUse = [
          ...((useBrandElements && slide.selectedProducts) ? slide.selectedProducts : []),
          ...(slide.quickAddImages || [])
        ];
        if (productsToUse.length === 0 && useBrandElements && slide.productImageUrl) {
          productsToUse.push({ id: 'dummy_sc', url: slide.productImageUrl, name: "Product" });
        }

        const refinedSlideUrl = await generateArtwork(
          overlayConceptClean,
          brand.logoUrl || undefined, // Allow passing logoUrl to all slides so we can place it cleanly
          "1:1",
          productsToUse,
          useBrandElements ? brand?.brandTemplates : [],
          brandContext,
          sliceData,
          refinementPromptClean,
          brand.id,
          brand?.approvedMedia
        );

        if (isCancelled && isCancelled()) {
          onProgress('Stopped by user.');
          return false;
        }

        // Upload refined slide to cloud storage
        onProgress(`Saving Slide ${i + 1} of ${numSlides} to storage...`);
        const thumbBase64 = await generateThumbnail(refinedSlideUrl);
        const hash = await getBase64Hash(refinedSlideUrl);
        const thumbHash = await getBase64Hash(thumbBase64);
        
        const storageRef = ref(storage, `artwork/${user.uid}/${planId}_${postId}_${slide.id}_${hash}.png`);
        const thumbRef = ref(storage, `artwork/${user.uid}/${planId}_${postId}_${slide.id}_thumb_${thumbHash}.jpg`);
        
        await uploadString(storageRef, refinedSlideUrl, 'data_url');
        await uploadString(thumbRef, thumbBase64, 'data_url');
        
        const url = await getDownloadURL(storageRef);
        const thumbnailUrl = await getDownloadURL(thumbRef);

        let backgroundSliceUrl = "";
        try {
          const sliceHash = await getBase64Hash(sliceData);
          const sliceRef = ref(storage, `artwork/${user.uid}/${planId}_${postId}_${slide.id}_slice_${sliceHash}.png`);
          await uploadString(sliceRef, sliceData, 'data_url');
          backgroundSliceUrl = await getDownloadURL(sliceRef);
        } catch (sliceErr) {
          console.error("Failed to upload raw slice background backdrop:", sliceErr);
        }

        completedSlides.push({ id: slide.id, url, thumbnailUrl, backgroundSliceUrl });

        // Deduct 1 token for this individual slide
        if (!isAdmin && activeBrandId) {
          await updateDoc(doc(db, 'users', brand.ownerId), { tokens: increment(-1) });
          await updateDoc(doc(db, 'brands', activeBrandId), {
            'usage.totalGenerations': increment(1),
            'usage.totalImagesGenerated': increment(1)
          });
        }
      }

      if (isCancelled && isCancelled()) {
        onProgress('Stopped by user.');
        return false;
      }

      // Save all updated slides into document calendar at once!
      onProgress('Updating post calendar with final slides...');
      const updatedCalendar = plan.calendar.map(item => {
        if (item.id === postId) {
          const updatedSlides = item.slides?.map(s => {
            const completed = completedSlides.find(cs => cs.id === s.id);
            if (completed) {
              return { 
                ...s, 
                url: completed.url, 
                thumbnailUrl: completed.thumbnailUrl, 
                backgroundSliceUrl: completed.backgroundSliceUrl || s.backgroundSliceUrl || "",
                isVideo: false 
              };
            }
            return s;
          }) || [];
          return { ...item, slides: updatedSlides, status: 'pending' as const };
        }
        return item;
      });

      await handleUpdatePlan(planId, { calendar: updatedCalendar }, true);
      onProgress('Seamless carousel completed successfully!');
      setToast({ message: "Seamless carousel generated successfully!", type: 'success' });
      return true;
    } catch (err: any) {
      console.error("Error in slide overlaying:", err);
      setToast({ message: `Overlaying failed: ${err.message || err}`, type: 'error' });
      return false;
    }
  };

  const handleUploadArtworkForPost = async (planId: string, postId: string, slideId: string, file: File, currentSlideData?: any) => {
    if (!canEdit(brand)) {
      setToast({ message: "You do not have permission to upload artwork for this brand.", type: 'error' });
      return;
    }
    
    const maxFileSize = (effectivePlan === 'trial' ? 2 : 15) * 1024 * 1024;
    if (file.size > maxFileSize) {
      setToast({ message: `File is too large. Max size on your current plan is ${effectivePlan === 'trial' ? 2 : 15}MB.`, type: 'error' });
      return;
    }

    const plan = plans.find(p => p.id === planId);
    if (!plan || !user) return;

    try {
      const isVideo = file.type.startsWith('video/');
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const thumbBase64 = await generateThumbnail(base64);
      
      let url = "";
      let thumbnailUrl = thumbBase64;

      const oldSlide = plan.calendar.find(p => p.id === postId)?.slides?.find(s => s.id === slideId);
      const oldUrl = oldSlide?.url;
      const oldThumbUrl = oldSlide?.thumbnailUrl;

      try {
        const hash = await getFileHash(file);
        const thumbHash = await getBase64Hash(thumbBase64);
        const storageRef = ref(storage, `artwork/${user.uid}/${planId}_${postId}_${slideId}_manual_${hash}`);
        const thumbRef = ref(storage, `artwork/${user.uid}/${planId}_${postId}_${slideId}_manual_thumb_${thumbHash}.jpg`);
        
        const uploadWithTimeout = (ref: any, data: any, isBytes = false) => 
          Promise.race([
            isBytes ? uploadBytes(ref, data) : uploadString(ref, data, 'data_url'),
            new Promise((_, reject) => setTimeout(() => reject(new Error("Storage upload timeout")), 120000))
          ]);

        await uploadWithTimeout(storageRef, file, true);
        await uploadWithTimeout(thumbRef, thumbBase64);
        
        url = await getDownloadURL(storageRef);
        thumbnailUrl = await getDownloadURL(thumbRef);

        // cleanup old
        if (oldUrl && oldUrl !== url) safeDeleteFile(oldUrl);
        if (oldThumbUrl && oldThumbUrl !== thumbnailUrl) safeDeleteFile(oldThumbUrl);
      } catch (storageErr: any) {
        console.error("Manual upload storage failed:", storageErr);
        setToast({ message: "Failed to upload file to storage.", type: 'error' });
        return;
      }

      const updatedCalendar = plan.calendar.map(item => {
        if (item.id === postId) {
          const updatedSlides = item.slides?.map(s => 
            s.id === slideId ? { ...s, ...(currentSlideData || {}), url, thumbnailUrl, isVideo } : s
          ) || [];
          return { ...item, slides: updatedSlides, status: 'approved' as const };
        }
        return item;
      });
      await handleUpdatePlan(planId, { calendar: updatedCalendar }, true);
    } catch (err: any) {
      console.error("Error in handleUploadArtworkForPost:", err);
      throw err;
    }
  };

  const handleUpdatePostPlatforms = async (planId: string, postId: string, platforms: string[]) => {
    if (!canEdit(brand)) {
      setToast({ message: "You do not have permission to update posts for this brand.", type: 'error' });
      return;
    }
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    const updatedCalendar = plan.calendar.map(item => 
      item.id === postId ? { ...item, platforms } : item
    );
    await handleUpdatePlan(planId, { calendar: updatedCalendar }, true);
  };

  const handleUpdatePostStatus = async (planId: string, postId: string, status: 'approved' | 'rejected' | 'scheduled' | 'pending' | 'none', scheduledAt?: string, platform?: string) => {
    if (!canApprove(brand)) {
      setToast({ message: "You do not have permission to update post status for this brand.", type: 'error' });
      return;
    }
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    // Convert local time string to UTC ISO string for consistent server-side comparison
    let finalScheduledAt = scheduledAt;
    if (status === 'scheduled' && scheduledAt && !scheduledAt.endsWith('Z')) {
      try {
        finalScheduledAt = new Date(scheduledAt).toISOString();
      } catch (e) {
        console.error("Failed to parse scheduled date:", scheduledAt, e);
      }
    }

    const updatedCalendar = plan.calendar.map(item => {
      if (item.id === postId) {
        if (status === 'scheduled' && platform) {
          const platformStatus = { ...(item.platformStatus || {}) };
          platformStatus[platform] = {
            ...platformStatus[platform],
            status: 'scheduled',
            scheduledAt: finalScheduledAt
          };
          return { ...item, status: 'approved' as const, platformStatus };
        }
        
        if (status === 'approved' && platform) {
          const platformStatus = { ...(item.platformStatus || {}) };
          if (platformStatus[platform]) {
             delete platformStatus[platform];
          }
          return { ...item, platformStatus };
        }

        if (status === 'none') {
          const { status: oldStatus, ...rest } = item;
          return rest as ContentIdea;
        }

        return { ...item, status: (status === 'pending' ? 'pending' : status) as any };
      }
      return item;
    });
    await handleUpdatePlan(planId, { calendar: updatedCalendar }, true);
  };

  const handleScheduleAll = async (plan: MonthlyPlan) => {
    if (!brand) return;

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const eligiblePosts = plan.calendar.filter(p => {
      const postDate = new Date(p.date);
      postDate.setHours(0, 0, 0, 0);
      
      const hasUnprocessedPlatforms = p.platforms.some(platform => {
        const s = p.platformStatus?.[platform]?.status;
        return s !== 'published' && s !== 'publishing' && s !== 'scheduled';
      });
      return p.status === 'approved' && hasUnprocessedPlatforms && postDate >= now;
    });

    if (eligiblePosts.length === 0) {
      setToast({ message: "No approved upcoming posts to schedule. Ensure posts are approved and dates aren't in the past.", type: 'error' });
      return;
    }

    // Check for connected social accounts
    const missingPlatforms = new Set<string>();
    eligiblePosts.forEach(post => {
      post.platforms.forEach(platform => {
        const hasAccount = brand.socialAccounts?.some(acc => acc.platform.toLowerCase() === platform.toLowerCase());
        if (!hasAccount) missingPlatforms.add(platform);
      });
    });

    if (missingPlatforms.size > 0) {
      setToast({ 
        message: `Missing connected accounts for: ${Array.from(missingPlatforms).join(', ')}. Please connect them in Settings first.`, 
        type: 'error' 
      });
      return;
    }

    try {
      const updatedCalendar = plan.calendar.map(post => {
        const postDate = new Date(post.date);
        postDate.setHours(0, 0, 0, 0);
        
        const hasUnprocessedPlatforms = post.platforms.some(platform => {
          const s = post.platformStatus?.[platform]?.status;
          return s !== 'published' && s !== 'publishing' && s !== 'scheduled';
        });
        if (post.status !== 'approved' || !hasUnprocessedPlatforms || postDate < now) return post;

        // Determine optimal schedule time
        let finalScheduledAt = post.scheduledAt;
        if (!finalScheduledAt && post.bestTimeToPost) {
          const timeMatch = post.bestTimeToPost.match(/(\d{1,2}):(\d{2})/);
          if (timeMatch) {
            const date = new Date(post.date);
            date.setHours(parseInt(timeMatch[1]), parseInt(timeMatch[2]), 0, 0);
            finalScheduledAt = date.toISOString();
          }
        }
        
        // Fallback: 10:00 AM on original date if no suggestion
        if (!finalScheduledAt) {
          const date = new Date(post.date);
          date.setHours(10, 0, 0, 0);
          finalScheduledAt = date.toISOString();
        }

        const platformStatus = { ...(post.platformStatus || {}) };
        post.platforms.forEach(p => {
          const currentStatus = platformStatus[p]?.status;
          if (currentStatus === 'published' || currentStatus === 'publishing' || currentStatus === 'scheduled') {
            return;
          }

          platformStatus[p] = {
            ...platformStatus[p],
            status: 'scheduled',
            scheduledAt: finalScheduledAt
          };
        });

        return {
          ...post,
          status: 'scheduled' as const,
          scheduledAt: finalScheduledAt,
          publishedStatus: 'scheduled' as const,
          platformStatus
        };
      });

      await handleUpdatePlan(plan.id!, { calendar: updatedCalendar });
      setToast({ message: `Successfully scheduled ${eligiblePosts.length} posts at their optimal times!`, type: 'success' });
    } catch (err) {
      console.error("Schedule all error:", err);
      setToast({ message: "Failed to schedule all posts.", type: 'error' });
    }
  };

  const handleGeneratePlanArtwork = async (planId: string) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan || !brand) return;

    if (isSystemPaused) {
      setToast({ message: "System Maintenance: Generations are temporarily paused.", type: 'error' });
      return;
    }

    const breakerError = checkCircuitBreaker(brand.id);
    if (breakerError) {
      setToast({ message: `Security Warning: ${breakerError}. System paused for safety.`, type: 'error' });
      return;
    }

    // Solo and Trial Tier Restriction: No Bulk Generation
    if (!isAdmin && (brand.subscription?.plan === 'solo' || isTrialUI)) {
      setToast({ message: isTrialUI ? "Trial Tier: Bulk generation is not available. Please generate artwork for posts individually." : "Solo Tier: Bulk generation is not available. Please generate artwork for posts individually.", type: 'error' });
      return;
    }

    // Profitability Guardrail for Bulk
    if (!isAdmin) {
      const profit = calculateProfitability(brand);
      const postCount = plan.calendar.length;
      const estimatedBulkCost = postCount * INTERNAL_COSTS.image; // Assuming images for bulk
      if (profit.margin < profit.target || (profit.cost + estimatedBulkCost) / profit.revenue > (1 - profit.target/100)) {
        setToast({ message: `Insufficient margin for bulk generation. Please generate individually or upgrade plan.`, type: 'error' });
        return;
      }
    }

    setIsBulkGenerating(planId);
    stopSprintRef.current = false;
    setShowSprintVideo(true);
    
    // Prime the chime audio so it's ready to play even if user hasn't interacted recently
    if (chimeRef.current) {
      chimeRef.current.load();
    }

    setToast({ message: "Starting bulk generation for all approved posts in this plan...", type: 'success' });

    try {
      const approvedPosts = plan.calendar.filter(post => {
        const isAlreadyProcessed = post.status === 'scheduled' || 
            (post.platformStatus && Object.values(post.platformStatus).some(s => s.status === 'published' || s.status === 'publishing'));
        return post.status === 'approved' && !isAlreadyProcessed;
      });
      
      // Calculate total slides to generate
      let totalToGenerate = 0;
      approvedPosts.forEach(post => {
        post.slides?.forEach(slide => {
          if (!slide.url) totalToGenerate++;
        });
      });

      if (totalToGenerate === 0) {
        setToast({ message: "All approved posts already have artwork.", type: 'success' });
        setIsBulkGenerating(null);
        return;
      }

      let generatedCount = 0;
      for (const post of approvedPosts) {
        if (stopSprintRef.current) break;
        if (!post.slides) continue;
        
        let previousSlideUrl = "";
        const isCarousel = post.format === 'carousel' || post.slides.length > 1;

        for (let idx = 0; idx < post.slides.length; idx++) {
          const slide = post.slides[idx];
          if (stopSprintRef.current) break;
          if (!slide.url) {
            try {
              setSprintProgress({ 
                current: generatedCount + 1, 
                total: totalToGenerate, 
                postTitle: post.copy.substring(0, 30) + '...',
                postId: post.id,
                slideId: slide.id
              });
              
              const inspirationToPass = (isCarousel && idx > 0 && previousSlideUrl) ? previousSlideUrl : undefined;
              const refinementPromptToPass = (isCarousel && idx > 0 && previousSlideUrl) 
                ? "CAROUSEL STYLE REFERENCE: Maintain the exact same aesthetic, textures, colors, and illustrative approach as this reference image. Adapt the subjects for the new concept, but the 'world' must look identical." 
                : undefined;

              const resultUrl = await handleGenerateArtworkForPost(planId, post.id, slide.id, slide.concept, 'image', true, undefined, refinementPromptToPass, inspirationToPass);
              if (resultUrl && typeof resultUrl === 'string') {
                previousSlideUrl = resultUrl;
              }

              generatedCount++;
              // Extra safety sleep to prevent rapid burst
              await new Promise(resolve => setTimeout(resolve, 2000));
            } catch (err) {
              console.error(`Failed to generate artwork for post ${post.id}, slide ${slide.id}:`, err);
            }
          } else {
            if (typeof slide.url === 'string') {
              previousSlideUrl = slide.url;
            }
          }
        }
      }
      
      if (stopSprintRef.current) {
        setToast({ message: `Bulk generation stopped. Successfully generated ${generatedCount} artworks.`, type: 'success' });
      } else {
        setToast({ message: `Bulk generation complete! Successfully generated ${generatedCount} artworks.`, type: 'success' });
        // Play success chime
        if (chimeRef.current) {
          chimeRef.current.currentTime = 0;
          chimeRef.current.play().catch(e => console.log('Audio play blocked', e));
        }
      }
    } catch (err) {
      console.error("Bulk generation error:", err);
      setToast({ message: "An error occurred during bulk generation.", type: 'error' });
    } finally {
      setIsBulkGenerating(null);
      setSprintProgress(null);
      stopSprintRef.current = false;
    }
  };

  const handleAddPost = async (
    description: string, 
    date: string, 
    planId: string, 
    preferredFormat?: 'feed' | 'story' | 'carousel',
    manualData?: { copy: string; platforms: string[]; format: 'feed' | 'story' | 'carousel' },
    aiPlatforms?: string[],
    visualDirection?: string
  ) => {
    if (!brand) return;
    
    setIsAddingPost(true);
    try {
      let newPosts: any[] = [];
      if (manualData) {
        const postId = 'post_' + Math.random().toString(36).substring(2, 11);
        const slidesCount = manualData.format === 'carousel' ? 3 : 1;
        const slides = Array.from({ length: slidesCount }).map((_, index) => ({
          id: 'slide_' + Math.random().toString(36).substring(2, 11),
          concept: manualData.format === 'carousel' 
            ? `Slide ${index + 1}: Graphic supporting: ${manualData.copy.substring(0, 50)}...` 
            : `Visual supporting: ${manualData.copy.substring(0, 100)}`,
          url: '',
          thumbnailUrl: ''
        }));

        newPosts.push({
          id: postId,
          date: date,
          platforms: manualData.platforms,
          copy: manualData.copy,
          concept: `Manual: ${manualData.copy.substring(0, 50)}`,
          format: manualData.format,
          type: 'evergreen' as const,
          bestTimeToPost: '12:00',
          slides: slides,
          status: 'pending' as const
        });
      } else {
        if (!description) {
          setIsAddingPost(false);
          return;
        }
        // If we have a prefilledConcept, this is likely an authoritative trend concept
        const isTrendPost = !!prefilledConcept && description.slice(0, 100).includes(prefilledConcept.slice(0, 50));
        
        // Generate separate posts for each platform to ensure platform-native copy
        const platformsToGenerate = aiPlatforms && aiPlatforms.length > 0 ? aiPlatforms : brand?.platforms || ['Instagram'];
        
        let visualBlueprint = undefined;
        if (visualDirection && visualDirection.startsWith('Apply Custom Template Blueprint: ')) {
          const blueprintNarrative = visualDirection.replace('Apply Custom Template Blueprint: ', '');
          visualBlueprint = brandBlueprints.find(b => b.narrative === blueprintNarrative);
        }

        for (const platform of platformsToGenerate) {
          const brandToUse = { ...brand, platforms: [platform] };
          const generatedPost = await generateSinglePost(brandToUse as any, description, date, preferredFormat, isTrendPost, visualDirection, visualBlueprint);
          generatedPost.platforms = [platform];
          
          // Slight date staggering if they are on the same day? Not strictly necessary, but helpful.
          newPosts.push(generatedPost);
        }
      }
      
      const plan = plans.find(p => p.id === planId);
      if (plan) {
        const updatedCalendar = [...plan.calendar, ...newPosts].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
        await handleUpdatePlan(planId, { calendar: updatedCalendar }, true);
        setToast({ message: manualData ? "Custom post added successfully!" : "Post added to your plan!", type: 'success' });
        
        // Clean up
        setShowAddPostModal(null);
        setPrefilledConcept('');
        const url = new URL(window.location.href);
        url.searchParams.delete('action');
        url.searchParams.delete('concept');
        window.history.replaceState({}, '', url.toString());
      }
    } catch (err: any) {
      console.error("Failed to add post:", err);
      setToast({ message: "Failed to add post. Please try again.", type: 'error' });
    } finally {
      setIsAddingPost(false);
    }
  };

  const handleRequestSupport = async () => {};

  const handleSupportFormSubmit = async () => {};

  const handleImpersonate = (userId: string | null) => {
    if (!userId || userId === user?.uid) {
      localStorage.removeItem('impersonatedUserId');
      localStorage.removeItem('activeBrandId');
      setImpersonatedUserId(null);
      // Return to admin tab
      window.location.replace('/dashboard?tab=admin');
    } else {
      localStorage.setItem('impersonatedUserId', userId);
      setImpersonatedUserId(userId);
      window.location.reload();
    }
  };

  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareConfig, setShareConfig] = useState<{ planId?: string, ideaIds?: string[], type: 'plan' | 'post' }>({ type: 'plan' });
  const [previewMedia, setPreviewMedia] = useState<{ url: string; type: 'image' | 'video'; slide?: any; format?: string } | null>(null);

  const handleSignIn = async () => {
    console.log("handleSignIn triggered, setting isAuthModalOpen to true");
    setIsAuthModalOpen(true);
  };

  useEffect(() => {
    if (isAuthReady && user && (location.pathname === '/' || location.pathname === '/login')) {
      navigate('/dashboard');
    }
  }, [isAuthReady, user, location.pathname, navigate]);

  const renderDashboard = () => (
    <div className="min-h-screen">
      {isImpersonating && (
          <AdminManagementBar 
            impersonatedUserId={impersonatedUserId!} 
            onExit={() => handleImpersonate(user!.uid)} 
          />
        )}
        <CommandPalette open={commandOpen} setOpen={setCommandOpen} setActiveTab={setActiveTab} />
        <div className="min-h-screen bg-gray-50 flex flex-col text-gray-900">
          <main className="flex-1 max-w-full w-full mx-auto px-6 lg:px-10 py-6 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
          {/* Sidebar */}
          <aside className={`hidden lg:block space-y-6 sticky ${isImpersonating ? 'top-[140px]' : 'top-[84px]'} max-h-[calc(100vh-140px)] overflow-y-auto pr-2 pb-12 pt-4 scrollbar-thin`}>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] px-3">{isBrazil ? 'Marca Ativa' : 'Active Brand'}</label>
              <div className="space-y-1">
                {brands.filter(b => b && b.id).map(b => (
                  <button
                    key={b.id}
                    onClick={() => {
                      setActiveBrandId(b.id || null);
                      localStorage.setItem('activeBrandId', b.id || '');
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all group ${
                      activeBrandId === b.id 
                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${
                      activeBrandId === b.id ? 'bg-white/20' : 'bg-indigo-100 text-indigo-600'
                    }`}>
                      {b.companyName?.charAt(0) || 'B'}
                    </div>
                    <span className="text-sm font-bold truncate">{b.companyName || (isBrazil ? 'Marca Sem Nome' : 'Unnamed Brand')}</span>
                  </button>
                ))}
                <button
                  onClick={() => {
                    if (!isAdmin) {
                      const ownedBrands = brands.filter(b => b.ownerId === user.uid);
                      
                      // Use user-level subscription as fallback for limit check
                      const userPlan = currentUserProfile?.subscription?.plan;
                      const hasEnterprise = userPlan === 'enterprise' || ownedBrands.some(b => b.subscription?.plan === 'enterprise');
                      const hasAgency = userPlan === 'agency' || ownedBrands.some(b => b.subscription?.plan === 'agency');
                      const hasGrowth = userPlan === 'growth' || ownedBrands.some(b => b.subscription?.plan === 'growth');
                      
                      const limit = hasEnterprise ? 50 : hasAgency ? 10 : hasGrowth ? 3 : 1;

                      if (ownedBrands.length >= limit) {
                        setToast({ 
                          message: isBrazil 
                            ? `Limite de marcas atingido (${limit}). Por favor, faça upgrade da sua marca existente para criar mais.`
                            : `Brand limit reached (${limit}). Please upgrade your existing brand to create more.`, 
                          type: 'error' 
                        });
                        return;
                      }
                    }
                    setActiveBrandId('new');
                    setActiveTab('brand');
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-indigo-600 hover:bg-indigo-50 transition-all border border-dashed border-indigo-200 mt-2"
                >
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                    <Plus className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-bold">{t.newBrand}</span>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] px-3">{t.navigation}</label>
              <SidebarItem 
                active={activeTab === 'dashboard'} 
                onClick={() => setActiveTab('dashboard')} 
                icon={<LayoutDashboard className="w-5 h-5" />} 
                label={t.dashboard} 
              />
              <SidebarItem 
                active={activeTab === 'create-plan'} 
                onClick={() => setActiveTab('create-plan')} 
                icon={<Sparkles className="w-5 h-5" />} 
                label={t.createMonthlyPlan} 
              />
              <SidebarItem 
                active={activeTab === 'brand'} 
                onClick={() => setActiveTab('brand')} 
                icon={<Palette className="w-5 h-5" />} 
                label={t.brandProfile} 
              />
              <SidebarItem 
                active={activeTab === 'approved-media'} 
                onClick={() => setActiveTab('approved-media')} 
                icon={
                  <div className="relative">
                    <ImageIcon className="w-5 h-5" />
                    {(effectivePlan === 'trial' || effectivePlan === 'solo') && !isAdmin && (
                      <span className="absolute -top-1 -right-1 bg-indigo-50 border border-indigo-100 p-0.5 rounded-full">
                        <Lock className="w-2 h-2 text-indigo-600 shrink-0" />
                      </span>
                    )}
                  </div>
                } 
                label={isBrazil ? "Biblioteca de Mídia" : "Approved Media"} 
              />
              <SidebarItem 
                active={activeTab === 'plans'} 
                onClick={() => setActiveTab('plans')} 
                icon={<CalendarIcon className="w-5 h-5" />} 
                label={t.contentPlans} 
              />
              {isTester && (
                <SidebarItem 
                  active={activeTab === 'trends'} 
                  onClick={() => setActiveTab('trends')} 
                  icon={<Sparkles className="w-5 h-5" />} 
                  label={isBrazil ? "Tendências e Foco Estratégico" : "Trends & Strategic Focus"} 
                />
              )}
              {isAdmin && (
                <SidebarItem 
                  active={activeTab === 'bookings'} 
                  onClick={() => setActiveTab('bookings')} 
                  icon={<CalendarIcon className="w-5 h-5" />} 
                  label={t.calendarBookings} 
                />
              )}
              <SidebarItem 
                active={activeTab === 'engagement'} 
                onClick={() => {
                  if (!isAdmin && effectivePlan === 'trial') {
                    // Check if it's the first month
                    const createdAt = brand.createdAt ? new Date(brand.createdAt) : new Date();
                    const now = new Date();
                    const diffDays = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 3600 * 24));
                    if (diffDays > 31) {
                      setToast({ message: "Social Insights are only available for the first 30 days of the free trial. Please upgrade to maintain access.", type: 'warning' });
                      setActiveTab('settings');
                      return;
                    }
                  }
                  setActiveTab('engagement');
                }} 
                icon={<BarChart3 className="w-5 h-5" />} 
                label={t.insights} 
              />
              <SidebarItem 
                active={activeTab === 'inbox'} 
                onClick={() => {
                  if (!isAdmin && effectivePlan === 'trial') {
                    setToast({ message: "Social Inbox is only available on paid subscriptions. Please upgrade to access this feature.", type: 'warning' });
                    setActiveTab('settings');
                    return;
                  }
                  setActiveTab('inbox');
                }} 
                icon={<Inbox className="w-5 h-5" />} 
                label={t.socialInbox} 
              />
              <SidebarItem 
                active={activeTab === 'review'} 
                onClick={() => {
                  if (!isAdmin && effectivePlan === 'trial') {
                    setToast({ message: "Client Review Space is not available on the trial tier. Please upgrade.", type: 'warning' });
                    setActiveTab('settings');
                    return;
                  }
                  setActiveTab('review');
                }} 
                icon={
                  <div className="relative">
                    <MessageSquare className="w-5 h-5" />
                    {shareFeedback.length > 0 && (
                      <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-indigo-600 rounded-full border-2 border-white animate-pulse" />
                    )}
                  </div>
                } 
                label={isBrazil ? "Espaço de Revisão" : "Client Review Space"} 
              />
              <SidebarItem 
                active={activeTab === 'settings'} 
                onClick={() => setActiveTab('settings')} 
                icon={<Settings className="w-5 h-5" />} 
                label={t.settings} 
              />
              {isAdmin && (
                <SidebarItem 
                  active={false} 
                  onClick={() => navigate('/showcase')} 
                  icon={<Eye className="w-5 h-5" />} 
                  label={t.portfolio} 
                />
              )}
              {user?.email === 'dewaldjordaan5@gmail.com' && (
                <>
                  <SidebarItem 
                    active={activeTab === 'admin'} 
                    onClick={() => setActiveTab('admin')} 
                    icon={<Shield className="w-5 h-5" />} 
                    label={t.adminStats} 
                  />
                  <SidebarItem 
                    active={activeTab === 'blueprint-engine'} 
                    onClick={() => setActiveTab('blueprint-engine')} 
                    icon={<FileCode className="w-5 h-5" />} 
                    label="Blueprint Engine" 
                  />
                  <SidebarItem 
                    active={activeTab === 'carousel-test'} 
                    onClick={() => setActiveTab('carousel-test')} 
                    icon={<Images className="w-5 h-5" />} 
                    label="Carousel Tester" 
                  />
                  <SidebarItem 
                    active={activeTab === 'html-lab'} 
                    onClick={() => setActiveTab('html-lab')} 
                    icon={<FileCode className="w-5 h-5" />} 
                    label="HTML Layout Lab" 
                  />
                  <SidebarItem 
                    active={activeTab === 'template-baker'} 
                    onClick={() => setActiveTab('template-baker')} 
                    icon={<FileImage className="w-5 h-5" />} 
                    label="Template Baker" 
                  />
                </>
              )}
              {isAdmin && (
                <>
                  <div className="pt-4 pb-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] px-3">{t.adminContent}</label>
                  </div>
                  <Link 
                    to="/blog"
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all text-gray-600 hover:bg-gray-100"
                  >
                    <Mail className="w-5 h-5" />
                    <span>{t.manageBlog}</span>
                  </Link>
                  <Link 
                    to="/showcase"
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all text-gray-600 hover:bg-gray-100"
                  >
                    <Layout className="w-5 h-5" />
                    <span>{t.manageShowcase}</span>
                  </Link>
                </>
              )}
            </div>
          </aside>

          {/* Content Area */}
          <div className="space-y-8 pb-12">
            <AnimatePresence mode="wait">
              {activeTab === 'dashboard' && (
                <motion.div 
                  key="dashboard"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <header>
                    <h2 className="text-3xl font-bold text-gray-900">
                      {isImpersonating 
                        ? `${t.viewingWorkspace} ${impersonatedUserId?.slice(0, 8)}` 
                        : `${t.welcomeBack}, ${user?.displayName?.split(' ')[0]}`
                      }
                    </h2>
                    <p className="text-gray-500 mt-1">
                      {isImpersonating 
                        ? t.observingStrategy 
                        : t.happeningStrategy
                      }
                    </p>
                  </header>

                  {!brand ? (
                    <div className="bg-indigo-50 border border-indigo-100 p-8 rounded-3xl flex flex-col items-center text-center">
                      <div className="bg-white p-4 rounded-2xl shadow-sm mb-4">
                        <Palette className="w-8 h-8 text-indigo-600" />
                      </div>
                      <h3 className="text-xl font-bold text-indigo-900 mb-2">{t.completeBrandProfile}</h3>
                      <p className="text-indigo-700 mb-6 max-w-md">{t.toGenerateContent}</p>
                      <button 
                        onClick={() => setActiveTab('brand')}
                        className="bg-indigo-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
                      >
                        {t.getStarted}
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                      <StatCard icon={<CalendarIcon className="w-6 h-6 text-blue-600" />} label={t.activePlans} value={plans.length.toString()} color="blue" onClick={() => setActiveTab('plans')} />
                      <StatCard icon={<CheckCircle2 className="w-6 h-6 text-green-600" />} label={t.approvedPosts} value={plans.reduce((acc, p) => acc + p.calendar.filter(c => c.slides?.some(s => s.url)).length, 0).toString()} color="green" />
                      <StatCard icon={<Clock className="w-6 h-6 text-orange-600" />} label={t.pendingReview} value={plans.filter(p => p.status === 'generated').length.toString()} color="orange" />
                      
                      {userRole !== 'viewer' && (
                        <div className="bg-indigo-600 p-6 rounded-3xl shadow-lg shadow-indigo-100 flex flex-col justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-xl bg-white/20">
                              <Zap className="w-5 h-5 text-white" />
                            </div>
                            <p className="text-xs font-bold text-white/80 uppercase tracking-widest leading-none">
                              {isAdmin ? 'Super Admin' : (isCollaborator ? t.brands : (isTrialUI ? 'Trial Tokens' : 'AI Tokens'))}
                            </p>
                          </div>
                          <div className="flex items-end justify-between">
                            <p className="text-2xl font-black text-white">
                              {isAdmin || (isCollaborator && isActuallyTrial) ? t.unlimited : `${tokens} ${t.left}`}
                            </p>
                            {!isAdmin && (isTrialUI || tokens <= 0) && !isCollaborator && (
                              <button 
                                onClick={isTrialUI ? () => setActiveTab('settings') : handleTopUp}
                                className="px-3 py-1 bg-white text-indigo-600 rounded-lg text-xs font-bold hover:bg-indigo-50 transition-all"
                              >
                                {isTrialUI ? t.upgrade : t.topUp}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {brand && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 text-left">
                      {/* Left: Next Upcoming Content Preview Card (lg:col-span-2) */}
                      <div className="lg:col-span-2 flex flex-col gap-6">
                        <div className="bg-white p-6 md:p-8 rounded-3xl shadow-sm border border-gray-100 flex-1 flex flex-col justify-between">
                          <div>
                            <div className="flex items-center justify-between border-b border-gray-50 pb-4 mb-6">
                              <div>
                                <h3 className="text-xl font-extrabold text-gray-900 tracking-tight">
                                  {isBrazil ? "Próxima Postagem Agendada" : "Next Upcoming Post"}
                                </h3>
                                <p className="text-xs text-gray-400 mt-0.5 text-left">
                                  {isBrazil ? "Pré-visualização do conteúdo estratégico mais próximo" : "High-fidelity preview of your soonest strategic drop"}
                                </p>
                              </div>
                              <span className="px-3 py-1 bg-indigo-50 text-indigo-700 text-xs font-black uppercase rounded-full tracking-wider animate-pulse">
                                {isBrazil ? "Fila Ativa" : "Active Queue"}
                              </span>
                            </div>

                            {upcomingPost ? (
                              <div className="space-y-6">
                                {/* Brand Plan Header info */}
                                <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50/60 p-4 rounded-2xl border border-slate-100">
                                  <div className="text-left">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-indigo-500 block">
                                      {isBrazil ? "Plano Associado" : "Associated Campaign"}
                                    </span>
                                    <span className="text-sm font-bold text-slate-800">
                                      {upcomingPost.plan.title || `${upcomingPost.plan.month} ${upcomingPost.plan.year}`}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-semibold text-slate-400 bg-white border border-slate-200/80 px-2.5 py-1 rounded-lg">
                                      {upcomingPost.post.date}
                                    </span>
                                    <span className="text-xs font-extrabold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-lg capitalize">
                                      {upcomingPost.post.type || 'Campaign'}
                                    </span>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 text-left">
                                  {/* Media Block (col-span-5) */}
                                  <div className="md:col-span-5 flex flex-col items-center justify-center">
                                    <div className={`relative w-full bg-slate-50 border border-slate-100 rounded-2xl overflow-hidden group shadow-sm flex flex-col items-center justify-center p-3 ${
                                      upcomingPost.post.format === 'story' ? 'aspect-[9/16] md:max-h-[380px] w-auto mx-auto' : 'aspect-square'
                                    }`}>
                                      {upcomingPost.post.slides && upcomingPost.post.slides[0]?.url ? (
                                        <div className="absolute inset-0 w-full h-full">
                                          {upcomingPost.post.slides[0].isVideo ? (
                                            <video 
                                              src={upcomingPost.post.slides[0].url} 
                                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
                                              autoPlay 
                                              muted 
                                              loop 
                                              playsInline
                                            />
                                          ) : (
                                            <img 
                                              src={upcomingPost.post.slides[0].thumbnailUrl || upcomingPost.post.slides[0].url} 
                                              alt="Preview" 
                                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                              referrerPolicy="no-referrer"
                                            />
                                          )}
                                        </div>
                                      ) : (
                                        <div className="text-center p-4">
                                          <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-normal">
                                            {isBrazil ? "Design Conceitual" : "Concept Set"}
                                          </p>
                                          <p className="text-[10px] text-slate-400 mt-1">
                                            {isBrazil ? "Arte gráfica pronta para geração" : "Artwork pending generation"}
                                          </p>
                                        </div>
                                      )}
                                      
                                      {/* Slide counter badge */}
                                      {upcomingPost.post.slides && upcomingPost.post.slides.length > 1 && (
                                        <div className="absolute bottom-2.5 right-2.5 bg-black/60 backdrop-blur-md text-[10px] font-black text-white px-2 py-1 rounded-lg">
                                          +{upcomingPost.post.slides.length - 1} Carousel Slides
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Concept & Copy Block (col-span-7) */}
                                  <div className="md:col-span-7 space-y-4 text-left">
                                    <div className="text-left">
                                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">
                                        {isBrazil ? "Diretrizes Conceituais" : "Creative Concept"}
                                      </span>
                                      <h4 className="text-sm font-bold text-gray-900 leading-snug text-left">
                                        {upcomingPost.post.concept}
                                      </h4>
                                    </div>

                                    {upcomingPost.post.copy && (
                                      <div className="bg-slate-50 border border-slate-100 p-4 rounded-2xl relative text-left">
                                        <span className="text-[9px] font-black uppercase text-slate-400 tracking-wider block mb-1.5">
                                          {isBrazil ? "Cópia / Legenda Final" : "Final Generated Placement Copy"}
                                        </span>
                                        <p className="text-xs text-slate-800 text-left line-clamp-4 leading-normal font-medium whitespace-pre-wrap">
                                          {upcomingPost.post.copy}
                                        </p>
                                      </div>
                                    )}

                                    {/* Targeted Platform Icons */}
                                    <div className="flex flex-wrap items-center gap-1.5 pt-1 text-left">
                                      <span className="text-[10px] font-bold text-gray-400 mr-1.5">{isBrazil ? "Canais:" : "Target channels:"}</span>
                                      {upcomingPost.post.platforms && upcomingPost.post.platforms.map((plat: string) => {
                                        let iconNode = <Globe className="w-3.5 h-3.5" />;
                                        if (plat.toLowerCase().includes('instagram')) iconNode = <Instagram className="w-3.5 h-3.5 text-pink-600" />;
                                        if (plat.toLowerCase().includes('linkedin')) iconNode = <Linkedin className="w-3.5 h-3.5 text-blue-700" />;
                                        if (plat.toLowerCase().includes('facebook')) iconNode = <Facebook className="w-3.5 h-3.5 text-blue-600" />;
                                        if (plat.toLowerCase().includes('twitter') || plat.toLowerCase().includes('x')) iconNode = <Twitter className="w-3.5 h-3.5 text-gray-900" />;
                                        if (plat.toLowerCase().includes('youtube')) iconNode = <Youtube className="w-3.5 h-3.5 text-red-600" />;
                                        return (
                                          <span key={plat} className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 border border-slate-200/40 rounded-full text-[10px] font-bold text-slate-700">
                                            {iconNode}
                                            <span className="select-none">{plat}</span>
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center p-8 bg-slate-50 border border-dashed border-slate-200 rounded-2xl text-center my-6">
                                <CalendarIcon className="w-12 h-12 text-slate-300 mb-3" />
                                <h4 className="text-slate-800 font-extrabold text-sm">
                                  {isBrazil ? "Plano Limpo" : "Active Calendar Clear"}
                                </h4>
                                <p className="text-xs text-slate-400 max-w-sm mt-1 leading-normal text-center">
                                  {isBrazil 
                                    ? "Não há posts agendados na fila no momento. Vá para Criar Plano para começar sua estratégia!" 
                                    : "There are currently no generated social posts active in your brand workspace."}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Quick Calendar Footer Redirect Link */}
                          <div className="mt-6 pt-4 border-t border-gray-50 flex items-center justify-between">
                            <span className="text-[11px] text-gray-400 font-semibold text-left">
                              {upcomingPost 
                                ? (isBrazil ? "Deseja agendar alterações directas?" : "Fine-tune design sequences directly:") 
                                : (isBrazil ? "Ative seu primeiro plano estratégico!" : "Setup campaign outlines in seconds:")
                              }
                            </span>
                            <button 
                              onClick={() => setActiveTab('plans')}
                              className="px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-xl text-xs font-bold transition-all flex items-center gap-2 group/btn"
                            >
                              <span>{isBrazil ? "Ver Calendários" : "View Entire Calendar"}</span>
                              <ArrowRight className="w-3.5 h-3.5 group-hover/btn:translate-x-1 transition-transform" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Right: Helpful Actions and Strategy Advisor Column (lg:col-span-1) */}
                      <div className="lg:col-span-1 flex flex-col gap-6 text-left">
                        {/* 1. Proactive CTA: "Create plan" Banner Button */}
                        <div className="p-6 bg-gradient-to-br from-indigo-600 to-indigo-800 text-white rounded-3xl shadow-sm border border-indigo-700/60 relative overflow-hidden flex flex-col justify-between gap-4 text-left">
                          <div className="absolute right-0 top-0 translate-x-4 -translate-y-4 w-28 h-28 bg-white/10 rounded-full blur-xl pointer-events-none" />
                          <div className="absolute left-0 bottom-0 -translate-x-4 translate-y-4 w-24 h-24 bg-indigo-900/40 rounded-full blur-xl pointer-events-none" />

                          <div className="z-10 text-left">
                            <div className="flex items-center gap-2 mb-2 text-left">
                              <span className="p-1 px-2 bg-indigo-550 bg-indigo-500/60 rounded text-[10px] font-black uppercase tracking-widest text-indigo-50 match-brand">
                                {isBrazil ? "Inteligência" : "AI Powered"}
                              </span>
                            </div>
                            <h3 className="text-xl font-bold tracking-tight mb-2 text-left text-white">
                              {isBrazil ? "Iniciar Novo Plano" : "Create Monthly Plan"}
                            </h3>
                            <p className="text-xs text-indigo-100 leading-relaxed text-left">
                              {isBrazil 
                                ? "Crie calendários de alta conversão estruturados com postagens dinâmicas baseadas no seu perfil." 
                                : "Generate structural campaign sequence batches tailored perfectly to your brand document DNA."}
                            </p>
                          </div>

                          <button
                            onClick={() => setActiveTab('create-plan')}
                            className="w-full py-3 bg-white text-indigo-900 font-extrabold text-xs rounded-xl hover:bg-indigo-50 transition-all flex items-center justify-center gap-2 shadow-md z-10"
                          >
                            <Sparkles className="w-4 h-4 text-indigo-600" />
                            <span>{isBrazil ? "Criar Plano de Postagens" : "Generate Monthly Plan"}</span>
                          </button>
                        </div>

                        {/* 2. Strategy Assistant Insight Card */}
                        <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 space-y-4 text-left">
                          <div className="flex items-center gap-2 text-left">
                            <span className="p-1.5 bg-amber-50 rounded-lg text-amber-600">
                              <Zap className="w-4 h-4" />
                            </span>
                            <h4 className="text-xs font-black uppercase tracking-wider text-gray-500 text-left">
                              {isBrazil ? "Dicas Estratégicas" : "Strategy Tips"}
                            </h4>
                          </div>

                          <div className="space-y-3.5 text-xs text-gray-600 text-left">
                            <div className="p-3 bg-indigo-50/50 rounded-xl border border-indigo-100/30 text-left">
                              <span className="font-extrabold text-indigo-950 block mb-0.5 text-left">
                                🎯 {isBrazil ? "Aproveitamento de Persona" : "Harness Persona Style"}
                              </span>
                              <span className="text-gray-500 leading-normal block text-left">
                                {isBrazil 
                                  ? `Seu tom é configurado como "${brand.questionnaire?.brandVoice || 'Profissional'}". Misture posts de bastidores com depoimentos.` 
                                  : `We've synced your tone with "${brand.questionnaire?.brandVoice || 'Professional'}". Try introducing client reviews to bolster authority.`}
                              </span>
                            </div>

                            <div className="p-3 bg-amber-50/40 rounded-xl border border-amber-100/40 text-left">
                              <span className="font-extrabold text-amber-950 block mb-0.5 text-left">
                                ⏱️ {isBrazil ? "Janela de Engajamento" : "Peak Coverage Slots"}
                              </span>
                              <span className="text-gray-500 leading-normal block text-left">
                                {isBrazil 
                                  ? "Programe suas entregas nas quartas e quintas por volta das 11:00h e 18:00h para os melhores alcances."
                                  : "Target LinkedIn distribution between Tuesday and Thursday at exactly 09:00 AM for peak business reach."}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === 'create-plan' && (
                <motion.div 
                  key="create-plan"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8 text-left"
                >
                  <header>
                    <h2 className="text-3xl font-bold text-gray-900">{t.createMonthlyPlan}</h2>
                    <p className="text-gray-500 mt-1">
                      {isBrazil 
                        ? "Preencha as informações do público-alvo e canais de destino para que nossa inteligência gere seu calendário de postagens completo."
                        : "Define audience insights and target platforms for our AI to craft your fully-realized content strategy calendar."}
                    </p>
                  </header>

                  {!brand ? (
                    <div className="bg-indigo-50 border border-indigo-100 p-8 rounded-3xl flex flex-col items-center text-center">
                      <div className="bg-white p-4 rounded-2xl shadow-sm mb-4">
                        <Palette className="w-8 h-8 text-indigo-600" />
                      </div>
                      <h3 className="text-xl font-bold text-indigo-900 mb-2">{t.completeBrandProfile}</h3>
                      <p className="text-indigo-700 mb-6 max-w-md text-center">{t.toGenerateContent}</p>
                      <button 
                        onClick={() => setActiveTab('brand')}
                        className="bg-indigo-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
                      >
                        {t.getStarted}
                      </button>
                    </div>
                  ) : canEdit(brand) ? (
                    <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 text-left">
                      <div className="flex items-center justify-between mb-8 text-left">
                        <h3 className="text-xl font-bold text-gray-900 text-left">{t.createMonthlyPlan}</h3>
                        <Sparkles className="w-6 h-6 text-indigo-500" />
                      </div>
                      <PlanGenerator onGenerate={handleGeneratePlan} isGenerating={isGenerating} platforms={brand?.platforms || []} platformPreset={brand?.platformInstructions || {}} enableSmartTrends={brand?.enableSmartTrends} setToast={setToast} />
                    </div>
                  ) : (
                    <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 text-center">
                      <div className="p-4 bg-indigo-50 rounded-2xl inline-block mb-4">
                        <Users className="w-8 h-8 text-indigo-500" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">{t.viewerAccess}</h3>
                      <p className="text-gray-500 mt-2 text-center">{t.readOnlyAccess}</p>
                    </div>
                  )}
                </motion.div>
              )}


              {activeTab === 'brand' && (
                <motion.div 
                  key={`brand-${brand?.id || 'new'}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <BrandProfileForm 
                    initialData={brand} 
                    onSave={handleSaveBrand} 
                    onDelete={handleDeleteBrand}
                    setToast={setToast} 
                    isOwner={brand?.ownerId === effectiveUserId || (brand?.email?.toLowerCase() === (impersonatedUserId ? 'non-existent' : user?.email?.toLowerCase()) && !!user?.email) || isAdmin || !brand}
                    isTrial={isTrialUI}
                    sendEmailNotification={sendEmailNotification}
                    setActiveTab={setActiveTab}
                    onSocialConnected={() => {}}
                    user={user}
                  />
                </motion.div>
              )}

              {activeTab === 'approved-media' && (
                <motion.div 
                  key={`approved-media-${brand?.id || 'empty'}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <ApprovedMediaTab 
                    brand={brand}
                    onSaveBrand={handleSaveBrand}
                    effectivePlan={effectivePlan}
                    isAdmin={isAdmin}
                    setToast={setToast}
                    isBrazil={isBrazil}
                    setActiveTab={setActiveTab}
                  />
                </motion.div>
              )}

              {activeTab === 'plans' && (
                <motion.div 
                  key="plans"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <header className="flex items-center justify-between">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900">{t.contentPlans}</h2>
                      <p className="text-gray-500 mt-1">{t.manageReviewPlans}</p>
                    </div>
                  </header>

                  {plans.length === 0 ? (
                    <div className="bg-white p-12 rounded-3xl border border-dashed border-gray-300 text-center">
                      <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">{t.noPlansYet}</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {plans.map(plan => (
                        <PlanCard 
                          key={plan.id} 
                          plan={plan} 
                          brand={brand}
                          shareFeedback={shareFeedback}
                          onDeleteFeedback={handleDeleteFeedback}
                          isExpanded={expandedPlanIds.has(plan.id)}
                          onToggle={() => togglePlanExpansion(plan.id)}
                          onUpdate={handleUpdatePlan} 
                          onGenerateArtwork={handleGenerateArtworkForPost}
                          onGenerateSeamlessCarousel={handleGeneratePanoramaOnly}
                          onOverlaySlides={handleOverlaySlides}
                          onPublish={handlePublishPost}
                          onUploadArtwork={handleUploadArtworkForPost}
                          onUpdateStatus={handleUpdatePostStatus}
                          onUpdatePlatforms={handleUpdatePostPlatforms}
                          onScheduleAll={handleScheduleAll}
                          onAddPost={(id) => setShowAddPostModal(id)}
                          onRepurposePost={(id, post) => {
                            setPrefilledConcept(`Create a repurposed variation of this post:\n\n${post.copy}`);
                            setShowAddPostModal(id);
                          }}
                          onDuplicatePost={handleDuplicatePost}
                          onDuplicatePlan={handleDuplicatePlan}
                          onRefinePlan={handleRefinePlan}
                          onDeletePlan={handleDeletePlan}
                          onPreview={(url, type, slide, format) => setPreviewMedia({ url, type, slide, format })}
                          onSuggestEdit={(postId) => {}}
                          onApproveSuggestion={(postId, suggestionId) => {}}
                          onRejectSuggestion={(postId, suggestionId) => {}}
                          brandPlatforms={brand?.platforms || []}
                          productImages={brand?.productImages || []}
                          brandTemplates={brand?.brandTemplates || []}
                          brandBlueprints={brandBlueprints}
                          isAdmin={isAdmin}
                          hasApiKey={hasApiKey}
                          setToast={setToast}
                          setActiveTab={setActiveTab}
                          canEdit={canEdit(brand)}
                          isTrial={isTrialUI}
                          user={user}
                          editingPostId={editingPostId}
                          setEditingPostId={setEditingPostId}
                          editingPostData={editingPostData}
                          setEditingPostData={setEditingPostData}
                          onBulkGenerateArtwork={handleGeneratePlanArtwork}
                          isBulkGenerating={isBulkGenerating === plan.id}
                          sprintProgress={isBulkGenerating === plan.id ? sprintProgress : null}
                          onToggleSelectPost={(postId) => {
                            setSelectedPostIds(prev => {
                              const current = prev[plan.id!] || new Set();
                              const next = new Set(current);
                              if (next.has(postId)) next.delete(postId);
                              else next.add(postId);
                              return { ...prev, [plan.id!]: next };
                            });
                          }}
                          selectedPostIds={selectedPostIds[plan.id!] || new Set()}
                          onClearSelection={() => {
                            setSelectedPostIds(prev => {
                              const next = { ...prev };
                              delete next[plan.id!];
                              return next;
                            });
                          }}
                          onArchive={handleArchivePlan}
                          isArchiving={isArchiving === plan.id}
                          onLogAudit={logAudit}
                          onShare={(planId, ideaIds) => {
                            if (!isAdmin && effectivePlan === 'trial') {
                              setToast({ message: "Client Review sharing is not available on the trial tier. Please upgrade.", type: 'warning' });
                              setActiveTab('settings');
                              return;
                            }
                            setShareConfig({ planId, ideaIds, type: ideaIds ? 'post' : 'plan' });
                            setIsShareModalOpen(true);
                          }}
                          tokens={tokens}
                        />
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === 'trends' && isTester && (
                <motion.div
                  key="trends"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <TrendsManager
                    brand={brand}
                    onSaveBrand={handleSaveBrand}
                    isSavingBrand={isSavingBrand}
                    user={user}
                    setToast={setToast}
                    isTrial={isTrialUI}
                    handleUpgradeFeature={handleUpgradeFeature}
                    isBrazil={isBrazil}
                  />
                </motion.div>
              )}

              {activeTab === 'engagement' && brand && (
                <motion.div 
                  key="engagement"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <SocialInsights 
                    brandId={brand.id!} 
                    accounts={brand.socialAccounts || []} 
                  />
                </motion.div>
              )}

              {activeTab === 'inbox' && brand && (
                <motion.div 
                  key="inbox"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <SocialInbox 
                    brandId={brand.id!} 
                    accounts={brand.socialAccounts || []}
                    onReadPost={() => {}}
                    publishedPosts={plans.flatMap(p => {
                      const calendar = p.calendar || [];
                      return calendar.filter((item: any) => 
                        item && item.platformStatus && Object.values(item.platformStatus).some((s: any) => s && s.status === 'published')
                      );
                    })}
                  />
                </motion.div>
              )}

              {activeTab === 'bookings' && isAdmin && user && (
                <motion.div 
                  key="bookings"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <BookingsView hostId={user.uid} />
                </motion.div>
              )}

              {activeTab === 'review' && brand && (
                <motion.div 
                  key="review"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <ReviewFeedbackView 
                    brandId={brand.id!} 
                    onViewInCalendar={handleViewFeedbackInCalendar}
                  />
                </motion.div>
              )}

              {activeTab === 'settings' && (
                <motion.div 
                  key="settings"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <header>
                    <h2 className="text-3xl font-bold text-gray-900">{t.settings}</h2>
                    <p className="text-gray-500 mt-1">{isBrazil ? "Configure sua aplicação e preferências de IA." : "Configure your application and AI preferences."}</p>
                  </header>

                  <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 space-y-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="space-y-1">
                        <h3 className="text-xl font-bold text-gray-900">{isBrazil ? "Assinatura & Faturamento" : "Subscription & Billing"}</h3>
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2 bg-gray-50 p-1 rounded-xl w-fit border border-gray-100">
                                <button 
                                  onClick={() => setBillingCycle('monthly')}
                                  className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${billingCycle === 'monthly' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                  {t.billingMonthly}
                                </button>
                                <button 
                                  onClick={() => setBillingCycle('yearly')}
                                  className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${billingCycle === 'yearly' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                  {t.billingYearly} <span className="text-green-600 ml-1">{isBrazil ? "" : "-20%"}</span>
                                </button>
                              </div>

                              <div className="flex items-center gap-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t.promoCode}</label>
                                <input 
                                  type="text" 
                                  placeholder="WELCOME2026"
                                  value={promoCode}
                                  onChange={(e) => setPromoCode(e.target.value)}
                                  className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all uppercase placeholder:text-gray-300"
                                />
                                {isPromoValid && <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
                              </div>
                            </div>
                          
                          {isAdmin && (
                            <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-xl w-fit border border-gray-200">
                              {pricingRegions.map((r) => (
                                <button
                                  key={r.id}
                                  onClick={() => setRegion(r.id as any)}
                                  className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${selectedRegion.id === r.id ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                  {r.id}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      {(effectivePlan !== 'trial' || currentUserProfile?.stripeCustomerId) && (
                        <button 
                          onClick={handleManageSubscription}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-bold hover:bg-gray-200 transition-all shadow-sm"
                        >
                          <CreditCard className="w-4 h-4" />
                          <span>{t.manageBilling}</span>
                        </button>
                      )}
                    </div>
                    <div className="grid md:grid-cols-4 gap-6">
                      <div className={`p-6 rounded-3xl border-2 transition-all ${effectivePlan === 'trial' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-100'}`}>
                        <div className="flex justify-between items-start mb-4">
                          <p className="font-black text-gray-900 uppercase tracking-widest text-sm">{t.freeTrial}</p>
                          {effectivePlan === 'trial' && <CheckCircle2 className="w-5 h-5 text-indigo-600" />}
                        </div>
                        <div className="text-2xl font-black text-gray-900 mb-2">{selectedRegion.currency}0</div>
                        <p className="text-sm text-gray-500 mb-6">{t.freeTrialFeature}</p>
                      </div>
                      
                      <div className={`p-6 rounded-3xl border-2 transition-all ${effectivePlan === 'solo' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-100'}`}>
                        <div className="flex justify-between items-start mb-4">
                          <p className="font-black text-gray-900 uppercase tracking-widest text-sm">{t.solo}</p>
                          {effectivePlan === 'solo' && <CheckCircle2 className="w-5 h-5 text-indigo-600" />}
                        </div>
                        <div className="text-2xl font-black text-gray-900 mb-2">
                          {getEffectivePrice('solo', billingCycle)}
                          <span className="text-sm font-bold text-gray-500">/{billingCycle === 'monthly' ? t.monthShort : t.yearShort}</span>
                        </div>
                        <p className="text-sm text-gray-500 mb-6">{t.soloPostsFeature}</p>
                        {effectivePlan !== 'solo' && effectivePlan !== 'growth' && effectivePlan !== 'agency' && effectivePlan !== 'enterprise' && (
                          <button 
                            onClick={() => handleUpgrade('solo')}
                            className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all font-display uppercase tracking-widest text-xs"
                          >
                            {t.upgradeTo} Solo
                          </button>
                        )}
                      </div>

                      <div className={`p-6 rounded-3xl border-2 transition-all ${effectivePlan === 'growth' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-100'}`}>
                        <div className="flex justify-between items-start mb-4">
                          <p className="font-black text-gray-900 uppercase tracking-widest text-sm">{t.growth}</p>
                          {effectivePlan === 'growth' && <CheckCircle2 className="w-5 h-5 text-indigo-600" />}
                        </div>
                        <div className="text-2xl font-black text-gray-900 mb-2">
                          {getEffectivePrice('growth', billingCycle)}
                          <span className="text-sm font-bold text-gray-500">/{billingCycle === 'monthly' ? t.monthShort : t.yearShort}</span>
                        </div>
                        <p className="text-sm text-gray-500 mb-6">{t.growthPostsFeature}</p>
                        {effectivePlan !== 'growth' && effectivePlan !== 'agency' && effectivePlan !== 'enterprise' && (
                          <button 
                            onClick={() => handleUpgrade('growth')}
                            className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all font-display uppercase tracking-widest text-xs"
                          >
                            {t.upgradeTo} Growth
                          </button>
                        )}
                      </div>

                      <div className={`p-6 rounded-3xl border-2 transition-all ${effectivePlan === 'agency' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-100'}`}>
                        <div className="flex justify-between items-start mb-4">
                          <p className="font-black text-gray-900 uppercase tracking-widest text-sm">{t.agency}</p>
                          {effectivePlan === 'agency' && <CheckCircle2 className="w-5 h-5 text-indigo-600" />}
                        </div>
                        <div className="text-2xl font-black text-gray-900 mb-2">
                          {getEffectivePrice('agency', billingCycle)}
                          <span className="text-sm font-bold text-gray-500">/{billingCycle === 'monthly' ? t.monthShort : t.yearShort}</span>
                        </div>
                        <p className="text-sm text-gray-500 mb-6">{t.agencyPostsFeature}</p>
                        {effectivePlan !== 'agency' && effectivePlan !== 'enterprise' && (
                          <button 
                            onClick={() => handleUpgrade('agency')}
                            className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all font-display uppercase tracking-widest text-xs"
                          >
                            {t.upgradeTo} Agency
                          </button>
                        )}
                      </div>

                      <div className="p-6 rounded-3xl border-2 border-gray-100 bg-gray-50/50 transition-all">
                        <div className="flex justify-between items-start mb-4">
                          <p className="font-black text-gray-900 uppercase tracking-widest text-sm">Enterprise</p>
                        </div>
                        <p className="text-2xl font-black text-gray-900 mb-2">Custom</p>
                        <p className="text-sm text-gray-500 mb-6">Custom Brand Limits, Dedicated Support, White-label Options</p>
                        <button 
                          onClick={() => setToast({ message: "Sales request sent! Our team will contact you shortly.", type: 'success' })}
                          className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-all"
                        >
                          Contact Sales
                        </button>
                      </div>
                    </div>
                    <div className="mt-8 p-6 bg-gray-50 rounded-2xl border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4">
                      <div>
                        <h4 className="font-bold text-gray-900">Need something even bigger?</h4>
                        <p className="text-sm text-gray-500">We offer custom white-label solutions and dedicated AI instances for large agencies.</p>
                      </div>
                      <button className="px-6 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition-all">
                        Contact Sales
                      </button>
                    </div>

                    {(user?.email === 'dewaldjordaan5@gmail.com' || isAdmin) && (
                      <div className="mt-8">
                        <StorageCalculator />
                      </div>
                    )}
                  </div>

                  {(isAdmin || (brand && brand.ownerId === user?.uid)) && (
                    <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 space-y-6">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">AI Configuration (Admin Only)</h3>
                      <p className="text-gray-600 mb-6">
                        {hasApiKey 
                          ? "The application is currently using high-quality Gemini 3.1 models for image and video generation." 
                          : "The application is currently using the free Gemini 2.5 model for image generation. Video generation requires a paid API key."}
                      </p>
                      
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-6 bg-amber-50 rounded-2xl border border-amber-100">
                          <div className="flex items-center gap-4">
                            <div className="bg-white p-3 rounded-xl shadow-sm">
                              <CheckCircle2 className={`w-6 h-6 ${hasApiKey ? 'text-green-600' : 'text-amber-600'}`} />
                            </div>
                            <div>
                              <p className="font-bold text-amber-900">Gemini API Key</p>
                              <p className="text-sm text-amber-700">{hasApiKey ? 'Key selected and active.' : 'No key selected. Required for real video generation.'}</p>
                            </div>
                          </div>
                          <button 
                            onClick={handleOpenKeyDialog}
                            className="bg-amber-600 text-white px-4 py-2 rounded-xl text-sm font-bold hover:bg-amber-700 transition-all shadow-sm"
                          >
                            {hasApiKey ? 'Change Key' : 'Select Key'}
                          </button>
                        </div>
                        
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100 italic">
                          <p className="text-xs text-gray-500">If your key status doesn't update after selection, try a manual refresh.</p>
                          <button 
                            onClick={handleManualRefresh}
                            className="text-xs font-bold text-gray-600 underline hover:text-gray-900 transition-all"
                          >
                            Refresh Environment
                          </button>
                        </div>
                      </div>
                      </div>
                    </div>
                  )}

                  <div className="pt-6 border-t border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Account & Support</h3>
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                        {user.photoURL ? (
                          <img src={user.photoURL} alt={user.displayName || ''} className="w-12 h-12 rounded-full" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold border border-indigo-200 text-lg">
                            {user.displayName?.charAt(0) || 'U'}
                          </div>
                        )}
                        <div>
                          <p className="font-bold text-gray-900">{user.displayName}</p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                        <button 
                          onClick={logOut}
                          className="ml-auto p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                        >
                          <LogOut className="w-5 h-5" />
                        </button>
                      </div>
                      

                      <div className="p-6 bg-red-50 rounded-2xl border border-red-100 flex flex-col md:flex-row items-center justify-between gap-4 mt-4">
                        <div>
                          <h4 className="font-bold text-red-900">Data Privacy & Removal</h4>
                          <p className="text-sm text-red-700">Need to remove your account or specific platform data? We prioritize your privacy.</p>
                        </div>
                        <Link 
                          to="/data-deletion"
                          className="px-6 py-2 bg-white border border-red-200 text-red-600 rounded-xl font-bold hover:bg-red-50 transition-all flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Deletion Instructions
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
               {activeTab === 'blueprint-engine' && user?.email === 'dewaldjordaan5@gmail.com' && (
                <motion.div 
                  key="blueprint-engine"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <BlueprintEngine userEmail={user.email} />
                </motion.div>
              )}
              {activeTab === 'carousel-test' && isAdmin && (
                <motion.div 
                  key="carousel-test"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <CarouselTester brands={brands} />
                </motion.div>
              )}
              {activeTab === 'html-lab' && user?.email === 'dewaldjordaan5@gmail.com' && (
                <motion.div 
                  key="html-lab"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <HtmlOverlayLab brand={brand} brands={brands} plans={allUserPlans} />
                </motion.div>
              )}
              {activeTab === 'template-baker' && user?.email === 'dewaldjordaan5@gmail.com' && (
                <motion.div 
                  key="template-baker"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <TemplateBakerLab brand={brand} brands={brands} />
                </motion.div>
              )}
              {activeTab === 'admin' && isAdmin && (
                <motion.div 
                  key="admin"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <header>
                    <h2 className="text-3xl font-bold text-gray-900">{t.adminStats}</h2>
                    <p className="text-gray-500 mt-1">{isBrazil ? "Visão geral global da plataforma" : "Global platform overview"}</p>
                  </header>
                  <div className="grid md:grid-cols-4 gap-6">
                    <StatCard icon={<Users className="w-6 h-6 text-indigo-600" />} label={t.users} value={adminStats?.users?.length.toString() || '0'} color="indigo" />
                    <StatCard icon={<Layout className="w-6 h-6 text-emerald-600" />} label={t.brands} value={adminStats?.brands?.length.toString() || '0'} color="emerald" />
                    <StatCard icon={<CalendarIcon className="w-6 h-6 text-blue-600" />} label={t.activePlans} value={adminStats?.plans?.length.toString() || '0'} color="blue" />
                    <StatCard icon={<BarChart3 className="w-6 h-6 text-amber-600" />} label={isBrazil ? "Visitas ao Site" : "Site Visits"} value={adminStats?.visits?.toString() || '0'} color="amber" />
                  </div>

                  <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 mt-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">{isBrazil ? "Banco de Usuários" : "User Database"}</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="py-3 px-4 font-bold text-gray-600 text-sm">{t.user}</th>
                            <th className="py-3 px-4 font-bold text-gray-600 text-sm">{isBrazil ? "Cargo" : "Role"}</th>
                            <th className="py-3 px-4 font-bold text-gray-600 text-sm">Tokens</th>
                            <th className="py-3 px-4 font-bold text-gray-600 text-sm">{t.brands}</th>
                            <th className="py-3 px-4 font-bold text-gray-600 text-sm">{isBrazil ? "Ações" : "Actions"}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminStats?.users?.map(u => (
                            <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                              <td className="py-3 px-4">
                                <div className="flex flex-col">
                                  <span className="font-bold text-gray-900">{u.displayName || 'No Name'}</span>
                                  <span className="text-xs text-gray-500">{u.email || u.id.substring(0, 8) + '...'}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4"><span className="px-2 py-1 bg-gray-100 text-xs rounded-full">{u.role || 'user'}</span></td>
                              <td className="py-3 px-4">
                                <span className="font-bold text-indigo-600">{u.tokens || 0}</span>
                              </td>
                              <td className="py-3 px-4 text-gray-700">
                                <div className="flex flex-col gap-1.5">
                                  {adminStats?.brands?.filter(b => b.ownerId === u.id).map(b => {
                                    const profit = calculateProfitability(b);
                                    return (
                                      <div key={b.id} className="flex flex-col border border-gray-100 rounded-lg p-2 bg-gray-50/30">
                                        <div className="flex items-center justify-between gap-4">
                                          <div className="flex flex-col">
                                            <span className="text-xs font-bold text-gray-900">{b.companyName}</span>
                                            <span className="text-[10px] text-gray-400 font-mono select-all uppercase">ID: {b.id}</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            {b.usage?.isPromotional && <span className="text-[8px] bg-yellow-100 text-yellow-700 px-1 rounded">PROMO</span>}
                                            <span className="text-[9px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-black uppercase tracking-wider">
                                              {b.subscription?.plan || 'Free'}
                                            </span>
                                            <button 
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                syncBrandSubscription(b.id);
                                              }}
                                              title="Sync with Stripe"
                                              className="p-1 hover:bg-gray-200 rounded transition-colors text-gray-400 hover:text-indigo-600"
                                            >
                                              <RefreshCw className="w-2.5 h-2.5" />
                                            </button>
                                          </div>
                                        </div>
                                        <div className="flex items-center justify-end mt-1">
                                          <div className={`flex items-center gap-1 text-[10px] font-bold ${profit.status === 'healthy' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                            {profit.margin.toFixed(0)}% margin
                                            {profit.status === 'risk' && <AlertTriangle className="w-3 h-3" />}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {adminStats?.brands?.filter(b => b.ownerId === u.id).length === 0 && (
                                    <span className="text-gray-400 italic text-xs">No brands</span>
                                  )}
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <Link 
                                  to={`/admin/impersonate?userId=${u.id}`}
                                  className="text-xs font-bold text-indigo-600 hover:bg-indigo-600 hover:text-white px-3 py-1.5 rounded-lg transition-all flex items-center gap-2 justify-center w-fit border border-indigo-100 hover:border-indigo-600"
                                >
                                  Impersonate
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 mt-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Booking Leads</h3>
                    <p className="text-sm text-gray-500 mb-6">Contact info collected from guest bookings across all hosts.</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="py-3 px-4 font-bold text-gray-600 text-sm">Guest Name</th>
                            <th className="py-3 px-4 font-bold text-gray-600 text-sm">Email</th>
                            <th className="py-3 px-4 font-bold text-gray-600 text-sm">Host Id</th>
                            <th className="py-3 px-4 font-bold text-gray-600 text-sm text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminStats?.bookingGuests?.map(guest => (
                            <tr key={guest.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                              <td className="py-3 px-4 font-bold text-gray-900">{guest.guestName}</td>
                              <td className="py-3 px-4 text-gray-600">{guest.guestEmail}</td>
                              <td className="py-3 px-4 text-xs font-mono text-gray-400">{guest.hostId}</td>
                              <td className="py-3 px-4 text-right">
                                {confirmDeleteGuestId === guest.id ? (
                                  <div className="flex items-center justify-end gap-2">
                                    <button 
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          console.log("Attempting to delete lead:", guest.id);
                                          await deleteDoc(doc(db, 'booking_guests', guest.id));
                                          setAdminStats(prev => prev ? {
                                            ...prev,
                                            bookingGuests: prev.bookingGuests.filter(g => g.id !== guest.id)
                                          } : null);
                                          setConfirmDeleteGuestId(null);
                                          console.log("Lead deleted successfully:", guest.id);
                                        } catch (err) {
                                          console.error("Failed to delete lead:", guest.id, err);
                                          alert("Failed to delete lead: " + err);
                                        }
                                      }}
                                      className="px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-lg hover:bg-red-700 transition"
                                    >
                                      Confirm
                                    </button>
                                    <button 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setConfirmDeleteGuestId(null);
                                      }}
                                      className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-bold rounded-lg hover:bg-gray-200 transition"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setConfirmDeleteGuestId(guest.id);
                                    }}
                                    className="text-red-500 hover:bg-red-50 p-1 rounded"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                          {(!adminStats?.bookingGuests || adminStats.bookingGuests.length === 0) && (
                            <tr>
                              <td colSpan={4} className="py-6 px-4 text-center text-gray-500">No booking guests found yet.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 mt-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-indigo-600" />
                      Brand Utilities
                    </h3>
                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="p-6 bg-indigo-50/50 rounded-2xl border border-indigo-100">
                        <h4 className="font-bold text-indigo-900 mb-2 flex items-center gap-2">
                          <ArrowRightLeft className="w-4 h-4" />
                          Transfer Ownership
                        </h4>
                        <p className="text-xs text-indigo-700 mb-4">Moves a brand and all associated plans to a different user's profile.</p>
                        <div className="space-y-3">
                          <div>
                            <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest block mb-1">Brand ID</label>
                            <input 
                              type="text" 
                              value={transferBrandData.sourceBrandId}
                              onChange={e => setTransferBrandData(prev => ({ ...prev, sourceBrandId: e.target.value }))}
                              placeholder="e.g. brand_123"
                              className="w-full px-4 py-2 text-sm bg-white border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest block mb-1">Target User Email</label>
                            <input 
                              type="email" 
                              value={transferBrandData.targetEmail}
                              onChange={e => setTransferBrandData(prev => ({ ...prev, targetEmail: e.target.value }))}
                              placeholder="shana@riderresidential.com"
                              className="w-full px-4 py-2 text-sm bg-white border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                            />
                          </div>
                          <button 
                            onClick={handleTransferBrand}
                            disabled={transferBrandData.isTransferring}
                            className="w-full py-2 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 transition-all flex items-center justify-center gap-2"
                          >
                            {transferBrandData.isTransferring ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                            Transfer Now
                          </button>
                        </div>
                      </div>

                          <div className="p-6 bg-emerald-50/50 rounded-2xl border border-emerald-100">
                        <h4 className="font-bold text-emerald-900 mb-2 flex items-center gap-2">
                          <Coins className="w-4 h-4" />
                          Adjust Tokens
                        </h4>
                        <p className="text-xs text-emerald-700 mb-4">Directly set the token balance for any user.</p>
                        <div className="space-y-3">
                          <div>
                            <label className="text-[10px] font-black text-emerald-400 uppercase tracking-widest block mb-1">User ID or Brand ID</label>
                            <input 
                              type="text" 
                              value={adjustTokenData.brandId}
                              onChange={e => setAdjustTokenData(prev => ({ ...prev, brandId: e.target.value }))}
                              placeholder="e.g. user_123 or brand_456"
                              className="w-full px-4 py-2 text-sm bg-white border border-emerald-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-black text-emerald-400 uppercase tracking-widest block mb-1">New Token Balance</label>
                            <input 
                              type="number" 
                              value={adjustTokenData.amount}
                              onChange={e => setAdjustTokenData(prev => ({ ...prev, amount: e.target.value }))}
                              placeholder="200"
                              className="w-full px-4 py-2 text-sm bg-white border border-emerald-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
                            />
                          </div>
                          <button 
                            onClick={handleAdjustTokens}
                            disabled={adjustTokenData.isProcessing}
                            className="w-full py-2 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-700 transition-all flex items-center justify-center gap-2"
                          >
                            {adjustTokenData.isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
                            Update Tokens
                          </button>
                        </div>
                      </div>

                      <div className="p-6 bg-gray-50/50 rounded-2xl border border-gray-100">
                        <h4 className="font-bold text-gray-900 mb-2">Platform Tips</h4>
                        <ul className="text-xs text-gray-500 space-y-2">
                          <li className="flex items-center gap-2">
                            <div className="w-1 h-1 bg-indigo-400 rounded-full" />
                            Transfering updates both ownerId and plan uid (owner ref).
                          </li>
                          <li className="flex items-center gap-2">
                            <div className="w-1 h-1 bg-indigo-400 rounded-full" />
                            Use "Impersonate" to verify after transfer.
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <ErrorLogsView />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </main>
      </div>
    </div>
  );

  if (!isAuthReady) return <LoadingScreen message="Checking authentication..." />;
  if (user && loading && location.pathname.startsWith('/dashboard')) return <LoadingScreen message="Syncing your brand data..." />;

  return (
    <ErrorBoundary>
      <ScrollToTop />
      <div className={`min-h-screen flex flex-col ${location.pathname.startsWith('/share') || location.pathname.startsWith('/connect') ? '' : 'pt-16 lg:pt-0'}`}>
        {!location.pathname.startsWith('/dashboard') && !location.pathname.startsWith('/share') && !location.pathname.startsWith('/connect') && <Header onSignIn={handleSignIn} user={user} isSigningIn={isSigningIn} />}
        {location.pathname.startsWith('/dashboard') && (
          <DashboardNavbar 
            user={user} 
            brand={brand} 
            onSignOut={async () => {
              localStorage.removeItem('impersonatedUserId');
              localStorage.removeItem('activeBrandId');
              await logOut();
              setActiveBrandId(null);
              setBrands([]);
              setPlans([]);
              navigate('/');
            }} 
            onOpenCommand={() => setCommandOpen(true)} 
            isAdmin={isAdmin} 
            isTrial={isTrialUI} 
            userRole={userRole}
            isCollaborator={isCollaborator || false}
            onSignIn={handleSignIn}
            isSigningIn={isSigningIn}
            tokens={tokens}
            userStats={userStats}
            setActiveTab={setActiveTab}
            activeTab={activeTab}
            brands={brands}
            activeBrandId={activeBrandId}
            setActiveBrandId={setActiveBrandId}
            onNewBrand={() => {
              if (user) {
                if (!isAdmin) {
                  const ownedBrands = brands.filter(b => b.ownerId === user.uid);
                  const userPlan = currentUserProfile?.subscription?.plan;
                  const hasEnterprise = userPlan === 'enterprise' || ownedBrands.some(b => b.subscription?.plan === 'enterprise');
                  const hasAgency = userPlan === 'agency' || ownedBrands.some(b => b.subscription?.plan === 'agency');
                  const hasGrowth = userPlan === 'growth' || ownedBrands.some(b => b.subscription?.plan === 'growth');
                  const limit = hasEnterprise ? 50 : hasAgency ? 10 : hasGrowth ? 3 : 1;

                  if (ownedBrands.length >= limit) {
                    setToast({ 
                      message: isBrazil 
                        ? `Limite de marcas atingido (${limit}). Por favor, faça upgrade da sua marca existente para criar mais.`
                        : `Brand limit reached (${limit}). Please upgrade your existing brand to create more.`, 
                      type: 'error' 
                    });
                    return;
                  }
                }
                setActiveBrandId('new');
                setActiveTab('brand');
              }
            }}
          />
        )}
        <main className="flex-1 flex flex-col">
          <Routes>
            <Route path="/" element={
            <>
              <SEO 
                title={t.seoHomeTitle}
                description={t.seoHomeDesc}
              />
              <Home onSignIn={handleSignIn} user={user} isSigningIn={isSigningIn} isAdmin={isAdmin} />
            </>
          } />
            <Route path="/features" element={
              <>
                <SEO 
                  title={t.featuresTools}
                  description={t.featuresSubtitle}
                />
                <Features />
              </>
            } />
            <Route path="/pricing" element={
              <>
                <SEO 
                  title={t.pricingPlans}
                  description={t.pricingSubtitle}
                />
                <Pricing user={user} onSignIn={handleSignIn} isAdmin={isAdmin} />
              </>
            } />
            <Route path="/how-it-works" element={
              <>
                <SEO 
                  title={t.howItWorks}
                  description={t.howItWorksSubtitle}
                />
                <HowItWorks />
              </>
            } />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/features/ai-post-generator" element={<AIPostGenerator />} />
            <Route path="/features/social-media-calendar" element={<SocialMediaCalendar />} />
            <Route path="/features/ai-caption-generator" element={<AICaptionGenerator />} />
            <Route path="/features/post-scheduler" element={<PostScheduler />} />
            <Route path="/features/brand-voice" element={<BrandVoice />} />
            <Route path="/platforms/instagram" element={<InstagramPage />} />
            <Route path="/platforms/linkedin" element={<LinkedInPage />} />
            <Route path="/platforms/tiktok" element={<TikTokPage />} />
            <Route path="/platforms/facebook" element={<FacebookPage />} />
            <Route path="/platforms/x-twitter" element={<XTwitterPage />} />
            <Route path="/use-cases/small-businesses" element={<SmallBusiness />} />
            <Route path="/use-cases/agencies" element={<Agencies />} />
            <Route path="/use-cases/creators" element={<Creators />} />
            <Route path="/use-cases/marketing-teams" element={<MarketingTeams />} />
            <Route path="/blog/ai-for-smbs" element={<AIForSMBs />} />
            <Route path="/blog/social-media-trends-2026" element={<SocialMediaTrends2026 />} />
            <Route path="/blog/scaling-your-agency" element={<HowToScaleAgency />} />
            <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Home onSignIn={handleSignIn} user={user} isSigningIn={isSigningIn} />} />
            <Route path="/book/:hostId" element={<BookingPage />} />
            <Route path="/booking" element={<Navigate to="/book/dewaldjordaan5" replace />} />
            <Route path="/dashboard" element={user ? renderDashboard() : <Navigate to="/" replace />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/showcase" element={<Showcase />} />
            <Route path="/showcase/:id" element={<ShowcaseDetail />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="/admin/impersonate" element={isAdmin ? <AdminImpersonate /> : <Navigate to="/" replace />} />
            <Route path="/share/:token" element={<ShareView />} />
            <Route path="/connect/:token" element={<ConnectPortal />} />
          </Routes>
        </main>
        {location.pathname.startsWith('/dashboard') ? (
          <DashboardFooter user={user} setActiveTab={setActiveTab} isAdmin={isAdmin} onSignIn={handleSignIn} />
        ) : location.pathname.startsWith('/share') || location.pathname.startsWith('/connect') ? null : (
          <Footer />
        )}
      </div>
      <audio 
        ref={chimeRef} 
        src="/chime.mp3" 
        preload="auto" 
      />

      <ShareModal 
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        brandId={activeBrandId || ''}
        planId={shareConfig.planId}
        ideaIds={shareConfig.ideaIds}
        type={shareConfig.type}
        user={user!}
      />

      <AnimatePresence>
        {planToArchive && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[150] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center space-y-6"
            >
              <div className="mx-auto w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center">
                <Archive className="w-8 h-8" />
              </div>

              <div className="space-y-2">
                <h3 className="text-xl font-bold text-gray-900">
                  {archiveStep === 'confirm' ? 'Finalize & Archive Month?' : 
                   archiveStep === 'downloading' ? 'Generating Archives...' : 
                   'Verify Downloads'}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {archiveStep === 'confirm' ? 
                    "This will download your monthly assets and summary, then PERMANENTLY delete all large media files from our servers to save storage. Thumbnails will remain visible." :
                   archiveStep === 'downloading' ? 
                    "Please wait while we package your high-resolution media and generate your PDF summary. This may take a minute..." :
                    "Downloads should have started. Did you receive your ZIP and PDF files? Only proceed if you have saved your content."
                  }
                </p>
              </div>

              <div className="flex gap-3">
                {archiveStep === 'confirm' ? (
                  <>
                    <button 
                      onClick={() => setPlanToArchive(null)}
                      className="flex-1 py-3 text-sm font-bold text-gray-500 hover:bg-gray-50 rounded-xl transition-all"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={() => executeArchivePlan(planToArchive)}
                      className="flex-1 py-3 text-sm font-bold bg-indigo-600 text-white rounded-xl shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all"
                    >
                      Archive Month
                    </button>
                  </>
                ) : archiveStep === 'downloading' ? (
                  <div className="w-full py-3 flex items-center justify-center gap-3 text-indigo-600 font-bold">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  <>
                    <button 
                      onClick={() => {
                        setIsArchiving(null);
                        setPlanToArchive(null);
                      }}
                      className="flex-1 py-3 text-sm font-bold text-red-600 hover:bg-red-50 rounded-xl transition-all"
                    >
                      No, Restart
                    </button>
                    <button 
                      onClick={() => handleFinalizeCleanup(planToArchive)}
                      className="flex-1 py-3 text-sm font-bold bg-green-600 text-white rounded-xl shadow-lg shadow-green-100 hover:bg-green-700 transition-all"
                    >
                      Yes, Delete Server Files
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isAuthModalOpen && (
          <AuthModal 
            isOpen={isAuthModalOpen}
            onClose={() => setIsAuthModalOpen(false)}
            onSuccess={() => {
              setIsAuthModalOpen(false);
              navigate('/dashboard');
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddPostModal && (
          <AddPostModal 
            onClose={() => {
              setShowAddPostModal(null);
              setPrefilledConcept('');
              const url = new URL(window.location.href);
              url.searchParams.delete('action');
              url.searchParams.delete('concept');
              window.history.replaceState({}, '', url.toString());
            }}
            onAdd={handleAddPost}
            isAdding={isAddingPost}
            initialDescription={prefilledConcept}
            availablePlans={plans.filter(p => p.status !== 'archived')}
            initialPlanId={showAddPostModal}
            brand={brand}
            setToast={setToast}
            brandBlueprints={brandBlueprints}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toast && (
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[110] max-w-md w-full px-4"
          >
            <div className={`px-6 py-3 rounded-2xl shadow-2xl flex items-center justify-between gap-3 border ${
              toast.type === 'success' ? 'bg-green-600 border-green-500 text-white' : 
              toast.type === 'warning' ? 'bg-amber-600 border-amber-500 text-white' : 
              'bg-red-600 border-red-500 text-white'
            }`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {toast.type === 'success' ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : 
                 toast.type === 'warning' ? <AlertTriangle className="w-5 h-5 shrink-0 text-amber-200" /> : 
                 <AlertCircle className="w-5 h-5 shrink-0" />}
                <span className="font-bold text-sm leading-snug break-words">{toast.message}</span>
              </div>
              {(toast.duration === null || toast.type === 'warning') && (
                <button 
                  onClick={() => setToast(null)}
                  className="ml-2 shrink-0 bg-white/20 hover:bg-white/35 active:scale-95 px-3 py-1.5 rounded-xl transition-all font-black text-[10px] uppercase tracking-wider text-white cursor-pointer"
                >
                  {isBrazil ? "Entendi" : "Got it"}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {previewMedia && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPreviewMedia(null)}
            className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-5xl w-full h-full flex items-center justify-center"
              onClick={e => e.stopPropagation()}
            >
              {previewMedia.url && (
                previewMedia.type === 'video' ? (
                  <video 
                    src={previewMedia.url} 
                    controls 
                    autoPlay 
                    className="max-w-full max-h-full rounded-lg shadow-2xl"
                  />
                ) : (
                  <div className="relative max-w-full max-h-full flex items-center justify-center">
                    <div className={`relative max-h-[80vh] max-w-full overflow-hidden rounded-lg shadow-2xl ${
                      (previewMedia.format === "story" || previewMedia.slide?.format === "story") ? "aspect-[9/16] h-[80vh]" : "aspect-square h-[80vh] w-[80vh]"
                    }`} style={{ containerType: "inline-size" }}>
                      <img 
                        src={previewMedia.slide?.backgroundSliceUrl || previewMedia.url} 
                        alt="Full Preview" 
                        className="w-full h-full object-cover"
                      />
                      {previewMedia.slide?.layoutTemplate && (
                        <RenderSlideOverlay 
                          slide={previewMedia.slide} 
                          brand={brand} 
                          slideIndex={0} 
                          totalSlides={1} 
                        />
                      )}
                    </div>
                  </div>
                )
              )}
              <button 
                onClick={() => setPreviewMedia(null)}
                className="absolute top-0 right-0 m-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isBulkGenerating && sprintProgress && (
           <motion.div 
             initial={{ y: 100, opacity: 0 }}
             animate={{ y: 0, opacity: 1 }}
             exit={{ y: 100, opacity: 0 }}
             className="fixed bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4 z-[80]"
           >
             <AnimatePresence>
               {showSprintVideo && (
                 <motion.div
                   initial={{ scale: 0.8, opacity: 0, y: 20 }}
                   animate={{ scale: 1, opacity: 1, y: 0 }}
                   exit={{ scale: 0.8, opacity: 0, y: 20 }}
                   className="relative w-[500px] aspect-video bg-gray-900 rounded-3xl overflow-hidden shadow-2xl border border-white/10 group"
                 >
                   <video 
                     src="/sprint-video.mp4" 
                     autoPlay 
                     muted 
                     loop 
                     playsInline
                     className="w-full h-full object-cover"
                   />
                   <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                   <button
                     onClick={() => setShowSprintVideo(false)}
                     className="absolute top-3 right-3 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100"
                   >
                     <X className="w-4 h-4" />
                   </button>
                   <div className="absolute bottom-3 left-4 right-4">
                     <p className="text-[10px] font-bold text-white/70 uppercase tracking-widest flex items-center gap-2">
                       <Sparkles className="w-3 h-3 text-indigo-400" />
                       Generation in progress...
                     </p>
                   </div>
                 </motion.div>
               )}
             </AnimatePresence>

             <div className="bg-gray-900 border border-white/10 text-white px-6 py-4 rounded-3xl shadow-2xl flex items-center gap-8 min-w-[500px]">
               <div className="flex items-center gap-4">
                  <div className="bg-indigo-600 p-2 rounded-xl">
                     <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                  <div>
                     <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Sprinting Progress</p>
                     <p className="text-sm font-black">Slide {sprintProgress.current} of {sprintProgress.total}</p>
                  </div>
               </div>

               <div className="flex-1 space-y-2">
                  <div className="flex justify-between text-[10px] font-bold text-indigo-400 uppercase tracking-widest">
                     <span className="truncate max-w-[200px]">{sprintProgress.postTitle}</span>
                     <span>{Math.round((sprintProgress.current / sprintProgress.total) * 100)}%</span>
                  </div>
                  <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                     <motion.div 
                       className="h-full bg-indigo-500"
                       initial={{ width: 0 }}
                       animate={{ width: `${(sprintProgress.current / sprintProgress.total) * 100}%` }}
                       transition={{ duration: 0.5 }}
                     />
                  </div>
               </div>

               <div className="h-10 w-px bg-white/10" />

               <button 
                  onClick={() => {
                    stopSprintRef.current = true;
                    setToast({ message: "Stopping after current slide...", type: 'success' });
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600/20 text-red-500 hover:bg-red-600/30 rounded-xl font-bold transition-all border border-red-500/20"
               >
                  <Square className="w-4 h-4 fill-current" />
                  <span>Stop</span>
               </button>
             </div>
           </motion.div>
        )}
      </AnimatePresence>
    </ErrorBoundary>
  );
}

// --- Sub-components ---

const SidebarItem = ({ active, onClick, icon, label, badge }: { active: boolean, onClick: () => void, icon: React.ReactNode, label: string, badge?: number }) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium text-left transition-all ${
      active 
        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' 
        : 'text-gray-600 hover:bg-gray-100'
    }`}
  >
    <div className="flex items-center gap-3 text-left">
      <div className="shrink-0">{icon}</div>
      <span className="text-left">{label}</span>
    </div>
    {badge !== undefined && badge > 0 && (
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-black shrink-0 ${active ? 'bg-white text-indigo-600' : 'bg-indigo-600 text-white animate-pulse'}`}>
        {badge}
      </span>
    )}
  </button>
);

const StatCard = ({ icon, label, value, color, onClick }: { icon: React.ReactNode, label: string, value: string, color: string, onClick?: () => void }) => (
  <div 
    onClick={onClick}
    className={`bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex items-center gap-4 ${onClick ? 'cursor-pointer hover:shadow-md hover:border-indigo-100 transition-all' : ''}`}
  >
    <div className={`p-3 rounded-2xl bg-${color}-50`}>
      {icon}
    </div>
    <div>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  </div>
);

const AccordionSection = ({ title, description, icon: Icon, defaultOpen = false, children }: { title: string, description?: string, icon?: any, defaultOpen?: boolean, children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border text-gray-900 border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-indigo-500" />}
          <div>
            <h3 className="text-sm font-bold text-gray-900">{title}</h3>
            {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
          </div>
        </div>
        <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-gray-200"
          >
            <div className="p-6 space-y-6">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const BrandProfileForm = ({ 
  initialData, 
  onSave, 
  onDelete,
  setToast, 
  isOwner, 
  isTrial,
  sendEmailNotification,
  setActiveTab,
  onSocialConnected,
  user
}: { 
  initialData: BrandProfile | null, 
  onSave: (data: Partial<BrandProfile>, skipRedirect?: boolean, skipToast?: boolean) => void, 
  onDelete?: (brandId: string) => Promise<void> | void,
  setToast: (t: { message: string, type: 'success' | 'error' | 'warning', duration?: number | null } | null) => void, 
  isOwner: boolean, 
  isTrial: boolean,
  sendEmailNotification: (to: string, subject: string, body: string) => Promise<any>,
  setActiveTab?: (tab: 'dashboard' | 'brand' | 'plans' | 'settings' | 'admin') => void,
  onSocialConnected?: () => void,
  user?: any
}) => {
  const { t, isBrazil } = useTranslation();
  const isTester = user?.email?.toLowerCase() === 'dewaldjordaan5@gmail.com' || user?.email?.toLowerCase() === 'dewald.jordan@sparkpr.com';

  const handleUpgradeFeature = async (feature: string) => {
    if (!user || !formData.id) return;
    try {
      setIsSaving(true);
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: feature, // Use feature ID as plan
          type: 'subscription',
          brandId: formData.id,
          userId: user.uid,
          interval: 'monthly'
        })
      });
      if (!response.ok) throw new Error('Failed to create session');
      const { url } = await response.json();
      window.location.href = url;
    } catch (err: any) {
      console.error("Upgrade error:", err);
      setToast({ message: "Failed to initiate upgrade: " + err.message, type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const [showBakerModal, setShowBakerModal] = useState(false);
  const [editingBlueprint, setEditingBlueprint] = useState<any>(null);
  const [deletingBlueprintId, setDeletingBlueprintId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<BrandProfile>>(initialData || {
    companyName: '',
    logoUrl: '',
    logoLightUrl: '',
    logoDarkUrl: '',
    email: '',
    phone: '',
    website: '',
    brandingDetails: '',
    platforms: ['Facebook', 'Instagram'],
    productImages: [],
    brandTemplates: [],
    contextDocuments: [],
    socialAccounts: [],
    visualStyles: [],
    enableSmartTrends: false,
    smartTrendsTime: '08:00',
    questionnaire: {
      targetAudience: '',
      brandVoice: '',
      visualAesthetic: '',
      primaryGoals: '',
      competitors: ''
    }
  });

  const [brandBlueprints, setBrandBlueprints] = useState<any[]>([]);

  useEffect(() => {
    if (!formData.id) return;
    const q = query(
      collection(db, 'carousel_blueprints'),
      where('brandId', '==', formData.id)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBrandBlueprints(data);
    });
    return () => unsubscribe();
  }, [formData.id]);

  // Sync formData with initialData when it changes (e.g. after a save or brand switch)
  useEffect(() => {
    setAiWebsiteWarning(null);
    if (initialData) {
      setFormData(prev => {
        // Only update if the brand ID changed or if we're not currently editing/saving
        // If the ID is the same, we should be careful not to overwrite fields the user might be editing
        if (prev.id === initialData.id) {
          return { ...initialData, ...prev }; 
        }
        return { ...initialData };
      });
    }
  }, [initialData?.id]); // Only trigger when the brand ID actually changes

  // Dynamic Web Font link injection to support live typographic previews
  useEffect(() => {
    const heading = formData.fontHeading || 'Space Grotesk';
    const body = formData.fontBody || 'Inter';
    
    const id = 'brand-preview-fonts';
    let link = document.getElementById(id) as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    
    const headingFamily = heading.replace(/\s+/g, '+');
    const bodyFamily = body.replace(/\s+/g, '+');
    link.href = `https://fonts.googleapis.com/css2?family=${headingFamily}:wght@400;700;800&family=${bodyFamily}:wght@400;500;600&display=swap`;
  }, [formData.fontHeading, formData.fontBody]);

  useEffect(() => {
    formData.socialAccounts?.forEach(account => {
      const p = account.platform.toLowerCase();
      if ((p === 'facebook' || p === 'instagram' || p === 'linkedin') && account.accessToken) {
        // Only auto-fetch if we don't have pages yet to avoid redundant logs/calls on mount
        if (p === 'facebook' && availablePages.length === 0) {
          fetchAvailablePages(account.platform, account.accessToken);
        } else if (p === 'instagram' && availableIgAccounts.length === 0) {
          fetchAvailablePages(account.platform, account.accessToken);
        } else if (p === 'linkedin' && availableLinkedinOrgs.length === 0) {
          fetchAvailablePages(account.platform, account.accessToken);
        }
      }
    });
  }, []); // Run once on mount

  const availablePlatforms = ['Facebook', 'Instagram', 'Threads', 'LinkedIn', 'Twitter', 'Reddit', 'Pinterest', 'TikTok'];

  const togglePlatform = (platform: string) => {
    const current = formData.platforms || [];
    if (current.includes(platform)) {
      setFormData({ ...formData, platforms: current.filter(p => p !== platform) });
    } else {
      setFormData({ ...formData, platforms: [...current, platform] });
    }
  };

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingLight, setIsUploadingLight] = useState(false);
  const [isUploadingDark, setIsUploadingDark] = useState(false);
  const [isUploadingProduct, setIsUploadingProduct] = useState(false);
  const [isUploadingTemplate, setIsUploadingTemplate] = useState(false);
  const [templateUploadStatus, setTemplateUploadStatus] = useState<string | null>(null);
  const [templateDragActive, setTemplateDragActive] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [isConnecting, setIsConnecting] = useState<string | null>(null);
  const [showTroubleshooting, setShowTroubleshooting] = useState<{ isOpen: boolean, tab: 'instagram' | 'linkedin' }>({ isOpen: false, tab: 'instagram' });
  const [viewingDoc, setViewingDoc] = useState<any>(null);
  const [miningContent, setMiningContent] = useState('');
  const [miningPlatform, setMiningPlatform] = useState('LinkedIn');
  const [isMining, setIsMining] = useState(false);
  const [isMiningVisual, setIsMiningVisual] = useState(false);
  const [visualContext, setVisualContext] = useState('');

  const handleMinePersonality = async () => {
    if (!miningContent.trim()) return;
    setIsMining(true);
    try {
      const result = await extractPersonality(miningContent, miningPlatform);
      
      // Update form data with extracted results
      const updatedData = {
        ...formData,
        questionnaire: {
          ...formData.questionnaire!,
          brandVoice: result.brandVoice || formData.questionnaire?.brandVoice || '',
          targetAudience: result.targetAudience || formData.questionnaire?.targetAudience || ''
        },
        brandingDetails: (formData.brandingDetails ? formData.brandingDetails + "\n\n" : "") + 
          `Extracted Pillars from ${miningPlatform}:\n` + 
          (result.keyPillars?.map((p: string) => `- ${p}`).join('\n') || '')
      };

      setFormData(updatedData);
      setMiningContent('');
      setToast({ message: "Brand voice and pillars extracted successfully!", type: 'success' });
    } catch (err: any) {
      console.error("Style mining error:", err);
      setToast({ message: "Failed to extract style data. Please try with more or different content.", type: 'error' });
    } finally {
      setIsMining(false);
    }
  };

  const handleMineVisualStyle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsMiningVisual(true);
    try {
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read image file.'));
        reader.readAsDataURL(file);
      });
      const base64 = await base64Promise;
      const result = await analyzeInspirationImage(base64, file.type, visualContext);
      
      setFormData(prev => ({
        ...prev,
        questionnaire: {
          ...prev.questionnaire!,
          visualAesthetic: prev.questionnaire?.visualAesthetic ? prev.questionnaire.visualAesthetic : result.aesthetic
        },
        visualStyles: [
          ...(prev.visualStyles || []),
          { usage: result.usage, aesthetic: result.aesthetic, promptDNA: result.promptDNA }
        ]
      }));
      setToast({ message: `Extracted visual style for ${result.usage}!`, type: 'success' });
    } catch (err: any) {
      console.error("Failed to mine visual style:", err);
      const errorMsg = err.message || (typeof err === 'string' ? err : JSON.stringify(err));
      if (errorMsg.includes('403') || errorMsg.includes('API_KEY_HTTP_REFERRER_BLOCKED') || errorMsg.includes('Forbidden')) {
        setToast({ message: `API Key restricted (403). For Google Cloud, referrers are often hidden in previews. Use an unrestricted API key, or if restricting, try '*.run.app/*' or use IP/API-level restrictions instead.`, type: 'error' });
      } else {
        setToast({ message: "Failed to analyze image style.", type: 'error' });
      }
    } finally {
      setIsMiningVisual(false);
      // Reset input
      e.target.value = '';
    }
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate origin is from AI Studio preview or localhost
      const origin = event.origin;
      if (!origin.endsWith('.run.app') && !origin.includes('localhost')) {
        return;
      }
      if (event.data?.type === 'OAUTH_AUTH_SUCCESS') {
        const { platform, account, brandId: msgBrandId } = event.data;
        
        // CRITICAL: Ensure this connection only applies to the brand that initiated it
        if (!msgBrandId) {
          console.warn("[Isolation] Received OAUTH_SUCCESS with missing brandId. Possible spoofing or session mismatch.");
          return;
        }
        
        if (msgBrandId !== formData.id) {
          console.warn(`[Isolation] Ignoring social connection for brand ${msgBrandId} in tab for brand ${formData.id}. This account will NOT be saved to ${formData.id}.`);
          setToast({ 
            message: "Social connection ignored: It belongs to a different brand session. Please try connecting from that brand's settings.", 
            type: 'error' 
          });
          setIsConnecting(null);
          return;
        }
        
        const isLinkedIn = platform.toLowerCase() === 'linkedin';
        const isThreads = platform.toLowerCase() === 'threads';
        let newAccounts = [];
        
        if (isLinkedIn || isThreads) {
          newAccounts = [
            ...(formData.socialAccounts?.filter(a => !(a.platform.toLowerCase() === platform.toLowerCase() && a.accountId === account.accountId)) || []),
            account
          ];
        } else {
          newAccounts = [
            ...(formData.socialAccounts?.filter(a => a.platform.toLowerCase() !== platform.toLowerCase()) || []),
            account
          ];
        }

        const updatedData = {
          ...formData,
          socialAccounts: newAccounts
        };

        setFormData(updatedData);
        
        // Auto-save social account connection without redirecting
        onSave(updatedData, true);
        
        // Fetch pages immediately after connection
        const p = platform.toLowerCase();
        if ((p === 'facebook' || p === 'instagram' || p === 'linkedin') && account.accessToken && !account.isDirectLogin) {
          fetchAvailablePages(platform, account.accessToken);
        }

        setToast({ message: `Connected to ${platform}!`, type: 'success' });
        setIsConnecting(null);
        onSocialConnected?.();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [formData, onSave]); // Add dependencies to ensure handleMessage has access to current state

  const handleConnectSocial = async (platform: string, rerequest: boolean = false, direct: boolean = false, isPages: boolean = false) => {
    setIsConnecting(platform);
    try {
      const p = platform.toLowerCase();
      const shouldRerequest = rerequest || p === 'facebook' || p === 'instagram';
      const type = isPages && p === 'linkedin' ? 'linkedin/pages' : direct ? `${p}_direct` : p;
      const response = await fetch(`/api/auth/${type}/url?rerequest=${shouldRerequest}&brandId=${formData.id}`);
      if (!response.ok) throw new Error('Failed to get auth URL');
      const { url } = await response.json();

      const authWindow = window.open(url, 'oauth_popup', 'width=600,height=700');
      if (!authWindow) {
        setToast({ message: "Please allow popups to connect your account.", type: 'error' });
        setIsConnecting(null);
      } else {
        const checkClosed = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(checkClosed);
            setIsConnecting(prev => prev === platform ? null : prev);
          }
        }, 1000);
      }
    } catch (err) {
      console.error("OAuth error:", err);
      setToast({ message: "Failed to initiate connection.", type: 'error' });
      setIsConnecting(null);
    }
  };

  const [availablePages, setAvailablePages] = useState<{id: string, name: string}[]>([]);
  const [availableIgAccounts, setAvailableIgAccounts] = useState<{pageId: string, pageName: string, igId: string, igUsername: string, igName: string}[]>([]);
  const [availableLinkedinOrgs, setAvailableLinkedinOrgs] = useState<{id: string, name: string}[]>([]);
  const [isLoadingPages, setIsLoadingPages] = useState(false);

  const fetchAvailablePages = async (platform: string, accessToken: string, manualIds?: { igId: string, pageId: string }) => {
    if (isLoadingPages) return;
    setIsLoadingPages(true);
    try {
      let endpoint = '';
      const p = platform.toLowerCase();
      if (p === 'facebook') endpoint = '/api/facebook/pages';
      else if (p === 'instagram') endpoint = '/api/instagram/accounts';
      else if (p === 'linkedin') endpoint = '/api/linkedin/organizations';

      if (!endpoint) {
        setIsLoadingPages(false);
        return;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          accessToken,
          manualIgId: manualIds?.igId,
          manualPageId: manualIds?.pageId
        })
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        console.error(`[App] Failed to fetch pages response data for ${platform}:`, errData);
        throw new Error(errData.details ? JSON.stringify(errData.details) : (errData.error || `Failed to fetch ${platform} accounts`));
      }
      const data = await response.json();
      if (p === 'facebook') {
        setAvailablePages(data);
      } else if (p === 'linkedin') {
        setAvailableLinkedinOrgs(data);
      } else {
        setAvailableIgAccounts(data);
        if (data.length === 0) {
          setToast({ 
            message: "No Instagram Business accounts found. Ensure your Instagram is a Professional account and linked to a Facebook Page.", 
            type: 'error' 
          });
        } else if (manualIds) {
          setToast({ message: "Successfully connected via manual IDs!", type: 'success' });
        }
      }
    } catch (err: any) {
      console.error("Failed to fetch pages:", err);
      setToast({ message: err.message || "Failed to fetch accounts.", type: 'error' });
    } finally {
      setIsLoadingPages(false);
    }
  };

  const updateSelectedPage = (platform: string, pageId: string, pageName: string, accountId?: string) => {
    const updatedAccounts = formData.socialAccounts?.map(a => 
      (a.platform.toLowerCase() === platform.toLowerCase() && (!accountId || a.accountId === accountId)) 
        ? { ...a, selectedPageId: pageId, selectedPageName: pageName } 
        : a
    ) || [];

    const updatedData = {
      ...formData,
      socialAccounts: updatedAccounts
    };

    setFormData(updatedData);
    onSave(updatedData, true); // Auto-save on page selection without redirecting
  };

  const checkFacebookPermissions = (permissions?: string[]) => {
    if (!permissions) return false;
    const required = ['pages_show_list', 'pages_manage_posts', 'pages_read_engagement', 'email', 'business_management', 'read_insights'];
    return required.every(p => permissions.includes(p));
  };

  const checkInstagramPermissions = (permissions?: string[]) => {
    if (!permissions) return false;
    const required = [
      'instagram_basic', 
      'instagram_content_publish', 
      'pages_show_list', 
      'instagram_manage_insights', 
      'pages_read_engagement', 
      'email', 
      'instagram_manage_comments'
    ];
    return required.every(p => permissions.includes(p));
  };

  const checkLinkedInPermissions = (permissions?: string[]) => {
    if (!permissions) return true; // LinkedIn doesn't always return permissions in the same way
    const required = ['r_basicprofile', 'w_member_social', 'w_organization_social'];
    return required.every(p => permissions.includes(p));
  };

  const disconnectSocial = (platform: string, accountId?: string) => {
    const updatedAccounts = formData.socialAccounts?.filter(a => 
      !(a.platform.toLowerCase() === platform.toLowerCase() && (!accountId || a.accountId === accountId))
    ) || [];
    
    const updatedData = {
      ...formData,
      socialAccounts: updatedAccounts
    };
    
    setFormData(updatedData);
    onSave(updatedData, true); // Auto-save on disconnect without redirecting
  };

  const handleLightLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !auth.currentUser) return;

    const maxFileSize = (isTrial ? 1 : 5) * 1024 * 1024;
    if (file.size > maxFileSize) {
      setToast({ message: `Light logo file is too large. Please use a file smaller than ${isTrial ? 1 : 5}MB.`, type: 'error' });
      return;
    }

    setIsUploadingLight(true);
    try {
      const hash = await getFileHash(file);
      const sanitizedName = file.name.replace(/[^a-z0-9.]/gi, '_').toLowerCase();
      const storageRef = ref(storage, `logos/${auth.currentUser.uid}/logo_light_${hash}_${sanitizedName}`);
      
      try {
        const existingUrl = await getDownloadURL(storageRef);
        const oldLogoUrl = formData.logoLightUrl;
        
        const updatedData = { ...formData, logoLightUrl: existingUrl };
        setFormData(updatedData);
        await onSave(updatedData, true);
        
        if (oldLogoUrl && oldLogoUrl !== existingUrl && oldLogoUrl.includes('firebasestorage.googleapis.com')) {
          const oldRef = ref(storage, oldLogoUrl);
          deleteObject(oldRef).catch(() => {});
        }
        
        setToast({ message: "Light logo updated (reused existing)!", type: 'success' });
        setIsUploadingLight(false);
        return;
      } catch (e) {
        // Not found, continue with upload
      }

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      const oldLogoUrl = formData.logoLightUrl;
      const updatedData = { ...formData, logoLightUrl: url };
      setFormData(updatedData);
      await onSave(updatedData, true);
      
      if (oldLogoUrl && oldLogoUrl !== url && oldLogoUrl.includes('firebasestorage.googleapis.com')) {
        const oldRef = ref(storage, oldLogoUrl);
        deleteObject(oldRef).catch(() => {});
      }
      
      setToast({ message: "Light logo uploaded successfully!", type: 'success' });
    } catch (err: any) {
      console.error("Light logo upload error:", err);
      setToast({ message: "Failed to upload light logo. Please try again.", type: 'error' });
    } finally {
      setIsUploadingLight(false);
    }
  };

  const handleDarkLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !auth.currentUser) return;

    const maxFileSize = (isTrial ? 1 : 5) * 1024 * 1024;
    if (file.size > maxFileSize) {
      setToast({ message: `Dark logo file is too large. Please use a file smaller than ${isTrial ? 1 : 5}MB.`, type: 'error' });
      return;
    }

    setIsUploadingDark(true);
    try {
      const hash = await getFileHash(file);
      const sanitizedName = file.name.replace(/[^a-z0-9.]/gi, '_').toLowerCase();
      const storageRef = ref(storage, `logos/${auth.currentUser.uid}/logo_dark_${hash}_${sanitizedName}`);
      
      try {
        const existingUrl = await getDownloadURL(storageRef);
        const oldLogoUrl = formData.logoDarkUrl;
        
        const updatedData = { ...formData, logoDarkUrl: existingUrl };
        setFormData(updatedData);
        await onSave(updatedData, true);
        
        if (oldLogoUrl && oldLogoUrl !== existingUrl && oldLogoUrl.includes('firebasestorage.googleapis.com')) {
          const oldRef = ref(storage, oldLogoUrl);
          deleteObject(oldRef).catch(() => {});
        }
        
        setToast({ message: "Dark logo updated (reused existing)!", type: 'success' });
        setIsUploadingDark(false);
        return;
      } catch (e) {
        // Not found, continue with upload
      }

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      const oldLogoUrl = formData.logoDarkUrl;
      const updatedData = { ...formData, logoDarkUrl: url };
      setFormData(updatedData);
      await onSave(updatedData, true);
      
      if (oldLogoUrl && oldLogoUrl !== url && oldLogoUrl.includes('firebasestorage.googleapis.com')) {
        const oldRef = ref(storage, oldLogoUrl);
        deleteObject(oldRef).catch(() => {});
      }
      
      setToast({ message: "Dark logo uploaded successfully!", type: 'success' });
    } catch (err: any) {
      console.error("Dark logo upload error:", err);
      setToast({ message: "Failed to upload dark logo. Please try again.", type: 'error' });
    } finally {
      setIsUploadingDark(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !auth.currentUser) return;

    // Limit file size
    const maxFileSize = (isTrial ? 1 : 5) * 1024 * 1024;
    if (file.size > maxFileSize) {
      setToast({ message: `Logo file is too large. Please use a file smaller than ${isTrial ? 1 : 5}MB.`, type: 'error' });
      return;
    }

    setIsUploading(true);
    try {
      const hash = await getFileHash(file);
      const sanitizedName = file.name.replace(/[^a-z0-9.]/gi, '_').toLowerCase();
      const storageRef = ref(storage, `logos/${auth.currentUser.uid}/logo_${hash}_${sanitizedName}`);
      
      // Check if already exists
      try {
        const existingUrl = await getDownloadURL(storageRef);
        const oldLogoUrl = formData.logoUrl;
        
        const updatedData = { ...formData, logoUrl: existingUrl };
        setFormData(updatedData);
        await onSave(updatedData, true);
        
        // Cleanup old logo if it's different
        if (oldLogoUrl && oldLogoUrl !== existingUrl && oldLogoUrl.includes('firebasestorage.googleapis.com')) {
          const oldRef = ref(storage, oldLogoUrl);
          deleteObject(oldRef).catch(() => {});
        }
        
        setToast({ message: "Logo updated (reused existing)!", type: 'success' });
        setIsUploading(false);
        return;
      } catch (e) {
        // Not found, continue with upload
      }

      const uploadWithTimeout = (ref: any, data: File) => 
        Promise.race([
          uploadBytes(ref, data),
          new Promise((_, reject) => setTimeout(() => reject(new Error("Storage upload timeout")), 120000))
        ]);

      await uploadWithTimeout(storageRef, file);
      const url = await getDownloadURL(storageRef);
      console.log("[LogoUpload] Success. URL:", url);
      
      const oldLogoUrl = formData.logoUrl;
      const updatedData = { ...formData, logoUrl: url };
      setFormData(updatedData);
      
      // Auto-save to Firestore immediately
      try {
        await onSave(updatedData, true);
        
        // Cleanup old logo
        if (oldLogoUrl && oldLogoUrl !== url && oldLogoUrl.includes('firebasestorage.googleapis.com')) {
          const oldRef = ref(storage, oldLogoUrl);
          deleteObject(oldRef).catch(() => {});
        }
      } catch (saveErr) {
        console.error("[LogoUpload] Auto-save to Firestore failed:", saveErr);
      }
      
      setToast({ message: "Logo uploaded successfully!", type: 'success' });
    } catch (err: any) {
      console.error("Logo upload error:", err);
      const message = err.message === "Storage upload timeout" 
        ? "Upload timed out. Please check your connection or try a smaller file." 
        : "Failed to upload logo. Please try again.";
      setToast({ message, type: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleProductUpload = async (e: React.ChangeEvent<HTMLInputElement>, existingId?: string) => {
    const file = e.target.files?.[0];
    if (!file || !auth.currentUser) return;

    // Limit file size
    const maxFileSize = (isTrial ? 1 : 5) * 1024 * 1024;
    if (file.size > maxFileSize) {
      setToast({ message: `Product image is too large. Please use a file smaller than ${isTrial ? 1 : 5}MB.`, type: 'error' });
      return;
    }

    setIsUploadingProduct(true);
    try {
      const hash = await getFileHash(file);
      const sanitizedName = file.name.replace(/[^a-z0-9.]/gi, '_').toLowerCase();
      const storageRef = ref(storage, `logos/${auth.currentUser.uid}/products/prod_${hash}_${sanitizedName}`);
      
      let url = "";
      try {
        url = await getDownloadURL(storageRef);
      } catch (e) {
        const uploadWithTimeout = (ref: any, data: File) => 
          Promise.race([
            uploadBytes(ref, data),
            new Promise((_, reject) => setTimeout(() => reject(new Error("Storage upload timeout")), 120000))
          ]);

        await uploadWithTimeout(storageRef, file);
        url = await getDownloadURL(storageRef);
      }

      console.log("[ProductUpload] Success. URL:", url);
      
      let updatedData = { ...formData };
      
      if (existingId) {
        updatedData.productImages = (updatedData.productImages || []).map(img => 
           img.id === existingId ? { ...img, url } : img
        );
      } else {
        const newProduct = { 
          id: Date.now().toString(), 
          url, 
          name: file.name, 
          description: '', 
          price: '' 
        };
        updatedData.productImages = [...(formData.productImages || []), newProduct];
      }
      
      setFormData(updatedData);
      
      // Auto-save to Firestore immediately
      try {
        await onSave(updatedData, true);
        console.log("[ProductUpload] Auto-save to Firestore successful");
      } catch (saveErr) {
        console.error("[ProductUpload] Auto-save to Firestore failed:", saveErr);
      }
      
      setToast({ message: "Product image uploaded!", type: 'success' });
    } catch (err: any) {
      console.error("Product upload error:", err);
      const message = err.message === "Storage upload timeout" 
        ? "Upload timed out. Please check your connection or try a smaller file." 
        : "Failed to upload product image.";
      setToast({ message, type: 'error' });
    } finally {
      setIsUploadingProduct(false);
      // Reset input value to allow uploading the same file again if needed
      e.target.value = '';
    }
  };

  const handleTeamImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, existingId: string) => {
    const file = e.target.files?.[0];
    if (!file || !auth.currentUser) return;

    // Limit file size
    const maxFileSize = (isTrial ? 1 : 5) * 1024 * 1024;
    if (file.size > maxFileSize) {
      setToast({ message: `Image is too large. Please use a file smaller than ${isTrial ? 1 : 5}MB.`, type: 'error' });
      return;
    }

    try {
      const hash = await getFileHash(file);
      const sanitizedName = file.name.replace(/[^a-z0-9.]/gi, '_').toLowerCase();
      const storageRef = ref(storage, `logos/${auth.currentUser.uid}/team/team_${hash}_${sanitizedName}`);
      
      let url = "";
      try {
        url = await getDownloadURL(storageRef);
      } catch (err) {
        const uploadWithTimeout = (ref: any, data: File) => 
          Promise.race([
            uploadBytes(ref, data),
            new Promise((_, reject) => setTimeout(() => reject(new Error("Storage upload timeout")), 120000))
          ]);

        await uploadWithTimeout(storageRef, file);
        url = await getDownloadURL(storageRef);
      }

      console.log("[TeamUpload] Success. URL:", url);
      
      let updatedData = { ...formData };
      updatedData.teamDirectory = (updatedData.teamDirectory || []).map(member => 
         member.id === existingId ? { ...member, imageUrl: url } : member
      );
      
      setFormData(updatedData);
      try {
        await onSave(updatedData, true);
      } catch (saveErr) {
        console.error("[TeamUpload] Auto-save to Firestore failed:", saveErr);
      }
      
      setToast({ message: "Team member image uploaded!", type: 'success' });
    } catch (err: any) {
      console.error("Team upload error:", err);
      const message = err.message === "Storage upload timeout" 
        ? "Upload timed out. Please check your connection or try a smaller file." 
        : "Failed to upload image.";
      setToast({ message, type: 'error' });
    } finally {
      e.target.value = '';
    }
  };

  const uploadAndProcessTemplates = async (files: File[]) => {
    if (!files || files.length === 0 || !auth.currentUser) return;

    setIsUploadingTemplate(true);
    let successfullyUploaded = 0;
    let failedUploaded = 0;
    
    let currentBrandTemplates = [...(formData.brandTemplates || [])];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const maxFileSize = (isTrial ? 1 : 5) * 1024 * 1024;
      if (file.size > maxFileSize) {
        setToast({ 
          message: `File "${file.name}" is too large. Please use a file smaller than ${isTrial ? 1 : 5}MB.`, 
          type: 'error' 
        });
        failedUploaded++;
        continue;
      }

      setTemplateUploadStatus(`Optimizing and calibrating template ${i + 1} of ${files.length}: "${file.name}"...`);
      try {
        const hash = await getFileHash(file);
        const sanitizedName = file.name.replace(/[^a-z0-9.]/gi, '_').toLowerCase();
        const storageRef = ref(storage, `logos/${auth.currentUser.uid}/templates/tpl_${hash}_${sanitizedName}`);
        
        let url = "";
        try {
          url = await getDownloadURL(storageRef);
        } catch (e) {
          const uploadWithTimeout = (ref: any, data: File) => 
            Promise.race([
              uploadBytes(ref, data),
              new Promise((_, reject) => setTimeout(() => reject(new Error("Storage upload timeout")), 120000))
            ]);

          await uploadWithTimeout(storageRef, file);
          url = await getDownloadURL(storageRef);
        }

        console.log(`[TemplateUpload] File ${i + 1}/${files.length} uploaded. URL:`, url);

        // Convert to Base64 for Gemini vision analysis
        const fileToBase64 = (f: File): Promise<string> => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(f);
            reader.onload = () => {
              const res = reader.result as string;
              resolve(res.split(',')[1]);
            };
            reader.onerror = err => reject(err);
          });
        };
        
        const base64Data = await fileToBase64(file);

        const aiResponse = await analyzeAndProcessBrandTemplate(
          base64Data,
          file.type,
          file.name,
          formData.companyName || ""
        );

        const newTemplate = { 
          id: (Date.now() + i).toString(), 
          url, 
          shellUrl: aiResponse.shellUrl || "",
          name: aiResponse.name || file.name, 
          description: aiResponse.description || '',
          role: aiResponse.role || 'general_layout'
        };
        
        currentBrandTemplates.push(newTemplate);
        successfullyUploaded++;
      } catch (err: any) {
        console.error(`Template "${file.name}" upload/processing error:`, err);
        failedUploaded++;
        const message = err.message === "Storage upload timeout" 
          ? `Upload timed out for "${file.name}".` 
          : `Failed to process template image "${file.name}".`;
        setToast({ message, type: 'error' });
      }
    }

    if (successfullyUploaded > 0) {
      const updatedData = { 
        ...formData, 
        brandTemplates: currentBrandTemplates 
      };
      
      setFormData(updatedData);
      
      // Auto-save to Firestore immediately
      try {
        await onSave(updatedData, true);
        console.log("[TemplateUpload] Auto-save to Firestore successful");
      } catch (saveErr) {
        console.error("[TemplateUpload] Auto-save to Firestore failed:", saveErr);
      }
      
      setToast({ 
        message: `Successfully processed ${successfullyUploaded} template(s)${failedUploaded > 0 ? ` (${failedUploaded} failed)` : ''}! Clean shell(s) generated.`, 
        type: 'success' 
      });
    }

    setIsUploadingTemplate(false);
    setTemplateUploadStatus(null);
  };

  const handleTemplateUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    await uploadAndProcessTemplates(files);
    e.target.value = ''; // Reset input element
  };

  const handleCustomShellUpload = async (e: React.ChangeEvent<HTMLInputElement>, templateId: string) => {
    const file = e.target.files?.[0];
    if (!file || !auth.currentUser) return;
    
    setToast({ message: "Uploading custom shell...", type: 'success' });
    
    try {
      const hash = await getFileHash(file);
      const storageRef = ref(storage, `logos/${auth.currentUser.uid}/shells/custom_${hash}`);
      
      let shellUrl = "";
      try {
        shellUrl = await getDownloadURL(storageRef);
      } catch (err) {
        await uploadBytes(storageRef, file);
        shellUrl = await getDownloadURL(storageRef);
      }
      
      const updatedTemplates = (formData.brandTemplates || []).map(t => {
        if (t.id === templateId) {
          return { ...t, shellUrl };
        }
        return t;
      });
      
      const updatedData = { ...formData, brandTemplates: updatedTemplates };
      setFormData(updatedData);
      
      try {
        await onSave(updatedData, true);
      } catch (saveErr) {}
      
      setToast({ message: "Custom shell uploaded successfully", type: 'success' });
    } catch (err) {
      console.error("Shell upload error:", err);
      setToast({ message: "Failed to upload custom shell", type: 'error' });
    }
  };

  const handleTemplateDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setTemplateDragActive(true);
    } else if (e.type === "dragleave") {
      setTemplateDragActive(false);
    }
  };

  const handleTemplateDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setTemplateDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
      if (files.length === 0) {
        setToast({ message: "Please drop image files only.", type: 'error' });
        return;
      }
      await uploadAndProcessTemplates(files);
    }
  };

  const handleContextDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !auth.currentUser) return;

    // Limit file size for documents
    const maxFileSize = (isTrial ? 2 : 10) * 1024 * 1024;
    if (file.size > maxFileSize) {
      setToast({ message: `Document is too large. Please use a file smaller than ${isTrial ? 2 : 10}MB.`, type: 'error' });
      return;
    }

    setIsUploadingDocument(true);
    try {
      const hash = await getFileHash(file);
      const sanitizedName = file.name.replace(/[^a-z0-9.]/gi, '_').toLowerCase();
      const storageRef = ref(storage, `logos/${auth.currentUser.uid}/documents/doc_${hash}_${sanitizedName}`);
      
      let url = "";
      try {
        url = await getDownloadURL(storageRef);
      } catch (e) {
        const uploadWithTimeout = (ref: any, data: File) => 
          Promise.race([
            uploadBytes(ref, data),
            new Promise((_, reject) => setTimeout(() => reject(new Error("Storage upload timeout")), 120000))
          ]);

        await uploadWithTimeout(storageRef, file);
        url = await getDownloadURL(storageRef);
      }

      console.log("[DocumentUpload] Success. URL:", url);
      
      const newDoc = { 
        id: Date.now().toString(), 
        url, 
        name: file.name, 
        mimeType: file.type,
        description: '' 
      };
      
      const updatedData = { 
        ...formData, 
        contextDocuments: [...(formData.contextDocuments || []), newDoc] 
      };
      
      setFormData(updatedData);
      
      // Auto-save to Firestore immediately
      try {
        await onSave(updatedData, true);
        console.log("[DocumentUpload] Auto-save to Firestore successful");
      } catch (saveErr) {
        console.error("[DocumentUpload] Auto-save to Firestore failed:", saveErr);
      }
      
      setToast({ message: "Context document uploaded!", type: 'success' });
    } catch (err: any) {
      console.error("Document upload error:", err);
      const message = err.message === "Storage upload timeout" 
        ? "Upload timed out. Please check your connection or try a smaller file." 
        : "Failed to upload document.";
      setToast({ message, type: 'error' });
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const removeProductImage = (id: string) => {
    setFormData(prev => ({
      ...prev,
      productImages: prev.productImages?.filter(img => img.id !== id) || []
    }));
  };

  const removeTemplate = (id: string) => {
    setFormData(prev => ({
      ...prev,
      brandTemplates: prev.brandTemplates?.filter(t => t.id !== id) || []
    }));
  };

  const removeContextDocument = (id: string) => {
    setFormData(prev => ({
      ...prev,
      contextDocuments: prev.contextDocuments?.filter(d => d.id !== id) || []
    }));
  };

  const updateProductMetadata = (id: string, field: 'description' | 'price' | 'name' | 'isService' | 'url', value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      productImages: prev.productImages?.map(img => img.id === id ? { ...img, [field]: value } : img) || []
    }));
  };

  const updateTeamMetadata = (id: string, field: 'name' | 'role' | 'bio' | 'imageUrl', value: string) => {
    setFormData(prev => ({
      ...prev,
      teamDirectory: prev.teamDirectory?.map(member => member.id === id ? { ...member, [field]: value } : member) || []
    }));
  };

  const removeTeamMember = (id: string) => {
    setFormData(prev => ({
      ...prev,
      teamDirectory: prev.teamDirectory?.filter(member => member.id !== id) || []
    }));
  };

  const updateTemplateMetadata = (id: string, field: 'description' | 'name', value: string) => {
    setFormData(prev => ({
      ...prev,
      brandTemplates: prev.brandTemplates?.map(t => t.id === id ? { ...t, [field]: value } : t) || []
    }));
  };

  const updateBlueprintMetadata = async (id: string, field: 'description' | 'name', value: string) => {
    setBrandBlueprints(prev => prev.map(b => b.id === id ? { ...b, [field]: value } : b));
    try {
      await updateDoc(doc(db, 'carousel_blueprints', id), { [field]: value });
    } catch (e) {
      console.error("Failed to update blueprint metadata:", e);
    }
  };

  const updateContextDocumentMetadata = (id: string, field: 'description' | 'name', value: string) => {
    setFormData(prev => ({
      ...prev,
      contextDocuments: prev.contextDocuments?.map(d => d.id === id ? { ...d, [field]: value } : d) || []
    }));
  };

  const [isSyncingWebsite, setIsSyncingWebsite] = useState(false);
  const [isSyncingScreenshot, setIsSyncingScreenshot] = useState(false);
  const [aiWebsiteWarning, setAiWebsiteWarning] = useState<string | null>(null);

  const handleSyncWebsite = async () => {
    if (!formData.website) return;
    setIsSyncingWebsite(true);
    setAiWebsiteWarning(null);
    let websiteUrl = formData.website.trim();
    if (!/^https?:\/\//i.test(websiteUrl)) {
      websiteUrl = `https://${websiteUrl}`;
    }

    try {
      const context = await generateWebsiteContext(websiteUrl);
      
      let hostname = 'Website';
      try {
        hostname = new URL(websiteUrl).hostname;
      } catch (e) {
        // Fallback if URL is still invalid
      }

      const newDoc = {
        id: `website-sync-${Date.now()}`,
        name: `Website Context (${hostname})`,
        description: `Automatically generated context from ${websiteUrl}`,
        url: `data:text/plain;base64,${btoa(encodeURIComponent(context).replace(/%([0-9A-F]{2})/g, (match, p1) => String.fromCharCode(parseInt(p1, 16))))}`,
        mimeType: 'text/plain',
        createdAt: new Date().toISOString()
      };

      // Auto-extract and enrich brand profile fields using Gemini
      let extractedData: any = {};
      try {
        extractedData = await extractBrandProfileFromContext(context);
        if (extractedData.isAiOrLowInfoWebsite) {
          setAiWebsiteWarning(extractedData.aiWarningReason || "Generic or AI-generated template detected.");
        }
      } catch (extractErr) {
        console.error("Failed to extract brand context:", extractErr);
      }

      // Extract products and services
      const extractedProducts = (extractedData.productsAndServices || []).filter((p: any) => p.name).map((p: any) => ({
        id: `prod-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        name: p.name,
        description: p.description,
        price: p.price,
        url: p.url || '',
        isService: p.isService
      }));

      // Extract team members
      const extractedTeam = (extractedData.teamDirectory || []).filter((t: any) => t.name).map((t: any) => ({
        id: `team-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        name: t.name,
        role: t.role || '',
        bio: t.bio,
        imageUrl: t.imageUrl || ''
      }));

      const updatedData = {
        ...formData,
        companyName: extractedData.companyName || formData.companyName || '',
        brandingDetails: ((formData.brandingDetails ? formData.brandingDetails + "\n\n" : "") + (extractedData.brandingDetails || '')).substring(0, 4950),
        fontHeading: extractedData.fontHeading || formData.fontHeading || 'Space Grotesk',
        fontBody: extractedData.fontBody || formData.fontBody || 'Inter',
        colorPalette: (extractedData.colorPalette && extractedData.colorPalette.length > 0) ? extractedData.colorPalette : formData.colorPalette,
        colorPaletteLabels: (extractedData.colorPalette && extractedData.colorPalette.length > 0) ? [] : formData.colorPaletteLabels,
        questionnaire: {
          ...formData.questionnaire!,
          brandVoice: (extractedData.brandVoice || formData.questionnaire?.brandVoice || '').substring(0, 950),
          targetAudience: (extractedData.targetAudience || formData.questionnaire?.targetAudience || '').substring(0, 4950),
          visualAesthetic: (extractedData.visualAesthetic || formData.questionnaire?.visualAesthetic || '').substring(0, 4950),
          primaryGoals: (extractedData.primaryGoals || formData.questionnaire?.primaryGoals || '').substring(0, 4950),
        },
        productImages: [...(formData.productImages || []), ...extractedProducts],
        teamDirectory: [...(formData.teamDirectory || []), ...extractedTeam],
        contextDocuments: [...(formData.contextDocuments || []), newDoc]
      };
      
      setFormData(updatedData);
      onSave(updatedData, true, true);

      // Play chime notification sound on successful finish using standalone Audio
      try {
        const audio = new Audio('/chime.mp3');
        audio.volume = 0.5;
        audio.play().catch(e => console.log('Audio play blocked', e));
      } catch (soundErr) {
        console.warn("Chime play failed:", soundErr);
      }

      if (extractedData.isAiOrLowInfoWebsite) {
        setToast({ 
          message: isBrazil 
            ? "⚠️ Site importado, mas detectamos que é um template genérico/AI com baixo contexto. Revise os campos!" 
            : "⚠️ Context synced, but we detected a generic or AI-generated site layout with low context. Please review carefully!", 
          type: 'warning',
          duration: null
        });
      } else {
        setToast({ message: "Website context synced & brand profile fields pre-populated!", type: 'success' });
      }
    } catch (err: any) {
      console.error("Failed to sync website:", err);
      logError("Failed to sync website context", { url: websiteUrl, error: err.message }, err);
      setToast({ message: err.message || "Failed to sync website context.", type: 'error' });
    } finally {
      setIsSyncingWebsite(false);
    }
  };

  const handleWebsiteScreenshotUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsSyncingScreenshot(true);
    try {
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read screenshot file.'));
        reader.readAsDataURL(file);
      });
      const base64 = await base64Promise;
      
      const result = await analyzeWebsiteScreenshot(base64, file.type);
      
      const updatedData = {
        ...formData,
        companyName: result.companyName || formData.companyName || '',
        brandingDetails: ((formData.brandingDetails ? formData.brandingDetails + "\n\n" : "") + 
                         (result.brandingDetails ? `Screenshot Details:\n${result.brandingDetails}` : "") +
                         (result.primaryColors ? `\nColors Seen: ${result.primaryColors}` : "")).substring(0, 4950),
        fontHeading: result.fontHeading || formData.fontHeading || 'Space Grotesk',
        fontBody: result.fontBody || formData.fontBody || 'Inter',
        colorPalette: (result.colorPalette && result.colorPalette.length > 0) ? result.colorPalette : formData.colorPalette,
        colorPaletteLabels: (result.colorPalette && result.colorPalette.length > 0) ? [] : formData.colorPaletteLabels,
        questionnaire: {
          ...formData.questionnaire!,
          brandVoice: (result.brandVoice || formData.questionnaire?.brandVoice || '').substring(0, 950),
          targetAudience: (result.targetAudience || formData.questionnaire?.targetAudience || '').substring(0, 4950),
          visualAesthetic: (result.visualAesthetic || formData.questionnaire?.visualAesthetic || '').substring(0, 4950),
        }
      };

      setFormData(updatedData);
      onSave(updatedData, true, true);
      setToast({ message: "Website screenshot analyzed and styles applied!", type: 'success' });
    } catch (err: any) {
      console.error("Failed to analyze website screenshot:", err);
      const errorMsg = err.message || (typeof err === 'string' ? err : JSON.stringify(err));
      if (errorMsg.includes('403') || errorMsg.includes('API_KEY_HTTP_REFERRER_BLOCKED') || errorMsg.includes('Forbidden')) {
        setToast({ message: `Access forbidden (403/Forbidden). Please verify your Google API key settings.`, type: 'error' });
      } else {
        setToast({ message: "Failed to analyze website screenshot.", type: 'error' });
      }
    } finally {
      setIsSyncingScreenshot(false);
      // Reset input val
      e.target.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave(formData);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 space-y-8 max-w-3xl text-gray-900">
      <header>
        <h2 className="text-2xl font-bold text-gray-900">{isBrazil ? "Perfil da Marca" : "Brand Profile"}</h2>
        <p className="text-gray-500">{isBrazil ? "Defina sua identidade de marca para alimentar o motor de conteúdo de IA." : "Define your brand identity to power the AI content engine."}</p>
      </header>

      <div className="space-y-6">
        <AccordionSection title={isBrazil ? "Informações Básicas" : "Basic Information"} defaultOpen={true} icon={Building2}>
          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-2">
            <label className="block text-sm font-bold text-gray-700">{isBrazil ? "Tipo de Perfil" : "Profile Type"}</label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setFormData({...formData, accountType: 'company'})}
                className={`flex-1 sm:flex-none px-4 py-2 border rounded-xl text-sm font-bold transition-all ${
                  (!formData.accountType || formData.accountType === 'company') 
                    ? 'border-indigo-600 bg-indigo-50 text-indigo-700' 
                    : 'border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
              >
                {isBrazil ? "Empresa / Negócio" : "Company / Business"}
              </button>
              <button
                type="button"
                onClick={() => setFormData({...formData, accountType: 'personal'})}
                className={`flex-1 sm:flex-none px-4 py-2 border rounded-xl text-sm font-bold transition-all ${
                  formData.accountType === 'personal' 
                    ? 'border-indigo-600 bg-indigo-50 text-indigo-700' 
                    : 'border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
              >
                {isBrazil ? "Marca Pessoal / Criador" : "Personal Brand / Creator"}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700">{isBrazil ? "Logo da Marca" : (formData.accountType === 'personal' ? "Profile Picture" : "Brand Logo")}</label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-2xl bg-gray-50 border border-dashed border-gray-300 flex items-center justify-center overflow-hidden relative text-gray-400">
                {formData.logoUrl ? (
                  <img 
                    key={formData.logoUrl}
                    src={formData.logoUrl} 
                    alt="Logo" 
                    className="w-full h-full object-contain" 
                    referrerPolicy="no-referrer" 
                    crossOrigin="anonymous"
                    onError={(e) => {
                      console.error("Logo image failed to load:", formData.logoUrl);
                    }}
                  />
                ) : (
                  <ImageIcon className="w-8 h-8 text-gray-300" />
                )}
                {isUploading && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleLogoUpload}
                  className="hidden" 
                  id="logo-upload"
                />
                <div className="flex items-center gap-2">
                  <label 
                    htmlFor="logo-upload"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-700 hover:bg-gray-50 cursor-pointer transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    <span>{formData.logoUrl ? (isBrazil ? 'Mudar Imagem' : 'Change Image') : (isBrazil ? 'Upload' : 'Upload')}</span>
                  </label>
                  {formData.logoUrl && (
                    <button 
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, logoUrl: '' }))}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-100 rounded-xl text-sm font-bold text-red-600 hover:bg-red-100 transition-all cursor-pointer"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>{isBrazil ? 'Remover' : 'Remove'}</span>
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-2">{isBrazil ? 'Recomendado: PNG ou SVG com fundo transparente.' : 'Recommended: PNG or SVG with transparent background.'}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input 
              label={formData.accountType === 'personal' ? (isBrazil ? "Seu Nome / Marca Pessoal" : "Your Name / Personal Brand") : (isBrazil ? "Nome da Empresa" : "Company Name")} 
              value={formData.companyName} 
              onChange={v => setFormData({...formData, companyName: v})} 
              placeholder={formData.accountType === 'personal' ? (isBrazil ? "ex: João da Silva" : "e.g. John Doe") : (isBrazil ? "ex: Acme Corp" : "e.g. Acme Corp")}
              required
              maxLength={200}
            />
            <div className="relative">
              <Input 
                label={isBrazil ? "Website" : "Website"} 
                value={formData.website} 
                onChange={v => setFormData({...formData, website: v})} 
                placeholder="https://example.com"
                description={isBrazil ? "A IA usará seu site para informar cores, tom e estilo da marca." : "The AI will use your website to inform brand colors, tone, and style."}
                maxLength={500}
              />
              {formData.website && (
                <button
                  type="button"
                  onClick={handleSyncWebsite}
                  disabled={isSyncingWebsite}
                  className="absolute top-0 right-0 text-xs font-bold text-indigo-600 hover:text-indigo-500 flex items-center gap-1 disabled:opacity-50"
                >
                  {isSyncingWebsite ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3 h-3" />
                  )}
                  {isBrazil ? "Sincronizar" : "Sync to Context"}
                </button>
              )}
              
              <div className="mt-3 pt-3 border-t border-gray-100 flex flex-col gap-1">
                <input 
                  type="file" 
                  id="website-screenshot-upload" 
                  className="hidden" 
                  accept="image/*" 
                  onChange={handleWebsiteScreenshotUpload}
                  disabled={isSyncingScreenshot}
                />
                <label 
                  htmlFor="website-screenshot-upload"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-500 cursor-pointer disabled:opacity-50 select-none"
                >
                  {isSyncingScreenshot ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Camera className="w-3.5 h-3.5" />
                  )}
                  <span>{isBrazil ? "Upload de print do site (Estilo Visual)" : "Upload layout screenshot (Sync Visual Style)"}</span>
                </label>
                <p className="text-[10px] text-gray-400 leading-normal">
                  {isBrazil 
                    ? "Envie um print da página inicial do seu site para a IA ler visualmente sua paleta de cores, tipografia e identidade layout direta."
                    : "Upload a screenshot of your website's home page for the AI to visually extract brand colors, design typography, and layout aesthetics directly."}
                </p>
              </div>

              {aiWebsiteWarning && (
                <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 flex flex-col gap-1.5 animate-fadeIn">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                    <span className="text-[10px] font-black uppercase tracking-wider text-amber-800">
                      {isBrazil ? "Aviso de Site AI / Baixo Contexto" : "AI/Low-Context Site Detected"}
                    </span>
                  </div>
                  <p className="text-[11px] text-amber-850 leading-relaxed font-medium">
                    {isBrazil 
                      ? `Detectamos que este site possui informações genéricas ou estrutura simplificada gerada por IA (Motivo: "${aiWebsiteWarning}"). Recomendamos revisar os campos pré-preenchidos ou preenchê-los manualmente.`
                      : `We detected that this website has generic details, low context, or an AI-generated layout structure (Reason: "${aiWebsiteWarning}"). We recommend reviewing the pre-populated fields below carefully and completing them manually.`}
                  </p>
                </div>
              )}
            </div>
          </div>

          {!isTrial && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input 
                label={isBrazil ? "E-mail de Contato" : "Contact Email"} 
                value={formData.email} 
                onChange={v => setFormData({...formData, email: v})} 
                placeholder="hello@example.com"
                maxLength={200}
              />
              <Input 
                label={isBrazil ? "Telefone de Contato" : "Contact Phone"} 
                value={formData.phone} 
                onChange={v => setFormData({...formData, phone: v})} 
                placeholder="+55 (11) 99999-9999"
                maxLength={50}
              />
            </div>
          )}
          </div>
        </AccordionSection>

        <AccordionSection title={isBrazil ? "DNA e Voz da Marca" : "Brand Identity & Voice"} icon={MessageSquare}>
          <div className="space-y-6">
            <TextArea 
              label={isBrazil ? "Detalhes do Branding" : "Branding Details"} 
              value={formData.brandingDetails} 
              onChange={v => setFormData({...formData, brandingDetails: v})} 
              placeholder={isBrazil ? "Descreva os principais produtos, serviços, história e posicionamento da sua marca..." : "Describe key products, services, story, and unique positioning of your brand..."}
              required
              maxLength={5000}
            />

            {!isTrial && (
              <div className="border-t border-gray-100 pt-6 space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-indigo-600 animate-pulse" />
                      <span className="text-xs font-bold uppercase tracking-wider text-indigo-600">Style Miner (Pro Tool)</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mb-4">{isBrazil ? "Cole seu conteúdo existente abaixo. Nossa IA analisará sua estrutura de frases e vocabulário para construir seu DNA de Estilo." : "Paste your existing content below. Our AI will analyze your sentence structure and vocabulary to build your Style DNA."}</p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <select 
                        value={miningPlatform}
                        onChange={(e) => setMiningPlatform(e.target.value)}
                        className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 font-bold"
                      >
                        <option value="LinkedIn">LinkedIn</option>
                        <option value="Facebook">Facebook</option>
                        <option value="Instagram">Instagram</option>
                        <option value="X/Twitter">X/Twitter</option>
                        <option value="Personal Blog">Personal Blog</option>
                        <option value="Mixed">{isBrazil ? "Plataformas Mistas" : "Mixed Platforms"}</option>
                      </select>
                    </div>
                    <TextArea 
                      label={isBrazil ? "Colagem de Calibração" : "Calibration Paste"}
                      value={miningContent}
                      onChange={setMiningContent}
                      placeholder={isBrazil ? "Cole seus melhores posts, biografia do perfil ou uma combinação de conteúdo aqui..." : "Paste your best posts, profile bio, or a combination of content here..."}
                      description={isBrazil ? "Quanto mais conteúdo você fornecer, mais precisamente a IA poderá imitar sua voz natural." : "The more content you provide, the more accurately the AI can mimic your natural voice."}
                    />
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleMinePersonality}
                        disabled={isMining || !miningContent.trim()}
                        className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 transition-all shadow-md shadow-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isMining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                        <span>{isBrazil ? "Extrair DNA" : "Extract DNA"}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-6 grid grid-cols-1 gap-6 text-gray-900">
                  <Input 
                    label={isBrazil ? "Voz e Tom da Marca" : "Brand Voice & Tone"} 
                    value={formData.questionnaire?.brandVoice} 
                    onChange={v => setFormData({...formData, questionnaire: {...formData.questionnaire!, brandVoice: v}})} 
                    placeholder={isBrazil ? "ex: Profissional, Divertido, Inspirador" : "e.g. Professional, Witty, Inspirational"}
                    description={isBrazil ? "Descreva seu estilo de escrita (ex: 'Sério e autoritativo' ou 'Descontraído e amigável')." : "Describe your writing style (e.g., 'Formal and authoritative' or 'Casual and friendly')."}
                    maxLength={1000}
                  />
                  <TextArea 
                    label={isBrazil ? "Público-Alvo" : "Target Audience"} 
                    value={formData.questionnaire?.targetAudience} 
                    onChange={v => setFormData({...formData, questionnaire: {...formData.questionnaire!, targetAudience: v}})} 
                    placeholder={isBrazil ? "Quem você está tentando alcançar? Defina seu leitor/cliente ideal." : "Who are you trying to reach? Define your ideal reader/customer."}
                    maxLength={5000}
                  />
                </div>
              </div>
            )}
          </div>
        </AccordionSection>

        <AccordionSection title={isBrazil ? "Identidade Visual e Design" : "Visual Identity & Style"} icon={Palette}>
          <div className="space-y-6">
            {!isTrial && (
              <TextArea 
                label={isBrazil ? "Estilo Visual Desejado" : "Visual Aesthetic"} 
                value={formData.questionnaire?.visualAesthetic} 
                onChange={v => setFormData({...formData, questionnaire: {...formData.questionnaire!, visualAesthetic: v}})} 
                placeholder={isBrazil ? "Descreva seu estilo visual. ex: Minimalista com sombras suaves, ou Vibrante de Alto Contraste." : "Describe your visual style. e.g. Minimalist with soft shadows, or Vibrant and High-Contrast."}
                description={isBrazil ? "Orientação geral de estilo visual para criativos gerados." : "General style guidance for generated creatives."}
                maxLength={5000}
              />
            )}

            {/* Visual Brand Identity Styling (Typography & Colors) */}
            <div className="bg-gray-50 rounded-2xl p-5 border border-gray-150 space-y-6 text-gray-950">
              <div>
                <h4 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-indigo-600 animate-pulse" />
                  {isBrazil ? "Estilo Visual da Marca" : "Visual Brand Identity"}
                </h4>
                <p className="text-[11px] text-gray-500 mt-1">
                  {isBrazil 
                    ? "Personalize as fontes e paleta de cores utilizadas nas suas imagens e criativos gerados por IA." 
                    : "Configure the fonts and color palette used for AI image and slide styles."}
                </p>
              </div>

              {/* Fonts Config */}
              <datalist id="google-fonts-list">
                {POPULAR_GOOGLE_FONTS.map((font) => (
                  <option key={font} value={font} />
                ))}
              </datalist>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">{isBrazil ? "Fonte para Títulos" : "Heading Font"}</label>
                  <input 
                    type="text"
                    list="google-fonts-list"
                    value={formData.fontHeading || ''}
                    placeholder="e.g. Space Grotesk"
                    onChange={(e) => setFormData({ ...formData, fontHeading: e.target.value })}
                    className="w-full text-sm bg-white border border-gray-200 rounded-xl px-3 py-2.5 outline-none text-gray-950 focus:ring-2 focus:ring-indigo-500 transition-all font-bold"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    {isBrazil ? "Usada para títulos nos slides e templates (Fontes do Google)" : "Used for prominent headlines and key tags (Google Fonts)"}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">{isBrazil ? "Fonte para Corpo / Parágrafos" : "Body / Paragraph Font"}</label>
                  <input 
                    type="text"
                    list="google-fonts-list"
                    value={formData.fontBody || ''}
                    placeholder="e.g. Inter"
                    onChange={(e) => setFormData({ ...formData, fontBody: e.target.value })}
                    className="w-full text-sm bg-white border border-gray-200 rounded-xl px-3 py-2.5 outline-none text-gray-950 focus:ring-2 focus:ring-indigo-500 transition-all"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    {isBrazil ? "Usada para legendas e parágrafos nos criativos (Fontes do Google)" : "Used for descriptive and standard body copy (Google Fonts)"}
                  </p>
                </div>
              </div>

              {/* Typography Live Sandbox Preview */}
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm space-y-4">
                <span className="text-[9px] font-black uppercase tracking-widest text-indigo-600">
                  {isBrazil ? "Pré-visualização de Fontes" : "Live Typography Preview"}
                </span>
                <div className="space-y-2">
                  <h3 
                    style={{ fontFamily: formData.fontHeading || 'Space Grotesk' }}
                    className="text-2xl font-bold tracking-tight text-gray-900 leading-snug"
                  >
                    {isBrazil ? "Títulos Impactantes da Sua Marca" : "Stunning Designs Crafted in Seconds"}
                  </h3>
                  <p 
                    style={{ fontFamily: formData.fontBody || 'Inter' }}
                    className="text-xs text-gray-600 leading-relaxed max-w-md"
                  >
                    {isBrazil 
                      ? "Esta é uma demonstração em tempo real de como os textos serão diagramados em seus criativos com a fonte de corpo selecionada." 
                      : "This displays how your body copy and secondary captions will render across social posts, templates, and imagery styles."}
                  </p>
                </div>
              </div>

              {/* Sample Color Palette Blocks */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-xs font-bold text-gray-700">
                      {isBrazil ? "Paleta de Cores" : "Brand Color Palette"}
                    </label>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {isBrazil 
                        ? "Cores extraídas. Toque em uma caixa para mudar ou edite o código HEX direto." 
                        : "Colors extracted. Tap any block to pick colors, or edit HEX strings below."}
                    </p>
                  </div>
                  {(formData.colorPalette || ['#4F46E5', '#10B981', '#111827', '#F9FAFB', '#6366F1']).length < 6 && (
                    <button
                      type="button"
                      onClick={() => {
                        const palette = formData.colorPalette || ['#4F46E5', '#10B981', '#111827', '#F9FAFB'];
                        setFormData({ ...formData, colorPalette: [...palette, '#6366F1'] });
                      }}
                      className="flex items-center gap-1 text-[10px] font-black uppercase text-indigo-600 hover:text-indigo-700 tracking-wider transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                      <span>{isBrazil ? "Adicionar Cor" : "Add Color"}</span>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {(formData.colorPalette || ['#4F46E5', '#10B981', '#111827', '#F9FAFB', '#6366F1']).map((color, idx) => {
                    const cleanColor = (color || '').trim();
                    const isValidHTMLColor = /^#[0-9A-Fa-f]{6}$/i.test(cleanColor);
                    
                    return (
                    <div key={idx} className="bg-white border border-gray-100 p-2.5 rounded-xl flex flex-col items-center gap-2 relative shadow-sm group">
                      {/* Color block with nested raw color picker */}
                      <div 
                        style={{ backgroundColor: cleanColor.length >= 4 ? cleanColor : undefined }}
                        className="w-12 h-12 rounded-lg border border-gray-200 relative cursor-pointer overflow-hidden shadow-inner flex items-center justify-center transition-transform hover:scale-105"
                      >
                        <input 
                          type="color"
                          value={isValidHTMLColor ? cleanColor : '#000000'}
                          onChange={(e) => {
                            const newPalette = [...(formData.colorPalette || ['#4F46E5', '#10B981', '#111827', '#F9FAFB', '#6366F1'])];
                            newPalette[idx] = e.target.value.toUpperCase();
                            setFormData({ ...formData, colorPalette: newPalette });
                          }}
                          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full scale-150"
                        />
                        {/* Visual cue */}
                        <span className="text-[9px] bg-black/60 text-white rounded px-1.5 py-0.5 text-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity font-bold">
                          Pick
                        </span>
                      </div>

                      {/* Raw HEX text input */}
                      <div className="flex items-center gap-0.5 w-full bg-gray-50 border border-gray-100 px-1 rounded-md">
                        <span className="text-[9px] text-gray-400 font-mono font-bold">#</span>
                        <input 
                          type="text"
                          value={cleanColor.startsWith('#') ? cleanColor.slice(1) : cleanColor}
                          onChange={(e) => {
                            let val = e.target.value.replace(/[^0-9A-Fa-f]/ig, '').toUpperCase();
                            const newPalette = [...(formData.colorPalette || ['#4F46E5', '#10B981', '#111827', '#F9FAFB', '#6366F1'])];
                            newPalette[idx] = '#' + val;
                            setFormData({ ...formData, colorPalette: newPalette });
                          }}
                          className="w-full text-[9px] text-gray-900 text-center font-mono focus:outline-none py-0.5 font-bold uppercase"
                          maxLength={6}
                        />
                      </div>

                      {/* Color general use description */}
                      {(() => {
                        const detail = getDynamicColorDetails(cleanColor, isBrazil);
                        const labelValue = (formData.colorPaletteLabels && formData.colorPaletteLabels[idx]) || detail.role;
                        return (
                          <div className="text-center mt-1 space-y-0.5 max-w-full flex flex-col items-center w-full">
                            <input 
                              type="text"
                              value={labelValue}
                              onChange={(e) => {
                                const newLabels = [...(formData.colorPaletteLabels || Array((formData.colorPalette || []).length).fill(''))];
                                newLabels[idx] = e.target.value;
                                setFormData({ ...formData, colorPaletteLabels: newLabels });
                              }}
                              className="w-full text-[9px] font-black tracking-tight text-indigo-700 bg-indigo-50 border border-indigo-150 focus:border-indigo-300 focus:ring-1 focus:ring-indigo-300 rounded-md px-1 py-0.5 uppercase text-center outline-none transition-all"
                            />
                            <span className="text-[8px] text-gray-400 leading-normal block text-center max-w-full mt-1 px-1 line-clamp-2 h-6" title={detail.desc}>
                              {detail.desc}
                            </span>
                          </div>
                        );
                      })()}

                      {/* Delete pill */}
                      {(formData.colorPalette || ['#4F46E5', '#10B981', '#111827', '#F9FAFB', '#6366F1']).length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newPalette = (formData.colorPalette || ['#4F46E5', '#10B981', '#111827', '#F9FAFB', '#6366F1']).filter((_, i) => i !== idx);
                            const newLabels = (formData.colorPaletteLabels || []).filter((_, i) => i !== idx);
                            setFormData({ ...formData, colorPalette: newPalette, colorPaletteLabels: newLabels });
                          }}
                          className="absolute -top-1.5 -right-1.5 p-1 bg-red-50 hover:bg-red-100 text-red-500 rounded-full transition-all border border-red-100 opacity-0 group-hover:opacity-100 animate-fade-in"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
                </div>
              </div>

              {/* Optional Alternative Logos */}
              <div className="border-t border-gray-150 pt-5 space-y-4">
                <div>
                  <h5 className="text-[11px] font-black uppercase tracking-widest text-indigo-600">
                    {isBrazil ? "Logos Alternativos (Opcional)" : "Alternative Logos (Optional)"}
                  </h5>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    {isBrazil 
                      ? "Se sua marca precisa de variações específicas para fundos claros ou escuros, faça o upload delas aqui." 
                      : "If your brand needs specific logo files optimized for light or dark layouts, add them below."}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Light Backing Logo */}
                  <div className="bg-white border border-gray-150 rounded-xl p-4 flex flex-col items-center gap-3">
                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                      {isBrazil ? "Logo Fundo Claro" : "Light BG Logo"}
                    </span>
                    <div className="w-16 h-16 rounded-xl bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center overflow-hidden relative text-gray-400 shadow-inner">
                      {formData.logoLightUrl ? (
                        <img 
                          src={formData.logoLightUrl}
                          alt="Light Logo" 
                          className="w-full h-full object-contain p-1" 
                          referrerPolicy="no-referrer"
                          crossOrigin="anonymous"
                        />
                      ) : (
                        <Sparkles className="w-4 h-4 text-gray-300" />
                      )}
                      {isUploadingLight && (
                        <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                          <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 w-full">
                      <label className="flex-1">
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={handleLightLogoUpload}
                          className="hidden" 
                          disabled={isUploadingLight}
                        />
                        <div className="text-center py-1 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-md text-[10px] font-bold cursor-pointer transition-all uppercase select-none">
                          {formData.logoLightUrl ? (isBrazil ? "Alterar" : "Change") : (isBrazil ? "Upload" : "Upload")}
                        </div>
                      </label>
                      {formData.logoLightUrl && (
                        <button 
                          type="button"
                          onClick={async () => {
                            const oldUrl = formData.logoLightUrl;
                            setFormData(prev => ({ ...prev, logoLightUrl: '' }));
                            if (oldUrl && oldUrl.includes('firebasestorage.googleapis.com')) {
                              const oldRef = ref(storage, oldUrl);
                              deleteObject(oldRef).catch(() => {});
                            }
                            await onSave({ ...formData, logoLightUrl: '' }, true);
                          }}
                          className="px-2 py-1 bg-red-50 text-red-600 hover:bg-red-100 rounded-md border border-red-100 cursor-pointer transition-all"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Dark Backing Logo */}
                  <div className="bg-white border border-gray-150 rounded-xl p-4 flex flex-col items-center gap-3">
                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                      {isBrazil ? "Logo Fundo Escuro" : "Dark BG Logo"}
                    </span>
                    <div className="w-16 h-16 rounded-xl bg-gray-900 border border-dashed border-gray-750 flex items-center justify-center overflow-hidden relative text-gray-600 shadow-inner">
                      {formData.logoDarkUrl ? (
                        <img 
                          src={formData.logoDarkUrl} 
                          alt="Dark Logo" 
                          className="w-full h-full object-contain p-1" 
                          referrerPolicy="no-referrer"
                          crossOrigin="anonymous"
                        />
                      ) : (
                        <Sparkles className="w-4 h-4 text-gray-750" />
                      )}
                      {isUploadingDark && (
                        <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                          <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 w-full">
                      <label className="flex-1">
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={handleDarkLogoUpload}
                          className="hidden" 
                          disabled={isUploadingDark}
                        />
                        <div className="text-center py-1 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-md text-[10px] font-bold cursor-pointer transition-all uppercase select-none font-bold">
                          {formData.logoDarkUrl ? (isBrazil ? "Alterar" : "Change") : (isBrazil ? "Upload" : "Upload")}
                        </div>
                      </label>
                      {formData.logoDarkUrl && (
                        <button 
                          type="button"
                          onClick={async () => {
                            const oldUrl = formData.logoDarkUrl;
                            setFormData(prev => ({ ...prev, logoDarkUrl: '' }));
                            if (oldUrl && oldUrl.includes('firebasestorage.googleapis.com')) {
                              const oldRef = ref(storage, oldUrl);
                              deleteObject(oldRef).catch(() => {});
                            }
                            await onSave({ ...formData, logoDarkUrl: '' }, true);
                          }}
                          className="px-2 py-1 bg-red-50 text-red-600 hover:bg-red-100 rounded-md border border-red-100 cursor-pointer transition-all"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {!isTrial && (
              <div className="border-t border-gray-100 pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1">
                    <h4 className="text-sm font-bold text-gray-900">{isBrazil ? "Categorias de Estilo Visual Descobertas" : "Extracted Visual Buckets"}</h4>
                    <p className="text-[10px] text-gray-500 mb-2">{isBrazil ? "Estilos identificados pela IA para diferentes contextos de imagem." : "AI-mined styles for different usage contexts."}</p>
                    <input 
                      type="text"
                      value={visualContext}
                      onChange={(e) => setVisualContext(e.target.value)}
                      maxLength={1000}
                      placeholder={isBrazil ? "Notas de extração de imagem (ex: 'foco em iluminação high-tech')..." : "Image extraction notes (e.g. 'focus on lighting')..."}
                      className="w-full px-3 py-1.5 bg-gray-100/50 border border-gray-200 rounded-lg text-[10px] outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 font-bold"
                    />
                  </div>
                  <label className="cursor-pointer self-end">
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*"
                      onChange={handleMineVisualStyle}
                      disabled={isMiningVisual}
                    />
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-bold hover:bg-indigo-100 transition-all select-none">
                      {isMiningVisual ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                      <span>{isBrazil ? "Extrair DNA de Imagem" : "Extract DNA from Image"}</span>
                    </div>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {formData.visualStyles?.map((style, idx) => (
                    <div key={idx} className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm relative group">
                      <button 
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            visualStyles: formData.visualStyles?.filter((_, i) => i !== idx)
                          });
                        }}
                        className="absolute top-2 right-2 p-1 text-gray-300 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-indigo-400" />
                        <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest">{style.usage}</span>
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-6 mb-2">{style.aesthetic}</p>
                      <div className="bg-gray-50 rounded-lg p-2">
                        <span className="text-[9px] font-mono text-gray-400 break-all">{style.promptDNA}</span>
                      </div>
                    </div>
                  ))}
                  {(!formData.visualStyles || formData.visualStyles.length === 0) && (
                    <div className="col-span-full py-8 text-center bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
                      <p className="text-xs text-gray-400">{isBrazil ? "Nenhum slot de estilo de imagem ainda. Insira uma imagem de referência para minerar o estilo visual." : "No Visual Buckets yet. Upload an reference image to extract style DNA."}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </AccordionSection>

        {!isTrial && (
            <AccordionSection title={isBrazil ? "Plataformas & Conexões" : "Platforms & Channels"} icon={Share2}>
              <div className="space-y-4">
                <label className="text-sm font-semibold text-gray-700">{isBrazil ? "Plataformas Alvo" : "Target Platforms"}</label>
                <div className="flex flex-wrap gap-2 text-gray-700">
                  {availablePlatforms.map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => togglePlatform(p)}
                      className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border ${
                        formData.platforms?.includes(p)
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-100'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {formData.platforms && formData.platforms.length > 0 && (
                <div className="space-y-4 pt-6 border-t border-gray-100">
                  <h3 className="text-sm font-bold text-gray-900">Default Platform Instructions</h3>
                  <p className="text-xs text-gray-500">Provide default instructions for AI content generation per platform. You can override these when creating a plan.</p>
                  <div className="space-y-4 pl-2 border-l-2 border-indigo-100">
                    {formData.platforms.map(p => (
                      <TextArea 
                        key={p}
                        label={`${p} Default Instructions`}
                        value={formData.platformInstructions?.[p] || ''} 
                        onChange={(v) => {
                          setFormData(prev => ({
                            ...prev,
                            platformInstructions: {
                              ...prev.platformInstructions,
                              [p]: v
                            }
                          }));
                        }} 
                        placeholder={`e.g. Always include #hashtags and emojis for ${p}...`}
                        maxLength={500}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-gray-100 pt-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Social Connections</h3>
                    <p className="text-xs text-gray-500">Connect your social media accounts for auto-posting.</p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const userToken = await auth.currentUser?.getIdToken();
                        const response = await fetch('/api/connect/create', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${userToken}`
                          },
                          body: JSON.stringify({ brandId: formData.id })
                        });
                        if (!response.ok) throw new Error('Failed to generate link');
                        const { url } = await response.json();
                        await navigator.clipboard.writeText(url);
                        setToast({ message: "Secure connection link copied to clipboard! Send this to the owner.", type: 'success' });
                      } catch (err) {
                        console.error(err);
                        setToast({ message: "Failed to generate link", type: 'error' });
                      }
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-[11px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 hover:bg-indigo-100 rounded-lg transition-colors"
                  >
                    <LinkIcon className="w-3.5 h-3.5" />
                    Not the owner? Ask them to link
                  </button>
                </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {['LinkedIn', 'Facebook', 'Instagram', 'Threads'].map(platform => {
              const accounts = formData.socialAccounts?.filter(a => a.platform.toLowerCase() === platform.toLowerCase()) || [];
              const isFacebook = platform.toLowerCase() === 'facebook';
              const isInstagram = platform.toLowerCase() === 'instagram';
              const isThreads = platform.toLowerCase() === 'threads';
              const isLinkedIn = platform.toLowerCase() === 'linkedin';
              
              const displayAccounts = accounts.length > 0 ? accounts : [{ platform, isPlaceholder: true }];

              return displayAccounts.map((account: any, idx) => {
                const hasAllPerms = isFacebook 
                ? checkFacebookPermissions(account?.permissions)
                : isInstagram 
                  ? (account?.isDirectLogin ? true : checkInstagramPermissions(account?.permissions))
                  : isLinkedIn
                    ? checkLinkedInPermissions(account?.permissions)
                    : true;

                return (
                  <div key={`${platform}-${account.accountId || idx}`} className="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-xl ${!account.isPlaceholder ? 'bg-green-50 text-green-600' : 'bg-gray-200 text-gray-400'}`}>
                        {platform.toLowerCase() === 'facebook' && <Facebook className="w-5 h-5" />}
                        {platform.toLowerCase() === 'instagram' && <Instagram className="w-5 h-5" />}
                        {platform.toLowerCase() === 'linkedin' && <Linkedin className="w-5 h-5" />}
                        {platform.toLowerCase() === 'threads' && <Share2 className="w-5 h-5" />}
                      </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">{platform}</p>
                          {!account.isPlaceholder ? (
                            <div className="flex items-center gap-1">
                              {account.accountImage && (
                              <img src={account.accountImage} className="w-4 h-4 rounded-full" />
                            )}
                              <p className="text-[10px] text-gray-500 truncate max-w-[100px]">{account.accountName}</p>
                            </div>
                          ) : (
                            <p className="text-[10px] text-gray-400">Not connected</p>
                          )}
                        </div>
                      </div>
                      {!account.isPlaceholder ? (
                        <div className="flex items-center gap-2">
                          {(isLinkedIn || isThreads) && (
                            <>
                              <button 
                                type="button"
                                onClick={() => handleConnectSocial(platform, false, false, isLinkedIn)}
                                className="text-[10px] font-bold text-indigo-600 hover:underline"
                              >
                                Add Account
                              </button>
                            </>
                          )}
                          <button 
                            type="button"
                            onClick={() => disconnectSocial(platform, account.accountId)}
                            className="text-xs font-bold text-red-600 hover:text-red-700 ml-2"
                          >
                            Disconnect
                          </button>
                        </div>
                      ) : (
                      <div className="flex gap-2">
                        {platform.toLowerCase() === 'instagram' ? (
                          <>
                            <button 
                              type="button"
                              disabled={isConnecting === platform}
                              onClick={() => handleConnectSocial(platform)}
                              className="px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all"
                            >
                              {isConnecting === platform ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Connect'}
                            </button>
                          </>
                        ) : platform.toLowerCase() === 'linkedin' ? (
                          <div className="flex gap-2">
                            <button 
                              type="button"
                              disabled={isConnecting === platform}
                              onClick={() => handleConnectSocial(platform, false, false, true)}
                              className="px-4 py-1.5 rounded-lg text-[10px] font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all"
                            >
                              {isConnecting === platform ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Connect Page / Profile'}
                            </button>
                          </div>
                        ) : (
                          <button 
                            type="button"
                            disabled={isConnecting === platform || (platform.toLowerCase() !== 'facebook' && platform.toLowerCase() !== 'threads')}
                            onClick={() => handleConnectSocial(platform)}
                            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                              (platform.toLowerCase() === 'facebook' || platform.toLowerCase() === 'threads')
                                ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {isConnecting === platform ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Connect'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {!account.isPlaceholder && (isFacebook || (isInstagram && !account.isDirectLogin) || isLinkedIn) && (
                    <div className="pt-3 border-t border-gray-200 space-y-3">
                      {(isFacebook || isInstagram || isLinkedIn) && (!hasAllPerms || (!account.selectedPageId && platform.toLowerCase() !== 'linkedin')) && (
                        <div className="p-2 bg-amber-50 border border-amber-100 rounded-xl flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-3 h-3 text-amber-600" />
                            <p className="text-[10px] text-amber-800 font-medium">Missing permissions</p>
                          </div>
                          <button 
                            type="button"
                            onClick={() => handleConnectSocial(platform, true)}
                            className="text-[10px] font-bold text-amber-700 hover:underline"
                          >
                            Fix Connection
                          </button>
                        </div>
                      )}

                        {(isFacebook || isInstagram || isLinkedIn) && (
                          <div>
                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">
                              Target {isFacebook ? 'Facebook Page' : isInstagram ? 'Instagram Account' : 'LinkedIn Page'}
                            </label>
                            <div className="flex gap-2">
                              <select 
                                value={account.selectedPageId || ''}
                                  onChange={(e) => {
                                    let selected: any = null;
                                    if (isFacebook) selected = availablePages.find(p => p.id === e.target.value);
                                    else if (isInstagram) selected = availableIgAccounts.find(a => a.igId === e.target.value);
                                    else if (isLinkedIn) selected = availableLinkedinOrgs.find(o => o.id === e.target.value);

                                    if (selected) {
                                      updateSelectedPage(platform, e.target.value, isFacebook ? selected.name : isInstagram ? selected.igUsername : selected.name, account.accountId);
                                    } else if (e.target.value === 'personal') {
                                      updateSelectedPage(platform, '', 'Personal Profile', account.accountId);
                                    }
                                  }}
                                onFocus={() => {
                                  if (isFacebook && availablePages.length === 0) fetchAvailablePages(platform, account.accessToken);
                                  else if (isInstagram && availableIgAccounts.length === 0) fetchAvailablePages(platform, account.accessToken);
                                  else if (isLinkedIn && availableLinkedinOrgs.length === 0) fetchAvailablePages(platform, account.accessToken);
                                }}
                                className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                              >
                                <option value="">{isLoadingPages ? 'Loading...' : `Select ${isFacebook ? 'Page' : isInstagram ? 'Account' : 'Page'}`}</option>
                                {isLinkedIn && <option value="personal">Personal Profile (Active)</option>}
                                {isLinkedIn && availableLinkedinOrgs.length === 0 && !isLoadingPages && (
                                  <option disabled>── Page API not yet active ──</option>
                                )}
                                {isFacebook ? (
                                  availablePages.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                  ))
                                ) : isInstagram ? (
                                  availableIgAccounts.map(a => (
                                    <option key={a.igId} value={a.igId}>{a.igUsername} ({a.pageName})</option>
                                  ))
                                ) : (
                                  availableLinkedinOrgs.map(o => (
                                    <option key={o.id} value={o.id}>{o.name}</option>
                                  ))
                                )}
                              </select>
                              <button 
                                type="button"
                                onClick={() => fetchAvailablePages(platform, account.accessToken)}
                                className="p-1.5 text-gray-400 hover:text-indigo-600 transition-all"
                              >
                                <RefreshCw className={`w-3 h-3 ${isLoadingPages ? 'animate-spin' : ''}`} />
                              </button>
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              );
            });
          })}
        </div>
          <p className="mt-4 text-[10px] text-gray-400 italic">* Currently supporting LinkedIn, Facebook, and Instagram for this prototype. We are building our social connections as we grow! More platforms coming soon.</p>
          
          {/* Threads Expert Override Section */}
          <div className="mt-6 p-4 bg-gray-50 rounded-2xl border border-gray-100">
            <h4 className="flex items-center gap-2 mb-2 text-xs font-black tracking-widest text-gray-400 uppercase">
              <Shield className="w-3 h-3" />
              Threads Expert Settings
            </h4>
            <p className="mb-4 text-[10px] text-gray-500 leading-relaxed">
              If your Threads replies aren't loading, paste your <b>User Token Generator</b> token from Meta Portal here. 
              This bypasses restrictions for Tester accounts.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                id="threads-token-override"
                placeholder="Paste THAATOG token here..."
                className="flex-1 px-4 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              />
              <button
                type="button"
                onClick={async () => {
                  const token = (document.getElementById('threads-token-override') as HTMLInputElement).value;
                  if (!token || !token.startsWith('THAATOG')) {
                    setToast({ message: "Invalid token. Should start with 'THAATOG'", type: 'error' });
                    return;
                  }
                  
                  const threadsAcc = formData.socialAccounts?.find(a => a.platform.toLowerCase() === 'threads');
                  if (!threadsAcc) {
                    setToast({ message: "Please connect Threads normally once first.", type: 'error' });
                    return;
                  }

                  const updatedAccounts = formData.socialAccounts?.map(a => 
                    a.platform.toLowerCase() === 'threads' 
                      ? { ...a, accessToken: token, tokenType: 'manual_override' } 
                      : a
                  ) || [];

                  const updatedData = { ...formData, socialAccounts: updatedAccounts };
                  setFormData(updatedData);
                  onSave(updatedData, true);
                  setToast({ message: "Threads override active!", type: 'success' });
                }}
                className="px-4 py-2 text-[10px] font-bold text-white uppercase bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-all shadow-sm"
              >
                Force Update
              </button>
            </div>
            </div>
            </div>
          </AccordionSection>
        )}

        {!isTrial && (
          <AccordionSection title={isBrazil ? "Base de Conhecimento e Ativos" : "Knowledge Base & Assets"} icon={Layers}>
            <div className="space-y-8">
              <div>
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                  <h3 className="text-lg font-bold text-gray-900">Products & Services</h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          productImages: [...(prev.productImages || []), { id: `prod-${Date.now()}`, url: '', name: '', description: '', isService: true }]
                        }));
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-bold hover:bg-gray-50 transition-all"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Text Only</span>
                    </button>
                    <label className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-sm font-bold hover:bg-indigo-100 cursor-pointer transition-all">
                      <Plus className="w-4 h-4" />
                      <span>Upload Image</span>
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*" 
                        onChange={handleProductUpload}
                        disabled={isUploadingProduct}
                      />
                    </label>
                  </div>
            </div>
            
            {isUploadingProduct && (
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl mb-6 animate-pulse">
                <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
                <p className="text-sm text-gray-600 font-medium">Uploading product image...</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {formData.productImages?.map(img => (
                <div key={img.id} className="group bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden flex flex-col">
                  {img.url ? (
                    <div className="aspect-video relative bg-white border-b border-gray-100">
                      <img 
                        key={img.url}
                        src={img.url} 
                        alt={img.name} 
                        className="w-full h-full object-contain" 
                        referrerPolicy="no-referrer" 
                        crossOrigin="anonymous"
                      />
                      <button 
                        type="button"
                        onClick={() => removeProductImage(img.id)}
                        className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="aspect-video relative bg-gray-50 flex items-center justify-center border-b border-gray-100 border-dashed">
                      <button 
                        type="button"
                        onClick={() => removeProductImage(img.id)}
                        className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <label className="flex flex-col items-center gap-2 cursor-pointer text-indigo-600 hover:text-indigo-700 transition-colors p-4 rounded-xl hover:bg-indigo-50">
                        <Upload className="w-6 h-6" />
                        <span className="text-xs font-bold text-center">Upload Image<br/><span className="font-normal text-gray-500">(Optional)</span></span>
                        <input 
                          type="file" 
                          className="hidden" 
                          accept="image/*" 
                          onChange={e => handleProductUpload(e, img.id)}
                          disabled={isUploadingProduct}
                        />
                      </label>
                    </div>
                  )}
                  <div className="p-4 space-y-3">
                    <div className="flex gap-2 items-center">
                      <input 
                        value={img.url || ''}
                        onChange={e => updateProductMetadata(img.id, 'url', e.target.value)}
                        placeholder="Image URL (optional if text-only)"
                        className="flex-1 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-[10px] outline-none focus:ring-2 focus:ring-indigo-500 text-gray-500"
                      />
                      {img.url && (
                        <label className="flex items-center justify-center p-1.5 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 cursor-pointer transition-all shrink-0">
                          <Upload className="w-3.5 h-3.5" />
                          <input 
                            type="file" 
                            className="hidden" 
                            accept="image/*" 
                            onChange={e => handleProductUpload(e, img.id)}
                            disabled={isUploadingProduct}
                          />
                        </label>
                      )}
                    </div>
                    <div className="flex gap-2">
                       <input 
                         value={img.name}
                         onChange={e => updateProductMetadata(img.id, 'name', e.target.value)}
                         placeholder={img.isService ? "Service Name" : "Product Name"}
                         className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                       />
                       <button
                         type="button"
                         onClick={e => updateProductMetadata(img.id, 'isService', !img.isService)}
                         className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${img.isService ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-gray-50 border-gray-200 text-gray-600'}`}
                       >
                         {img.isService ? 'Service' : 'Product'}
                       </button>
                    </div>
                    <div className="flex gap-2">
                      <input 
                        value={img.price}
                        onChange={e => updateProductMetadata(img.id, 'price', e.target.value)}
                        placeholder="Price (e.g. $49.99)"
                        className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                      />
                    </div>
                    <textarea 
                      value={img.description}
                      onChange={e => updateProductMetadata(img.id, 'description', e.target.value)}
                      placeholder="Short description for AI..."
                      className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-indigo-500 min-h-[60px] text-gray-900"
                    />
                  </div>
                </div>
              ))}
              {(!formData.productImages || formData.productImages.length === 0) && !isUploadingProduct && (
                <div className="col-span-full py-12 text-center bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                  <ImageIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No product images uploaded yet.</p>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Team Directory</h3>
                <p className="text-xs text-gray-500">Add key team members to feature in social posts and content.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    teamDirectory: [...(prev.teamDirectory || []), { id: `team-${Date.now()}`, name: '', role: '', bio: '', imageUrl: '' }]
                  }));
                }}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-sm font-bold hover:bg-indigo-100 transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>Add Team Member</span>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {formData.teamDirectory?.map(member => (
                <div key={member.id} className="group bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden flex flex-col p-4 space-y-3 relative">
                  <button 
                    type="button"
                    onClick={() => removeTeamMember(member.id)}
                    className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-red-700 z-10"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="flex gap-4">
                    <div className="w-16 h-16 rounded-full bg-gray-200 border-2 border-white shrink-0 overflow-hidden relative">
                       {member.imageUrl ? (
                         <img src={member.imageUrl} alt={member.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" crossOrigin="anonymous" />
                       ) : (
                         <div className="w-full h-full flex items-center justify-center bg-indigo-100 text-indigo-400">
                            <ImageIcon className="w-6 h-6" />
                         </div>
                       )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input 
                        value={member.name}
                        onChange={e => updateTeamMetadata(member.id, 'name', e.target.value)}
                        placeholder="Full Name"
                        className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                      />
                      <input 
                        value={member.role}
                        onChange={e => updateTeamMetadata(member.id, 'role', e.target.value)}
                        placeholder="Job Title / Role"
                        className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <input 
                      value={member.imageUrl || ''}
                      onChange={e => updateTeamMetadata(member.id, 'imageUrl', e.target.value)}
                      placeholder="Image URL (optional)"
                      className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                    />
                    <label className="flex items-center justify-center p-1.5 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 cursor-pointer transition-all shrink-0">
                      <Upload className="w-4 h-4" />
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*" 
                        onChange={e => handleTeamImageUpload(e, member.id)}
                      />
                    </label>
                  </div>
                  <textarea 
                    value={member.bio || ''}
                    onChange={e => updateTeamMetadata(member.id, 'bio', e.target.value)}
                    placeholder="Short bio or key achievements..."
                    className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-indigo-500 min-h-[60px] text-gray-900"
                  />
                </div>
              ))}
              {(!formData.teamDirectory || formData.teamDirectory.length === 0) && (
                <div className="col-span-full py-12 text-center bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                  <p className="text-sm text-gray-400">No team members added yet.</p>
                </div>
              )}
            </div>
          </div>

          <div 
            className={`border-t border-gray-100 pt-8 transition-all ${templateDragActive ? 'bg-purple-50/50 p-4 rounded-3xl border border-dashed border-purple-300' : ''}`}
            onDragEnter={handleTemplateDrag}
            onDragOver={handleTemplateDrag}
            onDragLeave={handleTemplateDrag}
            onDrop={handleTemplateDrop}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Brand Templates & Guidelines</h3>
                <p className="text-xs text-gray-500">Upload examples of your preferred visual style or layout templates. Supports multiple files & drag-and-drop.</p>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <input 
                    type="text"
                    value={visualContext}
                    onChange={(e) => setVisualContext(e.target.value)}
                    maxLength={1000}
                    placeholder="Describe what to focus on in this image..."
                    className="w-full px-3 py-1.5 bg-white border border-gray-100 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                  />
                </div>
                <label className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-600 rounded-xl text-sm font-bold hover:bg-amber-100 cursor-pointer transition-all shadow-sm">
                  {isMiningVisual ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  <span>Calibrate Style</span>
                  <input 
                    type="file" 
                    className="hidden" 
                    accept="image/*" 
                    onChange={handleMineVisualStyle}
                    disabled={isMiningVisual}
                  />
                </label>
                <div className="relative group z-20">
                  <button type="button" className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-600 rounded-xl text-sm font-bold hover:bg-purple-100 transition-all shadow-sm">
                    <Plus className="w-4 h-4" />
                    <span>Add Template</span>
                  </button>
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-100 shadow-xl rounded-xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all transform origin-top-right scale-95 group-hover:scale-100">
                    <label className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer text-sm text-gray-700 transition-colors">
                      <ImageIcon className="w-4 h-4 text-gray-400" />
                      <span>Upload Base Design</span>
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*" 
                        multiple
                        onChange={handleTemplateUpload}
                        disabled={isUploadingTemplate}
                      />
                    </label>
                    <button 
                      type="button" 
                      onClick={() => setShowBakerModal(true)} 
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left text-sm text-gray-700 border-t border-gray-50 transition-colors"
                    >
                      <Layers className="w-4 h-4 text-purple-500" />
                      <div>
                         <span className="block font-bold text-purple-900">Create Template</span>
                         <span className="block text-[10px] text-gray-500">Design an HTML blueprint</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {templateDragActive && (
              <div className="flex items-center justify-center p-8 border-2 border-dashed border-purple-400 bg-purple-50/80 rounded-2xl mb-6 text-purple-700 animate-pulse">
                <div className="text-center">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-purple-600 animate-bounce" />
                  <p className="text-base font-bold">Drop files to add as Brand Templates</p>
                  <p className="text-xs text-purple-500 mt-1">Images will be processed and visual layout shells generated</p>
                </div>
              </div>
            )}

            {isUploadingTemplate && (
              <div className="flex items-center gap-3 p-4 bg-purple-50 border border-purple-100 rounded-2xl mb-6 animate-pulse shadow-sm">
                <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-purple-950">AI Brand Calibration Active</p>
                  <p className="text-xs text-purple-700">{templateUploadStatus || "Processing template image..."}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {formData.brandTemplates?.map(t => (
                <div key={t.id} className="group bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden flex flex-col shadow-sm hover:shadow transition-all duration-200">
                  <div className="grid grid-cols-2 bg-white border-b border-gray-100 relative">
                    {/* Original image */}
                    <div className="aspect-video relative bg-slate-50 border-r border-gray-100 p-1 flex items-center justify-center">
                      <div className="absolute top-1.5 left-1.5 bg-gray-900/60 text-[9px] text-white rounded px-1.5 py-0.5 font-bold tracking-wider uppercase z-10">
                        Reference
                      </div>
                      <img 
                        key={t.url}
                        src={t.url} 
                        alt={t.name} 
                        className="w-full h-full object-contain max-h-[140px]" 
                        referrerPolicy="no-referrer" 
                        crossOrigin="anonymous"
                      />
                    </div>
                    {/* Shell image */}
                    <div className="aspect-video relative bg-slate-50 p-1 flex items-center justify-center group/shell">
                      <div className="absolute top-1.5 left-1.5 bg-purple-600/80 text-[9px] text-white rounded px-1.5 py-0.5 font-bold tracking-wider uppercase z-10">
                        Empty Shell
                      </div>
                      {t.shellUrl ? (
                        <img 
                          key={t.shellUrl}
                          src={t.shellUrl} 
                          alt="Layout shell" 
                          className="w-full h-full object-contain max-h-[140px]" 
                          referrerPolicy="no-referrer" 
                          crossOrigin="anonymous"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-[10px] text-gray-400 font-medium">
                          <span>Aesthetic Base</span>
                          <span className="text-[9px] text-gray-300 font-normal">Uses original layout</span>
                        </div>
                      )}
                      
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/shell:opacity-100 transition-opacity flex items-center justify-center gap-2 z-20">
                        {t.shellUrl && (
                          <a 
                            href={t.shellUrl}
                            download={`empty-shell-${t.id}.png`}
                            target="_blank"
                            rel="noreferrer"
                            className="p-2 bg-white/90 hover:bg-white text-gray-900 rounded-lg shadow-sm transition-all"
                            title="Download Shell"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        )}
                        <label 
                          className="p-2 bg-white/90 hover:bg-white text-gray-900 rounded-lg shadow-sm transition-all cursor-pointer"
                          title="Upload Custom Shell"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Upload className="w-4 h-4" />
                          <input 
                            type="file" 
                            className="hidden" 
                            accept="image/*" 
                            onChange={(e) => handleCustomShellUpload(e, t.id)}
                          />
                        </label>
                      </div>
                    </div>
                    <button 
                      type="button"
                      onClick={() => removeTemplate(t.id)}
                      className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-red-700 z-10 shadow-sm"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="p-4 space-y-3 flex-1 flex flex-col">
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-block bg-indigo-50 text-indigo-600 font-mono text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                        {t.role ? t.role.replace('_', ' ') : 'general layout'}
                      </span>
                    </div>
                    <input 
                      value={t.name}
                      onChange={e => updateTemplateMetadata(t.id, 'name', e.target.value)}
                      placeholder="Template Name"
                      className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                    />
                    <textarea 
                      value={t.description}
                      onChange={e => updateTemplateMetadata(t.id, 'description', e.target.value)}
                      placeholder="Style guidelines for this template..."
                      className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px] text-gray-900 flex-1"
                    />
                  </div>
                </div>
              ))}
              {brandBlueprints.map(b => (
                <div key={b.id} className="group bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden flex flex-col shadow-sm hover:shadow transition-all duration-200">
                  <div className="grid grid-cols-2 bg-white border-b border-gray-100 relative">
                    <div className="aspect-video relative bg-slate-50 border-r border-gray-100 p-1 flex items-center justify-center">
                      <div className="absolute top-1.5 left-1.5 bg-gray-900/60 text-[9px] text-white rounded px-1.5 py-0.5 font-bold tracking-wider uppercase z-10">
                        Reference
                      </div>
                      <div className="w-full h-full flex flex-col items-center justify-center text-[10px] text-gray-400 font-medium bg-gray-100 rounded overflow-hidden">
                        {b.layoutTemplate?.backgroundImage ? (
                           <img 
                             src={b.layoutTemplate.backgroundImage} 
                             alt={b.name} 
                             className="w-full h-full object-contain mix-blend-multiply opacity-90 transition-transform duration-300 group-hover:scale-[1.02]"
                             crossOrigin="anonymous"
                           />
                        ) : (
                          <>
                            <Layers className="w-8 h-8 text-gray-300 mb-2" />
                            <span className="font-bold text-gray-600">No Image</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="aspect-video relative bg-slate-50 p-1 flex items-center justify-center">
                      <div className="absolute top-1.5 left-1.5 bg-purple-600/80 text-[9px] text-white rounded px-1.5 py-0.5 font-bold tracking-wider uppercase z-10">
                        HTML Blueprint
                      </div>
                      <div className="w-full h-full flex flex-col items-center justify-center text-[10px] text-gray-400 font-medium bg-gray-100 rounded relative">
                        {b.layoutTemplate?.backgroundImage && (
                           <img 
                             src={b.layoutTemplate.backgroundImage} 
                             alt={b.name} 
                             className="w-full h-full object-contain absolute inset-0 opacity-20 pointer-events-none"
                             crossOrigin="anonymous"
                           />
                        )}
                        <Layers className="w-8 h-8 text-purple-300 mb-2 z-10" />
                        <span className="font-bold text-gray-600 z-10">{b.category} Base</span>
                        <span className="text-[9px] text-gray-400 font-normal z-10">{b.slideCount} Slides Generated</span>
                      </div>
                    </div>
                    <button 
                      type="button"
                      onClick={() => {
                        setEditingBlueprint(b);
                        setShowBakerModal(true);
                      }}
                      className="absolute top-2 right-10 p-1.5 bg-indigo-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-indigo-700 z-10 shadow-sm"
                      title="Edit Blueprint Layout"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {deletingBlueprintId === b.id ? (
                      <div className="absolute top-2 right-2 flex items-center justify-center gap-1 z-10">
                        <button 
                          type="button"
                          onClick={() => setDeletingBlueprintId(null)}
                          className="p-1.5 bg-gray-600 text-white rounded-lg transition-all hover:bg-gray-700 shadow-sm"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <button 
                          type="button"
                          onClick={async () => {
                            try {
                              await deleteDoc(doc(db, 'carousel_blueprints', b.id));
                              setBrandBlueprints(prev => prev.filter(x => x.id !== b.id));
                            } catch(e) {}
                            setDeletingBlueprintId(null);
                          }}
                          className="px-2 py-1 bg-red-600 text-white font-bold text-xs rounded-lg transition-all hover:bg-red-700 shadow-sm"
                        >
                          Confirm
                        </button>
                      </div>
                    ) : (
                      <button 
                        type="button"
                        onClick={() => setDeletingBlueprintId(b.id)}
                        className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-red-700 z-10 shadow-sm"
                        title="Delete Blueprint"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="p-4 space-y-3 flex-1 flex flex-col">
                    <div className="flex items-center justify-between gap-2">
                       <span className="inline-block bg-indigo-50 text-indigo-600 font-mono text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                         {b.category}
                       </span>
                    </div>
                    <input 
                      value={b.name}
                      onChange={e => updateBlueprintMetadata(b.id, 'name', e.target.value)}
                      placeholder="Template Name"
                      className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                    />
                    <textarea 
                      value={b.description}
                      onChange={e => updateBlueprintMetadata(b.id, 'description', e.target.value)}
                      placeholder="Style guidelines for this template..."
                      className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px] text-gray-900 flex-1"
                    />
                  </div>
                </div>
              ))}
              {(!formData.brandTemplates || formData.brandTemplates.length === 0) && brandBlueprints.length === 0 && !isUploadingTemplate && (
                <div className="col-span-full py-12 text-center bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                  <Layout className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No templates uploaded or generated yet.</p>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-8">
            <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Context Documents</h3>
                <p className="text-xs text-gray-500">Upload PDFs or text files with company info, pricing, or strategy to help the AI.</p>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  type="button"
                  onClick={() => {
                    const newDoc = {
                      id: Math.random().toString(36).substr(2, 9),
                      name: "Manual Context",
                      url: `data:text/plain;base64,${btoa("Enter your brand context here...")}`,
                      mimeType: "text/plain",
                      description: "Manually added brand context"
                    };
                    setFormData(prev => ({
                      ...prev,
                      contextDocuments: [...(prev.contextDocuments || []), newDoc]
                    }));
                    setViewingDoc(newDoc);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-sm font-bold hover:bg-indigo-100 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span>Manual Context</span>
                </button>
                <label className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-xl text-sm font-bold hover:bg-blue-100 cursor-pointer transition-all">
                  <Plus className="w-4 h-4" />
                  <span>Add Document</span>
                  <input 
                    type="file" 
                    className="hidden" 
                    accept=".pdf,.txt,.doc,.docx,.csv" 
                    onChange={handleContextDocumentUpload}
                    disabled={isUploadingDocument}
                  />
                </label>
              </div>
            </div>

            {isUploadingDocument && (
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl mb-6 animate-pulse">
                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                <p className="text-sm text-gray-600 font-medium">Uploading document...</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {formData.contextDocuments?.map(doc => (
                <div key={doc.id} className="group bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden flex flex-col">
                  <div className="p-4 flex items-center gap-3 bg-white border-b border-gray-100">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{doc.name}</p>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">{doc.mimeType.split('/')[1] || 'DOC'}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button 
                        type="button"
                        onClick={() => setViewingDoc(doc)}
                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                        title="View Content"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button 
                        type="button"
                        onClick={() => removeContextDocument(doc.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-all"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    <input 
                      value={doc.name}
                      onChange={e => updateContextDocumentMetadata(doc.id, 'name', e.target.value)}
                      placeholder="Document Name"
                      className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                    />
                    <textarea 
                      value={doc.description}
                      onChange={e => updateContextDocumentMetadata(doc.id, 'description', e.target.value)}
                      placeholder="What's in this document? (e.g. Q2 Pricing Guide)"
                      className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-indigo-500 min-h-[60px] text-gray-900"
                    />
                  </div>
                </div>
              ))}
              {(!formData.contextDocuments || formData.contextDocuments.length === 0) && !isUploadingDocument && (
                <div className="col-span-full py-12 text-center bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                  <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No context documents uploaded yet.</p>
                </div>
              )}
            </div>
          </div>
          </div>
        </AccordionSection>
      )}

        <AccordionSection title={isBrazil ? "Equipe e Colaboração" : "Team & Collaboration"} icon={Users}>
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Team Members</h3>
                <p className="text-xs text-gray-500">Invite colleagues to collaborate on this brand profile and content plans.</p>
              </div>
              {isOwner && (
                isTrial ? (
                  <div className="flex flex-col items-center justify-center p-6 bg-indigo-50/50 rounded-2xl border border-indigo-100 border-dashed text-center">
                    <div className="flex items-center gap-2 text-indigo-600 mb-2">
                      <Lock className="w-4 h-4" />
                      <span className="text-sm font-bold">Unlock Team Invites</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-4 max-w-[280px]">Invite colleagues and clients safely with a subscription plan. Start collaborating today!</p>
                    <button 
                      type="button"
                      onClick={() => setActiveTab?.('dashboard')}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition-all shadow-md shadow-indigo-100"
                    >
                      View Plans
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <select 
                      id="team-role"
                      className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                    >
                      <option value="editor">Editor</option>
                      <option value="approver">Approver</option>
                      <option value="viewer">Viewer</option>
                    </select>
                    <input 
                      type="email"
                      id="team-email"
                      placeholder="colleague@example.com"
                      className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const input = e.currentTarget;
                          const email = input.value.trim();
                          const roleInput = document.getElementById('team-role') as HTMLSelectElement;
                          const role = roleInput.value as 'viewer' | 'editor' | 'approver';
                          if (email && !formData.teamMembers?.includes(email)) {
                            setFormData({ 
                              ...formData, 
                              teamMembers: [...(formData.teamMembers || []), email],
                              teamRoles: { ...(formData.teamRoles || {}), [email]: role }
                            });
                            input.value = '';

                            // Send invitation email
                            sendEmailNotification(
                              email,
                              `Invitation to collaborate on ${formData.companyName}`,
                              `Hi there!\n\nYou've been invited to collaborate on the brand profile "${formData.companyName}" as a ${role}. Log in to start contributing!`
                            );
                          }
                        }
                      }}
                    />
                    <button 
                      type="button"
                      onClick={() => {
                        const input = document.getElementById('team-email') as HTMLInputElement;
                        const email = input.value.trim();
                        const roleInput = document.getElementById('team-role') as HTMLSelectElement;
                        const role = roleInput.value as 'viewer' | 'editor' | 'approver';
                        if (email && !formData.teamMembers?.includes(email)) {
                          setFormData({ 
                            ...formData, 
                            teamMembers: [...(formData.teamMembers || []), email],
                            teamRoles: { ...(formData.teamRoles || {}), [email]: role }
                          });
                          input.value = '';

                          // Send invitation email
                          sendEmailNotification(
                            email,
                            `Invitation to collaborate on ${formData.companyName}`,
                            `Hi there!\n\nYou've been invited to collaborate on the brand profile "${formData.companyName}" as a ${role}. Log in to start contributing!`
                          );
                        }
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 transition-all"
                    >
                      Invite
                    </button>
                  </div>
                )
              )}
            </div>

            <div className="space-y-3">
              {formData.teamMembers?.map(email => (
                <div key={email} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Users className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-700">{email}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                        {formData.teamRoles?.[email] || 'editor'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isOwner ? (
                      <>
                        <select 
                          value={formData.teamRoles?.[email] || 'editor'}
                          onChange={(e) => {
                            const newRole = e.target.value as 'viewer' | 'editor' | 'approver';
                            setFormData({
                              ...formData,
                              teamRoles: { ...(formData.teamRoles || {}), [email]: newRole }
                            });
                          }}
                          className="text-xs bg-transparent border-none focus:ring-0 text-indigo-600 font-bold cursor-pointer"
                        >
                          <option value="editor">Editor</option>
                          <option value="approver">Approver</option>
                          <option value="viewer">Viewer</option>
                        </select>
                        <button 
                          type="button"
                          onClick={() => {
                            const newRoles = { ...(formData.teamRoles || {}) };
                            delete newRoles[email];
                            setFormData({ 
                              ...formData, 
                              teamMembers: formData.teamMembers?.filter(e => e !== email),
                              teamRoles: newRoles
                            });
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-all"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                        {formData.teamRoles?.[email] || 'editor'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {(!formData.teamMembers || formData.teamMembers.length === 0) && (
                <div className="py-8 text-center bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                  <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No team members invited yet.</p>
                </div>
              )}
            </div>
          </div>
        </AccordionSection>

        {!isTrial && (
          <div className="space-y-6 pt-8">
            {/* Section 3: Strategic Roadmap */}
            <AccordionSection title="Strategic Roadmap" icon={LayoutDashboard}>
              <p className="text-xs text-gray-500 mb-6">Your goals, market positioning, and competitors.</p>
              <div className="grid grid-cols-1 gap-6">
                <TextArea 
                  label="Primary Goals" 
                  value={formData.questionnaire?.primaryGoals} 
                  onChange={v => setFormData({...formData, questionnaire: {...formData.questionnaire!, primaryGoals: v}})} 
                  placeholder="What do you want to achieve on social media? (e.g., Growth, Lead Gen, Authority)"
                  maxLength={5000}
                />
                <TextArea 
                  label="Competitors & Inspirations" 
                  value={formData.questionnaire?.competitors} 
                  onChange={v => setFormData({...formData, questionnaire: {...formData.questionnaire!, competitors: v}})} 
                  placeholder="Who else is doing well in your niche? Who do you admire or compete with?"
                  description="Helping the AI understand your market positioning."
                  maxLength={5000}
                />
              </div>
            </AccordionSection>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <button 
          type="submit" 
          disabled={isSaving || isDeleting}
          className="flex-1 bg-indigo-600 text-white py-4 rounded-2xl font-bold text-lg hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 flex items-center justify-center gap-2"
        >
          {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          <span>Save Brand Profile</span>
        </button>

        {formData.id && formData.id !== 'new' && isOwner && (
          <div className="flex items-center gap-2">
            {!showDeleteConfirm ? (
              <button 
                type="button"
                disabled={isSaving}
                onClick={() => setShowDeleteConfirm(true)}
                className="px-6 bg-red-50 text-red-600 py-4 rounded-2xl font-bold text-lg hover:bg-red-100 transition-all flex items-center justify-center gap-2 border border-red-100"
              >
                <Trash2 className="w-5 h-5" />
                <span className="hidden sm:inline">Delete Brand</span>
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button 
                  type="button"
                  disabled={isDeleting}
                  onClick={async () => {
                    console.log("[DeleteButton] Confirmed click. formData.id:", formData.id);
                    if (onDelete && formData.id) {
                      setIsDeleting(true);
                      try {
                        await onDelete(formData.id);
                      } catch (err) {
                        console.error("[DeleteButton] Delete failed:", err);
                      } finally {
                        setIsDeleting(false);
                        setShowDeleteConfirm(false);
                      }
                    }
                  }}
                  className="px-6 bg-red-600 text-white py-4 rounded-2xl font-bold text-lg hover:bg-red-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-100"
                >
                  {isDeleting ? <Loader2 className="w-5 h-5 animate-spin" /> : <AlertTriangle className="w-5 h-5" />}
                  <span>{isDeleting ? "Deleting..." : "Confirm Delete"}</span>
                </button>
                <button 
                  type="button"
                  disabled={isDeleting}
                  onClick={() => setShowDeleteConfirm(false)}
                  className="p-4 bg-gray-100 text-gray-600 rounded-2xl hover:bg-gray-200 transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <TroubleshootingModal 
        isOpen={showTroubleshooting.isOpen} 
        initialTab={showTroubleshooting.tab}
        onClose={() => setShowTroubleshooting({ ...showTroubleshooting, isOpen: false })} 
        setToast={setToast}
        fetchAvailablePages={fetchAvailablePages}
        socialAccounts={formData.socialAccounts || []}
      />
      <DocumentViewerModal 
        doc={viewingDoc} 
        onClose={() => setViewingDoc(null)} 
        onUpdateContent={(id, newContent) => {
          const base64 = btoa(encodeURIComponent(newContent).replace(/%([0-9A-F]{2})/g, (match, p1) => String.fromCharCode(parseInt(p1, 16))));
          const newUrl = `data:text/plain;base64,${base64}`;
          setFormData(prev => ({
            ...prev,
            contextDocuments: prev.contextDocuments?.map(d => d.id === id ? { ...d, url: newUrl } : d) || []
          }));
        }}
      />

    </form>

      {showBakerModal && (
        <div className="fixed inset-0 z-[100] bg-gray-900/60 p-4 sm:p-8 flex flex-col">
          <div className="flex-1 bg-gray-50 rounded-3xl shadow-2xl flex flex-col overflow-hidden w-full max-w-[1600px] mx-auto border border-white/20">
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shadow-sm shrink-0">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Template Baker Lab</h2>
                <p className="text-sm text-gray-500">Design HTML Layouts for {formData.companyName}</p>
              </div>
              <button 
                type="button"
                onClick={() => { setShowBakerModal(false); setEditingBlueprint(null); }}
                className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto w-full h-full p-2 bg-gray-50 relative">
              <TemplateBakerLab brand={formData as any} brands={[]} onComplete={() => setShowBakerModal(false)} initialBlueprint={editingBlueprint} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const DocumentViewerModal = ({ doc, onClose, onUpdateContent }: { doc: any, onClose: () => void, onUpdateContent: (id: string, content: string) => void }) => {
  const [content, setContent] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editedContent, setEditedContent] = useState('');

  useEffect(() => {
    if (doc) {
      if (doc.url.startsWith('data:')) {
        try {
          const base64 = doc.url.split(',')[1];
          const decoded = decodeURIComponent(escape(atob(base64)));
          setContent(decoded);
          setEditedContent(decoded);
        } catch (e) {
          console.error("Failed to decode data URI:", e);
          setContent("Error: Could not decode document content.");
        }
      } else {
        setIsLoading(true);
        fetch(doc.url)
          .then(res => res.text())
          .then(text => {
            setContent(text);
            setEditedContent(text);
            setIsLoading(false);
          })
          .catch(err => {
            console.error("Failed to fetch document content:", err);
            setContent("Error: Could not fetch document content.");
            setIsLoading(false);
          });
      }
    } else {
      setContent('');
      setIsEditing(false);
    }
  }, [doc]);

  const handleSave = () => {
    onUpdateContent(doc.id, editedContent);
    setContent(editedContent);
    setIsEditing(false);
  };

  if (!doc) return null;

  const isTextDoc = doc.mimeType.includes('text') || doc.mimeType.includes('plain') || doc.url.startsWith('data:text/plain');

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-indigo-50/30">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-600 p-2 rounded-xl">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">{doc.name}</h3>
              <p className="text-xs text-gray-500 uppercase tracking-wider">{doc.mimeType}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isTextDoc && !isEditing && (
              <button 
                type="button"
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition-all"
              >
                <Edit3 className="w-4 h-4" />
                <span>Edit Content</span>
              </button>
            )}
            {isEditing && (
              <button 
                type="button"
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-xs font-bold hover:bg-green-700 transition-all"
              >
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
            )}
            <a 
              href={doc.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-all"
              title="Open Original"
            >
              <ExternalLink className="w-5 h-5" />
            </a>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-all">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-8 bg-gray-50/50">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
              <p className="text-gray-500 font-medium">Loading content...</p>
            </div>
          ) : isEditing ? (
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="w-full h-full min-h-[400px] p-6 rounded-2xl border border-indigo-100 focus:ring-2 focus:ring-indigo-500 outline-none text-sm leading-relaxed font-mono bg-white text-gray-900"
              placeholder="Enter brand context here..."
            />
          ) : (
            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm min-h-full">
              <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed">
                {content}
              </pre>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-100 flex justify-end bg-white">
          <button 
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const TroubleshootingModal = ({ 
  isOpen, 
  initialTab, 
  onClose, 
  setToast, 
  fetchAvailablePages, 
  socialAccounts 
}: { 
  isOpen: boolean, 
  initialTab: 'instagram' | 'linkedin', 
  onClose: () => void, 
  setToast: (toast: { message: string, type: 'success' | 'error' | 'success' } | null) => void,
  fetchAvailablePages: (platform: string, accessToken: string, manualIds?: { igId: string, pageId: string }) => Promise<void>,
  socialAccounts: any[]
}) => {
  const [activeTab, setActiveTab] = useState<'instagram' | 'linkedin'>(initialTab);
  const [manualIgId, setManualIgId] = useState('');
  const [manualPageId, setManualPageId] = useState('');
  const [isManualConnecting, setIsManualConnecting] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  if (!isOpen) return null;
  
  const currentUrl = window.location.origin;
  const linkedinRedirect = `${currentUrl}/auth/linkedin/callback`;
  const igAccount = socialAccounts.find(a => a.platform.toLowerCase() === 'instagram');

  const handleManualConnect = async () => {
    if (!igAccount?.accessToken) {
      setToast({ message: "Please connect via Facebook first to get an access token.", type: 'error' });
      return;
    }
    if (!manualIgId || !manualPageId) {
      setToast({ message: "Please enter both Instagram and Page IDs.", type: 'error' });
      return;
    }
    setIsManualConnecting(true);
    try {
      await fetchAvailablePages('Instagram', igAccount.accessToken, { igId: manualIgId, pageId: manualPageId });
      onClose();
    } catch (err) {
      // Error handled by fetchAvailablePages
    } finally {
      setIsManualConnecting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[150] flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden"
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-indigo-50/50">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-600 p-2 rounded-xl">
              <HelpCircle className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Troubleshooting Guide</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-all">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex border-b border-gray-100">
          <button 
            onClick={() => setActiveTab('instagram')}
            className={`flex-1 py-3 text-sm font-bold transition-all ${activeTab === 'instagram' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/30' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            Instagram
          </button>
          <button 
            onClick={() => setActiveTab('linkedin')}
            className={`flex-1 py-3 text-sm font-bold transition-all ${activeTab === 'linkedin' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/30' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            LinkedIn
          </button>
        </div>
        
        <div className="p-8 space-y-6 max-h-[60vh] overflow-y-auto">
          {activeTab === 'instagram' ? (
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold">1</div>
                <div>
                  <p className="font-bold text-gray-900">Switch to Business/Creator Account</p>
                  <p className="text-sm text-gray-600 mt-1">Personal accounts cannot be used for automated publishing. In the Instagram app, go to <strong>Settings &gt; Account Type</strong> and switch to Business or Creator.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold">2</div>
                <div>
                  <p className="font-bold text-gray-900">Link to a Facebook Page</p>
                  <p className="text-sm text-gray-600 mt-1">Meta requires Instagram Business accounts to be linked to a Facebook Page. In Instagram, go to <strong>Edit Profile &gt; Page</strong> and connect your Page.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center font-bold">!</div>
                <div>
                  <p className="font-bold text-gray-900">Page Found but No Instagram Account?</p>
                  <div className="text-sm text-gray-600 mt-1">
                    If your Facebook Page appears but your Instagram account doesn't, it usually means:
                    <ul className="list-disc ml-5 mt-1 space-y-1">
                      <li>The Instagram account is still a <strong>Personal</strong> account (must be Professional).</li>
                      <li>The link between the Page and Instagram is broken. Re-link them in the Instagram app or <a href="https://business.facebook.com/latest/settings/instagram_account" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">Meta Business Suite</a>.</li>
                      <li><strong>Business Portfolios:</strong> If your Page is owned by a Business Portfolio, ensure the "App" is added as a "Connected Asset" to the Portfolio.</li>
                      <li>You didn't select the Instagram account in the Facebook login popup. Click <strong>"Fix Connection"</strong> and ensure <strong>all</strong> checkboxes are checked in the Meta popup.</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold">3</div>
                <div>
                  <p className="font-bold text-gray-900">Grant All Permissions</p>
                  <p className="text-sm text-gray-600 mt-1">When connecting, ensure you check <strong>all</strong> boxes in the Facebook login popup. If you have multiple Facebook Pages, make sure you select the specific Page that is linked to your Instagram account.</p>
                </div>
              </div>

              <div className="bg-indigo-50 p-6 rounded-3xl border border-indigo-100 space-y-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-indigo-600 p-1.5 rounded-lg">
                    <Settings className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="font-bold text-gray-900">Manual Connection (Last Resort)</h4>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  If discovery still fails, you can manually enter your IDs. You can find these in your <strong>Meta Business Suite &gt; Settings</strong>.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase mb-1 block">Instagram Business Account ID</label>
                    <input 
                      value={manualIgId}
                      onChange={e => setManualIgId(e.target.value)}
                      placeholder="e.g. 17841401234567890"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase mb-1 block">Linked Facebook Page ID</label>
                    <input 
                      value={manualPageId}
                      onChange={e => setManualPageId(e.target.value)}
                      placeholder="e.g. 103320522439394"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-gray-900"
                    />
                  </div>
                  <button 
                    onClick={handleManualConnect}
                    disabled={isManualConnecting || !igAccount?.accessToken}
                    className="w-full py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-indigo-200"
                  >
                    {isManualConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />}
                    <span>Connect Manually</span>
                  </button>
                  {!igAccount?.accessToken && (
                    <p className="text-[10px] text-red-500 text-center">Connect via Facebook first to enable manual entry.</p>
                  )}
                </div>
              </div>

              <div className="bg-amber-50 p-4 rounded-2xl border border-amber-100 flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div className="space-y-2">
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong>Note:</strong> After linking your account on Meta, come back here and click the <strong>Refresh</strong> icon in the dropdown to find your new account.
                  </p>
                  <p className="text-xs text-amber-800">
                    If you didn't see all your pages in the popup, you can re-authorize here:
                  </p>
                  <button 
                    onClick={() => {
                      onClose();
                    }}
                    className="text-xs font-bold text-indigo-600 hover:underline"
                  >
                    Use "Fix Connection" on the main screen.
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold">1</div>
                <div>
                  <p className="font-bold text-gray-900">Verify Redirect URI</p>
                  <p className="text-sm text-gray-600 mt-1">LinkedIn is very strict about the Redirect URI. Ensure the following URL is added to your LinkedIn App's <strong>"Authorized Redirect URLs"</strong> in the LinkedIn Developer portal:</p>
                  <div className="mt-2 p-3 bg-gray-100 rounded-xl font-mono text-xs break-all select-all">
                    {linkedinRedirect}
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold">2</div>
                <div>
                  <p className="font-bold text-gray-900">Check App Permissions</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Ensure your LinkedIn App has the following products enabled under the <strong>"Products"</strong> tab in the LinkedIn Developer Portal:
                  </p>
                  <ul className="list-disc ml-5 mt-2 text-sm text-gray-600 space-y-1">
                    <li><strong>Community Management API</strong> (for Company Page AND personal posting)</li>
                    <li><strong>(The new app should now be correctly configured for both profile and page connection)</strong></li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold">3</div>
                <div>
                  <p className="font-bold text-gray-900">Environment Mismatch</p>
                  <p className="text-sm text-gray-600 mt-1">If you are using a "Shared" URL vs a "Dev" URL, you must add <strong>both</strong> redirect URIs to your LinkedIn App settings.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end">
          <button 
            onClick={onClose}
            className="bg-indigo-600 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
          >
            Got it, thanks!
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const PlanGenerator = ({ 
  onGenerate, 
  isGenerating, 
  platforms = [], 
  platformPreset = {}, 
  enableSmartTrends = false,
  setToast
}: { 
  onGenerate: (m: string, y: number, h: string[], p: string, g: string, n: string | number, t: string, c: string, trends: boolean) => void, 
  isGenerating: boolean, 
  platforms?: string[], 
  platformPreset?: Record<string, string>, 
  enableSmartTrends?: boolean,
  setToast: (t: { message: string, type: 'success' | 'error' | 'warning', duration?: number | null } | null) => void
}) => {
  const { t, isBrazil } = useTranslation();
  const [month, setMonth] = useState(format(addDays(new Date(), 30), 'MMMM'));
  const [year, setYear] = useState(new Date().getFullYear());
  const [highlights, setHighlights] = useState(['', '', '', '', '']);
  const [products, setProducts] = useState('');
  const [goals, setGoals] = useState('');
  const [numberOfPosts, setNumberOfPosts] = useState<string | number>('');
  const [targetChannels, setTargetChannels] = useState<string[]>(['default']);
  const [customInstructions, setCustomInstructions] = useState('');
  const [platformInstructions, setPlatformInstructions] = useState<Record<string, string>>(platformPreset);
  const [includeLatestTrends, setIncludeLatestTrends] = useState(false);
  const [uploadedPlanText, setUploadedPlanText] = useState('');

  const handleHighlightChange = (index: number, value: string) => {
    const newHighlights = [...highlights];
    newHighlights[index] = value;
    setHighlights(newHighlights);
  };

  const handlePlatformInstructionChange = (platform: string, value: string) => {
    setPlatformInstructions(prev => ({ ...prev, [platform]: value }));
  };

  const constructFinalInstructions = () => {
    let finalInstructions = customInstructions ? `General Instructions:\n${customInstructions}\n\n` : '';
    const platformsToInclude = targetChannels.includes('default') ? platforms : targetChannels;
    
    let hasPlatformInstructions = false;
    let pInstructions = 'Platform Specific Instructions:\n';
    
    platformsToInclude.forEach(p => {
      if (platformInstructions[p]?.trim()) {
        hasPlatformInstructions = true;
        pInstructions += `- ${p}: ${platformInstructions[p].trim()}\n`;
      }
    });

    if (hasPlatformInstructions) {
      finalInstructions += pInstructions;
    }

    return finalInstructions;
  };

  const isBlank = numberOfPosts === 'blank';
  const isUpload = numberOfPosts === 'upload';
  const hasHighlights = highlights.filter(h => h.trim()).length > 0;
  const isInputsValid = isBlank || isUpload || (hasHighlights && products.trim() && goals.trim());

  const handleGenerateClick = () => {
    if (numberOfPosts === '') {
      setToast({
        message: isBrazil 
          ? "⚠️ Por favor, selecione uma opção de tamanho do plano antes de continuar." 
          : "⚠️ Please select a plan size option before continuing.",
        type: 'warning'
      });
      return;
    }

    if (isUpload && !uploadedPlanText.trim()) {
      setToast({
        message: isBrazil 
          ? "⚠️ Por favor, cole o seu plano no campo de texto." 
          : "⚠️ Please paste your plan in the text field.",
        type: 'warning'
      });
      return;
    }
    
    onGenerate(
      month, 
      year, 
      isBlank ? [] : highlights.filter(h => h.trim()), 
      isBlank ? "" : products, 
      isBlank ? "" : goals, 
      numberOfPosts, 
      targetChannels.includes('default') ? 'default' : targetChannels.join(', '), 
      (isBlank ? "" : constructFinalInstructions()) + (numberOfPosts === 'upload' && uploadedPlanText ? `\n\n[UPLOADED PLAN TO REVIEW & REFORMAT]\n${uploadedPlanText}` : ""), 
      isBlank ? false : includeLatestTrends
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700">{t.targetMonth}</label>
          <select 
            value={month} 
            onChange={e => setMonth(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all bg-gray-50 text-gray-900"
          >
            {Object.entries(t.months).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700">{t.year}</label>
          <input 
            type="number" 
            value={year} 
            onChange={e => setYear(parseInt(e.target.value))}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all bg-gray-50 text-gray-900"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700">
            {isBrazil ? "Tamanho do Plano (Nº de Posts)" : "Plan Size (Number of Posts)"}
          </label>
          <select 
            value={numberOfPosts} 
            onChange={e => {
              const val = e.target.value;
              if (val === 'blank') {
                setNumberOfPosts('blank');
              } else if (val === 'upload') {
                setNumberOfPosts('upload');
              } else if (val === '') {
                setNumberOfPosts('');
              } else {
                setNumberOfPosts(parseInt(val));
              }
            }}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all bg-gray-50 text-gray-900"
          >
            <option value="">{isBrazil ? "Selecione o tamanho do plano" : "Select Plan Size"}</option>
            {[5, 10, 15, 20, 25, 30].map(num => (
              <option key={num} value={num}>{num} Posts</option>
            ))}
            <option value="upload">{isBrazil ? "Upload de Plano Existente (Revisão da IA)" : "Upload Existing Plan (AI Review)"}</option>
            <option value="blank">{isBrazil ? "Calendário Vazio Personalizado (Plano do zero)" : "Custom Empty Calendar (Blank Plan)"}</option>
          </select>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700">Target Channels</label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setTargetChannels(['default'])}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                targetChannels.includes('default') ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
              } border`}
            >
              All Brand Channels
            </button>
            {platforms.map(platform => (
              <button
                key={platform}
                onClick={() => {
                  let newChannels = targetChannels.filter(c => c !== 'default');
                  if (newChannels.includes(platform)) {
                    newChannels = newChannels.filter(c => c !== platform);
                  } else {
                    newChannels.push(platform);
                  }
                  if (newChannels.length === 0) newChannels = ['default'];
                  setTargetChannels(newChannels);
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  targetChannels.includes(platform) ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                } border`}
              >
                {platform}
              </button>
            ))}
          </div>
        </div>
      </div>

      {!isBlank && (
        <div className="space-y-4">
          {numberOfPosts === 'upload' && (
            <TextArea 
              label={isBrazil ? "Cole seu plano de conteúdo aqui" : "Paste Your Proposed Plan Here"} 
              value={uploadedPlanText} 
              onChange={setUploadedPlanText} 
              placeholder={isBrazil ? "Cole o rascunho do seu calendário (texto, tópicos, etc.). A IA irá formatar e alinhar com a sua marca." : "Paste your calendar draft (text, bullet points, etc.). The AI will reformat and align it with your brand."}
              maxLength={15000}
            />
          )}
          <TextArea 
            label="General Custom Instructions (Optional)" 
            value={customInstructions} 
            onChange={setCustomInstructions} 
            placeholder="e.g. Focus exclusively on user generated content, or use a highly sarcastic tone for this campaign..."
            maxLength={1000}
          />
          
          {platforms.length > 0 && (
            <div className="mt-4 p-4 bg-gray-50 border border-gray-100 rounded-2xl space-y-4">
              <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-indigo-500" />
                Platform Specific Instructions
              </h4>
              <p className="text-xs text-gray-500 max-w-xl">
                Add specific instructions on how content should be generated for each individual platform (e.g., "For Instagram, focus heavily on visual aesthetics and use lots of emojis").
              </p>
              <div className="space-y-4 pl-2 border-l-2 border-indigo-100">
                {(targetChannels.includes('default') ? platforms : targetChannels).map(p => (
                  <TextArea 
                    key={p}
                    label={`${p} Instructions`}
                    value={platformInstructions[p] || ''} 
                    onChange={(v) => handlePlatformInstructionChange(p, v)} 
                    placeholder={`Optional instructions specifically for ${p}...`}
                    maxLength={500}
                  />
                ))}
              </div>
            </div>
          )}

          {!isUpload && (
            <>
              <TextArea 
                label={t.keyProducts} 
                value={products} 
                onChange={setProducts} 
                placeholder={t.keyProductsDesc}
                required
                maxLength={1000}
              />
              <TextArea 
                label={t.specificGoals} 
                value={goals} 
                onChange={setGoals} 
                placeholder={t.specificGoalsDesc}
                required
                maxLength={1000}
              />

              {enableSmartTrends && (
                <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-xl shadow-sm">
                      <Zap className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{isBrazil ? "Foco Estratégico Aprimorado" : "Enhanced Strategic Focus"}</p>
                      <p className="text-[10px] text-gray-500 font-medium">{isBrazil ? "Cruzar o plano com as últimas tendências e inteligência de mercado." : "Bridge your plan with the latest market trends and intelligence."}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIncludeLatestTrends(!includeLatestTrends)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${includeLatestTrends ? 'bg-indigo-600' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${includeLatestTrends ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!isBlank && !isUpload && (
        <div className="space-y-4">
          <label className="text-sm font-semibold text-gray-700">{t.monthlyHighlights}</label>
          {highlights.map((h, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 font-bold shrink-0">
                {i + 1}
              </div>
              <input 
                value={h} 
                onChange={e => handleHighlightChange(i, e.target.value.slice(0, 200))}
                placeholder={`${t.highlightPlaceholder} ${i + 1}...`}
                maxLength={200}
                className="flex-1 px-4 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-gray-900"
              />
            </div>
          ))}
        </div>
      )}

      <button 
        onClick={handleGenerateClick}
        disabled={isGenerating || (!isBlank && !isInputsValid)}
        className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold text-lg hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
      >
        {isGenerating ? <Loader2 className="w-6 h-6 animate-spin" /> : <Sparkles className="w-6 h-6" />}
        <span>
          {isGenerating 
            ? t.generating 
            : (isBlank 
                ? (isBrazil ? "Criar Calendário Vazio" : "Create Empty Calendar") 
                : t.generateMonthlyCalendar
              )
          }
        </span>
      </button>
    </div>
  );
};

const RenderSlideOverlay = ({ 
  slide, 
  brand, 
  slideIndex = 0, 
  totalSlides = 1 
}: { 
  slide: any; 
  brand: any; 
  slideIndex?: number; 
  totalSlides?: number; 
}) => {
  if (!slide || !slide.layoutTemplate) return null;
  const lt = slide.layoutTemplate;
  if ((lt.canvasEditingMode || "original") === "original") {
    return null;
  }
  const slideNumberString = totalSlides > 1 
    ? `${String(slideIndex + 1).padStart(2, '0')} / ${String(totalSlides).padStart(2, '0')}`
    : "";

  // Content Plaque Style
  const plaqueStyle: React.CSSProperties = {
    position: "absolute",
    top: `${lt.plaqueTop ?? 15}%`,
    left: `${lt.plaqueLeft ?? 10}%`,
    width: `${lt.plaqueWidth ?? 80}%`,
    height: `${lt.plaqueHeight ?? 70}%`,
    backgroundColor: lt.plaqueBgColor || "#FFFFFF",
    borderRadius: `${lt.plaqueBorderRadius ?? 16}px`,
    boxShadow:
      lt.plaqueStyle === "glass" || lt.plaqueStyle === "floating-modern" || lt.plaqueStyle === "split-left" || lt.plaqueStyle === "split-right"
        ? "0 10px 30px rgba(0,0,0,0.08)"
        : lt.plaqueStyle === "solid-shadow"
          ? "0 20px 40px rgba(0,0,0,0.12)"
          : "none",
    backdropFilter: lt.plaqueStyle === "glass" || lt.plaqueStyle === "floating-modern" ? "blur(16px)" : undefined,
    border:
      lt.plaqueStyle === "glass"
        ? "1px solid rgba(255, 255, 255, 0.4)"
        : "1px solid rgba(0,0,0,0.05)",
    opacity: (lt.plaqueOpacity ?? 100) / 100,
    zIndex: 1,
    pointerEvents: "none"
  };

  // Header Style
  const headerStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 4cqw",
    height: `${lt.headerHeight || 10}%`,
    backgroundColor: lt.headerBgColor || "transparent",
    borderBottom:
      lt.headerStyle === "bordered"
        ? "1px solid rgba(0,0,0,0.08)"
        : lt.headerStyle === "accent-bar"
          ? "2px solid #4f46e5"
          : "none",
    backdropFilter: lt.headerStyle === "glass" ? "blur(12px)" : undefined,
    color: lt.headerTextColor || "#333333",
    fontFamily: "Inter, sans-serif",
    pointerEvents: "none",
    zIndex: 10
  };

  // Brand Logo Style
  const logoWrapperStyle: React.CSSProperties = {
    position: "absolute",
    left: lt.logoPosition === "floating"
      ? `${lt.logoLeft || 5}%`
      : (lt.logoAlignment || "left") === "left"
        ? "5%"
        : lt.logoAlignment === "center"
          ? "42.5%"
          : "80%",
    top: lt.logoPosition === "floating"
      ? `${lt.logoTop || 5}%`
      : (lt.logoPosition || "content-top") === "content-top"
        ? "5%"
        : "85%",
    width: `${lt.logoSize || 15}%`,
    height: "auto",
    maxHeight: `${lt.logoSize || 15}%`,
    display: "flex",
    justifyContent: (lt.logoAlignment || "left") === "center" ? "center" : (lt.logoAlignment || "left") === "right" ? "flex-end" : "flex-start",
    pointerEvents: "none",
    zIndex: 10
  };

  // Footer Style
  const footerStyle: React.CSSProperties = {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 4cqw",
    height: `${lt.footerHeight || 10}%`,
    backgroundColor: lt.footerBgColor || "transparent",
    borderTop:
      lt.footerStyle === "bordered"
        ? "1px solid rgba(0,0,0,0.08)"
        : lt.footerStyle === "accent-bar"
          ? "2px solid #4f46e5"
          : "none",
    backdropFilter: lt.footerStyle === "glass" ? "blur(12px)" : undefined,
    color: lt.footerTextColor || "#666666",
    fontFamily: "font-sans",
    fontSize: `${lt.footerFontSize || 2.5}cqw`,
    pointerEvents: "none",
    zIndex: 10
  };

  return (
    <div className="absolute inset-0 w-full h-full pointer-events-none select-none z-10 overflow-hidden font-sans @container" style={{ containerType: "inline-size" }}>
      {/* Content Plaque */}
      {lt.enableContentPlaque && <div style={plaqueStyle} />}

      {/* Header */}
      {lt.enableHeader && (
        <div style={headerStyle}>
          <div className="flex items-center gap-2 h-full">
            {lt.headerShowLogo && brand?.logoUrl ? (
              <img
                src={brand.logoUrl}
                alt="Logo"
                className="object-contain"
                style={{ maxHeight: "60%" }}
                referrerPolicy="no-referrer"
              />
            ) : (
              lt.headerShowEyebrow && (
                <span 
                  style={{ fontSize: `${lt.headerFontSize || 3}cqw` }}
                  className="font-black tracking-widest uppercase opacity-75"
                >
                  {lt.headerText}
                </span>
              )
            )}
          </div>
          <div 
            style={{ fontSize: `calc(${lt.headerFontSize || 3}cqw * 1.1)` }}
            className="font-bold font-mono opacity-60"
          >
            {brand?.companyName || "PlataPost"}
          </div>
        </div>
      )}

      {/* Custom Brand Logo positioning */}
      {lt.logoPosition !== "none" && lt.logoPosition !== "header" && lt.logoPosition !== "footer" && (
        <div style={logoWrapperStyle}>
          {brand?.logoUrl ? (
            <img 
              src={brand.logoUrl} 
              alt="Logo" 
              className="max-w-full max-h-full object-contain pointer-events-none select-none" 
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="bg-black/10 text-black px-2 py-1 rounded text-[8px] font-black tracking-wider uppercase border border-dashed border-black/20 text-center flex items-center justify-center pointer-events-none select-none">
              {brand?.companyName || "Logo"}
            </div>
          )}
        </div>
      )}

      {/* Text Blocks */}
      {(lt.boxes || []).map((box: any) => {
        if (box.type === 'logo') {
          return (
            <div
              key={box.id}
              className="absolute pointer-events-none select-none flex items-center justify-center overflow-hidden"
              style={{
                top: `${box.top}%`,
                left: `${box.left}%`,
                width: `${box.width}%`,
                height: `${box.height}%`,
              }}
            >
              {brand?.logoUrl ? (
                <img 
                  src={brand.logoUrl} 
                  alt="Logo" 
                  className="max-w-full max-h-full object-contain mix-blend-multiply" 
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="bg-black/10 text-black px-2 py-1 rounded text-[8px] font-black tracking-wider uppercase border border-dashed border-black/20 text-center flex items-center justify-center pointer-events-none select-none">
                  {brand?.companyName || "Logo"}
                </div>
              )}
            </div>
          );
        }

        const isIconList = box.type === "icon-list";
        const justifyClass = isIconList ? "gap-2" : box.type === "heading" ? "justify-end" : "justify-center";
        return (
          <div
            key={box.id}
            className={`absolute flex flex-col overflow-hidden ${box.fontFamily || ''} ${justifyClass}`}
            style={{
              top: `${box.top}%`,
              left: `${box.left}%`,
              width: `${box.width}%`,
              height: box.flowNaturally ? "auto" : `${box.height}%`,
              minHeight: box.flowNaturally ? `${box.height}%` : undefined,
              textAlign: box.textAlign || box.align || "left",
              fontFamily: box.fontFamily === "serif" || box.fontFamily === "font-serif" 
                ? "Georgia, serif" 
                : box.fontFamily === "mono" || box.fontFamily === "font-mono" 
                  ? "'JetBrains Mono', monospace" 
                  : box.fontFamily === "display" || box.fontFamily === "font-display" 
                    ? "'Space Grotesk', sans-serif" 
                    : "Inter, system-ui, sans-serif",
              color: box.color || "#333333",
              fontSize: box.fontSize && box.fontSize < 12 
                ? `${box.fontSize}cqw` 
                : `max(6px, ${(box.fontSize || 16) * 0.2}cqw)`,
              fontWeight: box.fontWeight === "bold" ? 700 : box.fontWeight === "medium" ? 500 : 400,
              alignItems: (box.textAlign || box.align) === "center" ? "center" : (box.textAlign || box.align) === "right" ? "flex-end" : "flex-start",
              lineHeight: box.lineHeight ?? 1.1,
              letterSpacing: box.letterSpacing ?? "-0.02em",
              transform: `translate(${box.xOffset || 0}em, ${box.yOffset || 0}em)`,
              textTransform: box.textTransform === "uppercase" ? "uppercase" : "none",
              opacity: (box.opacity ?? 100) / 100,
              zIndex: 20
            }}
          >
            {isIconList ? (
              String(box.text)
                .split("\n")
                .filter(Boolean)
                .map((line, i) => {
                  const parsed = parseListLine(
                    line,
                    box.iconType,
                    box.customIcon,
                    i
                  );
                  return (
                    <div key={i} className="flex items-start gap-2">
                      {!parsed.noIcon && (
                        <RenderListIcon
                          iconType={parsed.iconType}
                          customIcon={parsed.customIcon}
                          containerShape={parsed.containerShape}
                          containerStyle={parsed.containerStyle}
                          iconColor={parsed.iconColor || box.color || "#333333"}
                          backgroundColor={parsed.backgroundColor || box.color || "#333333"}
                          index={i}
                          textColor={box.color || "#333333"}
                        />
                      )}
                      <div className="opacity-90 leading-tight w-full flex-1 min-w-0">
                        <AutoFitText
                          text={parsed.text}
                          box={{
                            ...box,
                            type: "eyebrow",
                            text: parsed.text,
                          }}
                        />
                      </div>
                    </div>
                  );
                })
            ) : box.type === "logo" ? (
              brand?.logoUrl ? (
                <img 
                  src={brand.logoUrl} 
                  alt="Logo" 
                  className="max-w-full max-h-full object-contain pointer-events-none mix-blend-multiply" 
                  referrerPolicy="no-referrer"
                />
              ) : null
            ) : box.type === "divider" ? (
              <div className="w-full h-full flex items-center justify-center pointer-events-none">
                <div
                  style={{
                    width: "100%",
                    height: `${box.borderWidth || 4}px`,
                    backgroundColor: box.color || "#e5e7eb",
                    borderRadius: "9999px",
                  }}
                />
              </div>
            ) : box.flowNaturally ? (
              <span className="whitespace-pre-wrap leading-tight block w-full">
                {box.text}
              </span>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <AutoFitText text={box.text} box={box} />
              </div>
            )}
          </div>
        );
      })}

      {/* Footer */}
      {lt.enableFooter && (
        <div style={footerStyle}>
          <div className="h-full flex items-center gap-2">
            {lt.footerShowUrl && lt.footerUrlAlign === "left" && (
              <span
                className="font-semibold transition-all inline-block"
                style={{
                  backgroundColor: lt.footerUrlBgColor,
                  border: `${lt.footerUrlBorderWidth || 0}px solid ${lt.footerUrlBorderColor}`,
                  borderRadius: `${lt.footerUrlBorderRadius || 0}px`,
                  padding: `${lt.footerUrlPadding || 0}px`,
                  color: lt.footerUrlTextColor || lt.footerTextColor,
                }}
              >
                {brand?.companyName
                  ? brand.companyName
                      .toLowerCase()
                      .replace(/\s+/g, "") + ".com"
                  : "example.com"}
              </span>
            )}
            {!lt.footerShowUrl && lt.footerShowLogo && brand?.logoUrl && lt.footerUrlAlign === "left" && (
              <img
                src={brand.logoUrl}
                alt="Logo"
                className="object-contain"
                style={{ maxHeight: "60%" }}
                referrerPolicy="no-referrer"
              />
            )}
            {lt.footerText && lt.footerTextAlign === "left" && (
              <span
                className="opacity-80 font-medium transition-all inline-block"
                style={{
                  backgroundColor: lt.footerLegalBgColor,
                  border: `${lt.footerLegalBorderWidth || 0}px solid ${lt.footerLegalBorderColor}`,
                  borderRadius: `${lt.footerLegalBorderRadius || 0}px`,
                  padding: `${lt.footerLegalPadding || 0}px`,
                  color: lt.footerLegalTextColor || lt.footerTextColor,
                }}
              >
                {(lt.footerText.includes('[LOREM') || lt.footerText.includes('FOOTER'))
                  ? (brand?.companyName || "")
                  : lt.footerText}
              </span>
            )}
            {lt.footerShowPageNum && lt.footerPageNumAlign === "left" && slideNumberString && (
              <span
                className="font-mono font-bold transition-all inline-block"
                style={{
                  fontSize: "0.9em",
                  backgroundColor: lt.footerPageNumBgColor,
                  border: `${lt.footerPageNumBorderWidth || 0}px solid ${lt.footerPageNumBorderColor}`,
                  borderRadius: `${lt.footerPageNumBorderRadius || 0}px`,
                  padding: `${lt.footerPageNumPadding || 0}px`,
                  color: lt.footerPageNumTextColor || lt.footerTextColor,
                }}
              >
                {slideNumberString}
              </span>
            )}
          </div>
          
          <div className="h-full flex items-center gap-2 justify-end">
            {lt.footerShowUrl && lt.footerUrlAlign === "right" && (
              <span
                className="font-semibold transition-all inline-block"
                style={{
                  backgroundColor: lt.footerUrlBgColor,
                  border: `${lt.footerUrlBorderWidth || 0}px solid ${lt.footerUrlBorderColor}`,
                  borderRadius: `${lt.footerUrlBorderRadius || 0}px`,
                  padding: `${lt.footerUrlPadding || 0}px`,
                  color: lt.footerUrlTextColor || lt.footerTextColor,
                }}
              >
                {brand?.companyName
                  ? brand.companyName
                      .toLowerCase()
                      .replace(/\s+/g, "") + ".com"
                  : "example.com"}
              </span>
            )}
            {!lt.footerShowUrl && lt.footerShowLogo && brand?.logoUrl && lt.footerUrlAlign === "right" && (
              <img
                src={brand.logoUrl}
                alt="Logo"
                className="object-contain"
                style={{ maxHeight: "60%" }}
                referrerPolicy="no-referrer"
              />
            )}
            {lt.footerText && lt.footerTextAlign === "right" && (
              <span
                className="opacity-80 font-medium transition-all inline-block"
                style={{
                  backgroundColor: lt.footerLegalBgColor,
                  border: `${lt.footerLegalBorderWidth || 0}px solid ${lt.footerLegalBorderColor}`,
                  borderRadius: `${lt.footerLegalBorderRadius || 0}px`,
                  padding: `${lt.footerLegalPadding || 0}px`,
                  color: lt.footerLegalTextColor || lt.footerTextColor,
                }}
              >
                {(lt.footerText.includes('[LOREM') || lt.footerText.includes('FOOTER'))
                  ? (brand?.companyName || "")
                  : lt.footerText}
              </span>
            )}
            {lt.footerShowPageNum && lt.footerPageNumAlign === "right" && slideNumberString && (
              <span
                className="font-mono font-bold transition-all inline-block"
                style={{
                  fontSize: "0.9em",
                  backgroundColor: lt.footerPageNumBgColor,
                  border: `${lt.footerPageNumBorderWidth || 0}px solid ${lt.footerPageNumBorderColor}`,
                  borderRadius: `${lt.footerPageNumBorderRadius || 0}px`,
                  padding: `${lt.footerPageNumPadding || 0}px`,
                  color: lt.footerPageNumTextColor || lt.footerTextColor,
                }}
              >
                {slideNumberString}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PlanCard = ({ 
  plan, 
  brand,
  shareFeedback,
  onDeleteFeedback,
  isExpanded,
  onToggle,
  onUpdate, 
  onGenerateArtwork,
  onGenerateSeamlessCarousel,
  onOverlaySlides,
  onPublish,
  onUploadArtwork,
  onUpdateStatus,
  onUpdatePlatforms,
  onAddPost,
  onRepurposePost,
  onDuplicatePost,
  onDuplicatePlan,
  onRefinePlan,
  onDeletePlan,
  onPreview,
  onSuggestEdit,
  onApproveSuggestion,
  onRejectSuggestion,
  brandPlatforms,
  productImages,
  brandTemplates,
  brandBlueprints = [],
  isAdmin,
  hasApiKey,
  setToast,
  setActiveTab,
  canEdit,
  isTrial,
  user,
  editingPostId,
  setEditingPostId,
  editingPostData,
  setEditingPostData,
  onBulkGenerateArtwork,
  isBulkGenerating,
  sprintProgress,
  onScheduleAll,
  onToggleSelectPost,
  selectedPostIds,
  onClearSelection,
  onArchive,
  isArchiving,
  onLogAudit,
  onShare,
  tokens
}: { 
  plan: MonthlyPlan, 
  brand: BrandProfile | null,
  shareFeedback: ShareFeedback[],
  isExpanded: boolean,
  onToggle: () => void,
  onUpdate: (id: string, u: Partial<MonthlyPlan>, silent?: boolean) => void, 
  onGenerateArtwork: (id: string, postId: string, slideId: string, concept: string, type?: 'image' | 'video', useBrandElements?: boolean, currentSlide?: any, refinementPrompt?: string, inspirationImage?: string) => Promise<string | void>,
  onGenerateSeamlessCarousel?: (planId: string, postId: string, baseConcept: string, onProgress: (status: string) => void, useBrandElements?: boolean) => Promise<string | null>,
  onOverlaySlides?: (planId: string, postId: string, basePanoramaUrl: string, onProgress: (status: string) => void, useBrandElements?: boolean, isCancelled?: () => boolean) => Promise<boolean>,
  onPublish: (planId: string, postId: string, platform: string, testMode?: boolean) => Promise<void>,
  onUploadArtwork: (id: string, postId: string, slideId: string, file: File, currentSlide?: any) => Promise<void>,
  onUpdateStatus: (id: string, postId: string, status: 'approved' | 'rejected' | 'scheduled' | 'pending' | 'none', scheduledAt?: string, platform?: string) => void,
  onUpdatePlatforms: (id: string, postId: string, platforms: string[]) => void,
  onAddPost: (id: string) => void,
  onRepurposePost: (id: string, post: ContentIdea) => void,
  onDeleteFeedback: (id: string) => void,
  onDuplicatePost: (planId: string, postId: string) => void,
  onDuplicatePlan: (planId: string, refinementPrompt?: string) => Promise<void>,
  onRefinePlan: (planId: string, refinementPrompt: string, targetChannels?: string[]) => Promise<void>,
  onDeletePlan?: (id: string) => void,
  onPreview: (url: string, type: any, slide?: any, format?: 'feed' | 'story' | 'carousel' | string) => void,
  onSuggestEdit: (postId: string) => void,
  onApproveSuggestion: (postId: string, suggestionId: string) => void,
  onRejectSuggestion: (postId: string, suggestionId: string) => void,
  brandPlatforms: string[],
  productImages: { id: string; url: string; name: string; description?: string; price?: string }[],
  brandTemplates: { id: string; url: string; name: string; description?: string }[],
  isAdmin: boolean,
  hasApiKey: boolean,
  setToast: (t: { message: string, type: 'success' | 'error' } | null) => void,
  setActiveTab: (tab: any) => void,
  canEdit: boolean,
  isTrial: boolean,
  user: User | null,
  editingPostId: string | null,
  setEditingPostId: (id: string | null) => void,
  editingPostData: { copy: string; slides: any[]; linkedinFormat?: 'pdf' | 'gallery'; format?: string } | null,
  brandBlueprints?: any[],
  setEditingPostData: (data: { copy: string; slides: any[]; linkedinFormat?: 'pdf' | 'gallery'; format?: string } | null) => void,
  onBulkGenerateArtwork: (planId: string) => Promise<void>,
  onScheduleAll: (plan: MonthlyPlan) => void,
  isBulkGenerating: boolean,
  sprintProgress: { current: number; total: number; postTitle: string } | null,
  onToggleSelectPost: (postId: string) => void,
  selectedPostIds: Set<string>,
  onClearSelection: () => void,
  onArchive: (planId: string) => void | Promise<void>,
  isArchiving: boolean,
  onLogAudit: (brand: BrandProfile, action: string, cost: number, tokensBefore: number, tokensAfter: number, planId?: string, status?: 'success' | 'failed' | 'started', metadata?: any) => Promise<void>,
  onShare?: (planId: string, ideaIds?: string[]) => void,
  tokens: number
}) => {
  const { t, isBrazil } = useTranslation();
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(plan.title || `${plan.month} ${plan.year}`);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicatePrompt, setDuplicatePrompt] = useState('');
  const [isDuplicatingPlan, setIsDuplicatingPlan] = useState(false);
  
  const [showRefineModal, setShowRefineModal] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState(plan.lastRefinementPrompt || '');
  const [refinePlatforms, setRefinePlatforms] = useState<string[]>([]);
  const [isRefiningPlan, setIsRefiningPlan] = useState(false);

  const handleSaveTitle = () => {
    onUpdate(plan.id!, { title: tempTitle });
    setIsEditingTitle(false);
  };

  const handleSavePost = (postId: string, updatedPost: ContentIdea) => {
    const updatedCalendar = plan.calendar.map(item => 
      item.id === postId ? updatedPost : item
    );
    onUpdate(plan.id!, { calendar: updatedCalendar }, true);
    setEditingPostId(null);
    setEditingPostData(null);
  };

  const handleDeletePost = (postId: string) => {
    const updatedCalendar = plan.calendar.filter(item => item.id !== postId);
    onUpdate(plan.id!, { calendar: updatedCalendar }, true);
    setToast({ message: "Post deleted successfully.", type: 'success' });
  };

  const currentSelected = selectedPostIds || new Set();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeletePlanConfirm, setShowDeletePlanConfirm] = useState(false);

  const handleBulkAction = async (action: 'approve' | 'delete' | 'share') => {
    console.log(`[PlanCard] handleBulkAction: ${action}, Selection Size: ${currentSelected.size}`);
    if (currentSelected.size === 0) return;
    
    try {
      if (action === 'share') {
        onShare?.(plan.id!, Array.from(currentSelected));
        onClearSelection();
        return;
      }

      if (action === 'delete') {
        if (!showDeleteConfirm) {
          setShowDeleteConfirm(true);
          return;
        }
        
        setToast({ message: "Deleting posts...", type: 'success' });
        const updatedCalendar = plan.calendar.filter(p => !currentSelected.has(p.id));
        console.log(`[PlanCard] Deleting posts. Old count: ${plan.calendar.length}, New count: ${updatedCalendar.length}`);
        
        await onUpdate(plan.id!, { calendar: updatedCalendar }, true);
        setToast({ message: `${currentSelected.size} posts deleted successfully.`, type: 'success' });
        setShowDeleteConfirm(false);
      } else {
        setToast({ message: "Approving posts...", type: 'success' });
        const updatedCalendar = plan.calendar.map(p => 
          currentSelected.has(p.id) ? { ...p, status: 'approved' as const } : p
        );
        await onUpdate(plan.id!, { calendar: updatedCalendar }, true);
        setToast({ message: `${currentSelected.size} posts approved.`, type: 'success' });
      }
      
      onClearSelection();
    } catch (err) {
      console.error("[PlanCard] Bulk action failed:", err);
      setToast({ message: `Failed to ${action} posts. Please try again.`, type: 'error' });
      setShowDeleteConfirm(false);
    }
  };

  const handleEditPost = (post: ContentIdea) => {
    setEditingPostData({ 
      copy: post.copy, 
      slides: [...(post.slides || [])],
      linkedinFormat: post.linkedinFormat || 'pdf',
      format: post.format || 'feed'
    });
    setEditingPostId(post.id);
  };

  const handleCancelEdit = () => {
    setEditingPostId(null);
    setEditingPostData(null);
  };

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
      <div 
        onClick={onToggle}
        className="p-6 flex items-center justify-between bg-white cursor-pointer select-none hover:bg-slate-50/40 transition-all"
      >
        <div className="flex items-center gap-4">
          <div className="bg-indigo-50 p-3 rounded-2xl">
            <CalendarIcon className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            {isEditingTitle ? (
              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                <input 
                  value={tempTitle}
                  onChange={e => setTempTitle(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSaveTitle()}
                  className="px-3 py-1 border border-indigo-200 rounded-lg text-lg font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  autoFocus
                />
                <button onClick={e => { e.stopPropagation(); handleSaveTitle(); }} className="p-1 text-green-600 hover:bg-green-50 rounded-lg transition-all">
                  <Check className="w-5 h-5" />
                </button>
                <button onClick={e => { e.stopPropagation(); setIsEditingTitle(false); }} className="p-1 text-red-600 hover:bg-red-50 rounded-lg transition-all">
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 group">
                <h3 className="text-xl font-bold text-gray-900">{plan.title || `${plan.month} ${plan.year}`}</h3>
                <button 
                  onClick={e => {
                    e.stopPropagation();
                    setTempTitle(plan.title || `${plan.month} ${plan.year}`);
                    setIsEditingTitle(true);
                  }} 
                  className="p-1 text-gray-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                plan.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
              }`}>
                {plan.status}
              </span>
              <span className="text-xs text-gray-400">• {plan.calendar.length} posts</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isExpanded && view === 'list' && (
            <button
              onClick={e => {
                e.stopPropagation();
                const todayStr = format(new Date(), 'yyyy-MM-dd');
                const todayOrFuturePost = [...plan.calendar]
                  .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
                  .find(p => p.date && p.date.substring(0, 10) >= todayStr);
                if (todayOrFuturePost) {
                  const el = document.getElementById(`post-${todayOrFuturePost.id}`);
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // Highlight effect
                    el.classList.add('ring-4', 'ring-indigo-500', 'ring-opacity-50');
                    setTimeout(() => el.classList.remove('ring-4', 'ring-indigo-500', 'ring-opacity-50'), 2000);
                  } else {
                    setToast({ message: isBrazil ? "Post de hoje não encontrado na tela." : "Post for today not found in view.", type: 'error' });
                  }
                } else {
                  setToast({ message: isBrazil ? "Nenhum post futuro encontrado." : "No upcoming posts found.", type: 'error' });
                }
              }}
              className="px-4 py-2 bg-blue-50 text-blue-600 rounded-xl text-xs font-bold hover:bg-blue-100 transition-all flex items-center gap-2"
              title={isBrazil ? "Ir para Hoje" : "Go to Today"}
            >
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">{isBrazil ? "Hoje" : "Today"}</span>
            </button>
          )}
          {onShare && (
            <button
              onClick={e => { e.stopPropagation(); onShare(plan.id!); }}
              className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-xs font-bold hover:bg-indigo-100 transition-all flex items-center gap-2"
              title={isBrazil ? "Compartilhar" : "Share"}
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">{isBrazil ? "Compartilhar" : "Share"}</span>
            </button>
          )}
          <button 
            onClick={e => { e.stopPropagation(); onToggle(); }}
            className="p-2 hover:bg-gray-100 rounded-xl transition-all"
          >
            {isExpanded ? <ChevronLeft className="w-6 h-6 text-gray-400" /> : <ChevronRight className="w-6 h-6 text-gray-400" />}
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {isExpanded && (
          <motion.div 
            key={`expanded-${plan.id}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-gray-100"
          >
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-4 border-b border-gray-100 pb-4">
                <button 
                  onClick={() => setView('list')}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${view === 'list' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  List View
                </button>
                <button 
                  onClick={() => setView('calendar')}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${view === 'calendar' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  Calendar View
                </button>
              </div>

              {view === 'list' ? (
                <motion.div layout className="space-y-4">
                  {[...plan.calendar].sort((a, b) => (a.date || '').localeCompare(b.date || '')).map(post => (
                        <PostRow 
                          key={post.id} 
                          post={post} 
                          brand={brand}
                          shareFeedback={shareFeedback.filter(f => String(f.ideaId) === String(post.id))}
                          onDeleteFeedback={onDeleteFeedback}
                          isEditing={editingPostId === post.id}
                          sprintProgress={sprintProgress}
                          onEdit={() => handleEditPost(post)}
                          onDuplicate={() => onDuplicatePost(plan.id!, post.id)}
                          onRepurpose={() => onRepurposePost(plan.id!, post)}
                          onSave={(updatedPost) => handleSavePost(post.id, updatedPost)}
                          onCancel={handleCancelEdit}
                          onGenerateArtwork={(slideId, concept, type, useBrandElements, currentSlide, refinementPrompt, inspirationImage) => onGenerateArtwork(plan.id!, post.id, slideId, concept, type, useBrandElements, currentSlide, refinementPrompt, inspirationImage)}
                          onGenerateSeamlessCarousel={(baseConcept, onProgress) => onGenerateSeamlessCarousel && onGenerateSeamlessCarousel(plan.id!, post.id, baseConcept, onProgress, true)}
                          onOverlaySlides={(basePanoramaUrl, onProgress, useBrandElements, isCancelled) => onOverlaySlides && onOverlaySlides(plan.id!, post.id, basePanoramaUrl, onProgress, useBrandElements, isCancelled)}
                          onPublish={(platform, testMode) => onPublish(plan.id!, post.id, platform, testMode)}
                          onUploadArtwork={(slideId, file, currentSlide) => onUploadArtwork(plan.id!, post.id, slideId, file, currentSlide)}
                          onUpdateStatus={(status, scheduledAt, platform) => onUpdateStatus(plan.id!, post.id, status, scheduledAt, platform)}
                          onUpdatePlatforms={(platforms) => onUpdatePlatforms(plan.id!, post.id, platforms)}
                          onPreview={onPreview}
                          onSuggestEdit={() => onSuggestEdit(post.id)}
                          onApproveSuggestion={(suggestionId) => onApproveSuggestion(post.id, suggestionId)}
                          onRejectSuggestion={(suggestionId) => onRejectSuggestion(post.id, suggestionId)}
                          brandPlatforms={brandPlatforms}
                          productImages={productImages}
                          brandTemplates={brandTemplates}
                          brandBlueprints={brandBlueprints}
                          isAdmin={isAdmin}
                          hasApiKey={hasApiKey}
                          setToast={setToast}
                          setActiveTab={setActiveTab}
                          canEdit={canEdit}
                          isTrial={isTrial}
                          user={user}
                          editingData={editingPostId === post.id ? editingPostData : null}
                          setEditingData={setEditingPostData}
                          isSelected={selectedPostIds.has(post.id)}
                          onToggleSelect={() => onToggleSelectPost(post.id)}
                          onLogAudit={onLogAudit}
                          tokens={tokens}
                        />
                  ))}
                </motion.div>
              ) : (
                <CalendarView 
                  calendar={plan.calendar}
                  brand={brand}
                  onPreview={onPreview}
                  onUpdatePost={(postId, date) => {
                    const postToUpdate = plan.calendar.find(p => p.id === postId);
                    if (postToUpdate) {
                      handleSavePost(postId, { ...postToUpdate, date });
                    }
                  }}
                  onSavePost={(postId, updatedPost) => {
                    handleSavePost(postId, updatedPost);
                  }}
                  onDeletePost={handleDeletePost}
                />
              )}

              {/* Hidden Archive Summary for PDF Generation */}
              <div id={`archive-summary-${plan.id}`} style={{ position: 'absolute', left: '-9999px', top: 0, width: '800px', padding: '60px', background: 'white', color: '#000' }}>
                <div style={{ borderBottom: '2px solid #6366f1', paddingBottom: '20px', marginBottom: '40px' }}>
                  <h1 style={{ fontSize: '32px', fontWeight: '900', marginBottom: '8px', color: '#111827' }}>{brand?.companyName}</h1>
                  <p style={{ fontSize: '18px', fontWeight: '600', color: '#6366f1' }}>{plan.month} {plan.year} Content Plan Summary</p>
                  <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Generated on {new Date().toLocaleDateString()}</p>
                </div>

                {/* Goals and Highlights */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '50px', background: '#f9fafb', padding: '30px', borderRadius: '24px' }}>
                  <div>
                    <h3 style={{ fontSize: '12px', fontWeight: '900', color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '12px' }}>Monthly Goal</h3>
                    <p style={{ fontSize: '15px', color: '#1f2937', fontWeight: '500', lineHeight: '1.6' }}>{plan.goals || "No specific goal set for this month."}</p>
                  </div>
                  <div>
                    <h3 style={{ fontSize: '12px', fontWeight: '900', color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '12px' }}>Monthly Highlights</h3>
                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                      {(plan.highlights || []).map((h, i) => (
                        <li key={i} style={{ fontSize: '14px', color: '#374151', marginBottom: '6px', fontWeight: '500' }}>{h}</li>
                      ))}
                      {(!plan.highlights || plan.highlights.length === 0) && <li style={{ fontSize: '14px', color: '#9ca3af' }}>No highlights recorded.</li>}
                    </ul>
                  </div>
                </div>
                
                {[...plan.calendar].sort((a, b) => (a.date || '').localeCompare(b.date || '')).map((post: any, i: number) => (
                  <div key={post.id} style={{ marginBottom: '60px', pageBreakInside: 'avoid' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #f3f4f6', paddingBottom: '12px' }}>
                      <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#111827' }}>{i + 1}. {post.title || `${post.platform} Post`}</h2>
                      <span style={{ fontSize: '11px', fontWeight: '900', background: '#6366f1', color: 'white', padding: '4px 12px', borderRadius: '100px', textTransform: 'uppercase' }}>{post.platform}</span>
                    </div>

                    <p style={{ fontSize: '14px', color: '#4b5563', marginBottom: '20px', lineHeight: '1.6' }}>{post.description}</p>
                    
                    <div style={{ background: '#f3f4f6', padding: '20px', borderRadius: '16px', marginBottom: '24px' }}>
                       <div style={{ fontSize: '11px', fontWeight: '800', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '8px' }}>Final Copy</div>
                       <div style={{ fontSize: '14px', color: '#1f2937', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>{post.copy}</div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                      {(post.slides || []).map((slide: any) => (
                        <div key={slide.id} style={{ border: '1px solid #e5e7eb', borderRadius: '16px', padding: '12px', background: '#fff' }}>
                          <div style={{ position: 'relative', width: '100%', aspectRatio: '1', overflow: 'hidden', borderRadius: '8px', marginBottom: '12px', background: '#f9fafb' }}>
                    {slide.thumbnailUrl ? (
                      <img src={slide.thumbnailUrl} alt="Thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#9ca3af', fontWeight: 'bold' }}>NO IMAGE</div>
                    )}
                            <div style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.5)', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '8px', fontWeight: 'bold' }}>{slide.isVideo ? 'VIDEO' : 'IMAGE'}</div>
                          </div>
                          <div style={{ fontSize: '10px', color: '#4b5563', fontWeight: '600', lineHeight: '1.4' }}>{slide.concept.substring(0, 80)}...</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-6 border-t border-gray-100 flex flex-col xl:flex-row xl:justify-between xl:items-center gap-4">
                <button 
                  onClick={() => onAddPost(plan.id!)}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 text-indigo-600 font-bold hover:bg-slate-50 border border-slate-200 xl:border-none rounded-xl transition-all w-full xl:w-auto"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add Extra Post</span>
                </button>
                
                <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4 w-full xl:w-auto">
                   {isBulkGenerating && sprintProgress && (
                     <div className="flex flex-col gap-1 min-w-[200px] w-full lg:w-auto mb-2 lg:mb-0">
                       <div className="flex justify-between text-[10px] font-bold text-indigo-600 uppercase tracking-wider">
                         <span>Generating Slide {sprintProgress.current} of {sprintProgress.total}</span>
                         <span>{Math.round((sprintProgress.current / sprintProgress.total) * 100)}%</span>
                       </div>
                       <div className="h-1.5 w-full bg-indigo-100 rounded-full overflow-hidden">
                         <motion.div 
                           className="h-full bg-indigo-600"
                           initial={{ width: 0 }}
                           animate={{ width: `${(sprintProgress.current / sprintProgress.total) * 100}%` }}
                         />
                       </div>
                       <span className="text-[10px] text-gray-500 truncate max-w-[200px]">
                         Current: {sprintProgress.postTitle}
                       </span>
                     </div>
                   )}

                   <div className="flex flex-wrap gap-2.5 w-full justify-stretch sm:justify-end">
                    <button 
                      onClick={() => onArchive(plan.id!)}
                      disabled={isArchiving}
                      className="bg-gray-100 text-gray-700 px-4 sm:px-5 py-2.5 text-xs sm:text-sm rounded-xl font-bold hover:bg-gray-200 transition-all flex items-center justify-center gap-2 flex-grow sm:flex-grow-0"
                    >
                      {isArchiving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Archive className="w-5 h-5" />}
                      <span>{plan.status === 'archived' ? 'Re-Archive' : 'Finalize & Archive'}</span>
                    </button>

                    <div className="relative flex-grow sm:flex-grow-0">
                      <button 
                        onClick={() => {
                          if (!showDeletePlanConfirm) {
                            setShowDeletePlanConfirm(true);
                          } else {
                            onDeletePlan?.(plan.id!);
                            setShowDeletePlanConfirm(false);
                          }
                        }}
                        className={`w-full px-4 sm:px-5 py-2.5 text-xs sm:text-sm rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                          showDeletePlanConfirm
                            ? 'bg-red-600 text-white hover:bg-red-700 animate-pulse'
                            : 'bg-red-50 text-red-600 hover:bg-red-100'
                        }`}
                      >
                        <Trash2 className="w-5 h-5" />
                        <span>{showDeletePlanConfirm ? 'Confirm Delete?' : 'Delete Plan'}</span>
                      </button>
                      {showDeletePlanConfirm && (
                        <button
                          onClick={() => setShowDeletePlanConfirm(false)}
                          className="absolute -right-2 -top-2 bg-gray-200 hover:bg-gray-300 text-gray-700 p-1 rounded-full z-10 shadow-sm"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    <button 
                      onClick={() => setShowDuplicateModal(true)}
                      className="bg-purple-50 text-purple-600 px-4 sm:px-5 py-2.5 text-xs sm:text-sm rounded-xl font-bold hover:bg-purple-100 transition-all flex items-center justify-center gap-2 flex-grow sm:flex-grow-0"
                    >
                      <Copy className="w-5 h-5" />
                      <span>Duplicate Plan</span>
                    </button>

                    <button 
                      onClick={() => setShowRefineModal(true)}
                      className="bg-emerald-50 text-emerald-600 px-4 sm:px-5 py-2.5 text-xs sm:text-sm rounded-xl font-bold hover:bg-emerald-100 transition-all flex items-center justify-center gap-2 flex-grow sm:flex-grow-0"
                    >
                      <Sparkles className="w-5 h-5" />
                      <span>Refine Plan</span>
                    </button>

                    <button 
                      onClick={() => {
                        setRefinePrompt("CRITICAL: The Brand Profile has been updated. Please re-write all visual concepts, styling instructions, and overlay text to match the newest Brand Profile guidelines. Keep post dates, topics, and copies exactly the same, but update the visual aesthetic concepts to align perfectly with the updated brand templates and tone.");
                        setShowRefineModal(true);
                      }}
                      className="bg-blue-50 text-blue-600 px-4 sm:px-5 py-2.5 text-xs sm:text-sm rounded-xl font-bold hover:bg-blue-100 transition-all flex items-center justify-center gap-2 flex-grow sm:flex-grow-0"
                      title="Sync concepts and visual directions to match any newly uploaded brand templates or guidelines"
                    >
                      <RefreshCw className="w-5 h-5" />
                      <span>Sync to Brand</span>
                    </button>

                   {plan.status === 'approved' ? (
                     <>
                      {(!isAdmin && (brand?.subscription?.plan === 'solo' || isTrial)) ? (
                        <div className="bg-orange-50 text-orange-600 px-4 py-2.5 rounded-xl text-[10px] font-bold flex items-center justify-center gap-2 w-full sm:w-auto">
                          <AlertTriangle className="w-4 h-4 shrink-0" />
                          <span>{isTrial ? "Trial Tier" : "Solo Tier"}: Individual Artworks Only</span>
                        </div>
                      ) : (
                        <button 
                          onClick={() => onBulkGenerateArtwork(plan.id!)}
                          disabled={isBulkGenerating}
                          className="bg-indigo-50 text-indigo-600 px-4 sm:px-5 py-2.5 text-xs sm:text-sm rounded-xl font-bold hover:bg-indigo-100 transition-all flex items-center justify-center gap-2 disabled:opacity-50 flex-grow sm:flex-grow-0"
                        >
                          {isBulkGenerating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                          <span>{isBulkGenerating ? 'Sprinting...' : 'Generate All Artwork'}</span>
                        </button>
                      )}

                      <button 
                         onClick={() => onScheduleAll(plan)}
                         className="bg-emerald-600 text-white px-4 sm:px-5 py-2.5 text-xs sm:text-sm rounded-xl font-bold hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-100 flex-grow sm:flex-grow-0"
                       >
                         <CalendarIcon className="w-5 h-5" />
                         <span>Schedule All</span>
                       </button>

                      <button 
                        onClick={() => onUpdate(plan.id!, { status: 'draft' })}
                        className="bg-gray-100 text-gray-600 px-4 sm:px-5 py-2.5 text-xs sm:text-sm rounded-xl font-bold hover:bg-gray-200 transition-all flex items-center justify-center gap-2 flex-grow sm:flex-grow-0"
                      >
                        <RotateCcw className="w-5 h-5" />
                        <span>{t.revertToDraft}</span>
                      </button>
                     </>
                   ) : (
                    <button 
                      onClick={() => {
                        const updatedCalendar = plan.calendar.map(post => ({ ...post, status: 'approved' as const }));
                        onUpdate(plan.id!, { status: 'approved', calendar: updatedCalendar });
                      }}
                      className="bg-green-600 text-white px-4 sm:px-5 py-2.5 text-xs sm:text-sm rounded-xl font-bold hover:bg-green-700 transition-all shadow-lg shadow-green-100 flex items-center justify-center gap-2 flex-grow"
                    >
                      <CheckCircle2 className="w-5 h-5" />
                      <span>{t.approveEntirePlan}</span>
                    </button>
                   )}
                </div>
              </div>
            </div>
          </div>
              
              {selectedPostIds.size > 0 && (
                 <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-6 z-[60] border border-white/10">
                   <div className="flex flex-col">
                     <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none">{t.selected}</span>
                     <span className="text-xl font-bold">{selectedPostIds.size} {t.posts}</span>
                   </div>
                   <div className="w-px h-8 bg-white/10" />
                   <div className="flex items-center gap-2">
                     <button 
                       onClick={() => handleBulkAction('share')}
                       className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-xl text-xs font-bold transition-all"
                     >
                       <Share2 className="w-4 h-4" />
                       Share Selected
                     </button>
                     <button 
                       onClick={() => handleBulkAction('approve')}
                       className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-xl text-xs font-bold transition-all"
                     >
                       <CheckCircle2 className="w-4 h-4" />
                       Approve
                     </button>
                     <button 
                       onClick={() => handleBulkAction('delete')}
                       className={`flex items-center gap-2 px-4 py-2 ${showDeleteConfirm ? 'bg-red-800 animate-pulse' : 'bg-red-600 hover:bg-red-700'} rounded-xl text-xs font-bold transition-all`}
                     >
                       <Trash2 className="w-4 h-4" />
                       {showDeleteConfirm ? 'Confirm Delete?' : 'Delete'}
                     </button>
                     {showDeleteConfirm && (
                       <button onClick={() => setShowDeleteConfirm(false)} className="p-2 hover:bg-white/10 rounded-xl transition-all text-gray-400">
                         <X className="w-4 h-4" />
                       </button>
                     )}
                     <button 
                       onClick={() => onClearSelection()}
                       className="px-4 py-2 hover:bg-white/10 rounded-xl text-xs font-bold transition-all text-gray-400"
                     >
                       Cancel
                     </button>
                   </div>
                 </div>
              )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDuplicateModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden"
            >
              <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-purple-50/50">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-600 p-2 rounded-xl">
                    <Copy className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Duplicate & Refine Plan</h3>
                </div>
                <button onClick={() => setShowDuplicateModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-8 space-y-6">
                <p className="text-sm text-gray-600">
                  Duplicate this plan to create a new one. You can optionally provide a refinement prompt to adjust the content (e.g., "Translate everything to Portuguese" or "Make tone more casual").
                </p>
                <TextArea 
                  label="Refinement Prompt (Optional)" 
                  value={duplicatePrompt} 
                  onChange={setDuplicatePrompt} 
                  placeholder="e.g. Translate entire plan to Brazilian Portuguese..."
                  description="Leave empty for a direct copy."
                />
                <div className="flex gap-3">
                  <button 
                    onClick={() => setShowDuplicateModal(false)}
                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={async () => {
                      setIsDuplicatingPlan(true);
                      try {
                        await onDuplicatePlan(plan.id!, duplicatePrompt);
                        setShowDuplicateModal(false);
                        setDuplicatePrompt('');
                      } finally {
                        setIsDuplicatingPlan(false);
                      }
                    }}
                    disabled={isDuplicatingPlan}
                    className="flex-[2] py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition-all shadow-lg shadow-purple-100 flex items-center justify-center gap-2"
                  >
                    {isDuplicatingPlan ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                    <span>{isDuplicatingPlan ? 'Duplicating...' : 'Duplicate & Refine'}</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showRefineModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden"
            >
              <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-emerald-50/50">
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-600 p-2 rounded-xl">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Refine Plan</h3>
                </div>
                <button onClick={() => setShowRefineModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-8 space-y-6">
                <p className="text-sm text-gray-600">
                  Refine the content of this plan using AI. Your changes will be applied directly to the current plan without creating a new copy.
                </p>
                
                <div className="space-y-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <p className="text-sm font-semibold text-gray-700">Target Platforms (Optional)</p>
                  <p className="text-xs text-gray-500 -mt-2">Leave empty to keep current platforms, or select the channels you want this plan optimized for.</p>
                  <div className="flex flex-wrap gap-2">
                    {brandPlatforms.map(platform => (
                      <button
                        key={platform}
                        onClick={() => setRefinePlatforms(prev => 
                          prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
                        )}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${
                          refinePlatforms.includes(platform)
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {platform}
                      </button>
                    ))}
                  </div>
                </div>

                <TextArea 
                  label="Refinement Prompt" 
                  value={refinePrompt} 
                  onChange={setRefinePrompt} 
                  placeholder="e.g. Focus less on my 13 years of experience and more on specific results..."
                  description="Be specific about what you want to change."
                />
                <div className="flex gap-3">
                  <button 
                    onClick={() => setShowRefineModal(false)}
                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={async () => {
                      if (!refinePrompt.trim() && refinePlatforms.length === 0) return;
                      setIsRefiningPlan(true);
                      try {
                        await onRefinePlan(plan.id!, refinePrompt, refinePlatforms.length > 0 ? refinePlatforms : undefined);
                        setShowRefineModal(false);
                      } finally {
                        setIsRefiningPlan(false);
                      }
                    }}
                    disabled={isRefiningPlan || (!refinePrompt.trim() && refinePlatforms.length === 0)}
                    className="flex-[2] py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-100 flex items-center justify-center gap-2"
                  >
                    {isRefiningPlan ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                    <span>{isRefiningPlan ? 'Refining...' : 'Refine Plan'}</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PostRow = ({ 
  post, 
  brand,
  shareFeedback,
  onDeleteFeedback,
  isEditing, 
  onEdit, 
  onDuplicate,
  onRepurpose,
  onSave, 
  onCancel, 
  onGenerateArtwork,
  onGenerateSeamlessCarousel,
  onOverlaySlides,
  onPublish,
  onUploadArtwork,
  onUpdateStatus,
  onUpdatePlatforms,
  onPreview,
  onSuggestEdit,
  onApproveSuggestion,
  onRejectSuggestion,
  brandPlatforms,
  productImages,
  brandTemplates,
  brandBlueprints = [],
  isAdmin,
  hasApiKey,
  setToast,
  setActiveTab,
  canEdit,
  isTrial,
  user,
  editingData,
  setEditingData,
  isSelected,
  onToggleSelect,
  onLogAudit,
  sprintProgress,
  tokens
}: { 
  post: ContentIdea, 
  brand: BrandProfile | null,
  shareFeedback: ShareFeedback[],
  onDeleteFeedback: (id: string) => void,
  brandBlueprints?: any[],
  isEditing: boolean, 
  sprintProgress: { current: number; total: number; postTitle: string; postId?: string; slideId?: string } | null,
  onEdit: () => void, 
  onDuplicate: () => void,
  onRepurpose: () => void,
  onSave: (updatedPost: ContentIdea) => void, 
  onCancel: () => void, 
  onGenerateArtwork: (slideId: string, concept: string, type?: 'image' | 'video', useBrandElements?: boolean, currentSlide?: any, refinementPrompt?: string, inspirationImage?: string) => Promise<string | void>,
  onGenerateSeamlessCarousel?: (baseConcept: string, onProgress: (status: string) => void, useBrandElements?: boolean) => Promise<string | null>,
  onOverlaySlides?: (basePanoramaUrl: string, onProgress: (status: string) => void, useBrandElements?: boolean, isCancelled?: () => boolean) => Promise<boolean>,
  onPublish: (platform: string, testMode?: boolean) => Promise<void>,
  onUploadArtwork: (slideId: string, file: File, currentSlide?: any) => Promise<void>,
  onUpdateStatus: (status: 'approved' | 'rejected' | 'scheduled' | 'pending' | 'none', scheduledAt?: string, platform?: string) => void,
  onUpdatePlatforms: (platforms: string[]) => void,
  onPreview: (url: string, type: any, slide?: any, format?: 'feed' | 'story' | 'carousel' | string) => void,
  onSuggestEdit: () => void,
  onApproveSuggestion: (suggestionId: string) => void,
  onRejectSuggestion: (suggestionId: string) => void,
  brandPlatforms: string[],
  productImages: { id: string; url: string; name: string; description?: string; price?: string }[],
  brandTemplates: { id: string; url: string; name: string; description?: string }[],
  isAdmin: boolean,
  hasApiKey: boolean,
  setToast: (t: { message: string, type: 'success' | 'error' } | null) => void,
  setActiveTab: (tab: any) => void,
  canEdit: boolean,
  isTrial: boolean,
  isSelected: boolean,
  onToggleSelect: () => void,
  onLogAudit: (brand: BrandProfile, action: string, cost: number, tokensBefore: number, tokensAfter: number, planId?: string, status?: 'success' | 'failed' | 'started', metadata?: any) => Promise<void>,
  user: User | null,
  editingData: { copy: string; slides: any[]; linkedinFormat?: 'pdf' | 'gallery'; format?: string } | null,
  setEditingData: (data: { copy: string; slides: any[]; linkedinFormat?: 'pdf' | 'gallery'; format?: string } | null) => void,
  tokens: number
}) => {
  const { t, isBrazil } = useTranslation();
  const [selectedSlideIds, setSelectedSlideIds] = useState<Set<string>>(new Set());
  const [loadingSlideId, setLoadingSlideId] = useState<string | null>(null);
  const [loadingType, setLoadingType] = useState<'generating' | 'uploading' | 'analyzing' | null>(null);
  const [showBrandPrompt, setShowBrandPrompt] = useState<{ slideId: string; concept: string; type: 'image' | 'video' } | null>(null);
  const [quickFix, setQuickFix] = useState<{ slideId: string; text: string } | null>(null);
  const [activeEditorSlideId, setActiveEditorSlideId] = useState<string | null>(null);
  const [showScheduler, setShowScheduler] = useState<string | null>(null); // platform name
  const [scheduleTime, setScheduleTime] = useState(() => {
    const anyScheduledTime = post.scheduledAt || Object.values(post.platformStatus || {}).find(s => s.scheduledAt)?.scheduledAt;
    if (anyScheduledTime) {
      try {
        return format(new Date(anyScheduledTime), "yyyy-MM-dd'T'HH:mm");
      } catch (e) {
        return format(parseLocalDate(post.date), "yyyy-MM-dd'T'HH:mm");
      }
    }
    return format(parseLocalDate(post.date), "yyyy-MM-dd'T'HH:mm");
  });

  const [refinePrompt, setRefinePrompt] = useState('');
  const [isRefining, setIsRefining] = useState<string | null>(null); // 'copy', 'reflow' or slideId
  const [localPublishing, setLocalPublishing] = useState<Set<string>>(new Set());

  // Local derived state from editingData
  const copy = editingData?.copy || post.copy;
  const slides = editingData?.slides || post.slides || [];
  const linkedinFormat = editingData?.linkedinFormat || post.linkedinFormat || 'pdf';
  const postFormat = (editingData?.format || post.format || 'feed') as 'feed' | 'story' | 'carousel';

  const [isGeneratingSeamless, setIsGeneratingSeamless] = useState(false);
  const [seamlessProgress, setSeamlessProgress] = useState('');
  const [seamlessBaseConcept, setSeamlessBaseConcept] = useState('');
  const [isOptimizingPrompt, setIsOptimizingPrompt] = useState(false);
  const [panoramaPreviewUrl, setPanoramaPreviewUrl] = useState<string | null>(null);
  const [isUploadingCustomPanorama, setIsUploadingCustomPanorama] = useState(false);
  const [isOverlaying, setIsOverlaying] = useState(false);
  const cancelRef = React.useRef(false);

  const handleOptimizePrompt = async () => {
    setIsOptimizingPrompt(true);
    setSeamlessProgress("Analyzing slides & optimizing panorama concept with AI...");
    try {
      const slidesList = (slides || []).map((s: any) => s.concept).filter(Boolean);
      const optimized = await generatePanoramaBackgroundConcept(
        post.copy || '',
        slidesList,
        brand?.companyName || '',
        brand?.questionnaire?.visualAesthetic || brand?.brandingDetails || ''
      );
      if (optimized) {
        setSeamlessBaseConcept(optimized);
        setToast({ message: "Background scenery concept optimized successfully!", type: "success" });
      }
    } catch (err: any) {
      console.error(err);
      setToast({ message: "Could not optimize prompt automatically. Feel free to edit manually.", type: "error" });
    } finally {
      setIsOptimizingPrompt(false);
      setSeamlessProgress("");
    }
  };

  const handleRunStep1 = async () => {
    if (!onGenerateSeamlessCarousel) return;
    setIsGeneratingSeamless(true);
    setSeamlessProgress("Initializing panorama background generator...");
    cancelRef.current = false;
    try {
      const panoramaUrl = await onGenerateSeamlessCarousel(seamlessBaseConcept, (prog) => {
        setSeamlessProgress(prog);
      });
      if (panoramaUrl) {
        setPanoramaPreviewUrl(panoramaUrl);
        setToast({ message: "Base panorama background generated successfully!", type: "success" });
      }
    } catch (e: any) {
      console.error(e);
    } finally {
      setIsGeneratingSeamless(false);
      setSeamlessProgress("");
    }
  };

  const handleUploadCustomPanorama = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (isTrial && file.size > 3 * 1024 * 1024) {
      setToast({ message: "File is too large. Please use a file smaller than 3MB on the trial plan.", type: 'error' });
      return;
    } else if (file.size > 15 * 1024 * 1024) {
      setToast({ message: "File is too large. Max size is 15MB.", type: 'error' });
      return;
    }

    setIsUploadingCustomPanorama(true);
    setSeamlessProgress("Uploading custom panorama backdrop to secure storage...");
    try {
      const hash = await getFileHash(file);
      const storageRef = ref(storage, `artwork/${user?.uid}/panoramas/custom_${hash}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      setPanoramaPreviewUrl(url);
      setToast({ message: "Custom panorama backdrop uploaded successfully!", type: "success" });
    } catch (err: any) {
      console.error("Custom panorama upload failed:", err);
      setToast({ message: `Failed to upload custom panorama: ${err.message || err}`, type: "error" });
    } finally {
      setIsUploadingCustomPanorama(false);
      setSeamlessProgress("");
    }
  };

  const handleRunStep2 = async () => {
    if (!onOverlaySlides || !panoramaPreviewUrl) return;
    setIsOverlaying(true);
    setSeamlessProgress("Starting slicing and layout overlays...");
    cancelRef.current = false;
    try {
      const success = await onOverlaySlides(panoramaPreviewUrl, (prog) => {
        setSeamlessProgress(prog);
      }, true, () => cancelRef.current);
      if (success) {
        setPanoramaPreviewUrl(null);
        setSeamlessProgress("");
      }
    } catch (e: any) {
      console.error(e);
    } finally {
      setIsOverlaying(false);
      setSeamlessProgress("");
    }
  };

  const handleStopProcess = () => {
    cancelRef.current = true;
    setIsGeneratingSeamless(false);
    setIsOverlaying(false);
    setSeamlessProgress("Stopping process...");
    setToast({ message: "Generation stopped by user.", type: "success" });
  };

  useEffect(() => {
    if (postFormat === 'carousel' && !seamlessBaseConcept) {
      // Extract brand-specific options
      const aestheticStr = (brand?.questionnaire?.visualAesthetic || brand?.brandingDetails || "").toLowerCase();
      
      const colors = new Set<string>();
      const styles = new Set<string>();
      const scenery = new Set<string>();

      // Known colors list
      const colorKeywords = [
        'sage green', 'mint green', 'forest green', 'olive green', 'light green', 'emerald green',
        'soft blue', 'navy blue', 'sky blue', 'midnight blue', 'royal blue', 'teal', 'cyan',
        'pastel pink', 'rose pink', 'blush pink', 'crimson', 'ruby red', 'terracotta',
        'warm oak', 'walnut wood', 'light wood', 'birch wood', 'golden brass', 'bronze', 'copper',
        'cream', 'sand beige', 'ivory', 'vanilla', 'pure white', 'off-white', 'minimalist gray',
        'slate gray', 'charcoal', 'lavender', 'violet', 'peach', 'amber', 'mustard gold'
      ];

      // Known style list
      const styleKeywords = [
        'minimalist', 'modern', 'organic', 'professional', 'corporate', 'soft', 'bright', 'clean', 
        'luxurious', 'warm', 'editorial', 'abstract', 'sleek', 'high-contrast', 'pastel', 'vibrant',
        'cozy', 'geometric', 'watercolor', 'gradient', 'airy', 'spacious', 'artistic'
      ];

      // Known scenery list
      const sceneryKeywords = [
        'dental office', 'medical clinic', 'dental studio', 'hospital room', 'laboratory', 'wellness spa',
        'office desk', 'conference room', 'corporate lounge', 'executive workspace', 'creative studio',
        'living room', 'modern library', 'natural garden', 'leafy foliage', 'sunny skies', 'soft clouds',
        'beach shore', 'hills', 'architecture', 'room interior', 'indoor plants', 'bamboo background', 'oak tables'
      ];

      // Scan brand questionnaires
      colorKeywords.forEach(keyword => {
        if (aestheticStr.includes(keyword)) colors.add(keyword);
      });
      styleKeywords.forEach(keyword => {
        if (aestheticStr.includes(keyword)) styles.add(keyword);
      });

      // Scan slides
      (slides || []).forEach((slide: any) => {
        const text = (slide.concept || "").toLowerCase() + " " + (slide.overlayText || "").toLowerCase();
        
        colorKeywords.forEach(keyword => {
          if (text.includes(keyword)) colors.add(keyword);
        });
        styleKeywords.forEach(keyword => {
          if (text.includes(keyword)) styles.add(keyword);
        });
        sceneryKeywords.forEach(keyword => {
          if (text.includes(keyword)) scenery.add(keyword);
        });
      });

      // Simple keywords search in overall post copy as second layer
      const copyLower = (post.copy || "").toLowerCase();
      colorKeywords.forEach(keyword => {
        if (copyLower.includes(keyword)) colors.add(keyword);
      });

      // Fallbacks if empty
      if (colors.size === 0) {
        colors.add('light cream');
        colors.add('soft ambient warm tones');
      }
      if (styles.size === 0) {
        styles.add('clean');
        styles.add('minimalist');
        styles.add('modern');
      }

      const styleList = Array.from(styles).slice(0, 4).join(', ');
      const colorList = Array.from(colors).slice(0, 3).join(' and ');
      
      let sceneryDesc = "";
      if (scenery.size > 0) {
        sceneryDesc = ` featuring a subtle theme of ${Array.from(scenery).slice(0, 3).join(', ')}`;
      } else {
        // Fallback guess based on company name/categories
        const companyName = (brand?.companyName || "").toLowerCase();
        if (companyName.includes('dental') || companyName.includes('smile') || companyName.includes('ortho') || copyLower.includes('dental') || copyLower.includes('teeth')) {
          sceneryDesc = " featuring a subtle theme of a bright professional wellness dental clinic with peaceful plants";
        } else if (companyName.includes('hospital') || companyName.includes('clinic') || companyName.includes('doc') || companyName.includes('med')) {
          sceneryDesc = " featuring a subtle medical studio or modern health workspace theme";
        } else {
          sceneryDesc = " with modern abstract fluid organic lines and generous spacious negative space";
        }
      }

      const builtPrompt = `A premium, continuous widescreen landscape panorama background. Designed in a ${styleList} aesthetic, themed around a beautiful seamless blend of ${colorList} colors${sceneryDesc}. Extremely clean with generous negative space, strictly textless, no borders, ready to serve as a matching backdrop.`;
      setSeamlessBaseConcept(builtPrompt);
    }
  }, [postFormat, post, slides, seamlessBaseConcept, brand]);

  const updateEditingData = (updates: Partial<{ copy: string; slides: any[]; linkedinFormat?: 'pdf' | 'gallery'; format?: string }>) => {
    if (editingData) {
      setEditingData({ ...editingData, ...updates });
    }
  };

  useEffect(() => {
    if (showScheduler) {
      const el = document.getElementById(`post-${post.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showScheduler, post.id]);

  const handleInspirationUpload = async (slideId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (isTrial && file.size > 3 * 1024 * 1024) {
      setToast({ message: "Inspiration image is too large. Please use a file smaller than 3MB on the trial plan.", type: 'error' });
      return;
    }

    setLoadingSlideId(slideId);
    setLoadingType('analyzing');
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read inspiration image.'));
        reader.readAsDataURL(file);
      });

      const result = await analyzeInspirationImage(base64, file.type, slides.find(s => s.id === slideId)?.concept);
      handleConceptChange(slideId, result.aesthetic);
    } catch (err: any) {
      console.error("Failed to analyze inspiration image:", err);
      const errorMsg = err.message || (typeof err === 'string' ? err : JSON.stringify(err));
      if (errorMsg.includes('403') || errorMsg.includes('API_KEY_HTTP_REFERRER_BLOCKED') || errorMsg.includes('Forbidden')) {
        setToast({ message: `API Key restricted (403). For Google Cloud, referrers are often hidden in previews. Use an unrestricted API key, or if restricting, try '*.run.app/*' or use IP/API-level restrictions instead.`, type: 'error' });
      } else {
        setToast({ message: `Failed to analyze image: ${errorMsg}`, type: 'error' });
      }
    } finally {
      setLoadingSlideId(null);
      setLoadingType(null);
    }
  };

  const handleConceptChange = (slideId: string, newConcept: string) => {
    updateEditingData({
      slides: slides.map(s => s.id === slideId ? { ...s, concept: newConcept } : s)
    });
  };

  const handleOverlayTextChange = (slideId: string, newOverlay: string) => {
    updateEditingData({
      slides: slides.map(s => {
        if (s.id !== slideId) return s;
        let updatedSlide = { ...s, overlayText: newOverlay };
        
        if (s.layoutTemplate && s.layoutTemplate.boxes) {
          const bpLt = JSON.parse(JSON.stringify(s.layoutTemplate));
          if (bpLt.boxes && bpLt.boxes.length > 0) {
            bpLt.boxes = parseOverlayTextToBoxes(newOverlay, bpLt.boxes);
          }
          updatedSlide.layoutTemplate = bpLt;
        }
        return updatedSlide;
      })
    });
  };

  const toggleProductSelect = (slideId: string, product: { id: string; url: string; name: string; description?: string; price?: string }) => {
    updateEditingData({
      slides: slides.map(s => {
        if (s.id !== slideId) return s;
        const current = s.selectedProducts || [];
        const exists = current.find(p => p.id === product.id);
        if (exists) {
          return { ...s, selectedProducts: current.filter(p => p.id !== product.id) };
        } else {
          return { ...s, selectedProducts: [...current, product] };
        }
      })
    });
  };

  const handleQuickAddImage = async (slideId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (isTrial && file.size > 3 * 1024 * 1024) {
      setToast({ message: "Image is too large. Please use a file smaller than 3MB on the trial plan.", type: 'error' });
      return;
    }

    setLoadingSlideId(slideId);
    setLoadingType('uploading');
    try {
      const hash = await getFileHash(file);
      const storageRef = ref(storage, `artwork/${user.uid}/quick-add/qa_${hash}_${file.name}`);
      
      // Attempt to reuse existing
      let url = "";
      try {
        url = await getDownloadURL(storageRef);
      } catch (e) {
        await uploadBytes(storageRef, file);
        url = await getDownloadURL(storageRef);
      }
      
      updateEditingData({
        slides: slides.map(s => {
          if (s.id !== slideId) return s;
          const current = s.quickAddImages || [];
          return { ...s, quickAddImages: [...current, { id: Math.random().toString(36).substr(2, 9), url, name: file.name }] };
        })
      });
    } catch (err) {
      console.error("Quick add upload failed:", err);
      setToast({ message: "Failed to upload image.", type: 'error' });
    } finally {
      setLoadingSlideId(null);
      setLoadingType(null);
    }
  };

  const removeQuickAddImage = (slideId: string, imageId: string) => {
    updateEditingData({
      slides: slides.map(s => {
        if (s.id !== slideId) return s;
        const current = s.quickAddImages || [];
        return { ...s, quickAddImages: current.filter(img => img.id !== imageId) };
      })
    });
  };

  const handleGenerate = async (
    slideId: string, 
    concept: string, 
    type: 'image' | 'video' = 'image', 
    useBrandElements: boolean = true, 
    refinementPrompt?: string, 
    inspirationImage?: string,
    customSlideData?: any
  ) => {
    setLoadingSlideId(slideId);
    setLoadingType('generating');
    setShowBrandPrompt(null);
    try {
      const currentSlide = customSlideData || slides?.find(s => s.id === slideId);
      await onGenerateArtwork(slideId, concept, type, useBrandElements, currentSlide, refinementPrompt, inspirationImage);
    } catch (err) {
      console.error("handleGenerate failed:", err);
    } finally {
      setLoadingSlideId(null);
      setLoadingType(null);
    }
  };

  const handleQuickFix = async (slideId: string) => {
    const slide = slides.find(s => s.id === slideId);
    if (!slide || !slide.url) return;
    setQuickFix({ slideId, text: '' });
  };

  const submitQuickFix = () => {
    if (!quickFix || !quickFix.text.trim()) return;
    const slide = slides.find(s => s.id === quickFix.slideId);
    if (!slide) return;

    handleGenerate(quickFix.slideId, slide.concept, slide.isVideo ? 'video' : 'image', true, `Quick fix: ${quickFix.text}`, slide.url);
    setQuickFix(null);
  };

  const triggerGenerate = (slideId: string, concept: string, type: 'image' | 'video') => {
    const hasProducts = productImages && productImages.length > 0;
    const hasTemplates = brandTemplates && brandTemplates.length > 0;
    
    if (hasProducts || hasTemplates) {
      setShowBrandPrompt({ slideId, concept, type });
    } else {
      // If no brand elements exist at all, just generate normally
      handleGenerate(slideId, concept, type, false);
    }
  };

  const handleFileChange = async (slideId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (isTrial && file.size > 3 * 1024 * 1024) {
      setToast({ message: "File is too large. Please use a file smaller than 3MB on the trial plan.", type: 'error' });
      return;
    }

    setLoadingSlideId(slideId);
    setLoadingType('uploading');
    try {
      const currentSlide = slides?.find(s => s.id === slideId);
      await onUploadArtwork(slideId, file, currentSlide);
    } catch (err) {
      console.error("handleFileChange failed:", err);
    } finally {
      setLoadingSlideId(null);
      setLoadingType(null);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'Facebook': return <Facebook className="w-4 h-4 text-blue-600" />;
      case 'LinkedIn': return <Linkedin className="w-4 h-4 text-blue-700" />;
      case 'Instagram': return <Instagram className="w-4 h-4 text-pink-600" />;
      case 'Threads': return <Share2 className="w-4 h-4 text-black" />; // Using Share2 as fallback until Threads icon available
      case 'Twitter': return <Twitter className="w-4 h-4 text-sky-500" />;
      case 'TikTok': return <Youtube className="w-4 h-4 text-black" />; // Fallback
      case 'Youtube': return <Youtube className="w-4 h-4 text-red-600" />;
      default: return <Share2 className="w-4 h-4 text-gray-400" />;
    }
  };

  const handleDownload = async (url: string, slideIndex: number, isVideo?: boolean) => {
    try {
      const slide = post.slides?.[slideIndex] as any;
      if (slide?.layoutTemplate && !isVideo) {
        const lt = slide.layoutTemplate;
        if ((lt.canvasEditingMode || "original") === "original") {
          const link = document.createElement("a");
          link.download = `slide-export-${slide.id || "render"}.png`;
          link.href = slide.url || slide.thumbnailUrl || slide.backgroundSliceUrl || url;
          link.target = "_blank";
          link.click();
          return;
        }

        setToast?.({ message: "Baking high-resolution rendering with crisp vector layers...", type: "success" as any });
        
        const bakeContainer = document.createElement("div");
        bakeContainer.style.position = "absolute";
        bakeContainer.style.left = "-9999px";
        bakeContainer.style.top = "-9999px";
        bakeContainer.style.width = "1085px";
        bakeContainer.style.height = (editingData?.format || post.format) === "story" ? "1920px" : "1085px";
        bakeContainer.style.containerType = "inline-size";
        bakeContainer.style.overflow = "hidden";
        
        const bgUrl = lt.backgroundImage || slide.url || slide.backgroundSliceUrl || "";
        
        // Background
        if (lt.backgroundType === "solid") {
          bakeContainer.style.backgroundColor = lt.backgroundColor || "#FAF8F4";
        } else if (lt.backgroundType === "gradient") {
          bakeContainer.style.backgroundImage = lt.backgroundGradient || "linear-gradient(to bottom, #FAF8F4, #E2DCD5)";
        } else if (lt.backgroundType === "image" && bgUrl) {
          const bgImg = document.createElement("img");
          bgImg.src = bgUrl;
          bgImg.crossOrigin = "anonymous";
          bgImg.referrerPolicy = "no-referrer";
          bgImg.style.position = "absolute";
          bgImg.style.inset = "0";
          bgImg.style.width = "100%";
          bgImg.style.height = "100%";
          bgImg.style.objectFit = "cover";
          bakeContainer.appendChild(bgImg);
        }
        
        // Content Plaque
        if (lt.enableContentPlaque) {
          const plaque = document.createElement("div");
          plaque.style.position = "absolute";
          plaque.style.top = `${lt.plaqueTop ?? 15}%`;
          plaque.style.left = `${lt.plaqueLeft ?? 10}%`;
          plaque.style.width = `${lt.plaqueWidth ?? 80}%`;
          plaque.style.height = `${lt.plaqueHeight ?? 70}%`;
          plaque.style.backgroundColor = lt.plaqueBgColor || lt.plaqueColor || "#FFFFFF";
          plaque.style.opacity = String(lt.plaqueOpacity ?? 0.95);
          plaque.style.borderRadius = `${(lt.plaqueBorderRadius ?? 16) * 2.17}px`;
          plaque.style.padding = `${(lt.plaquePadding ?? 16) * 2.17}px`;
          plaque.style.boxShadow = "0 12px 60px rgba(0,0,0,0.03)";
          plaque.style.border = "1px solid rgba(0,0,0,0.05)";
          bakeContainer.appendChild(plaque);
        }
        
        // Header
        if (lt.enableHeader) {
          const header = document.createElement("div");
          header.style.position = "absolute";
          header.style.top = "0";
          header.style.left = "0";
          header.style.right = "0";
          header.style.display = "flex";
          header.style.alignItems = "center";
          header.style.justifyContent = "space-between";
          header.style.padding = "0 40px";
          header.style.height = `${lt.headerHeight || 10}%`;
          header.style.backgroundColor = lt.headerBgColor || "transparent";
          header.style.color = lt.headerTextColor || "#333333";
          header.style.fontSize = `${(lt.headerFontSize || 12) * 2.17}px`;
          header.style.fontFamily = "Inter, sans-serif";
          header.style.fontWeight = "bold";
          header.style.letterSpacing = "0.05em";
          
          const headerSpan = document.createElement("span");
          headerSpan.innerText = "";
          header.appendChild(headerSpan);
          
          if (lt.headerShowLogo && brand?.logoUrl) {
            const logo = document.createElement("img");
            logo.src = brand.logoUrl;
            logo.crossOrigin = "anonymous";
            logo.referrerPolicy = "no-referrer";
            logo.style.height = "40%";
            logo.style.objectFit = "contain";
            header.appendChild(logo);
          }
          bakeContainer.appendChild(header);
        }
        
        // Brand Logo if logoPosition is active and not none/header/footer, and URL exists
        if (lt.logoPosition !== "none" && lt.logoPosition !== "header" && lt.logoPosition !== "footer" && brand?.logoUrl) {
          const logoWrapper = document.createElement("div");
          logoWrapper.style.position = "absolute";
          
          const leftVal = lt.logoPosition === "floating"
            ? `${lt.logoLeft || 5}%`
            : (lt.logoAlignment || "left") === "left"
              ? "5%"
              : lt.logoAlignment === "center"
                ? "42.5%"
                : "80%";
          const topVal = lt.logoPosition === "floating"
            ? `${lt.logoTop || 5}%`
            : (lt.logoPosition || "content-top") === "content-top"
              ? "5%"
              : "85%";
          const sizeVal = `${lt.logoSize || 15}%`;

          logoWrapper.style.left = leftVal;
          logoWrapper.style.top = topVal;
          logoWrapper.style.width = sizeVal;
          logoWrapper.style.height = sizeVal;
          logoWrapper.style.display = "flex";
          logoWrapper.style.justifyContent = (lt.logoAlignment || "left") === "center" ? "center" : (lt.logoAlignment || "left") === "right" ? "flex-end" : "flex-start";
          logoWrapper.style.alignItems = "center";
          
          const logo = document.createElement("img");
          logo.src = brand.logoUrl;
          logo.crossOrigin = "anonymous";
          logo.referrerPolicy = "no-referrer";
          logo.style.maxWidth = "100%";
          logo.style.maxHeight = "100%";
          logo.style.objectFit = "contain";
          
          logoWrapper.appendChild(logo);
          bakeContainer.appendChild(logoWrapper);
        }
        
        // Custom Text Boxes
        if (lt.boxes && lt.boxes.length > 0) {
          lt.boxes.forEach((box: any) => {
            if (box.type === 'logo') {
              const wrapperDiv = document.createElement("div");
              wrapperDiv.style.position = "absolute";
              wrapperDiv.style.top = `${box.top}%`;
              wrapperDiv.style.left = `${box.left}%`;
              wrapperDiv.style.width = `${box.width}%`;
              wrapperDiv.style.height = `${box.height}%`;
              wrapperDiv.style.display = "flex";
              wrapperDiv.style.alignItems = "center";
              wrapperDiv.style.justifyContent = "center";
              wrapperDiv.style.overflow = "hidden";

              if (brand?.logoUrl) {
                const img = document.createElement("img");
                img.src = brand.logoUrl;
                img.crossOrigin = "anonymous";
                img.referrerPolicy = "no-referrer";
                img.style.maxWidth = "100%";
                img.style.maxHeight = "100%";
                img.style.objectFit = "contain";
                wrapperDiv.appendChild(img);
              }
              bakeContainer.appendChild(wrapperDiv);
              return;
            }

            const textDiv = document.createElement("div");
            textDiv.style.position = "absolute";
            textDiv.style.top = `${box.top}%`;
            textDiv.style.left = `${box.left}%`;
            textDiv.style.width = `${box.width}%`;
            textDiv.style.height = `${box.height}%`;
            textDiv.style.display = "flex";
            textDiv.style.flexDirection = "column";
            textDiv.style.justifyContent = box.type === "heading" ? "flex-end" : "center";
            textDiv.style.textAlign = box.align || "left";
            textDiv.style.alignItems = box.align === "center" ? "center" : box.align === "right" ? "flex-end" : "flex-start";
            textDiv.style.color = box.color || "#333333";
            const rawFontSize = box.fontSize && box.fontSize < 12 
              ? box.fontSize * 10.85 
              : (box.fontSize || 16) * 2.17;
            textDiv.style.fontSize = `${rawFontSize}px`;
            textDiv.style.fontWeight = box.fontWeight === "bold" ? "700" : box.fontWeight === "medium" ? "500" : "400";
            textDiv.style.fontFamily = box.fontFamily === "serif" || box.fontFamily === "font-serif" 
              ? "Georgia, serif" 
              : box.fontFamily === "mono" || box.fontFamily === "font-mono" 
                ? "'JetBrains Mono', monospace" 
                : box.fontFamily === "display" || box.fontFamily === "font-display" 
                  ? "'Space Grotesk', sans-serif" 
                  : "Inter, system-ui, sans-serif";
            textDiv.style.lineHeight = "1.4";
            
            const innerSpan = document.createElement("span");
            innerSpan.style.whiteSpace = "pre-wrap";
            innerSpan.innerText = box.text;
            textDiv.appendChild(innerSpan);
            
            bakeContainer.appendChild(textDiv);
          });
        }
        
        // Footer
        if (lt.enableFooter) {
          const footer = document.createElement("div");
          footer.style.position = "absolute";
          footer.style.bottom = "0";
          footer.style.left = "0";
          footer.style.right = "0";
          footer.style.display = "flex";
          footer.style.alignItems = "center";
          footer.style.justifyContent = "space-between";
          footer.style.padding = "0 40px";
          footer.style.height = `${lt.footerHeight || 10}%`;
          footer.style.backgroundColor = lt.footerBgColor || "transparent";
          footer.style.color = lt.footerTextColor || "#666666";
          footer.style.fontSize = "22px";
          footer.style.fontFamily = "Inter, sans-serif";
          
          const label = document.createElement("span");
          label.style.fontWeight = "bold";
          label.innerText = lt.footerText || brand?.companyName || "";
          footer.appendChild(label);
          
          if (lt.footerShowPageNum && postFormat === 'carousel' && post.slides.length > 1 && lt.enableFooter) {
            const pageNum = document.createElement("span");
            pageNum.style.fontFamily = "monospace";
            pageNum.style.fontWeight = "bold";
            const pageStr = `${String(slideIndex + 1).padStart(2, '0')} / ${String(post.slides.length).padStart(2, '0')}`;
            pageNum.innerText = pageStr;
            footer.appendChild(pageNum);
          }
          bakeContainer.appendChild(footer);
        }
        
        document.body.appendChild(bakeContainer);
        
        const imgs = bakeContainer.querySelectorAll("img");
        if (imgs.length > 0) {
          await Promise.all(Array.from(imgs).map(img => {
            return new Promise((resolve) => {
              if (img.complete) resolve(null);
              else {
                img.onload = () => resolve(null);
                img.onerror = () => resolve(null);
              }
            });
          }));
        }
        
        await new Promise(r => setTimeout(r, 400));
        
        try {
          const dataUrl = await htmlToImage.toPng(bakeContainer, {
            cacheBust: true,
            pixelRatio: 1,
          });
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = `artwork-${post.id}-slide-${slideIndex + 1}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (e) {
          console.error("Critical baking error, falling back to legacy background download:", e);
          const response = await fetch(url, { referrerPolicy: 'no-referrer' });
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `artwork-${post.id}-slide-${slideIndex + 1}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } finally {
          document.body.removeChild(bakeContainer);
        }
        return;
      }

      const response = await fetch(url, { referrerPolicy: 'no-referrer' });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `artwork-${post.id}-slide-${slideIndex + 1}.${isVideo ? 'mp4' : 'png'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      // Fallback: try opening in a new tab if blob download fails
      const link = document.createElement('a');
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
    }
  };

  const handleRefine = async (type: 'copy' | 'concept', slideId?: string) => {
    if (!brand || !refinePrompt.trim()) return;
    
    // Check tokens for refinement (1 token)
    const tokenCost = 1;
    if (!isAdmin && tokens < tokenCost) {
      setToast({ message: `Insufficient tokens. You need ${tokenCost} token to refine content.`, type: 'error' });
      return;
    }

    setIsRefining(slideId || 'copy');
    try {
      const currentText = type === 'copy' ? copy : slides.find(s => s.id === slideId)?.concept || '';
      
      const parentContext = {
        copy,
        slides: slides.map((s: any) => ({
          id: s.id,
          concept: s.concept,
          overlayText: s.overlayText
        }))
      };

      const refined = await refineContent(brand, currentText, refinePrompt, type, parentContext, slideId);
      
      if (type === 'copy') {
        updateEditingData({ copy: refined });
      } else if (slideId) {
        handleConceptChange(slideId, refined);
      }
      
      // Deduct token
      if (!isAdmin && brand.id) {
        const brandRef = doc(db, 'brands', brand.id);
        const tokensBefore = tokens;
        await updateDoc(doc(db, 'users', brand.ownerId), { tokens: increment(-tokenCost) });
        await onLogAudit(brand, `refine_${type}`, 0.1, tokensBefore, tokensBefore - tokenCost, undefined, 'success', { slideId });
      }

      setRefinePrompt('');
      setToast({ message: "Content refined successfully!", type: 'success' });
    } catch (err: any) {
      console.error("Refinement error:", err);
      const errorMsg = err.message || (typeof err === 'string' ? err : JSON.stringify(err));
      setToast({ message: `Refinement failed: ${errorMsg}`, type: 'error' });
    } finally {
      setIsRefining(null);
    }
  };

  const toggleSlideSelection = (slideId: string) => {
    setSelectedSlideIds(prev => {
      const next = new Set(prev);
      if (next.has(slideId)) next.delete(slideId);
      else next.add(slideId);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedSlideIds.size === slides.length) {
      setSelectedSlideIds(new Set());
    } else {
      setSelectedSlideIds(new Set(slides.map(s => s.id)));
    }
  };

  const handleDeleteSelected = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (selectedSlideIds.size === 0) return;
    
    if (selectedSlideIds.size >= slides.length) {
      setToast({ message: "A post must have at least one slide remaining.", type: 'error' });
      return;
    }

    const count = selectedSlideIds.size;
    updateEditingData({
      slides: slides.filter(s => !selectedSlideIds.has(s.id))
    });
    setSelectedSlideIds(new Set());
    
    setTimeout(() => {
      setToast({ message: `${count} slides removed. Use "Reflow Carousel" to update the story flow.`, type: 'success' });
    }, 100);
  };

  const handleRemoveSlide = (e: React.MouseEvent, slideId: string) => {
    e.stopPropagation();
    e.preventDefault();
    if (slides.length <= 1) {
      setToast({ message: "A post must have at least one slide.", type: 'error' });
      return;
    }
    updateEditingData({
      slides: slides.filter(s => s.id !== slideId)
    });
    
    if (selectedSlideIds.has(slideId)) {
      setSelectedSlideIds(prev => {
        const next = new Set(prev);
        next.delete(slideId);
        return next;
      });
    }

    setTimeout(() => {
      setToast({ message: "Slide removed. You may want to 'Reflow Carousel' to fix the narrative.", type: 'success' });
    }, 100);
  };

  const handleReflow = async () => {
    if (!brand || slides.length === 0) return;
    setIsRefining('reflow');
    try {
      const result = await reflowCarousel(brand, copy, slides.map(s => ({ id: s.id, concept: s.concept })));
      updateEditingData({
        copy: result.copy,
        slides: slides.map(s => {
          const updated = result.slides.find(rs => rs.id === s.id);
          return updated ? { ...s, concept: updated.concept } : s;
        })
      });
      setToast({ message: "Carousel reflowed successfully!", type: 'success' });
    } catch (err: any) {
      console.error("Reflow error:", err);
      const errorMsg = err.message || (typeof err === 'string' ? err : JSON.stringify(err));
      setToast({ message: `Reflow failed: ${errorMsg}`, type: 'error' });
    } finally {
      setIsRefining(null);
    }
  };

  const togglePlatform = (p: string) => {
    if (post.platforms.includes(p)) {
      if (post.platforms.length > 1) {
        onUpdatePlatforms(post.platforms.filter(plat => plat !== p));
      }
    } else {
      onUpdatePlatforms([...post.platforms, p]);
    }
  };

  return (
    <motion.div 
      layout
      id={`post-${post.id}`}
      className={`p-6 rounded-2xl border transition-all ${
        post.status === 'approved' ? 'bg-green-50/50 border-green-100' : 
        post.status === 'rejected' ? 'bg-red-50/50 border-red-100' : 
        'bg-gray-50 border-gray-100'
      } flex flex-col gap-6 relative group/row`}
    >
      <div 
        onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
        className={`absolute top-6 -left-3 w-7 h-7 rounded-lg border-2 flex items-center justify-center cursor-pointer transition-all z-20 ${
          isSelected ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg' : 'bg-white border-gray-200 text-transparent opacity-0 group-hover/row:opacity-100 hover:border-indigo-400'
        }`}
      >
        <Check className="w-4 h-4" />
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        <div className="w-full md:w-48 shrink-0 space-y-4">
          <div className="grid grid-cols-1 gap-4">
             {(isEditing ? slides : post.slides)?.map((slide, idx) => (
              <div key={slide.id} className={`bg-white p-4 rounded-xl border border-gray-200 flex flex-col items-center justify-center text-center relative overflow-hidden group ${
                postFormat === 'story' ? 'aspect-[9/16]' : 'aspect-square'
              }`}>
                {slide.backgroundSliceUrl || slide.thumbnailUrl || slide.url ? (
                  <div className="absolute inset-0 w-full h-full">
                    {slide.isVideo ? (
                      <video 
                        src={slide.url} 
                        className="w-full h-full object-cover cursor-zoom-in" 
                        autoPlay 
                        muted 
                        loop 
                        playsInline
                        onClick={() => onPreview(slide.url!, 'video', slide, postFormat)}
                      />
                    ) : (
                      <div className="w-full h-full relative" style={{ containerType: "inline-size" }}>
                        <img 
                          src={slide.backgroundSliceUrl || slide.thumbnailUrl || slide.url} 
                          alt={`Slide ${idx + 1}`} 
                          className="w-full h-full object-cover cursor-zoom-in" 
                          onClick={() => onPreview(slide.url || slide.thumbnailUrl!, 'image', slide, postFormat)}
                        />
                        {slide.layoutTemplate && (
                          <RenderSlideOverlay 
                            slide={slide} 
                            brand={brand} 
                            slideIndex={idx} 
                            totalSlides={(isEditing ? slides : post.slides).length} 
                          />
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <ImageIcon className="w-8 h-8 text-gray-300 mb-2" />
                    <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Slide {idx + 1}</p>
                  </>
                )}
                
                {((slide.selectedProducts && slide.selectedProducts.length > 0) || slide.productImageUrl) && (
                  <div className="absolute top-2 left-2 z-10 bg-white/90 backdrop-blur-sm px-1.5 py-1 rounded-lg border border-indigo-100 shadow-sm flex items-center gap-1.5 pointer-events-none">
                    <div className="flex -space-x-2">
                      {(slide.selectedProducts || []).slice(0, 3).map((p, i) => (
                        <div key={`${p.id}-${i}`} className="w-4 h-4 rounded-md overflow-hidden border border-white shrink-0 shadow-sm">
                          <img src={p.url} className="w-full h-full object-cover" />
                        </div>
                      ))}
                      {slide.productImageUrl && !slide.selectedProducts?.length && (
                        <div className="w-4 h-4 rounded-md overflow-hidden border border-white shrink-0 shadow-sm">
                          <img src={slide.productImageUrl} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                        </div>
                      )}
                    </div>
                    <span className="text-[8px] font-bold text-indigo-600 uppercase tracking-tighter whitespace-nowrap">
                      {slide.selectedProducts?.length || 1} Product{((slide.selectedProducts?.length || 1) > 1) ? 's' : ''}
                    </span>
                  </div>
                )}
                
                <div 
                  className={`absolute inset-0 bg-black/70 transition-opacity flex flex-col items-center justify-center text-white p-2 z-20 ${
                    (loadingSlideId === slide.id || (sprintProgress?.postId === post.id && sprintProgress?.slideId === slide.id)) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                  }`}
                  onClick={() => slide.url && onPreview(slide.url, slide.isVideo ? 'video' : 'image', slide, postFormat)}
                >
                  {(loadingSlideId === slide.id || (sprintProgress?.postId === post.id && sprintProgress?.slideId === slide.id)) ? (
                    <div className="flex flex-col items-center gap-3 px-4" onClick={e => e.stopPropagation()}>
                      <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
                      <div className="text-center">
                        <p className="text-sm font-bold">
                          {(sprintProgress?.postId === post.id && sprintProgress?.slideId === slide.id) ? 'Sprinting...' : (loadingType === 'generating' ? 'Generating...' : 'Uploading...')}
                        </p>
                        <p className="text-[10px] text-gray-300 mt-1 animate-pulse">
                          Please be patient
                        </p>
                      </div>
                    </div>
                  ) : isEditing ? (
                    <div className="flex flex-col items-center gap-3 px-4 text-center" onClick={e => e.stopPropagation()}>
                       <p className="text-[10px] font-bold text-amber-300">
                          Please save changes or exit edit mode to generate or upload media.
                       </p>
                       {slide.url && (
                         <button 
                           onClick={() => onPreview(slide.url!, slide.isVideo ? 'video' : 'image', slide, postFormat)}
                           className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg text-xs font-bold hover:bg-white/30 transition-all justify-center border border-white/30"
                         >
                           <Sparkles className="w-4 h-4" />
                           <span>Preview</span>
                         </button>
                       )}
                    </div>
                  ) : (isAdmin || canEdit) ? (
                    <div className="grid grid-cols-1 gap-1.5 w-full max-w-[140px]" onClick={e => e.stopPropagation()}>
                      {slide.url && (
                        <button 
                          onClick={() => onPreview(slide.url!, slide.isVideo ? 'video' : 'image', slide, postFormat)}
                          className="flex items-center gap-2 bg-white/10 px-2 py-1 rounded-lg text-[10px] font-bold hover:bg-white/20 transition-all justify-center border border-white/20"
                        >
                          <Sparkles className="w-3 h-3" />
                          <span>{t.preview}</span>
                        </button>
                      )}

                        <button 
                          onClick={() => triggerGenerate(slide.id, slide.concept, 'image')}
                          className="flex items-center gap-2 bg-indigo-600 px-2 py-1 rounded-lg text-[10px] font-bold hover:bg-indigo-700 transition-all justify-center"
                        >
                          <Sparkles className="w-3 h-3" />
                          <span>{slide.url ? t.regenImage : t.genImage}</span>
                        </button>

                      {slide.url && (
                        <button 
                          onClick={() => handleQuickFix(slide.id)}
                          className="flex items-center gap-2 bg-amber-500 px-2 py-1 rounded-lg text-[10px] font-bold hover:bg-amber-600 transition-all justify-center"
                        >
                          <TypeIcon className="w-3 h-3" />
                          <span>{t.quickFix}</span>
                        </button>
                      )}

                      <button 
                        onClick={() => setActiveEditorSlideId(slide.id)}
                        className="flex items-center gap-2 bg-emerald-600 px-2 py-1 rounded-lg text-[10px] font-bold hover:bg-emerald-700 transition-all justify-center text-white"
                        title="Design & Edit Custom Layout Slide"
                      >
                        <Edit2 className="w-3 h-3" />
                        <span>Edit Layout</span>
                      </button>

                        <button 
                          onClick={() => triggerGenerate(slide.id, slide.concept, 'video')}
                          className="flex items-center gap-2 bg-purple-600 px-2 py-1 rounded-lg text-[10px] font-bold hover:bg-purple-700 transition-all justify-center"
                        >
                          <Youtube className="w-3 h-3" />
                          <span>{slide.url ? t.regenVideo : t.genVideo}</span>
                        </button>
                      
                      <div className="grid grid-cols-2 gap-1.5">
                        <label className={`flex items-center gap-1.5 bg-white/10 px-2 py-1 rounded-lg text-[10px] font-bold hover:bg-white/20 transition-all cursor-pointer justify-center`}>
                          <Upload className="w-3 h-3" />
                          <span>{t.upload}</span>
                          <input type="file" className="hidden" onChange={(e) => handleFileChange(slide.id, e)} accept="image/*,video/*" />
                        </label>

                        {slide.url && (
                          <button 
                            onClick={() => handleDownload(slide.url!, idx, slide.isVideo)}
                            className="flex items-center gap-1.5 bg-white text-gray-900 px-2 py-1 rounded-lg text-[10px] font-bold hover:bg-gray-100 transition-all justify-center"
                          >
                            <Download className="w-3 h-3" />
                            <span>{t.save}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ) : slide.url ? (
                    <div className="flex flex-col gap-2" onClick={e => e.stopPropagation()}>
                      <button 
                        onClick={() => onPreview(slide.url!, slide.isVideo ? 'video' : 'image', slide, postFormat)}
                        className="flex items-center gap-2 bg-white text-gray-900 px-4 py-2 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg"
                      >
                        <Sparkles className="w-5 h-5" />
                        <span>Preview</span>
                      </button>
                      <button 
                        onClick={() => handleDownload(slide.url!, idx, slide.isVideo)}
                        className="flex items-center gap-2 bg-white text-gray-900 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-100 transition-all w-32 justify-center"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                      </button>
                    </div>
                  ) : (
                    <p className="text-[10px] text-gray-400 font-bold">Awaiting Content</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {isEditing && (
            <button
              onClick={() => {
                const newSlide = {
                  id: `slide_${Math.random().toString(36).substr(2, 9)}`,
                  concept: "Describe your concept here...",
                  selectedProducts: []
                };
                updateEditingData({ slides: [...slides, newSlide] });
              }}
              className="w-full flex items-center justify-center gap-2 py-3 bg-white border-2 border-dashed border-gray-200 rounded-xl text-xs font-bold text-gray-400 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all mt-2"
            >
              <Plus className="w-4 h-4" />
              Add Slide
            </button>
          )}

          {post.slides?.some(s => s.url) && !post.status && (
            <div className="flex gap-2">
              <button 
                onClick={() => onUpdateStatus('approved')}
                className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-green-600 text-white rounded-lg text-[10px] font-bold hover:bg-green-700 transition-all"
              >
                <Check className="w-3 h-3" />
                Approve
              </button>
              <button 
                onClick={() => onUpdateStatus('rejected')}
                className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-red-600 text-white rounded-lg text-[10px] font-bold hover:bg-red-700 transition-all"
              >
                <X className="w-3 h-3" />
                Reject
              </button>
            </div>
          )}
          
          {post.status === 'approved' && (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-center gap-1 py-1.5 bg-green-100 text-green-700 rounded-lg text-[10px] font-bold">
                <CheckCircle2 className="w-3 h-3" />
                Approved
              </div>
              <button 
                onClick={() => {
                  if (confirm("Reset status to pending?")) {
                    onUpdateStatus('none');
                  }
                }}
                className="text-[9px] font-bold text-gray-400 hover:text-indigo-600 transition-all text-center underline uppercase tracking-tighter"
              >
                Undo Approval
              </button>
            </div>
          )}

          {post.status === 'rejected' && (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-center gap-1 py-1.5 bg-red-100 text-red-700 rounded-lg text-[10px] font-bold">
                <XCircle className="w-3 h-3" />
                Rejected
              </div>
              <button 
                onClick={() => {
                  if (confirm("Reset status to pending?")) {
                    onUpdateStatus('none');
                  }
                }}
                className="text-[9px] font-bold text-gray-400 hover:text-indigo-600 transition-all text-center underline uppercase tracking-tighter"
              >
                Undo Rejection
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex gap-1">
                {post.platforms.map((p, idx) => (
                  <div key={`${p}-${idx}`} className="bg-white p-2 rounded-lg shadow-sm border border-gray-100">
                    {getPlatformIcon(p)}
                  </div>
                ))}
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">{format(parseLocalDate(post.date), 'EEE, MMM do')}</p>
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest">{post.type}</span>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">• {post.format}</span>
                  </div>
                  {post.bestTimeToPost && (
                    <div className="flex items-center gap-1 text-[9px] text-amber-600 font-bold uppercase tracking-tighter">
                      <Clock className="w-2.5 h-2.5" />
                      <span>Best Time: {post.bestTimeToPost}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isEditing && (
                <>
                  <button 
                    onClick={onRepurpose}
                    title="Repurpose / Adapt Post"
                    className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-white rounded-lg transition-all"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={onDuplicate}
                    title="Duplicate Post"
                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-white rounded-lg transition-all"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={onEdit} 
                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-white rounded-lg transition-all"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {brandPlatforms.map((p, idx) => (
              <button
                key={`${p}-${idx}`}
                onClick={() => togglePlatform(p)}
                className={`px-3 py-1 rounded-full text-[10px] font-bold transition-all border flex items-center gap-1.5 ${
                  post.platforms.includes(p)
                    ? 'bg-indigo-50 text-indigo-600 border-indigo-200'
                    : 'bg-white text-gray-400 border-gray-100 hover:border-gray-200'
                }`}
              >
                {getPlatformIcon(p)}
                {p}
              </button>
            ))}
          </div>

          {isEditing ? (
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Post Copy</label>
                  <textarea 
                    value={copy} 
                    onChange={e => updateEditingData({ copy: e.target.value.slice(0, 2000) })}
                    maxLength={2000}
                    className="w-full p-4 rounded-xl border border-indigo-200 focus:ring-2 focus:ring-indigo-500 outline-none min-h-[120px] text-sm leading-relaxed text-gray-900"
                    placeholder="Enter the post caption..."
                  />
                  <div className="flex justify-end">
                    <span className="text-[10px] text-gray-400 font-medium">
                      {copy.length} / 2000
                    </span>
                  </div>
                  
                  <div className="mt-2 flex gap-2">
                    <div className="relative flex-1">
                      <input 
                        value={isRefining === 'copy' ? refinePrompt : (isRefining ? '' : refinePrompt)}
                        onChange={e => setRefinePrompt(e.target.value)}
                        placeholder="Refine copy with AI (e.g. 'add pricing details' or 'make it punchy')..."
                        className="w-full pl-9 pr-4 py-2 bg-indigo-50/50 border border-indigo-100 rounded-xl text-xs outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 transition-all"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && refinePrompt.trim()) {
                            e.preventDefault();
                            handleRefine('copy');
                          }
                        }}
                      />
                      <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-400" />
                    </div>
                    <button 
                      onClick={() => handleRefine('copy')}
                      disabled={isRefining === 'copy' || !refinePrompt.trim()}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-[10px] font-bold hover:bg-indigo-700 transition-all disabled:opacity-50 flex items-center gap-2 shadow-sm"
                    >
                      {isRefining === 'copy' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      Refine
                    </button>
                  </div>
                  
                  {/* Visual Format Selection for Post Layout */}
                  <div className="mt-4 p-4 bg-slate-50 border border-slate-100/80 rounded-xl flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-indigo-500" />
                      <div>
                        <span className="text-xs font-bold text-gray-700">Format Selection</span>
                        <p className="text-[10px] text-gray-400">Choose layout style for this post</p>
                      </div>
                    </div>
                    <select
                      value={postFormat}
                      onChange={(e: any) => updateEditingData({ format: e.target.value })}
                      className="px-3 py-1.5 text-xs text-gray-900 bg-white border border-gray-200 rounded-lg hover:border-indigo-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none font-medium transition-all cursor-pointer min-w-[140px]"
                    >
                      <option value="feed">Feed Post</option>
                      <option value="story">Story</option>
                      <option value="carousel">Carousel</option>
                    </select>
                  </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Slide Concepts & Visual Settings</label>
                  {selectedSlideIds.size > 0 && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-3 bg-red-50 px-3 py-1.5 rounded-xl border border-red-100"
                    >
                      <span className="text-[10px] font-bold text-red-600">{selectedSlideIds.size} selected</span>
                      <div className="w-px h-3 bg-red-200" />
                      <button 
                        onClick={handleDeleteSelected}
                        className="text-[10px] font-bold text-red-600 hover:text-red-700 flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete Selected
                      </button>
                    </motion.div>
                  )}
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <button 
                    onClick={handleSelectAll}
                    className="text-[10px] font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-indigo-50 transition-all"
                  >
                    {selectedSlideIds.size === slides.length ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                    {selectedSlideIds.size === slides.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                {slides.map((slide, idx) => (
                  <div 
                    key={slide.id} 
                    className={`bg-white p-5 rounded-2xl border transition-all space-y-4 shadow-sm relative ${
                      selectedSlideIds.has(slide.id) ? 'border-indigo-400 ring-1 ring-indigo-100' : 'border-indigo-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button 
                          onClick={() => toggleSlideSelection(slide.id)}
                          className={`p-1 rounded-md transition-all ${selectedSlideIds.has(slide.id) ? 'text-indigo-600' : 'text-gray-300 hover:text-gray-400'}`}
                        >
                          {selectedSlideIds.has(slide.id) ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                        </button>
                        <span className="text-xs font-bold text-indigo-600 flex items-center gap-2">
                          <span className="w-5 h-5 bg-indigo-600 text-white rounded-full flex items-center justify-center text-[10px]">
                            {idx + 1}
                          </span>
                          Slide {idx + 1}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {isEditing && slides.length > 1 && (
                          <button 
                            onClick={(e) => handleRemoveSlide(e, slide.id)}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                            title="Remove Slide"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <label className="flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-bold hover:bg-indigo-100 transition-all cursor-pointer">
                          <ImageIcon className="w-3 h-3" />
                          <span>{loadingSlideId === slide.id && loadingType === 'analyzing' ? 'Analyzing...' : 'Upload Inspiration'}</span>
                          <input 
                            type="file" 
                            className="hidden" 
                            accept="image/*,video/*" 
                            onChange={(e) => handleInspirationUpload(slide.id, e)}
                            disabled={loadingSlideId === slide.id}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <textarea 
                          value={slide.concept}
                          onChange={e => handleConceptChange(slide.id, e.target.value.slice(0, 1000))}
                          maxLength={1000}
                          className="w-full p-3 rounded-xl border border-gray-100 focus:ring-2 focus:ring-indigo-500 outline-none text-xs leading-relaxed min-h-[80px] bg-gray-50/50 text-gray-900 shadow-inner"
                          placeholder={`Describe the visual for slide ${idx + 1}...`}
                        />
                        <div className="flex justify-end">
                          <span className="text-[10px] text-gray-400 font-medium">
                            {slide.concept.length} / 1000
                          </span>
                        </div>
                      </div>

                      {/* Explicit Overlay Text Input */}
                      <div className="space-y-1 bg-violet-50/30 p-3 rounded-xl border border-violet-100/30">
                        <label className="text-[9px] font-bold text-violet-500 uppercase tracking-widest block mb-0.5">Overlay Text (Optional)</label>
                        <textarea 
                          value={slide.overlayText || ''}
                          onChange={e => handleOverlayTextChange(slide.id, e.target.value.slice(0, 500))}
                          maxLength={500}
                          className="w-full p-2.5 rounded-lg border border-violet-100/30 focus:border-violet-400 focus:ring-1 focus:ring-violet-200 outline-none text-xs leading-relaxed min-h-[50px] bg-white text-gray-900 placeholder:text-gray-400 shadow-sm"
                          placeholder={`Exact text, checklist items, headlines, or quotes to render on slide ${idx + 1}...`}
                        />
                      </div>
                      
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <input 
                            value={isRefining === slide.id ? refinePrompt : (isRefining ? '' : refinePrompt)}
                            onChange={e => setRefinePrompt(e.target.value)}
                            placeholder="Refine visual concept with AI..."
                            className="w-full pl-8 pr-3 py-1.5 bg-white border border-gray-100 rounded-lg text-[10px] outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 transition-all"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && refinePrompt.trim()) {
                                e.preventDefault();
                                handleRefine('concept', slide.id);
                              }
                            }}
                          />
                          <Sparkles className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-indigo-400" />
                        </div>
                        <button 
                          onClick={() => handleRefine('concept', slide.id)}
                          disabled={isRefining === slide.id || !refinePrompt.trim()}
                          className="px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-bold hover:bg-indigo-100 transition-all disabled:opacity-50 flex items-center gap-1.5"
                        >
                          {isRefining === slide.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RefreshCw className="w-2.5 h-2.5" />}
                          Refine
                        </button>
                      </div>
                      <p className="text-[9px] text-gray-400 italic px-1">
                        Tip: Use quotes for text you want rendered (e.g. "Legacy is Sustainable"). Avoid technical terms like "Geist Sans" unless you want them as text.
                      </p>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Showcase Products</label>
                        <span className="text-[9px] text-gray-400">Select multiple to include in layout</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {productImages?.map(img => {
                          const isSelected = slide.selectedProducts?.some(p => p.id === img.id) || slide.productImageUrl === img.url;
                          return (
                            <button
                              key={img.id}
                              type="button"
                              onClick={() => toggleProductSelect(slide.id, img)}
                              className={`relative w-12 h-12 rounded-xl border-2 transition-all overflow-hidden ${
                                isSelected ? 'border-indigo-600 ring-2 ring-indigo-100' : 'border-gray-100 hover:border-indigo-200'
                              }`}
                              title={`${img.name} ${img.price ? `(${img.price})` : ''}`}
                            >
                              {img.url && (
                                <img src={img.url} alt={img.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                              )}
                              {isSelected && (
                                <div className="absolute inset-0 bg-indigo-600/20 flex items-center justify-center">
                                  <Check className="w-5 h-5 text-white drop-shadow-sm" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                        {(!productImages || productImages.length === 0) && (
                          <p className="text-[10px] text-gray-400 italic">No products uploaded in Brand Profile.</p>
                        )}
                      </div>

                      <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Quick Add Images</label>
                        <span className="text-[9px] text-gray-400">Add temporary images for this design</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {slide.quickAddImages?.map(img => (
                          <div key={img.id} className="relative group">
                            <div className="w-12 h-12 rounded-xl border-2 border-indigo-600 ring-2 ring-indigo-100 overflow-hidden">
                              {img.url && (
                                <img src={img.url} alt={img.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                              )}
                            </div>
                            <button 
                              onClick={() => removeQuickAddImage(slide.id, img.id)}
                              className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        <label className="w-12 h-12 rounded-xl border-2 border-dashed border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all flex flex-col items-center justify-center cursor-pointer text-gray-400 hover:text-indigo-500">
                          <Plus className="w-5 h-5" />
                          <input 
                            type="file" 
                            className="hidden" 
                            accept="image/*" 
                            onChange={(e) => handleQuickAddImage(slide.id, e)} 
                          />
                        </label>
                      </div>
                    </div>

                    {slide.selectedProducts && slide.selectedProducts.length > 0 && (
                        <div className="bg-indigo-50/30 rounded-xl p-3 border border-indigo-100/30 space-y-2">
                          <p className="text-[9px] font-bold text-indigo-500 uppercase tracking-wider">Selected Metadata</p>
                          <div className="grid grid-cols-1 gap-2">
                            {slide.selectedProducts.map((p, i) => (
                              <div key={`${p.id}-${i}`} className="flex items-center gap-2 text-[10px] bg-white/50 p-1.5 rounded-lg border border-indigo-50">
                                <div className="w-6 h-6 rounded-md overflow-hidden border border-indigo-100 shrink-0">
                                  {p.url && (
                                    <img src={p.url} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-gray-700 truncate">{p.name}</p>
                                  <p className="text-gray-500 truncate text-[9px]">
                                    {p.price ? `${p.price} • ` : ''}{p.description || 'No description'}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {isEditing && slides.length > 1 && (
                  <div className="pt-4 flex justify-center">
                    <button 
                      onClick={handleReflow}
                      disabled={isRefining === 'reflow'}
                      className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-2xl text-xs font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 disabled:opacity-50"
                    >
                      {isRefining === 'reflow' ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Reflowing Messaging...</span>
                        </>
                      ) : (
                        <>
                          <RotateCcw className="w-4 h-4" />
                          <span>Reflow Carousel Messaging</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center pt-2">
                {slides.length > 1 && post.platforms.includes('LinkedIn') && (
                  <div className="flex items-center gap-3 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100">
                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">LinkedIn Format:</span>
                    <div className="flex bg-white rounded-lg p-0.5 border border-gray-100">
                      <button 
                        onClick={() => updateEditingData({ linkedinFormat: 'pdf' })}
                        className={`px-3 py-1 rounded-md text-[10px] font-bold transition-all ${linkedinFormat === 'pdf' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                      >
                        Carousel PDF
                      </button>
                      <button 
                        onClick={() => updateEditingData({ linkedinFormat: 'gallery' })}
                        className={`px-3 py-1 rounded-md text-[10px] font-bold transition-all ${linkedinFormat === 'gallery' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                      >
                        Gallery
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-2 flex-1">
                  <button onClick={onCancel} className="px-4 py-2 text-sm font-bold text-gray-500 hover:bg-gray-200 rounded-lg transition-all">Cancel</button>
                  <button 
                    onClick={() => onSave({ ...post, copy, slides, linkedinFormat, format: postFormat })} 
                    className="px-6 py-2 text-sm font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {post.bestTimeToPost && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 rounded-lg border border-amber-100 text-xs font-medium">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Best time to post: {post.bestTimeToPost}</span>
                </div>
              )}
              <div className="bg-white p-4 rounded-xl border border-gray-100 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {post.copy}
              </div>
            </div>
          )}

          <div className="space-y-2">
            {post.slides?.map((slide, idx) => (
              <div key={slide.id} className="bg-indigo-50/50 p-3 rounded-lg border border-indigo-100/50">
                <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest mb-1">Slide {idx + 1} Concept</p>
                <p className="text-xs text-indigo-900 italic">"{slide.concept}"</p>
                {slide.overlayText && (
                  <div className="mt-2 pt-2 border-t border-indigo-100/30">
                    <p className="text-[9px] font-bold text-violet-500 uppercase tracking-widest mb-0.5">Slide {idx + 1} Overlay Text</p>
                    <p className="text-xs text-violet-950 font-medium whitespace-pre-wrap bg-violet-50/50 p-2 rounded-lg border border-violet-100/30">"{slide.overlayText}"</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {postFormat === 'carousel' && (
            <div className="bg-gradient-to-r from-violet-500/5 via-indigo-500/5 to-blue-500/5 rounded-2xl p-5 border border-indigo-200/40 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-600 text-white rounded-xl shadow-lg shadow-indigo-100/50">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                      Generate Seamless Panorama Carousel
                      <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded-md text-[8px] font-bold uppercase tracking-wide">
                        Premium 2-Step Method
                      </span>
                    </h4>
                    <p className="text-[10px] text-gray-500 leading-normal">
                      Maintains a master, unbroken background scene. Click Step 1 to generate the background, see a widescreen preview, and then execute Step 2 to crop and add content!
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-1.5 bg-white/40 p-3.5 rounded-xl border border-indigo-100/40">
                <div className="flex items-center justify-between">
                  <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block">Background Scenery concept</label>
                  {!isGeneratingSeamless && !isOverlaying && !isOptimizingPrompt && !panoramaPreviewUrl && (
                    <button
                      onClick={handleOptimizePrompt}
                      className="px-2 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-md text-[9px] font-extrabold transition-all flex items-center gap-1"
                      title="AI will study your copy and slides to write a seamless panorama description"
                    >
                      <Sparkles className="w-2.5 h-2.5" />
                      Optimize Concept with AI
                    </button>
                  )}
                </div>
                <textarea
                  value={seamlessBaseConcept}
                  onChange={(e) => setSeamlessBaseConcept(e.target.value.substring(0, 500))}
                  maxLength={500}
                  rows={2}
                  disabled={isGeneratingSeamless || isOverlaying || !!panoramaPreviewUrl}
                  className="w-full p-3 rounded-xl border border-indigo-100 focus:ring-2 focus:ring-indigo-500 outline-none text-xs leading-normal text-gray-900 bg-white placeholder:text-gray-400 shadow-inner"
                  placeholder="Describe the unbroken scenery (e.g., 'An elegant and corporate bright modern medical studio office with warm wooden desks, dental diagnostics lights, and gentle leafy plants')"
                />
              </div>

              {!panoramaPreviewUrl ? (
                // Step 1: Render Generator
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
                    <div className="text-[10px] text-gray-500">
                      Step 1 Cost: <span className="font-bold text-indigo-600">1 Token</span> (panorama background sketch)
                    </div>
                    <button
                      onClick={handleRunStep1}
                      disabled={isGeneratingSeamless || isUploadingCustomPanorama || isOptimizingPrompt || !seamlessBaseConcept.trim() || isEditing}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-md shadow-indigo-100/80 disabled:opacity-50"
                      title={isEditing ? "Please save changes or exit edit mode before generating seamlessly" : ""}
                    >
                      {isGeneratingSeamless ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      <span>{isGeneratingSeamless ? "Creating Panorama..." : "✨ Step 1: Generate Panorama Background"}</span>
                    </button>
                  </div>

                  <div className="relative py-2 flex items-center justify-center">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200/50 block"></div>
                    </div>
                    <span className="relative px-3 bg-[#fdfcff] text-[9px] font-bold text-gray-400 uppercase tracking-widest leading-none">
                      Or Upload Custom Backdrop
                    </span>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-indigo-50/20 p-3.5 rounded-xl border border-indigo-100/30">
                    <div className="text-[10px] text-gray-500 leading-relaxed md:max-w-[65%]">
                      Skip AI Generation and upload your own unbroken panoramic widescreen image. <span className="font-bold text-emerald-600">0 Tokens Cost!</span>
                    </div>
                    <div className="relative">
                      <input 
                        type="file" 
                        onChange={handleUploadCustomPanorama} 
                        accept="image/*" 
                        className="hidden" 
                        id="custom-panorama-upload-input" 
                        disabled={isUploadingCustomPanorama || isGeneratingSeamless || isEditing}
                      />
                      <label 
                        htmlFor="custom-panorama-upload-input"
                        className={`px-4 py-2 bg-white hover:bg-gray-50 text-indigo-600 border border-indigo-200 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-sm cursor-pointer ${(isUploadingCustomPanorama || isGeneratingSeamless || isEditing) ? "opacity-50 pointer-events-none" : ""}`}
                      >
                        {isUploadingCustomPanorama ? <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-600" /> : <Upload className="w-3.5 h-3.5 text-indigo-600" />}
                        <span>{isUploadingCustomPanorama ? "Uploading..." : "Upload Panorama Backdrop"}</span>
                      </label>
                    </div>
                  </div>

                  {isEditing && (
                    <p className="text-[9px] text-amber-600 font-medium">
                      ⚠️ Leave Edit mode ("Save Changes" or "Cancel") to execute this premium generator!
                    </p>
                  )}
                </div>
              ) : (
                // Step 2: Render Preview & Overlay execute
                <div className="space-y-4 pt-1">
                  <div className="border border-green-200 bg-green-50/50 p-4 rounded-xl space-y-3">
                    <span className="text-[10px] font-bold text-green-700 uppercase tracking-widest block">✓ Step 1 Complete: Base Panorama Generated</span>
                    
                    <div className="relative aspect-[16/9] rounded-xl overflow-hidden border border-green-200/80 shadow-md bg-gray-50">
                      <img 
                        src={panoramaPreviewUrl} 
                        alt="Unbroken Wide Panorama Layout" 
                        className="w-full h-full object-cover" 
                        referrerPolicy="no-referrer" 
                      />
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
                      <button
                        onClick={() => {
                          setPanoramaPreviewUrl(null);
                          setToast({ message: "Ready to adjust background scenery description.", type: "success" });
                        }}
                        disabled={isOverlaying}
                        className="px-3.5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-xs rounded-xl transition-all flex items-center gap-1.5 self-start sm:self-auto disabled:opacity-50"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        <span>Regenerate Background</span>
                      </button>

                      <div className="flex flex-col items-end gap-1.5">
                        <span className="text-[9px] text-gray-400 font-medium">Step 2 Cost: <span className="font-bold text-indigo-600">{slides.length} Tokens</span></span>
                        <button
                          onClick={handleRunStep2}
                          disabled={isOverlaying || isEditing}
                          className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-indigo-600 hover:opacity-95 text-white font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-md shadow-indigo-100 disabled:opacity-50"
                        >
                          {isOverlaying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />}
                          <span>🚀 Step 2: Overlay Typography & Product Images</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {(isGeneratingSeamless || isOverlaying || isOptimizingPrompt || isUploadingCustomPanorama) && (
                <div className="bg-indigo-50 border border-indigo-100/80 rounded-xl p-4 flex flex-col gap-3">
                  <div className="flex items-center gap-2.5 animate-pulse">
                    <Loader2 className="w-3.5 h-3.5 text-indigo-600 animate-spin" />
                    <span className="text-[11px] font-semibold text-indigo-900">{seamlessProgress || "Rendering layout..."}</span>
                  </div>
                  {(isGeneratingSeamless || isOverlaying) && (
                    <button
                      onClick={handleStopProcess}
                      className="w-full py-1.5 bg-red-100 hover:bg-red-200 text-red-700 font-bold text-[10px] uppercase tracking-wider rounded-xl transition-all shadow-sm"
                    >
                      🛑 STOP GENERATION & REFUND TOKENS
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Suggestions List */}
          {post.suggestions && post.suggestions.length > 0 && (
            <div className="space-y-3 mt-4">
              <p className="text-[10px] font-bold text-amber-500 uppercase tracking-widest">Suggestions</p>
              {post.suggestions.map(suggestion => (
                <div key={suggestion.id} className={`p-4 rounded-2xl border ${
                  suggestion.status === 'approved' ? 'bg-green-50 border-green-100' :
                  suggestion.status === 'rejected' ? 'bg-red-50 border-red-100' :
                  'bg-amber-50 border-amber-100'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-amber-200 flex items-center justify-center text-[10px] font-bold text-amber-700">
                        {suggestion.userName[0].toUpperCase()}
                      </div>
                      <span className="text-xs font-bold text-gray-700">{suggestion.userName}</span>
                      <span className="text-[10px] text-gray-400">• {format(new Date(suggestion.createdAt), 'MMM d, HH:mm')}</span>
                    </div>
                    {suggestion.status === 'pending' && canEdit && (
                      <div className="flex items-center gap-1">
                        <button 
                          onClick={() => onApproveSuggestion(suggestion.id)}
                          className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-all"
                          title="Approve & Apply"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button 
                          onClick={() => onRejectSuggestion(suggestion.id)}
                          className="p-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                          title="Reject"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    {suggestion.status !== 'pending' && (
                      <span className={`text-[10px] font-bold uppercase tracking-widest ${
                        suggestion.status === 'approved' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {suggestion.status}
                      </span>
                    )}
                  </div>
                  {suggestion.suggestedCopy && (
                    <div className="text-xs text-gray-700 bg-white/50 p-2 rounded-lg border border-amber-100/50 mb-2 italic">
                      "{suggestion.suggestedCopy}"
                    </div>
                  )}
                  {suggestion.comment && (
                    <p className="text-xs text-gray-500 flex items-start gap-1.5">
                      <MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>{suggestion.comment}</span>
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between pt-6 border-t border-gray-100">
        <div className="flex flex-wrap items-center gap-3">
          {canEdit && !isTrial ? (
            <>
              <button 
                onClick={() => {
                  if (post.status === 'approved') {
                    if (confirm("Undo approval for this post?")) {
                      onUpdateStatus('none');
                    }
                  } else {
                    onUpdateStatus('approved');
                  }
                }}
                className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 flex-1 sm:flex-initial ${
                  post.status === 'approved' ? 'bg-green-600 text-white shadow-lg shadow-green-100' : 'bg-white text-gray-400 border border-gray-100 hover:border-green-200 hover:text-green-600'
                }`}
              >
                <Check className="w-4 h-4" />
                <span>{post.status === 'approved' ? 'Approved' : t.approve}</span>
              </button>

              <button 
                onClick={() => {
                  if (post.status === 'rejected') {
                    onUpdateStatus('none');
                  } else {
                    onUpdateStatus('rejected');
                  }
                }}
                className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 flex-1 sm:flex-initial ${
                  post.status === 'rejected' ? 'bg-red-600 text-white shadow-lg shadow-red-100' : 'bg-white text-gray-400 border border-gray-100 hover:border-red-200 hover:text-red-600'
                }`}
              >
                <X className="w-4 h-4" />
                <span>{post.status === 'rejected' ? 'Rejected' : t.reject}</span>
              </button>
            </>
          ) : (
            <button 
              onClick={onSuggestEdit}
              className="px-6 py-2.5 rounded-xl text-sm font-bold bg-amber-50 text-amber-600 border border-amber-100 hover:bg-amber-100 transition-all flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <MessageSquare className="w-4 h-4" />
              <span>{t.suggestEdit}</span>
            </button>
          )}
        </div>

        {post.status === 'approved' && (
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto justify-start sm:justify-end">
            {post.platforms.filter(p => p !== 'Twitter').map((platform, idx) => {
              const pStatusObj = post.platformStatus?.[platform];
              const pStatus = pStatusObj?.status || 'idle';
              const isPublishing = pStatus === 'publishing' || localPublishing.has(platform);
              const isPublished = pStatus === 'published';
              const hasError = pStatus === 'failed';
              const errorMsg = pStatusObj?.error;
              
              return (
                <div key={`${platform}-${idx}`} className="flex flex-col gap-1.5 w-full sm:w-auto">
                  <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start">
                    <div className="flex flex-col gap-1 flex-1 sm:flex-initial">
                      {platform === 'LinkedIn' && post.slides && post.slides.length > 1 && !isPublished && !isPublishing && (
                        <div className="flex bg-gray-50 rounded-lg p-0.5 border border-gray-100 mb-1">
                          <button 
                            onClick={() => onSave({ ...post, linkedinFormat: 'pdf' })}
                            className={`px-2 py-0.5 rounded-md text-[8px] font-bold transition-all flex-1 text-center ${post.linkedinFormat !== 'gallery' ? 'bg-white text-indigo-600 shadow-sm border border-gray-100' : 'text-gray-400'}`}
                          >
                            PDF
                          </button>
                          <button 
                            onClick={() => onSave({ ...post, linkedinFormat: 'gallery' })}
                            className={`px-2 py-0.5 rounded-md text-[8px] font-bold transition-all flex-1 text-center ${post.linkedinFormat === 'gallery' ? 'bg-white text-indigo-600 shadow-sm border border-gray-100' : 'text-gray-400'}`}
                          >
                            Gallery
                          </button>
                        </div>
                      )}
                      <button
                        disabled={isPublishing || isPublished}
                        onClick={async () => {
                          setLocalPublishing(prev => new Set(prev).add(platform));
                          try {
                            await onPublish(platform);
                          } finally {
                            setLocalPublishing(prev => {
                              const next = new Set(prev);
                              next.delete(platform);
                              return next;
                            });
                          }
                        }}
                        className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 w-full sm:w-auto ${
                          isPublished 
                            ? 'bg-green-50 text-green-600 border border-green-100 cursor-default' 
                            : isPublishing
                              ? 'bg-indigo-50 text-indigo-600 border border-indigo-100'
                              : hasError
                                ? 'bg-red-50 text-red-600 border border-red-100'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-100'
                        }`}
                      >
                        {isPublishing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Globe className="w-3 h-3" />}
                        <span>{isPublished ? 'Published' : hasError ? 'Retry Post' : `Post to ${platform}`}</span>
                      </button>
                    </div>
                    {!isPublished && !isPublishing && (
                      <button
                        onClick={() => setShowScheduler(showScheduler === platform ? null : platform)}
                        className={`p-2.5 rounded-xl border transition-all h-10 w-10 flex items-center justify-center shrink-0 ${
                          showScheduler === platform ? 'bg-indigo-100 border-indigo-200 text-indigo-600' : 'bg-white border-gray-100 text-gray-400 hover:border-indigo-200 hover:text-indigo-600'
                        }`}
                        title="Schedule Post"
                      >
                        <Clock className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {hasError && errorMsg && (
                    <div className="bg-red-50 p-2 rounded-lg border border-red-100 text-[10px] text-red-600 w-full sm:max-w-[180px] break-words">
                      <div className="flex items-center gap-1 font-bold mb-0.5">
                        <AlertCircle className="w-3 h-3" />
                        <span>Failed:</span>
                      </div>
                      {errorMsg}
                    </div>
                  )}

                  {showScheduler === platform && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-indigo-50 p-3 rounded-xl border border-indigo-100 space-y-2 mt-1 w-full"
                    >
                      <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest">Schedule for {platform}</p>
                      <input 
                        type="datetime-local" 
                        value={scheduleTime}
                        onChange={e => setScheduleTime(e.target.value)}
                        className="w-full p-2 rounded-lg border border-indigo-200 text-xs outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 bg-white"
                      />
                      <div className="flex gap-2">
                        <button 
                          onClick={() => {
                            onUpdateStatus('scheduled', scheduleTime, platform);
                            setShowScheduler(null);
                            setToast({ message: `Post scheduled for ${platform}`, type: 'success' });
                          }}
                          className="flex-1 py-1.5 bg-indigo-600 text-white rounded-lg text-[10px] font-bold hover:bg-indigo-700 transition-all text-center"
                        >
                          Confirm Schedule
                        </button>
                        <button 
                          onClick={() => setShowScheduler(null)}
                          className="px-3 py-1.5 bg-white text-gray-500 rounded-lg text-[10px] font-bold border border-gray-200 hover:bg-gray-50 text-center"
                        >
                          Cancel
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {pStatus === 'scheduled' && (
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-50 text-amber-700 rounded-lg border border-amber-100 text-[9px] font-bold self-start mt-0.5">
                      <Clock className="w-2.5 h-2.5" />
                      <span>Scheduled: {format(new Date(pStatusObj.scheduledAt!), 'MMM d, HH:mm')}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Fix Modal */}
      <AnimatePresence>
        {quickFix && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl shadow-2xl border border-gray-100 max-w-md w-full overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-8 space-y-6">
                <div className="flex items-center gap-4">
                  <div className="bg-amber-100 p-3 rounded-2xl">
                    <TypeIcon className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{isBrazil ? 'Ajuste Rápido' : 'Quick Fix'}</h3>
                    <p className="text-sm text-gray-500 mt-1">{isBrazil ? 'Diga à IA o que consertar ou alterar.' : 'Tell the AI what to fix or change.'}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-amber-50 p-4 rounded-2xl border border-amber-100">
                    <p className="text-xs text-amber-800 leading-relaxed">
                      <strong>{isBrazil ? 'Dica:' : 'Tip:'}</strong> {isBrazil ? 'Seja específico. Por exemplo: "Mudar \'estratégia\' para \'planejamento\'", "Aumentar o logo", ou "Mudar o fundo para azul".' : 'Be specific. For example: "Change \'strategy\' to \'planning\'", "Make the logo bigger", or "Change the background to blue".'}
                    </p>
                  </div>

                  <textarea
                    autoFocus
                    value={quickFix.text}
                    onChange={e => setQuickFix({ ...quickFix, text: e.target.value })}
                    placeholder={isBrazil ? 'Descreva as alterações que você deseja...' : 'Describe the changes you want...'}
                    className="w-full p-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-amber-500 outline-none min-h-[100px] text-sm text-gray-900"
                  />

                  <div className="flex gap-3 pt-2">
                    <button 
                      onClick={() => setQuickFix(null)}
                      className="flex-1 px-6 py-3 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition-all"
                    >
                      {t.cancel}
                    </button>
                    <button 
                      onClick={submitQuickFix}
                      disabled={!quickFix.text.trim()}
                      className="flex-1 px-6 py-3 rounded-xl font-bold bg-amber-500 text-white hover:bg-amber-600 transition-all shadow-lg shadow-amber-100 disabled:opacity-50"
                    >
                      {isBrazil ? 'Aplicar Ajuste' : 'Apply Fix'}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Slide Layout & Content Editor Modal */}
      <AnimatePresence>
        {activeEditorSlideId && (() => {
          const slide = slides.find(s => s.id === activeEditorSlideId);
          if (!slide) return null;
          return (
            <SlideLayoutEditorModal
              slide={slide}
              onClose={() => setActiveEditorSlideId(null)}
              onSave={async (layout) => {
                const updatedSlides = slides.map(s => s.id === slide.id ? { 
                  ...s, 
                  layoutTemplate: layout, 
                  overlayText: (layout.boxes || []).map((b: any) => b.text).join('\n') 
                } : s);
                if (isEditing) {
                  updateEditingData({ slides: updatedSlides });
                } else {
                  await onSave({ ...post, slides: updatedSlides });
                }
                setToast({ message: "Slide layout applied successfully!", type: "success" });
                setActiveEditorSlideId(null);
              }}
              onRegenArtwork={async (layout) => {
                // Apply the layout locally
                const updatedSlide = { ...slide, layoutTemplate: layout };
                const updatedSlides = slides.map(s => s.id === slide.id ? updatedSlide : s);
                if (isEditing) {
                  updateEditingData({ slides: updatedSlides });
                } else {
                  await onSave({ ...post, slides: updatedSlides });
                }
                // Trigger artwork generation, passing updatedSlide directly to avoid React state-stale asynchronous lag
                await handleGenerate(slide.id, slide.concept, 'image', true, undefined, undefined, updatedSlide);
              }}
              brandBlueprints={brandBlueprints}
              brand={brand}
              postFormat={postFormat}
              loadingSlideId={loadingSlideId}
              totalSlides={slides.length}
            />
          );
        })()}
      </AnimatePresence>

      {/* Brand Element Prompt Modal */}
      <AnimatePresence>
        {showBrandPrompt && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl shadow-2xl border border-gray-100 max-w-md w-full overflow-hidden"
            >
              <div className="p-8 space-y-6">
                <div className="flex items-center gap-4">
                  <div className="bg-indigo-100 p-3 rounded-2xl">
                    <Sparkles className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{isBrazil ? 'Usar Elementos da Marca?' : 'Use Brand Elements?'}</h3>
                    <p className="text-sm text-gray-500 mt-1">{isBrazil ? 'Devemos incorporar seus produtos e templates de marca nesta geração?' : 'Should we incorporate your products and brand templates into this generation?'}</p>
                  </div>
                </div>

                {showBrandPrompt.type === 'video' && (
                  <div className="bg-amber-50 p-3 rounded-xl border border-amber-200 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800 leading-relaxed">
                      <span className="font-bold">{isBrazil ? 'Nota sobre Geração de Vídeo:' : 'Video Generation Note:'}</span> {isBrazil ? 'Modelos de IA de vídeo atualmente têm dificuldade em recriar logos exatos, texto legível ou formas precisas de produtos. Eles são melhores para criar movimentos de fundo cinematográficos e podem distorcer elementos específicos da marca.' : 'AI Video models currently struggle to recreate exact logos, perfectly legible text, or precise product shapes. They are best used for creating cinematic "B-roll" or background movement, and may morph specific brand elements.'}
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  <button 
                    onClick={() => handleGenerate(showBrandPrompt.slideId, showBrandPrompt.concept, showBrandPrompt.type, true)}
                    className="w-full flex items-center justify-between p-4 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-2xl transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-white p-2 rounded-lg shadow-sm">
                        <Box className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-indigo-900">{isBrazil ? 'Sim, usar elementos da marca' : 'Yes, use brand elements'}</p>
                        <p className="text-[10px] text-indigo-700">{isBrazil ? 'A IA verificará as descrições para como usá-los.' : 'AI will check descriptions for how and when to use them.'}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-indigo-400 group-hover:translate-x-1 transition-transform" />
                  </button>

                  <button 
                    onClick={() => handleGenerate(showBrandPrompt.slideId, showBrandPrompt.concept, showBrandPrompt.type, false)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-2xl transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-white p-2 rounded-lg shadow-sm">
                        <ImageIcon className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-gray-900">{isBrazil ? 'Não, usar apenas o conceito' : 'No, use generated concept'}</p>
                        <p className="text-[10px] text-gray-500">{isBrazil ? 'Siga estritamente a cena e o conceito gerados.' : 'Stick strictly to the generated scene and concept.'}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>

                <button 
                  onClick={() => setShowBrandPrompt(null)}
                  className="w-full py-3 text-sm font-bold text-gray-400 hover:text-gray-600 transition-all"
                >
                  {t.cancel}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {shareFeedback.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100 bg-white/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-indigo-600" />
            <span className="text-xs font-black text-indigo-900 uppercase tracking-widest">Client Review Space ({shareFeedback.length})</span>
          </div>
          <div className="space-y-3">
            {shareFeedback.map((f, i) => (
              <div key={f.id || i} className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm group/fb">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-gray-900 uppercase">{f.reviewerName}</span>
                    <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded-full ${
                      f.status === 'approved' ? 'bg-green-100 text-green-700' :
                      f.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {f.status}
                    </span>
                  </div>
                  <button 
                    onClick={() => f.id && onDeleteFeedback(f.id)}
                    className="opacity-0 group-hover/fb:opacity-100 p-1 text-gray-300 hover:text-red-500 transition-all"
                    title="Remove feedback and status"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
                <p className="text-xs text-gray-600 italic">"{f.feedback}"</p>
                {f.createdAt && (
                  <p className="text-[8px] text-gray-400 mt-1">
                    {(() => {
                      try {
                        const date = (f.createdAt as any).seconds ? new Date((f.createdAt as any).seconds * 1000) : new Date(f.createdAt);
                        return format(date, 'MMM d, h:mm a');
                      } catch (e) {
                        return '';
                      }
                    })()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

const CalendarView = ({ 
  calendar,
  brand,
  onPreview,
  onUpdatePost,
  onSavePost,
  onDeletePost
 }: { 
  calendar: ContentIdea[],
  brand?: any,
  onPreview?: (url: string, type: 'image' | 'video' | 'info' | any, slide?: any, format?: string) => void,
  onUpdatePost?: (postId: string, date: string) => void,
  onSavePost?: (postId: string, updatedPost: ContentIdea) => void,
  onDeletePost?: (postId: string) => void
 }) => {
  const { t, isBrazil } = useTranslation();

  // Local state for full post preview and inline editing
  const [viewMode, setViewMode] = useState<'calendar' | 'agenda'>(() => {
    return typeof window !== 'undefined' && window.innerWidth < 768 ? 'agenda' : 'calendar';
  });
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editCopy, setEditCopy] = useState('');
  const [editStatus, setEditStatus] = useState<'pending' | 'approved' | 'rejected' | 'scheduled'>('pending');
  const [editFormat, setEditFormat] = useState<'feed' | 'story' | 'carousel'>('feed');
  const [editSlides, setEditSlides] = useState<any[]>([]);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [copiedText, setCopiedText] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const monthStart = startOfMonth(parseLocalDate(calendar[0]?.date));
  const monthEnd = endOfMonth(monthStart);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOfWeek = monthStart.getDay(); // 0 for Sun, 6 for Sat

  const sortedPosts = useMemo(() => {
    return [...calendar].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [calendar]);

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'Facebook': return <Facebook className="w-2.5 h-2.5" />;
      case 'LinkedIn': return <Linkedin className="w-2.5 h-2.5" />;
      case 'Instagram': return <Instagram className="w-2.5 h-2.5" />;
      case 'Twitter': return <Twitter className="w-2.5 h-2.5" />;
      case 'TikTok': return <Youtube className="w-2.5 h-2.5" />;
      case 'Youtube': return <Youtube className="w-2.5 h-2.5" />;
      default: return <Share2 className="w-2.5 h-2.5" />;
    }
  };

  const handleDragStart = (e: React.DragEvent, postId: string) => {
    e.dataTransfer.setData('text/plain', postId);
  };

  const handleDrop = (e: React.DragEvent, day: Date) => {
    e.preventDefault();
    const postId = e.dataTransfer.getData('text/plain');
    if (postId && onUpdatePost) {
      onUpdatePost(postId, format(day, 'yyyy-MM-dd'));
    }
  };

  const handlePostClick = (post: ContentIdea) => {
    setSelectedPostId(post.id);
    setIsEditing(false);
    setEditCopy(post.copy);
    setEditStatus(post.status || 'pending');
    setEditFormat(post.format || 'feed');
    setActiveSlideIndex(0);
    setCopiedText(false);
    setShowDeleteConfirm(false);
  };

  const selectedPost = calendar.find(p => p.id === selectedPostId);

  return (
    <div className="relative space-y-4">
      {/* View Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-white border border-gray-100 rounded-2xl shadow-sm">
        <div className="flex bg-gray-100 p-1 rounded-xl border border-gray-200 self-start sm:self-auto shrink-0 select-none">
          <button
            onClick={() => setViewMode('calendar')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
              viewMode === 'calendar' 
                ? 'bg-white text-indigo-600 shadow-sm' 
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <CalendarIcon className="w-3.5 h-3.5" />
            <span>{isBrazil ? 'Calendário' : 'Calendar'}</span>
          </button>
          <button
            onClick={() => setViewMode('agenda')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
              viewMode === 'agenda' 
                ? 'bg-white text-indigo-600 shadow-sm' 
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <ListFilter className="w-3.5 h-3.5" />
            <span>{isBrazil ? 'Agenda' : 'Agenda View'}</span>
          </button>
        </div>
        
        <p className="text-[10px] text-gray-400 font-medium">
          {viewMode === 'calendar' 
            ? (isBrazil ? '* Arraste para mover, clique para visualizar/editar' : '* Drag to move, click to preview/edit') 
            : (isBrazil ? '* Toque em qualquer post na lista para visualizar/editar' : '* Click any post below to preview/edit')}
        </p>
      </div>

      {viewMode === 'calendar' ? (
        <div className="overflow-x-auto pb-4 scrollbar-thin">
          <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-2xl overflow-hidden shadow-inner min-w-[750px] lg:min-w-0">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="bg-gray-50 p-3 text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                {d}
              </div>
            ))}
            {/* Padding cells or placeholders for days before the start of the month */}
            {Array.from({ length: startDayOfWeek }).map((_, idx) => (
              <div key={`pad-${idx}`} className="bg-gray-50/40 min-h-[120px] p-2" />
            ))}
            {days.map(day => {
              const dayStr = format(day, 'yyyy-MM-dd');
              const posts = calendar.filter(p => p.date && p.date.substring(0, 10) === dayStr);
              return (
                <div 
                  key={day.toString()} 
                  className="bg-white min-h-[120px] p-2 space-y-1 select-none transition-colors hover:bg-slate-50/50"
                  onDragOver={(e) => {
                    if (onUpdatePost) e.preventDefault();
                  }}
                  onDrop={(e) => handleDrop(e, day)}
                >
                  <p className={`text-xs font-bold ${isSameDay(day, new Date()) ? 'text-indigo-600 bg-indigo-50 px-1 rounded inline-block' : 'text-gray-400'}`}>
                    {format(day, 'd')}
                  </p>
                  {posts.map(p => {
                    const firstSlide = p.slides?.[0];
                    const thumbUrl = firstSlide?.thumbnailUrl || firstSlide?.url;
                    return (
                      <div 
                        key={p.id} 
                        className="p-1.5 rounded-lg bg-indigo-50 border border-indigo-100 text-[10px] font-medium text-indigo-700 cursor-pointer hover:bg-indigo-100/80 transition-all shadow-sm flex items-center justify-between gap-1.5 active:scale-[0.98]"
                        draggable={!!onUpdatePost}
                        onDragStart={(e) => handleDragStart(e, p.id)}
                        onClick={() => handlePostClick(p)}
                        title="Drag to move, click for quick preview and edit"
                      >
                        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                          <div className="flex shrink-0 opacity-70">
                            {p.platforms.map((plat, idx) => (
                              <span key={`${plat}-${idx}`} className="mr-0.5">{getPlatformIcon(plat)}</span>
                            ))}
                          </div>
                          <div className="truncate whitespace-nowrap overflow-hidden leading-tight font-sans">
                            {p.copy}
                          </div>
                        </div>
                        {thumbUrl && (
                          <div className="w-8 h-8 rounded-md overflow-hidden shrink-0 border border-indigo-200/40 bg-white">
                            <img 
                              src={thumbUrl} 
                              alt="" 
                              className="w-full h-full object-cover"
                              referrerPolicy="no-referrer"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedPosts.map(p => {
            const firstSlide = p.slides?.[0];
            const thumbUrl = firstSlide?.thumbnailUrl || firstSlide?.url;
            const postDate = parseLocalDate(p.date);
            const isTodayPost = isSameDay(postDate, new Date());
            
            return (
              <div 
                key={p.id}
                onClick={() => handlePostClick(p)}
                className={`flex gap-4 p-4 rounded-2xl bg-white border transition-all cursor-pointer hover:border-indigo-200 active:scale-[0.99] group ${
                  isTodayPost ? 'ring-2 ring-indigo-600 ring-offset-2 border-indigo-100' : 'border-gray-100 hover:shadow-sm'
                }`}
              >
                {/* Left Date badge */}
                <div className="flex flex-col items-center justify-center bg-gray-50 rounded-xl px-3 py-2 shrink-0 border border-gray-100 min-w-[56px] h-[56px]">
                  <span className="text-[10px] font-black uppercase tracking-wider text-gray-400 leading-none">
                    {format(postDate, 'EEE')}
                  </span>
                  <span className="text-xl font-black text-gray-800 leading-none mt-1">
                    {format(postDate, 'd')}
                  </span>
                </div>
                
                {/* Center Post info */}
                <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                  <div>
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <div className="flex items-center gap-1">
                        {p.platforms.map((plat, idx) => (
                          <span key={`${plat}-${idx}`} className="p-1 bg-slate-100 text-slate-600 rounded-md">
                            {getPlatformIcon(plat)}
                          </span>
                        ))}
                      </div>
                      
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                        p.status === 'approved' ? 'bg-green-50 text-green-700 border border-green-100' :
                        p.status === 'rejected' ? 'bg-red-50 text-red-700 border border-red-100' :
                        p.status === 'scheduled' ? 'bg-blue-50 text-blue-700 border border-blue-100' :
                        'bg-amber-50 text-amber-700 border border-amber-100'
                      }`}>
                        {p.status || 'pending'}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-700 font-sans line-clamp-2 pr-2 leading-relaxed">
                      {p.copy}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-3 text-[10px] text-gray-400 font-medium mt-1.5">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3 text-indigo-400" />
                      {p.bestTimeToPost || 'Auto-Optimized'}
                    </span>
                    <span className="capitalize px-1.5 py-0.5 bg-gray-50 text-gray-500 rounded border border-gray-100 font-semibold text-[9px]">
                      {p.format || 'feed'}
                    </span>
                  </div>
                </div>
                
                {/* Right image preview */}
                {thumbUrl ? (
                  <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 border border-gray-100 shadow-inner bg-gray-50">
                    <img 
                      src={thumbUrl} 
                      alt="" 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-gray-50 border border-gray-100 shrink-0 flex items-center justify-center text-gray-300">
                    <ImageIcon className="w-5 h-5" />
                  </div>
                )}
              </div>
            );
          })}

          {sortedPosts.length === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-100 p-6 space-y-2">
              <Sparkles className="w-8 h-8 text-indigo-300 mx-auto" />
              <p className="text-sm font-bold text-gray-500">
                {isBrazil ? 'Nenhuma postagem encontrada' : 'No posts found in calendar'}
              </p>
              <p className="text-xs text-gray-400">
                {isBrazil ? 'Gere um plano de conteúdo para ver as postagens listadas.' : 'Generate a content plan to see listed posts.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Interactive Post Preview Modal popup */}
      {selectedPost && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
            
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <span className="text-xs font-semibold text-indigo-600 tracking-wider uppercase">
                  Post Preview
                </span>
                <h4 className="text-base font-bold text-gray-900">
                  {format(parseLocalDate(selectedPost.date), 'EEEE, MMMM d, yyyy')}
                </h4>
              </div>
              <button 
                onClick={() => setSelectedPostId(null)}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Caption / Details Left */}
                <div className="flex flex-col space-y-4">
                  {/* Platforms & Status row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      {selectedPost.platforms.map((plat, idx) => (
                        <span key={`${plat}-${idx}`} className="p-1 bg-slate-100 rounded-md text-slate-700" title={plat}>
                          {getPlatformIcon(plat)}
                        </span>
                      ))}
                    </div>
                    
                    {/* Status Pill Badge */}
                    <div>
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${
                        selectedPost.status === 'approved' ? 'bg-green-50 text-green-700 border border-green-100' :
                        selectedPost.status === 'rejected' ? 'bg-red-50 text-red-700 border border-red-100' :
                        selectedPost.status === 'scheduled' ? 'bg-blue-50 text-blue-700 border border-blue-100' :
                        'bg-amber-50 text-amber-700 border border-amber-100'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          selectedPost.status === 'approved' ? 'bg-green-500' :
                          selectedPost.status === 'rejected' ? 'bg-red-500' :
                          selectedPost.status === 'scheduled' ? 'bg-blue-500' :
                          'bg-amber-500'
                        }`} />
                        {selectedPost.status || 'pending'}
                      </span>
                    </div>
                  </div>

                  {/* Editing vs View container */}
                  {isEditing ? (
                    <div className="space-y-3">
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                        Post Caption
                      </label>
                      <textarea
                        value={editCopy}
                        onChange={(e) => setEditCopy(e.target.value)}
                        className="w-full min-h-[140px] p-3 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:outline-none focus:border-indigo-500"
                        placeholder="Write your post caption here..."
                      />
                      
                      <div className="grid grid-cols-2 gap-3">
                        {/* Status selector within edit mode */}
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                            Status
                          </label>
                          <select
                            value={editStatus}
                            onChange={(e: any) => setEditStatus(e.target.value)}
                            className="w-full px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          >
                            <option value="pending">Pending Review</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="scheduled">Scheduled</option>
                          </select>
                        </div>

                        {/* Format selector within edit mode */}
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                            Format Type
                          </label>
                          <select
                            value={editFormat}
                            onChange={(e: any) => setEditFormat(e.target.value)}
                            className="w-full px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          >
                            <option value="feed">Feed Post</option>
                            <option value="story">Story</option>
                            <option value="carousel">Carousel</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-50 border border-slate-100 p-4 rounded-2xl relative group max-h-[220px] overflow-y-auto">
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(selectedPost.copy);
                          setCopiedText(true);
                          setTimeout(() => setCopiedText(false), 2000);
                        }}
                        className="absolute top-2 right-2 p-1.5 bg-white shadow-sm border border-slate-100 text-gray-400 hover:text-indigo-600 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                        title="Copy caption"
                      >
                        {copiedText ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                      <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap select-text pr-6 font-sans">
                        {selectedPost.copy}
                      </p>
                    </div>
                  )}

                  {/* Metadata row */}
                  <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-100/50">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5 text-indigo-500" />
                      Best time: {selectedPost.bestTimeToPost || 'Auto-Optimized'}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <span className="capitalize px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-md font-semibold tracking-wide">
                        {selectedPost.format || 'feed'}
                      </span>
                      <span className="capitalize px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md font-semibold tracking-wide">
                        {selectedPost.type || 'Standard'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Slides/Artwork Preview Right */}
                <div className="flex flex-col bg-slate-900 rounded-2xl p-4 text-white items-center justify-center min-h-[250px] relative overflow-hidden group/slide">
                  {selectedPost.slides && selectedPost.slides.length > 0 ? (
                    <div className="w-full h-full flex flex-col justify-between">
                      
                      {/* Visual Slide Display */}
                      <div className="flex-1 flex items-center justify-center relative min-h-[180px]">
                        {selectedPost.slides[activeSlideIndex]?.isVideo ? (
                          <div className="relative w-full h-full flex items-center justify-center max-h-[180px] overflow-hidden rounded-xl">
                            <video 
                              src={selectedPost.slides[activeSlideIndex]?.url} 
                              className="max-w-full max-h-full object-contain" 
                              controls
                            />
                          </div>
                        ) : selectedPost.slides[activeSlideIndex]?.url || selectedPost.slides[activeSlideIndex]?.thumbnailUrl ? (
                          <div className="relative" style={{ containerType: "inline-size" }}>
                            <img
                              src={(selectedPost.slides[activeSlideIndex] as any)?.backgroundSliceUrl || selectedPost.slides[activeSlideIndex]?.url || selectedPost.slides[activeSlideIndex]?.thumbnailUrl}
                              alt={`Slide ${activeSlideIndex + 1}`}
                              referrerPolicy="no-referrer"
                              className="max-w-full max-h-[200px] object-contain rounded-xl shadow-lg cursor-zoom-in hover:brightness-105 transition-all"
                              onClick={() => onPreview?.(
                                selectedPost.slides[activeSlideIndex].url || selectedPost.slides[activeSlideIndex].thumbnailUrl!, 
                                selectedPost.slides[activeSlideIndex].isVideo ? 'video' : 'image',
                                selectedPost.slides[activeSlideIndex],
                                selectedPost.format
                              )}
                              title="Click to view full screen"
                            />
                            {(selectedPost.slides[activeSlideIndex] as any)?.layoutTemplate && (
                              <RenderSlideOverlay 
                                slide={selectedPost.slides[activeSlideIndex] as any} 
                                brand={brand} 
                                slideIndex={activeSlideIndex} 
                                totalSlides={selectedPost.slides.length} 
                              />
                            )}
                          </div>
                        ) : (
                          <div className="text-center text-slate-400 text-xs flex flex-col items-center gap-2">
                            <ImageIcon className="w-8 h-8 text-slate-600" />
                            <span>{selectedPost.slides[activeSlideIndex]?.concept || 'Blank design concept'}</span>
                          </div>
                        )}
                      </div>

                      {/* Paginated Controller if multiple slides */}
                      {selectedPost.slides.length > 1 && (
                        <div className="flex items-center justify-between px-2 pt-2 border-t border-white/10 mt-2">
                          <button 
                            onClick={() => setActiveSlideIndex(prev => Math.max(0, prev - 1))}
                            disabled={activeSlideIndex === 0}
                            className="p-1 bg-white/10 hover:bg-white/20 disabled:opacity-30 rounded-lg transition-colors text-white"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                          <span className="text-[10px] font-mono tracking-widest text-slate-400">
                            SLIDE {activeSlideIndex + 1} OF {selectedPost.slides.length}
                          </span>
                          <button 
                            onClick={() => setActiveSlideIndex(prev => Math.min(selectedPost.slides.length - 1, prev + 1))}
                            disabled={activeSlideIndex === selectedPost.slides.length - 1}
                            className="p-1 bg-white/10 hover:bg-white/20 disabled:opacity-30 rounded-lg transition-colors text-white"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-slate-400 space-y-2">
                      <ImageIcon className="w-10 h-10 text-slate-600 mx-auto" />
                      <p className="text-xs">No media assets generated for this post.</p>
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* Footer controls */}
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-slate-50 gap-4">
              {isEditing ? (
                <div className="flex items-center gap-2 w-full justify-between">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200/50 rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (onSavePost) {
                        onSavePost(selectedPost.id, {
                          ...selectedPost,
                          copy: editCopy,
                          status: editStatus,
                          format: editFormat,
                          slides: editSlides
                        });
                        // Also update our local preview values
                        setSelectedPostId(null);
                        setIsEditing(false);
                      }
                    }}
                    className="px-5 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-all flex items-center gap-1.5 shadow-md shadow-indigo-100"
                  >
                    <Save className="w-4 h-4" />
                    Save Changes
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    {selectedPost.status !== 'approved' && (
                      <button
                        onClick={() => {
                          if (onSavePost) {
                            onSavePost(selectedPost.id, { ...selectedPost, status: 'approved' });
                            setSelectedPostId(null);
                          }
                        }}
                        className="px-4 py-2 text-xs font-bold bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 rounded-xl transition-all flex items-center gap-1"
                      >
                        <Check className="w-3.5 h-3.5" />
                        Approve Post
                      </button>
                    )}
                    
                    <button
                      onClick={() => {
                        setIsEditing(true);
                        setEditCopy(selectedPost.copy);
                        setEditStatus(selectedPost.status || 'pending');
                        setEditFormat(selectedPost.format || 'feed');
                        setEditSlides(selectedPost.slides || []);
                      }}
                      className="px-4 py-2 text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 hover:bg-indigo-100 rounded-xl transition-all flex items-center gap-1"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      Edit Post
                    </button>
                    {onDeletePost && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            if (!showDeleteConfirm) {
                              setShowDeleteConfirm(true);
                            } else {
                              onDeletePost(selectedPost.id);
                              setSelectedPostId(null);
                              setShowDeleteConfirm(false);
                            }
                          }}
                          className={`px-4 py-2 text-xs font-semibold border rounded-xl transition-all flex items-center gap-1 ${
                            showDeleteConfirm 
                              ? 'bg-red-600 text-white border-red-700 hover:bg-red-700 animate-pulse' 
                              : 'bg-red-50 text-red-700 border-red-100 hover:bg-red-100'
                          }`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          {showDeleteConfirm ? 'Confirm Delete' : 'Delete'}
                        </button>
                        {showDeleteConfirm && (
                          <button
                            onClick={() => setShowDeleteConfirm(false)}
                            className="p-2 hover:bg-gray-100 rounded-xl transition-all text-gray-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => {
                      setSelectedPostId(null);
                      setShowDeleteConfirm(false);
                    }}
                    className="px-4 py-2 text-sm font-bold bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-xl transition-all"
                  >
                    Close
                  </button>
                </>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

// --- UI Primitives ---

const Input = ({ label, value, onChange, placeholder, required = false, type = "text", description, maxLength = 1000 }: { label: string, value?: string, onChange: (v: string) => void, placeholder?: string, required?: boolean, type?: string, description?: string, maxLength?: number }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <span className="text-[10px] text-gray-400 font-medium">
        {(value || '').length} / {maxLength}
      </span>
    </div>
    <input 
      type={type} 
      value={value || ''} 
      onChange={e => onChange(e.target.value.slice(0, maxLength))} 
      placeholder={placeholder}
      required={required}
      maxLength={maxLength}
      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all bg-gray-50 text-gray-900"
    />
    {description && <p className="text-xs text-gray-400">{description}</p>}
  </div>
);

const TextArea = ({ label, value, onChange, placeholder, required = false, description, maxLength = 10000 }: { label: string, value?: string, onChange: (v: string) => void, placeholder?: string, required?: boolean, description?: string, maxLength?: number }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <span className="text-[10px] text-gray-400 font-medium">
        {(value || '').length} / {maxLength}
      </span>
    </div>
    <textarea 
      value={value || ''} 
      onChange={e => onChange(e.target.value.slice(0, maxLength))} 
      placeholder={placeholder}
      required={required}
      maxLength={maxLength}
      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all bg-gray-50 min-h-[100px] text-gray-900"
    />
    {description && <p className="text-xs text-gray-400">{description}</p>}
  </div>
);

const AddPostModal = ({ 
  onClose, 
  onAdd, 
  isAdding,
  initialDescription = '',
  initialDate = format(new Date(), 'yyyy-MM-dd'),
  availablePlans = [],
  initialPlanId,
  brand,
  setToast,
  brandBlueprints = []
}: { 
  onClose: () => void, 
  onAdd: (description: string, date: string, planId: string, preferredFormat?: 'feed' | 'story' | 'carousel', manualData?: { copy: string; platforms: string[]; format: 'feed' | 'story' | 'carousel' }, aiPlatforms?: string[], visualDirection?: string) => void, 
  isAdding: boolean,
  initialDescription?: string,
  initialDate?: string,
  availablePlans: any[],
  initialPlanId?: string | null,
  brand: any,
  setToast: (t: { message: string, type: 'success' | 'error' | 'warning', duration?: number | null } | null) => void,
  brandBlueprints?: any[]
}) => {
  const { t, isBrazil } = useTranslation();
  const [mode, setMode] = useState<'ai' | 'manual'>('ai');
  const [description, setDescription] = useState(initialDescription);
  const [date, setDate] = useState(initialDate);
  const [selectedPlanId, setSelectedPlanId] = useState<string>(initialPlanId || (availablePlans[0]?.id || ''));
  const [preferredFormat, setPreferredFormat] = useState<'feed' | 'story' | 'carousel' | undefined>(undefined);
  const [visualDirection, setVisualDirection] = useState<string>('');
  
  // Manual states
  const [manualCopy, setManualCopy] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [manualFormat, setManualFormat] = useState<'feed' | 'story' | 'carousel'>('feed');

  useEffect(() => {
    if (initialDescription) {
      setDescription(initialDescription);
    }
  }, [initialDescription]);

  useEffect(() => {
    if (initialPlanId) {
      setSelectedPlanId(initialPlanId);
    }
  }, [initialPlanId]);

  const selectedPlan = availablePlans.find(p => p.id === selectedPlanId);

  const handleSubmit = () => {
    if (selectedPlatforms.length === 0) {
      setToast({
        message: isBrazil 
          ? "⚠️ Por favor, selecione pelo menos um canal de destino antes de continuar." 
          : "⚠️ Please select at least one target channel before continuing.",
        type: 'warning'
      });
      return;
    }

    if (mode === 'ai') {
      if (!description.trim()) return;
      onAdd(description, date, selectedPlanId, preferredFormat, undefined, selectedPlatforms, visualDirection || undefined);
    } else {
      if (!manualCopy.trim()) return;
      onAdd('', date, selectedPlanId, undefined, { copy: manualCopy, platforms: selectedPlatforms, format: manualFormat });
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl"
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-indigo-600 text-white font-sans">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-xl">
              <Plus className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold">{t.addCustomPost}</h3>
              {selectedPlan && (
                <p className="text-indigo-100 text-xs font-semibold uppercase tracking-wider">Target: {selectedPlan.month} {selectedPlan.year}</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-all cursor-pointer">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-8 space-y-6 max-h-[75vh] overflow-y-auto">
          {/* Segmented Control for Mode selection: AI vs Manual */}
          <div className="grid grid-cols-2 p-1 bg-gray-100 rounded-2xl">
            <button
              type="button"
              onClick={() => setMode('ai')}
              className={`py-2 rounded-xl text-sm font-bold transition-all cursor-pointer ${
                mode === 'ai' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {isBrazil ? "Gerar com IA" : "Generate with AI"}
            </button>
            <button
              type="button"
              onClick={() => setMode('manual')}
              className={`py-2 rounded-xl text-sm font-bold transition-all cursor-pointer ${
                mode === 'manual' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {isBrazil ? "Criar Manualmente" : "Create Manually"}
            </button>
          </div>

          {availablePlans.length > 1 && (
            <div className="space-y-2">
              <label className="text-sm font-bold text-gray-700">Select Target Plan</label>
              <select 
                value={selectedPlanId}
                onChange={e => setSelectedPlanId(e.target.value)}
                className="w-full p-4 rounded-2xl border border-gray-200 outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm text-black bg-white"
              >
                {availablePlans.map(p => (
                  <option key={p.id} value={p.id}>{p.month} {p.year}</option>
                ))}
              </select>
            </div>
          )}

          {mode === 'ai' ? (
            <>
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700">{t.postTopic}</label>
                <textarea 
                  value={description}
                  onChange={e => setDescription(e.target.value.slice(0, 1000))}
                  placeholder={t.postTopicPlaceholder}
                  maxLength={1000}
                  className="w-full h-32 p-4 rounded-2xl border border-gray-200 outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm resize-none text-black placeholder:text-gray-400"
                />
                <div className="flex justify-end">
                  <span className="text-[10px] text-gray-400 font-medium">
                    {description.length} / 1000
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">{t.date}</label>
                  <input 
                    type="date"
                    value={date}
                    onChange={e => setDate(e.target.value)}
                    className="w-full p-4 rounded-2xl border border-gray-200 outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm text-black"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">{t.format}</label>
                  <select 
                    value={preferredFormat || ''}
                    onChange={e => setPreferredFormat(e.target.value as any || undefined)}
                    className="w-full p-4 rounded-2xl border border-gray-200 outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm text-black bg-white"
                  >
                    <option value="">{t.aiSelectsBest}</option>
                    <option value="feed">{t.singleFeedPost}</option>
                    <option value="story">{t.singleStoryPost}</option>
                    <option value="carousel">{t.carousel}</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2 mt-2">
                <label className="text-sm font-bold text-gray-700">
                  {isBrazil ? "Estilo Visual / Direção" : "Visual Direction / Style"}
                </label>
                <select 
                  value={visualDirection}
                  onChange={e => setVisualDirection(e.target.value)}
                  className="w-full p-4 rounded-2xl border border-gray-200 outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm text-black bg-white"
                >
                  <optgroup label="General Directions">
                    <option value="">{isBrazil ? "Qualquer Estilo (IA Escolhe)" : "Any Style (AI decides)"}</option>
                    <option value="standard product/lifestyle photography">Standard / Lifestyle Image</option>
                    <option value="text-heavy card with large typography">Text Card (Heavy Typography)</option>
                    <option value="inspirational quote card layout">Quote Card</option>
                    <option value="before and after comparison">Before & After</option>
                    <option value="meme format">Meme / Humor</option>
                    <option value="infographic / data visualization">Infographic / Diagram</option>
                  </optgroup>
                  {brandBlueprints.length > 0 && (
                    <optgroup label="Brand Custom Blueprints">
                      {brandBlueprints.map(b => (
                        <option key={b.id} value={`Apply Custom Template Blueprint: ${b.narrative}`}>{b.name || `${b.category} Blueprint`}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
            </>
          ) : (
            <>
              {/* Manual Mode Fields */}
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700 font-sans">
                  {isBrazil ? "Texto do Post (Legenda)" : "Post Caption / Copy"}
                </label>
                <textarea 
                  value={manualCopy}
                  onChange={e => setManualCopy(e.target.value.slice(0, 2200))}
                  placeholder={isBrazil ? "Digite o texto do post aqui..." : "Write your post copy or caption here..."}
                  maxLength={2200}
                  className="w-full h-32 p-4 rounded-2xl border border-gray-200 outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm resize-none text-black placeholder:text-gray-400"
                />
                <div className="flex justify-end">
                  <span className="text-[10px] text-gray-400 font-medium font-mono">
                    {manualCopy.length} / 2200
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 font-sans">
                  <label className="text-sm font-bold text-gray-700 font-sans">{t.date}</label>
                  <input 
                    type="date"
                    value={date}
                    onChange={e => setDate(e.target.value)}
                    className="w-full p-4 rounded-2xl border border-gray-200 outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm text-black"
                  />
                </div>

                <div className="space-y-2 font-sans">
                  <label className="text-sm font-bold text-gray-700 font-sans">{t.format}</label>
                  <select 
                    value={manualFormat}
                    onChange={e => setManualFormat(e.target.value as any)}
                    className="w-full p-4 rounded-2xl border border-gray-200 outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm text-black bg-white"
                  >
                    <option value="feed">{t.singleFeedPost}</option>
                    <option value="story">{t.singleStoryPost}</option>
                    <option value="carousel">{t.carousel}</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Channels Based on brand's active platforms */}
          <div className="space-y-2 font-sans">
            <label className="text-sm font-bold text-gray-700">
              {isBrazil ? "Canais de Destino" : "Target Channels"}
            </label>
            {(!brand?.platforms || brand.platforms.length === 0) ? (
              <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded-xl border border-amber-100">
                {isBrazil 
                  ? "Nenhum canal ativo encontrado no perfil da marca. Ative canais em Perfil da Marca primeiro." 
                  : "No active channels found in your brand profile. Please enable channels in your Brand Profile first."}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2 text-sans">
                {brand.platforms.map((p: string) => {
                  const isSelected = selectedPlatforms.includes(p);
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        if (isSelected) {
                          setSelectedPlatforms(prev => prev.filter(item => item !== p));
                        } else {
                          setSelectedPlatforms(prev => [...prev, p]);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all border flex items-center gap-1.5 cursor-pointer ${
                        isSelected 
                          ? 'bg-indigo-600 border-indigo-600 text-white' 
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {isSelected && <Check className="w-3.5 h-3.5 shrink-0" />}
                      <span>{p}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex gap-4 pt-4 font-sans">
            <button 
              type="button"
              onClick={onClose}
              className="flex-1 py-4 bg-gray-100 text-gray-600 rounded-2xl font-bold hover:bg-gray-200 transition-all cursor-pointer"
            >
              {t.cancel}
            </button>
            
            {mode === 'ai' ? (
              <button 
                type="button"
                onClick={handleSubmit}
                disabled={isAdding || !description.trim() || !selectedPlanId}
                className="flex-[2] py-4 bg-indigo-600 text-white rounded-2xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
              >
                {isAdding ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{t.generating}</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    <span>{t.generateAddPost}</span>
                  </>
                )}
              </button>
            ) : (
              <button 
                type="button"
                onClick={handleSubmit}
                disabled={isAdding || !manualCopy.trim() || !selectedPlanId}
                className="flex-[2] py-4 bg-indigo-600 text-white rounded-2xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
              >
                {isAdding ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{isBrazil ? "Adicionando..." : "Adding..."}</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    <span>{isBrazil ? "Adicionar Post" : "Add Custom Post"}</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

